var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/types.ts
var DEFAULT_ORDER_OPERATIONAL_MODEL = "hopdrive-managed";
var NO_ROOFTOP_FACTS = {
  id: 0,
  name: null,
  operationalModel: DEFAULT_ORDER_OPERATIONAL_MODEL,
  timezone: null,
  locationId: null,
  effectiveConfig: null
};
var ORDER_GROUP_NONE = "none";

// src/shared/platform/OrderIndexPlatformProvider.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var OrderIndexPlatformContext = createContext(null);
var OrderIndexPlatformProvider = ({ platform, children }) => /* @__PURE__ */ jsx(OrderIndexPlatformContext.Provider, { value: platform, children });
var useOrderIndexPlatform = () => {
  const platform = useContext(OrderIndexPlatformContext);
  if (!platform) throw new Error("OrderIndex renderers must be mounted inside an OrderIndexPlatformProvider.");
  return platform;
};

// src/shared/renderers/OrderRendererRegistryProvider.tsx
import { createContext as createContext2, useContext as useContext2 } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var OrderRendererRegistryContext = createContext2(null);
var OrderRendererRegistryProvider = ({ renderers, children }) => /* @__PURE__ */ jsx2(OrderRendererRegistryContext.Provider, { value: renderers, children });
var useOrderRenderers = () => {
  const renderers = useContext2(OrderRendererRegistryContext);
  if (!renderers) throw new Error("OrderIndex renderers must be mounted inside an OrderRendererRegistryProvider.");
  return renderers;
};

// src/shared/renderers/registry.ts
var isPlugin = (source) => typeof source.resolve === "function";
var toContribution = (source) => isPlugin(source) ? source.renderers ?? {} : source;
var cloneRegistry = (registry) => ({
  items: { ...registry.items },
  cells: { ...registry.cells },
  bodies: { ...registry.bodies },
  fallbackBody: registry.fallbackBody
});
var foldContribution = (registry, contribution) => ({
  items: { ...registry.items, ...contribution.items },
  cells: { ...registry.cells, ...contribution.cells },
  bodies: { ...registry.bodies, ...contribution.bodies },
  fallbackBody: registry.fallbackBody
});
var composeRenderers = (base, sources) => sources.map(toContribution).reduce(foldContribution, cloneRegistry(base));

// src/sharedRenderers.ts
import { createElement as createElement2 } from "react";

// src/OrderTracker/OrderTracker.tsx
import { Box as Box2, Typography } from "@mui/material";
import { BiErrorCircle } from "react-icons/bi";

// src/OrderTooltip/OrderTooltip.tsx
import { Box, Tooltip } from "@mui/material";

// src/shared/hooks/useOrderTokens.ts
import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";

// src/shared/appearance/tokens.ts
var ORDER_STATUS_TOKEN = {
  QUEUED: "queued",
  PLANNED: "planned",
  DRIVING: "driving",
  ON_SITE: "onSite",
  DONE: "done",
  CANCEL_PENDING: "cancelPending",
  CANCELLED: "cancelled"
};
var blendOnto = (color, base, alpha) => {
  const parse = (hex) => {
    const clean = hex.replace("#", "").slice(0, 6).padEnd(6, "0");
    return [0, 2, 4].map((index) => Number.parseInt(clean.slice(index, index + 2), 16));
  };
  const clamped = Math.min(1, Math.max(0, alpha));
  const [foreground, background] = [parse(color), parse(base)];
  return `#${foreground.map(
    (channel, index) => Math.round(channel * clamped + background[index] * (1 - clamped)).toString(16).padStart(2, "0")
  ).join("")}`;
};
var withAlpha = (color, alpha) => {
  const hex = color.replace("#", "").slice(0, 6).padEnd(6, "0");
  const clamped = Math.min(1, Math.max(0, alpha));
  const suffix = Math.round(clamped * 255).toString(16).padStart(2, "0");
  return `#${hex}${suffix}`;
};
var ON_DARK = "#ffffff";
var resolveOrderTokens = (theme) => {
  const { palette, shape } = theme;
  const ink = palette.text.primary;
  const baseRadius = typeof shape.borderRadius === "number" ? shape.borderRadius : Number.parseFloat(shape.borderRadius) || 6;
  return {
    surfaces: {
      page: palette.background.default,
      card: palette.background.paper,
      // The theme has no name for these, so each is its ink at a fixed alpha.
      legBackground: withAlpha(ink, 0.04),
      chipNeutral: withAlpha(ink, 0.06),
      chipStrong: withAlpha(ink, 0.12),
      skeleton: withAlpha(ink, 0.08),
      // The grounding ink at 88%, which the blur behind it turns into glass.
      tooltip: withAlpha(ink, 0.88),
      sectionHeader: palette.utility.light ?? palette.utility.main
    },
    text: {
      primary: palette.text.primary,
      secondary: palette.text.secondary,
      muted: palette.text.disabled,
      // Fainter than `secondary`, for a label whose value carries the emphasis.
      labelFaded: withAlpha(ink, 0.45),
      // The tooltip panel is dark in every theme, so its text cannot come from the page's ramp.
      onTooltip: ON_DARK,
      onTooltipMuted: withAlpha(ON_DARK, 0.72),
      onTooltipLabel: withAlpha(ON_DARK, 0.55),
      // The utility fill is dark in every theme too.
      onSectionHeader: withAlpha(ON_DARK, 0.85),
      onSectionHeaderMuted: withAlpha(ON_DARK, 0.6),
      onBadge: ON_DARK
    },
    lines: {
      divider: palette.divider,
      // At the strength of MUI's outlined border, opaque so outer borders and seams match.
      control: blendOnto(ink, palette.background.paper, 0.23),
      // An unreached bar reads as absent; reached bars take the status colour, so there is no neutral "on".
      barOff: withAlpha(ink, 0.12),
      slash: withAlpha(ON_DARK, 0.45),
      onTooltip: withAlpha(ON_DARK, 0.16),
      shimmerBadge: withAlpha(ON_DARK, 0.35),
      shimmerLine: withAlpha(ON_DARK, 0.4)
    },
    service: {
      concierge: palette.concierge.main,
      loaner: palette.loaner.main,
      operational: palette.ops.main,
      auto: palette.auto.main
    },
    status: {
      [ORDER_STATUS_TOKEN.QUEUED]: palette.utility.main,
      [ORDER_STATUS_TOKEN.PLANNED]: palette.info.main,
      // Driving and on-site share a colour; the badge word already makes the distinction.
      [ORDER_STATUS_TOKEN.DRIVING]: palette.warning.dark ?? palette.warning.main,
      [ORDER_STATUS_TOKEN.ON_SITE]: palette.warning.dark ?? palette.warning.main,
      [ORDER_STATUS_TOKEN.DONE]: palette.success.main,
      [ORDER_STATUS_TOKEN.CANCEL_PENDING]: palette.error.main,
      [ORDER_STATUS_TOKEN.CANCELLED]: palette.error.main
    },
    semantic: {
      info: palette.info.main,
      success: palette.success.main,
      warningStrong: palette.warning.dark ?? palette.warning.main,
      warning: palette.warning.main,
      error: palette.error.main,
      dangerRing: withAlpha(palette.error.main, 0.45),
      dangerRingFaded: withAlpha(palette.error.main, 0),
      // Plain warning, not warning.dark: attention-toned, while driving/on-site keep the darker shade.
      attentionRing: palette.warning.main,
      attentionRingFaded: withAlpha(palette.warning.main, 0)
    },
    // `md` is not a doubling of the theme radius, because the signed-off card corner is 8px.
    radii: {
      sm: baseRadius,
      md: 8,
      lg: 12,
      pill: 9999
    },
    /**
     * Not MUI's scale. `shadows[2]` is a tight dark stack that reads as a raised control, so
     * a page of cards under it looks like a page of buttons. The portal has no custom shadow
     * scale to derive from. The ink is the theme's, so a themed grounding colour carries through.
     */
    shadows: {
      card: `0px 4px 12px ${withAlpha(ink, 0.08)}, 0px 2px 4px ${withAlpha(ink, 0.04)}`,
      // Menus and the photo modal take the next step up, not a different kind of shadow.
      floating: `0px 8px 24px ${withAlpha(ink, 0.1)}, 0px 4px 8px ${withAlpha(ink, 0.05)}`,
      // On the dark glass panel the card's soft bloom would disappear, so the tooltip keeps the deeper step.
      tooltip: `0px 8px 24px ${withAlpha(ink, 0.1)}, 0px 4px 8px ${withAlpha(ink, 0.05)}`,
      photoVignette: `inset 0 0 0 1px ${withAlpha(ink, 0.08)}, inset 0 0 14px ${withAlpha(ink, 0.22)}`
    }
  };
};

// src/shared/hooks/useOrderTokens.ts
var useOrderTokens = () => {
  const theme = useTheme();
  return useMemo(() => resolveOrderTokens(theme), [theme]);
};

// src/OrderTooltip/OrderTooltip.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var OrderTooltipBody = ({ content }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx3(Box, { sx: { fontSize: "11px", lineHeight: 1.4 }, children: content.map((node, index) => {
    switch (node.kind) {
      case "title":
        return /* @__PURE__ */ jsx3(
          Box,
          {
            sx: {
              fontWeight: 700,
              fontSize: "11.5px",
              borderBottom: `1px solid ${tokens.lines.onTooltip}`,
              pb: "4px",
              mb: "5px"
            },
            children: node.text
          },
          index
        );
      case "section":
        return /* @__PURE__ */ jsx3(Box, { sx: { fontWeight: 700, fontSize: "10.5px", mb: "1px" }, children: node.text }, index);
      case "row":
        return /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", gap: "14px" }, children: [
          /* @__PURE__ */ jsx3(
            Box,
            {
              component: "span",
              sx: {
                color: tokens.text.onTooltipLabel,
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                lineHeight: 1.9
              },
              children: node.key
            }
          ),
          /* @__PURE__ */ jsx3(Box, { component: "span", sx: { fontWeight: 600, whiteSpace: "nowrap" }, children: node.value })
        ] }, index);
      case "divider":
        return /* @__PURE__ */ jsx3(Box, { sx: { height: "1px", bgcolor: tokens.lines.onTooltip, my: "6px" } }, index);
      case "description":
        return /* @__PURE__ */ jsx3(
          Box,
          {
            sx: {
              color: tokens.text.onTooltipMuted,
              // Only when it follows rows: a description-only tip needs no gap.
              mt: content.some((other) => other.kind === "row") ? "5px" : 0
            },
            children: node.text
          },
          index
        );
      default:
        return null;
    }
  }) });
};
var OrderTooltip = ({ content, children, placement = "top" }) => {
  const tokens = useOrderTokens();
  if (!content || !content.length) return children;
  return /* @__PURE__ */ jsx3(
    Tooltip,
    {
      placement,
      title: /* @__PURE__ */ jsx3(OrderTooltipBody, { content }),
      slotProps: {
        tooltip: {
          sx: {
            maxWidth: 480,
            p: "8px 10px",
            borderRadius: `${tokens.radii.md}px`,
            bgcolor: tokens.surfaces.tooltip,
            backdropFilter: "blur(12px)",
            color: tokens.text.onTooltip,
            boxShadow: tokens.shadows.tooltip
          }
        }
      },
      children
    }
  );
};
var OrderTooltip_default = OrderTooltip;

// src/OrderTracker/OrderTracker.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var TRACKER_SCALES = {
  card: { height: "7px", gap: "4px", overhang: "-3px" },
  mini: { height: "5px", gap: "3px", overhang: "-2px" }
};
var SKEW = "skewX(-22.5deg)";
var BAR_PULSE_KEYFRAMES = {
  "@keyframes order-tracker-pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.35 }
  }
};
var OrderTrackerSlipStretch = ({ fraction, slash }) => /* @__PURE__ */ jsx4(
  Box2,
  {
    sx: {
      position: "absolute",
      top: 0,
      bottom: 0,
      right: 0,
      width: `${Math.round(fraction * 100)}%`,
      background: `repeating-linear-gradient(135deg, ${slash} 0 3px, transparent 3px 6px)`
    }
  }
);
var OrderTrackerSegment = ({ bar, isFirst, isLast, color, scale, showSlip }) => {
  const tokens = useOrderTokens();
  const geometry = TRACKER_SCALES[scale];
  const isActive = bar.isReached || bar.isDrivingToward;
  const slipFraction = showSlip ? bar.slipFraction : 0;
  return /* @__PURE__ */ jsx4(OrderTooltip_default, { content: bar.tooltip, children: /* @__PURE__ */ jsx4(
    Box2,
    {
      sx: {
        flex: 1,
        height: geometry.height,
        backgroundColor: isActive ? color : tokens.lines.barOff,
        transform: SKEW,
        // Clipped to the bar's slant, so the slashes stop at the break instead of running under the neighbour.
        position: "relative",
        overflow: "hidden",
        transition: "background-color 200ms ease-in-out",
        ...isFirst ? { marginLeft: geometry.overhang } : {},
        ...isLast ? { marginRight: geometry.overhang } : {},
        ...bar.isDrivingToward ? { ...BAR_PULSE_KEYFRAMES, animation: "order-tracker-pulse 1.2s ease-in-out infinite" } : {}
      },
      children: slipFraction > 0 ? /* @__PURE__ */ jsx4(OrderTrackerSlipStretch, { fraction: slipFraction, slash: tokens.lines.slash }) : null
    }
  ) });
};
var OrderTrackerStatusLine = ({ statusLine, isLive }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx4(
    Box2,
    {
      sx: {
        backgroundColor: tokens.surfaces.legBackground,
        borderRadius: `0 0 ${tokens.radii.sm}px ${tokens.radii.sm}px`,
        padding: "0 8px 3px",
        width: "fit-content",
        // Capped so a long stop name cannot widen the tab into the bar row above it.
        maxWidth: "67%",
        margin: "0 auto",
        lineHeight: 1
      },
      children: /* @__PURE__ */ jsx4(
        Typography,
        {
          component: "span",
          sx: {
            display: "block",
            // Pulled up a pixel to close the gap the cap-height leaves under the bars.
            marginTop: "-1px",
            fontSize: "9.5px",
            fontWeight: 700,
            textTransform: "uppercase",
            color: isLive ? tokens.text.secondary : tokens.text.muted,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          },
          children: statusLine
        }
      )
    }
  );
};
var OrderTracker = ({ bars, status, statusLine, isLive, scale = "card", isChainBroken = false }) => {
  const tokens = useOrderTokens();
  if (isChainBroken) {
    return /* @__PURE__ */ jsx4(
      OrderTooltip_default,
      {
        content: [
          { kind: "title", text: "Progress unavailable" },
          { kind: "description", text: "This order's stops don't connect in sequence, so its progress can't be drawn." }
        ],
        children: /* @__PURE__ */ jsxs2(
          Box2,
          {
            sx: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              backgroundColor: tokens.surfaces.legBackground,
              borderRadius: `${tokens.radii.pill}px`,
              padding: scale === "mini" ? "2px 6px" : "5px 8px",
              ...scale === "card" ? { margin: "2px 0 8px" } : {},
              color: tokens.text.muted
            },
            children: [
              /* @__PURE__ */ jsx4(BiErrorCircle, { size: scale === "mini" ? 9 : 11, "aria-hidden": true, style: { flex: "none" } }),
              /* @__PURE__ */ jsx4(
                Typography,
                {
                  component: "span",
                  sx: {
                    fontSize: scale === "mini" ? "8.5px" : "9.5px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    lineHeight: 1,
                    color: "inherit",
                    whiteSpace: "nowrap"
                  },
                  children: "Progress unavailable"
                }
              )
            ]
          }
        )
      }
    );
  }
  if (!bars.length) return null;
  const color = tokens.status[status];
  const geometry = TRACKER_SCALES[scale];
  const showSlip = scale === "card";
  const segments = /* @__PURE__ */ jsx4(
    Box2,
    {
      sx: {
        display: "flex",
        gap: geometry.gap,
        // This clip hides the end bars' overhang.
        borderRadius: `${tokens.radii.pill}px`,
        overflow: "hidden",
        ...scale === "mini" ? { margin: "0 2px" } : {}
      },
      children: bars.map((bar, index) => /* @__PURE__ */ jsx4(OrderTrackerSegment, { bar, isFirst: index === 0, isLast: index === bars.length - 1, color, scale, showSlip }, bar.key))
    }
  );
  if (scale === "mini") return segments;
  return /* @__PURE__ */ jsxs2(Box2, { sx: { display: "flex", flexDirection: "column", margin: "2px 0 8px" }, children: [
    /* @__PURE__ */ jsx4(Box2, { sx: { backgroundColor: tokens.surfaces.legBackground, borderRadius: `${tokens.radii.pill}px`, padding: "6px" }, children: segments }),
    /* @__PURE__ */ jsx4(OrderTrackerStatusLine, { statusLine, isLive })
  ] });
};
var OrderTracker_default = OrderTracker;

// src/OrderCard/items/index.ts
import { createElement } from "react";
import { Box as Box3 } from "@mui/material";
var renderOrderItem = (items, item, onOpenPhotos) => {
  const Renderer = items[item.kind];
  return Renderer ? createElement(Renderer, { key: item.key, props: item.props, onOpenPhotos }) : null;
};
var OrderCardItems = ({ items, onOpenPhotos }) => {
  const renderers = useOrderRenderers();
  return createElement(Box3, { sx: { display: "flex", flexDirection: "column", gap: "4px", py: "4px", maxWidth: 576 } }, items.map((item) => renderOrderItem(renderers.items, item, onOpenPhotos)));
};

// src/OrderCard/OrderCardDetails.tsx
import { Box as Box5 } from "@mui/material";

// src/OrderInfoBlock/OrderInfoBlock.tsx
import { Box as Box4, Typography as Typography2 } from "@mui/material";
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var OrderInfoBlock = ({ label, value, treatment = "actual", valueColor, tooltip = null }) => {
  const tokens = useOrderTokens();
  const valueStyle = {
    actual: { color: tokens.text.primary, fontWeight: 700 },
    estimate: { color: tokens.text.secondary, fontWeight: 700 },
    pending: { color: tokens.text.muted, fontWeight: 400 },
    plain: { color: tokens.text.primary, fontWeight: 400 }
  };
  return /* @__PURE__ */ jsx5(OrderTooltip_default, { content: tooltip, children: /* @__PURE__ */ jsxs3(
    Box4,
    {
      sx: {
        display: "flex",
        // Baseline, because the label is 10px and the value 12px. Centering would float
        // the label above its text.
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "6px",
        minWidth: 0
      },
      children: [
        /* @__PURE__ */ jsx5(
          Typography2,
          {
            component: "span",
            sx: {
              fontSize: "10.5px",
              fontWeight: 600,
              color: tokens.text.labelFaded,
              whiteSpace: "nowrap",
              // The label gives way. Without `minWidth: 0` a nowrap flex item will not shrink
              // below its content, and the truncation lands on the value instead.
              flexShrink: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis"
            },
            children: label
          }
        ),
        /* @__PURE__ */ jsx5(Box4, { sx: { flex: 1, minWidth: "8px", borderBottom: `1px dashed ${tokens.lines.divider}`, transform: "translateY(-2px)" } }),
        /* @__PURE__ */ jsx5(
          Typography2,
          {
            component: "span",
            sx: {
              fontSize: "12px",
              ...valueStyle[treatment],
              ...valueColor ? { color: valueColor } : {},
              whiteSpace: "nowrap",
              // Never shrink the value. A half-shown phone number is worse than a clipped
              // label. `maxWidth` only catches a value wider than the whole row.
              flexShrink: 0,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis"
            },
            children: value
          }
        )
      ]
    }
  ) });
};
var OrderInfoBlock_default = OrderInfoBlock;

// src/OrderCard/OrderCardDetails.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var OrderCardDetails = ({ rows }) => {
  const tokens = useOrderTokens();
  if (!rows.length) return null;
  return /* @__PURE__ */ jsx6(
    Box5,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        // Breaks out of the body's 12px inset for a full-width border, like the times grid.
        margin: "8px -12px 0",
        padding: "8px 12px 0",
        borderTop: `1px solid ${tokens.lines.divider}`
      },
      children: rows.map((row2) => /* @__PURE__ */ jsx6(OrderInfoBlock_default, { label: row2.label, value: row2.value, treatment: row2.isAccented ? "actual" : "plain", valueColor: row2.isAccented ? tokens.service.concierge : void 0 }, row2.key))
    }
  );
};
var OrderCardDetails_default = OrderCardDetails;

// src/OrderCard/OrderCardTimesGrid.tsx
import { Box as Box6 } from "@mui/material";
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var OrderTimesColumn = ({ slots }) => /* @__PURE__ */ jsx7(Box6, { sx: { display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }, children: slots.map((slot) => /* @__PURE__ */ jsx7(OrderInfoBlock_default, { label: slot.label, value: slot.value, treatment: slot.treatment, tooltip: slot.tooltip }, slot.key)) });
var OrderCardTimesGrid = ({ times }) => {
  const tokens = useOrderTokens();
  if (!times.leftColumn.length && !times.rightColumn.length) return null;
  return /* @__PURE__ */ jsxs4(
    Box6,
    {
      sx: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        columnGap: "16px",
        // Cancels the body's 12px inset so the top border spans the card, and the padding
        // puts the content back in line.
        margin: "8px -12px 0",
        padding: "8px 12px 0",
        borderTop: `1px solid ${tokens.lines.divider}`
      },
      children: [
        /* @__PURE__ */ jsx7(OrderTimesColumn, { slots: times.leftColumn }),
        /* @__PURE__ */ jsx7(OrderTimesColumn, { slots: times.rightColumn })
      ]
    }
  );
};
var OrderCardTimesGrid_default = OrderCardTimesGrid;

// src/OrderCard/bodies/OrderCardBody.tsx
import { Fragment, jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
var OrderCardBody = ({ tracker, items, details, times, onOpenPhotos }) => /* @__PURE__ */ jsxs5(Fragment, { children: [
  /* @__PURE__ */ jsx8(OrderTracker_default, { ...tracker }),
  /* @__PURE__ */ jsx8(OrderCardItems, { items, onOpenPhotos }),
  /* @__PURE__ */ jsx8(OrderCardDetails_default, { rows: details.rows }),
  /* @__PURE__ */ jsx8(OrderCardTimesGrid_default, { times })
] });
var OrderCardBody_default = OrderCardBody;

// src/OrderItemStopOverflow/OrderItemStopOverflow.tsx
import { Box as Box7, Typography as Typography3 } from "@mui/material";

// src/OrderItemStopBase/stopRowGeometry.ts
var STOP_ROW_GEOMETRY = {
  regular: { height: 64, compactHeight: 48, markerSize: 22, gutter: 8, gap: 8, paddingRight: 6, maxWidth: 480 },
  dense: { height: 32, compactHeight: 32, markerSize: 18, gutter: 8, gap: 8, paddingRight: 6, maxWidth: 480 }
};
var stopRowTextInset = (density) => {
  const { gutter, markerSize, gap } = STOP_ROW_GEOMETRY[density];
  return gutter + markerSize + gap;
};

// src/OrderItemStopOverflow/overflowLabel.ts
var buildOverflowLabel = (count, position) => `+${count} ${position === "before" ? "earlier" : "more"} ${count === 1 ? "stop" : "stops"}`;

// src/OrderItemStopOverflow/OrderItemStopOverflow.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var OrderItemStopOverflow = ({ count, position }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx9(
    Box7,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        height: STOP_ROW_GEOMETRY.dense.compactHeight,
        maxWidth: STOP_ROW_GEOMETRY.dense.maxWidth,
        pl: `${stopRowTextInset("dense")}px`,
        pr: `${STOP_ROW_GEOMETRY.dense.paddingRight}px`,
        bgcolor: tokens.surfaces.legBackground,
        borderRadius: `${tokens.radii.md}px`
      },
      children: /* @__PURE__ */ jsx9(Typography3, { component: "span", sx: { fontSize: "12px", fontWeight: 600, lineHeight: 1, color: tokens.text.muted }, children: buildOverflowLabel(count, position) })
    }
  );
};
var OrderItemStopOverflow_default = OrderItemStopOverflow;

// src/OrderIndexTable/orderTableCells.tsx
import React3 from "react";
import { Box as Box14, Typography as Typography8 } from "@mui/material";
import { BiFingerprint } from "react-icons/bi";

// src/OrderDriverPill/OrderDriverPill.tsx
import React from "react";
import { Box as Box8, Typography as Typography4 } from "@mui/material";
import { BiTime, BiX } from "react-icons/bi";
import { presentationForAssignmentState } from "@hopdrive/sdk-ui/offer-status";
import { Fragment as Fragment2, jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
var initialsOf = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
var OrderDriverAvatarImage = ({ url }) => {
  const tokens = useOrderTokens();
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return /* @__PURE__ */ jsx10(
    Box8,
    {
      component: "img",
      src: url,
      alt: "",
      onError: () => setFailed(true),
      sx: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", borderRadius: `${tokens.radii.pill}px` }
    }
  );
};
var OrderDriverPill = ({ driverName, avatarUrl, state, isSettled, detail = null, isAssigning = false, tooltip = null, readOnly = false, onClick }) => {
  const tokens = useOrderTokens();
  const avatarSize = 28;
  const avatar = (content, overlay) => /* @__PURE__ */ jsxs6(
    Box8,
    {
      sx: {
        position: "relative",
        width: avatarSize,
        height: avatarSize,
        flex: "none",
        borderRadius: `${tokens.radii.pill}px`,
        bgcolor: tokens.surfaces.chipStrong,
        color: tokens.text.secondary,
        fontSize: "11px",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ".order-driver-pill:hover &  .order-driver-unassign": { display: "flex" }
      },
      children: [
        content,
        overlay
      ]
    }
  );
  const name = (text, color) => /* @__PURE__ */ jsx10(Typography4, { component: "span", sx: { fontSize: "12px", fontWeight: 600, color, flex: "none", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: text });
  const shell = (children, isInteractive, accent) => /* @__PURE__ */ jsx10(
    Box8,
    {
      className: "order-driver-pill",
      component: isInteractive ? "button" : "div",
      type: isInteractive ? "button" : void 0,
      onClick: isInteractive ? (event) => {
        event.stopPropagation();
        onClick();
      } : void 0,
      sx: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        // The pill sits beside the card footer's action buttons; when the column is narrow the
        // PILL yields (timestamp first, then the name's own 110px cap), never the buttons.
        minWidth: 0,
        maxWidth: "100%",
        padding: "0 10px 0 0",
        border: "none",
        borderRadius: `${tokens.radii.pill}px`,
        // A soft wash of the status tone with a matching hairline (inset, so nothing shifts):
        // the status-chip rule's light ground plus semantic accent, quick to read but quiet.
        bgcolor: accent ? `color-mix(in srgb, ${accent} 12%, ${tokens.surfaces.legBackground})` : tokens.surfaces.legBackground,
        boxShadow: accent ? `inset 0 0 0 1px color-mix(in srgb, ${accent} 30%, transparent)` : "none",
        fontFamily: "inherit",
        cursor: isInteractive ? "pointer" : "default",
        ...isInteractive ? { "&:focus-visible": { outline: `2px solid ${tokens.semantic.info}`, outlineOffset: "1px" } } : {}
      },
      children
    }
  );
  if (isAssigning) {
    return shell(
      /* @__PURE__ */ jsxs6(Fragment2, { children: [
        avatar(/* @__PURE__ */ jsx10(BiTime, { size: 13, "aria-hidden": true })),
        name("Updating\u2026", tokens.text.secondary)
      ] }),
      false
    );
  }
  if (driverName) {
    const isLocked = state === "locked" || readOnly;
    const { Icon: StatusIcon, tone } = presentationForAssignmentState(state);
    const accent = tokens.semantic[tone];
    return /* @__PURE__ */ jsx10(OrderTooltip_default, { content: tooltip, children: shell(
      /* @__PURE__ */ jsxs6(Fragment2, { children: [
        avatar(
          /* @__PURE__ */ jsxs6(Fragment2, { children: [
            initialsOf(driverName),
            avatarUrl ? /* @__PURE__ */ jsx10(OrderDriverAvatarImage, { url: avatarUrl }) : null
          ] }),
          // Locked drops the scrim rather than showing one that does nothing.
          isLocked ? null : /* @__PURE__ */ jsx10(
            Box8,
            {
              className: "order-driver-unassign",
              sx: {
                position: "absolute",
                inset: 0,
                borderRadius: `${tokens.radii.pill}px`,
                bgcolor: `color-mix(in srgb, ${tokens.text.primary} 55%, transparent)`,
                color: tokens.text.onTooltip,
                display: "none",
                alignItems: "center",
                justifyContent: "center"
              },
              children: /* @__PURE__ */ jsx10(BiX, { size: 16, "aria-hidden": true })
            }
          )
        ),
        name(driverName, tokens.text.primary),
        detail ? /* @__PURE__ */ jsx10(
          Typography4,
          {
            component: "span",
            sx: {
              fontSize: "11px",
              fontWeight: 500,
              color: tokens.text.secondary,
              whiteSpace: "nowrap",
              // First to yield when the footer is tight: the full moment lives in the tooltip.
              flex: "0 1 auto",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis"
            },
            children: detail
          }
        ) : null,
        /* @__PURE__ */ jsx10(StatusIcon, { size: 14, color: accent, "aria-hidden": true })
      ] }),
      !isLocked,
      accent
    ) });
  }
  if (isSettled || readOnly) {
    return /* @__PURE__ */ jsx10(Typography4, { component: "span", sx: { fontSize: "11px", fontWeight: 600, letterSpacing: "0.03em", color: tokens.text.muted }, children: "Not Assigned" });
  }
  const isLockedWithoutDriver = state === "locked";
  return /* @__PURE__ */ jsx10(OrderTooltip_default, { content: tooltip, children: shell(
    /* @__PURE__ */ jsxs6(Fragment2, { children: [
      avatar(
        /* @__PURE__ */ jsx10(Box8, { component: "span", sx: { fontSize: "14px", fontWeight: 600, lineHeight: 1, pb: "2px" }, children: isLockedWithoutDriver ? "\u2014" : "+" })
      ),
      name("Not Assigned", tokens.text.secondary)
    ] }),
    !isLockedWithoutDriver
  ) });
};
var OrderDriverPill_default = OrderDriverPill;

// src/OrderMilestone/OrderMilestone.tsx
import { Box as Box9, Typography as Typography5 } from "@mui/material";
import { jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
var OrderMilestone = ({ value, subtext, tooltip = null, size = "card", align = "left" }) => {
  const tokens = useOrderTokens();
  const toneColor = {
    neutral: tokens.text.labelFaded,
    amber: tokens.semantic.warningStrong,
    red: tokens.semantic.error,
    green: tokens.semantic.success
  };
  const block = /* @__PURE__ */ jsxs7(
    Box9,
    {
      sx: {
        flex: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: align === "right" ? "flex-end" : "flex-start",
        textAlign: align,
        gap: "1px",
        minWidth: 0
      },
      children: [
        /* @__PURE__ */ jsx11(
          Typography5,
          {
            component: "span",
            sx: {
              fontSize: size === "row" ? "12px" : "13px",
              fontWeight: 700,
              lineHeight: size === "row" ? 1.25 : 1.1,
              color: tokens.text.primary,
              whiteSpace: "nowrap"
            },
            children: value
          }
        ),
        /* @__PURE__ */ jsx11(
          Typography5,
          {
            component: "span",
            sx: {
              display: "block",
              fontSize: size === "row" ? "8.5px" : "9px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: toneColor[subtext.tone],
              whiteSpace: "nowrap"
            },
            children: subtext.text
          }
        )
      ]
    }
  );
  return /* @__PURE__ */ jsx11(OrderTooltip_default, { content: tooltip, children: block });
};
var OrderMilestone_default = OrderMilestone;

// src/OrderPhotoTile/OrderPhotoTile.tsx
import { Box as Box10 } from "@mui/material";
import { BiCamera } from "react-icons/bi";

// src/OrderActionMenu/OrderActionMenu.tsx
import { useState } from "react";
import { css } from "@emotion/css";
import { Divider, Menu, MenuItem, Tooltip as Tooltip2 } from "@mui/material";
import { Fragment as Fragment3, jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var DisabledReason = ({ action, children }) => action.disabled && action.disabledReason ? /* @__PURE__ */ jsx12(Tooltip2, { title: action.disabledReason, placement: "left", children: /* @__PURE__ */ jsx12("span", { children }) }) : children;
var OrderActionMenu = ({ actions, children }) => {
  const tokens = useOrderTokens();
  const cls = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (action) => {
    handleClose();
    action.handler?.();
  };
  const visible = actions.filter((action) => !action.hide);
  return /* @__PURE__ */ jsxs8(Fragment3, { children: [
    children({ onClick: (event) => setAnchorEl(event.currentTarget), isOpen: !!anchorEl }),
    visible.length ? /* @__PURE__ */ jsx12(
      Menu,
      {
        anchorEl,
        open: !!anchorEl,
        onClose: handleClose,
        onClick: (event) => event.stopPropagation(),
        children: visible.map(
          (action, index) => !action.label ? /* @__PURE__ */ jsx12(Divider, {}, `divider-${index}`) : /* @__PURE__ */ jsx12(DisabledReason, { action, children: /* @__PURE__ */ jsxs8(MenuItem, { disabled: action.disabled, onClick: () => handleSelect(action), sx: { color: action.isDanger ? tokens.semantic.error : "inherit" }, children: [
            action.icon ? /* @__PURE__ */ jsx12("span", { className: cls.menuIcon, children: action.icon }) : null,
            action.label
          ] }) }, `${action.label}-${index}`)
        )
      }
    ) : null
  ] });
};
var useStyles = () => ({
  menuIcon: css`
    display: inline-flex;
    align-items: center;
    flex: none;
    margin-right: 8px;
  `
});
var OrderActionMenu_default = OrderActionMenu;

// src/OrderPhotoTile/OrderPhotoTile.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var OrderPhotoTile = ({ photo, size, onOpenPhase }) => {
  const tokens = useOrderTokens();
  const isPlaceholder = photo.isPlaceholder || !photo.url;
  const isInert = !onOpenPhase || isPlaceholder || !(photo.hasPickupPhotos || photo.hasDeliveryPhotos);
  const phaseActions = [
    { label: "View Pickup Images", icon: /* @__PURE__ */ jsx13(BiCamera, {}), disabled: !photo.hasPickupPhotos, handler: () => onOpenPhase?.("pickup") },
    // Delivery captures land hours after pickup ones, so this is the common state.
    { label: "View Delivery Images", icon: /* @__PURE__ */ jsx13(BiCamera, {}), disabled: !photo.hasDeliveryPhotos, handler: () => onOpenPhase?.("delivery") }
  ];
  const buildSquare = (onClick) => /* @__PURE__ */ jsx13(
    Box10,
    {
      component: isInert ? "div" : "button",
      type: isInert ? void 0 : "button",
      onClick: (event) => {
        event.stopPropagation();
        if (!isInert) onClick?.(event);
      },
      sx: {
        flex: "none",
        ...size ? { width: size, height: size } : { height: "100%", aspectRatio: "1 / 1" },
        position: "relative",
        overflow: "hidden",
        borderRadius: `${tokens.radii.md}px`,
        border: "none",
        padding: 0,
        cursor: isInert ? "default" : "pointer",
        // The stripes read as "no photo yet" rather than as a broken image.
        ...isPlaceholder ? { background: `repeating-linear-gradient(135deg, ${tokens.lines.barOff} 0 8px, ${tokens.surfaces.legBackground} 8px 16px)` } : {
          bgcolor: tokens.surfaces.chipNeutral,
          // The inset vignette keeps a pale photo distinct from the leg's pale background.
          "&::after": { content: '""', position: "absolute", inset: 0, boxShadow: tokens.shadows.photoVignette }
        }
      },
      children: !isPlaceholder && /* @__PURE__ */ jsx13(Box10, { component: "img", src: photo.url ?? void 0, alt: "", sx: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" } })
    }
  );
  if (isInert) return buildSquare();
  return /* @__PURE__ */ jsx13(OrderActionMenu_default, { actions: phaseActions, children: ({ onClick }) => /* @__PURE__ */ jsx13(OrderTooltip_default, { content: [{ kind: "description", text: "View images" }], children: buildSquare(onClick) }) });
};
var OrderPhotoTile_default = OrderPhotoTile;

// src/OrderRouteChain/OrderRouteChain.tsx
import { Box as Box11, Typography as Typography6 } from "@mui/material";
import { BiSubdirectoryRight } from "react-icons/bi";
import { jsx as jsx14, jsxs as jsxs9 } from "react/jsx-runtime";
var OrderRouteChain = ({ stops, tooltip = null }) => {
  const tokens = useOrderTokens();
  if (!stops.length) return null;
  return /* @__PURE__ */ jsx14(OrderTooltip_default, { content: tooltip, children: /* @__PURE__ */ jsx14(Box11, { sx: { display: "flex", flexDirection: "column", fontSize: "11px", lineHeight: 1.25, color: tokens.text.primary, minWidth: 0 }, children: stops.map((stop, index) => /* @__PURE__ */ jsxs9(
    Typography6,
    {
      component: "span",
      sx: {
        display: "inline-flex",
        alignItems: "center",
        gap: "3px",
        fontSize: "inherit",
        lineHeight: "inherit",
        color: "inherit",
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      },
      children: [
        index > 0 ? /* @__PURE__ */ jsx14(BiSubdirectoryRight, { size: 10, color: tokens.text.muted, "aria-hidden": true, style: { flex: "none" } }) : null,
        stop.name
      ]
    },
    `${stop.key}:${index}`
  )) }) });
};
var OrderRouteChain_default = OrderRouteChain;

// src/OrderStatusBar/OrderStatusBarBadge.tsx
import { Box as Box12, Typography as Typography7 } from "@mui/material";
import { BiSolidCheckCircle, BiSolidPencil, BiSolidRightArrowCircle, BiSolidTime, BiSolidXCircle } from "react-icons/bi";
import { jsx as jsx15, jsxs as jsxs10 } from "react/jsx-runtime";
var STATUS_ICONS = {
  [ORDER_STATUS_TOKEN.QUEUED]: BiSolidTime,
  [ORDER_STATUS_TOKEN.PLANNED]: BiSolidPencil,
  [ORDER_STATUS_TOKEN.DRIVING]: BiSolidRightArrowCircle,
  [ORDER_STATUS_TOKEN.ON_SITE]: BiSolidRightArrowCircle,
  [ORDER_STATUS_TOKEN.DONE]: BiSolidCheckCircle,
  [ORDER_STATUS_TOKEN.CANCEL_PENDING]: BiSolidXCircle,
  [ORDER_STATUS_TOKEN.CANCELLED]: BiSolidXCircle
};
var BADGE_BURST_KEYFRAMES = {
  "@keyframes order-badge-burst-right": {
    "0%": { left: "50%", opacity: 1 },
    "10%": { left: "100%", opacity: 1 },
    "17%, 100%": { left: "100%", opacity: 0 }
  },
  "@keyframes order-badge-burst-left": {
    "0%": { right: "50%", opacity: 1 },
    "10%": { right: "100%", opacity: 1 },
    "17%, 100%": { right: "100%", opacity: 0 }
  }
};
var ATTENTION_RING_WIDTH_PX = 2;
var ATTENTION_RING_REACH_PX = 4;
var ATTENTION_RING_SPREAD_PX = ATTENTION_RING_WIDTH_PX + ATTENTION_RING_REACH_PX;
var ATTENTION_RING_KEYFRAMES = (ring, faded) => ({
  "@keyframes order-badge-attention": {
    "0%": { outlineColor: ring, outlineOffset: "0px" },
    "70%, 100%": { outlineColor: faded, outlineOffset: `${ATTENTION_RING_REACH_PX}px` }
  }
});
var OrderStatusBarBadge = ({ status, badge, isLive = false, needsAttention = false, size = "card" }) => {
  const tokens = useOrderTokens();
  const color = tokens.status[status];
  const Icon = STATUS_ICONS[status];
  const isCard = size === "card";
  const band = {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "26px",
    opacity: 0,
    background: `linear-gradient(90deg, transparent, ${tokens.lines.shimmerBadge}, transparent)`
  };
  return /* @__PURE__ */ jsxs10(
    Box12,
    {
      sx: {
        flex: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        borderRadius: `${tokens.radii.pill}px`,
        padding: isCard ? "3px 14px" : "2px 10px",
        border: `1px solid ${color}`,
        backgroundColor: `color-mix(in srgb, ${color} 12%, ${tokens.surfaces.card})`,
        color,
        whiteSpace: "nowrap",
        position: "relative",
        overflow: "hidden",
        ...isLive ? {
          ...BADGE_BURST_KEYFRAMES,
          // Offset by half their width so each band starts centered on the midpoint.
          "&::after": { ...band, marginLeft: "-13px", animation: "order-badge-burst-right 2.4s ease-out infinite" },
          "&::before": { ...band, marginRight: "-13px", animation: "order-badge-burst-left 2.4s ease-out infinite" }
        } : {},
        ...needsAttention ? {
          ...ATTENTION_RING_KEYFRAMES(tokens.semantic.attentionRing, tokens.semantic.attentionRingFaded),
          outline: `${ATTENTION_RING_WIDTH_PX}px solid ${tokens.semantic.attentionRing}`,
          animation: "order-badge-attention 1.8s ease-out infinite",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            outlineOffset: "1px"
          }
        } : {}
      },
      children: [
        /* @__PURE__ */ jsx15(Icon, { size: isCard ? 12 : 11, "aria-hidden": true }),
        /* @__PURE__ */ jsx15(
          Typography7,
          {
            component: "span",
            sx: {
              fontSize: isCard ? "10.5px" : "10px",
              fontWeight: 600,
              letterSpacing: "0.03em",
              lineHeight: 1.2,
              color: "inherit",
              position: "relative"
            },
            children: badge
          }
        )
      ]
    }
  );
};
var OrderStatusBarBadge_default = OrderStatusBarBadge;

// src/OrderStatusBar/OrderStatusBarStrap.tsx
import { Box as Box13 } from "@mui/material";
import { jsx as jsx16, jsxs as jsxs11 } from "react/jsx-runtime";
var LINE_SWEEP_KEYFRAMES = {
  "@keyframes order-line-sweep-right": {
    "0%, 6%": { left: "-44px", opacity: 0 },
    "10%": { opacity: 1 },
    "32%": { opacity: 1 },
    "42%": { opacity: 0 },
    "50%, 100%": { left: "100%", opacity: 0 }
  },
  "@keyframes order-line-sweep-left": {
    "0%, 6%": { right: "-44px", opacity: 0 },
    "10%": { opacity: 1 },
    "32%": { opacity: 1 },
    "42%": { opacity: 0 },
    "50%, 100%": { right: "100%", opacity: 0 }
  }
};
var OrderStatusBarStrap = ({ status, badge, isLive, needsAttention = false }) => {
  const tokens = useOrderTokens();
  const color = tokens.status[status];
  const line = (side) => /* @__PURE__ */ jsx16(
    Box13,
    {
      sx: {
        flex: 1,
        height: "2px",
        background: side === "leading" ? `linear-gradient(90deg, ${color}00 20%, ${color})` : `linear-gradient(90deg, ${color}, ${color}00 80%)`,
        position: "relative",
        overflow: "hidden",
        ...isLive ? {
          ...LINE_SWEEP_KEYFRAMES,
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "44px",
            opacity: 0,
            background: `linear-gradient(90deg, transparent, ${tokens.lines.shimmerLine}, transparent)`,
            animation: `order-line-sweep-${side === "leading" ? "left" : "right"} 2.4s ease-out infinite`
          }
        } : {}
      }
    }
  );
  return /* @__PURE__ */ jsxs11(
    Box13,
    {
      sx: {
        position: "absolute",
        // One pixel down, then up by half its height, centers the badge on the border line.
        top: "1px",
        left: 0,
        right: 0,
        zIndex: 2,
        transform: "translateY(-50%)",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        // Room for the attention ring, which the strap's clip would otherwise cut; symmetric, so the badge stays on the border.
        py: needsAttention ? `${ATTENTION_RING_SPREAD_PX}px` : 0,
        pointerEvents: "none"
      },
      children: [
        line("leading"),
        /* @__PURE__ */ jsx16(OrderStatusBarBadge_default, { status, badge, isLive, needsAttention }),
        line("trailing")
      ]
    }
  );
};
var OrderStatusBarStrap_default = OrderStatusBarStrap;

// src/OrderStatusBar/OrderStatusBar.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var OrderStatusBar = ({ variant, status, badge, isLive = false, needsAttention = false, size = "card" }) => variant === "strap" ? /* @__PURE__ */ jsx17(OrderStatusBarStrap_default, { status, badge, isLive, needsAttention }) : /* @__PURE__ */ jsx17(OrderStatusBarBadge_default, { status, badge, isLive, needsAttention, size });
var OrderStatusBar_default = OrderStatusBar;

// src/OrderIndexTable/orderTableCells.tsx
import { jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
var CellText = React3.forwardRef(({ children, isBold = false, color, size = 12, ...rest }, ref) => /* @__PURE__ */ jsx18(
  Typography8,
  {
    ref,
    component: "span",
    ...rest,
    sx: { display: "block", fontSize: size, lineHeight: 1.25, fontWeight: isBold ? 700 : size === 11 ? 500 : 400, color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    children
  }
));
CellText.displayName = "CellText";
var CellStack = React3.forwardRef(({ children, ...rest }, ref) => /* @__PURE__ */ jsx18(Box14, { ref, ...rest, sx: { display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0 }, children }));
CellStack.displayName = "CellStack";
var CELL_EMPTY = "\u2014";
var Empty = ({ tokens }) => /* @__PURE__ */ jsx18(CellText, { color: tokens.text.labelFaded, children: CELL_EMPTY });
var SHARED_CELL_RENDERERS = {
  text: ({ column, view, tokens }) => {
    const text = column.text?.(view);
    return text?.value ? /* @__PURE__ */ jsx18(CellText, { isBold: text.isBold, children: text.value }) : /* @__PURE__ */ jsx18(Empty, { tokens });
  },
  // No cell-wide tooltip. The tracker bars carry their own, and a wrapper would open two
  // panels on one hover with the outer one saying less.
  status: ({ column, view }) => {
    const props = column.cellProps?.(view);
    if (!props) return null;
    return /* @__PURE__ */ jsxs12(Box14, { sx: { display: "flex", flexDirection: "column", gap: "5px", width: "fit-content" }, children: [
      /* @__PURE__ */ jsx18(OrderTooltip_default, { content: [{ kind: "description", text: props.tracker.statusLine }], children: /* @__PURE__ */ jsx18(Box14, { sx: { display: "inline-flex" }, children: /* @__PURE__ */ jsx18(OrderStatusBar_default, { variant: "badge", size: "row", status: props.statusBar.status, badge: props.statusBar.badge, isLive: props.statusBar.isLive, needsAttention: props.statusBar.needsAttention }) }) }),
      /* @__PURE__ */ jsx18(OrderTracker_default, { ...props.tracker, scale: "mini" })
    ] });
  },
  identity: ({ column, view, context, tokens }) => {
    const props = column.cellProps?.(view);
    if (!props) return null;
    return /* @__PURE__ */ jsxs12(Box14, { sx: { display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }, children: [
      /* @__PURE__ */ jsx18(OrderPhotoTile_default, { photo: props.photo, size: 44, onOpenPhase: props.photoItemId !== null && context.onOpenPhotos ? (phase) => context.onOpenPhotos?.(props.photoItemId, phase) : void 0 }),
      /* @__PURE__ */ jsx18(OrderTooltip_default, { content: props.tooltip, children: /* @__PURE__ */ jsxs12(CellStack, { children: [
        /* @__PURE__ */ jsx18(CellText, { isBold: true, children: props.displayName }),
        /* @__PURE__ */ jsxs12(Box14, { sx: { display: "inline-flex", alignItems: "center", gap: "3px", color: tokens.text.secondary, minWidth: 0 }, children: [
          /* @__PURE__ */ jsx18(BiFingerprint, { size: 10, "aria-hidden": true, style: { flex: "none" } }),
          /* @__PURE__ */ jsx18(CellText, { size: 11, color: tokens.text.secondary, children: props.ids.join(" \xB7 ") })
        ] })
      ] }) })
    ] });
  },
  guest: ({ column, view, tokens }) => {
    const props = column.cellProps?.(view);
    return props ? /* @__PURE__ */ jsxs12(CellStack, { children: [
      /* @__PURE__ */ jsx18(CellText, { isBold: true, children: props.name }),
      /* @__PURE__ */ jsx18(CellText, { size: 11, color: tokens.text.secondary, children: props.phone })
    ] }) : /* @__PURE__ */ jsx18(Empty, { tokens });
  },
  route: ({ column, view, tokens }) => {
    const props = column.cellProps?.(view);
    return props?.stops.length ? /* @__PURE__ */ jsx18(OrderRouteChain_default, { stops: props.stops, tooltip: props.tooltip }) : /* @__PURE__ */ jsx18(Empty, { tokens });
  },
  promise: ({ column, view, tokens }) => {
    const props = column.cellProps?.(view);
    return props ? /* @__PURE__ */ jsx18(OrderTooltip_default, { content: props.tooltip, children: /* @__PURE__ */ jsx18(CellText, { isBold: true, children: props.value }) }) : /* @__PURE__ */ jsx18(Empty, { tokens });
  },
  milestone: ({ column, view, tokens }) => {
    const props = column.cellProps?.(view);
    return props ? /* @__PURE__ */ jsx18(OrderMilestone_default, { value: props.value, subtext: props.subtext, tooltip: props.tooltip, size: "row" }) : /* @__PURE__ */ jsx18(Empty, { tokens });
  },
  driver: ({ column, view, context, tokens }) => {
    const props = column.cellProps?.(view);
    return props?.showPill ? /* @__PURE__ */ jsx18(
      OrderDriverPill_default,
      {
        driverName: props.driverName,
        avatarUrl: props.avatarUrl,
        state: props.state,
        isSettled: props.isSettled,
        detail: props.detail,
        isAssigning: context.isOrderAssigning(view),
        tooltip: props.tooltip,
        readOnly: props.readOnly,
        onClick: () => context.onAssignDriver(view)
      }
    ) : /* @__PURE__ */ jsx18(Empty, { tokens });
  },
  actions: ({ view, context }) => context.renderActions(view)
};
var renderOrderCell = (cells, input) => (cells[input.column.cell] ?? cells.text)(input);

// src/sharedRenderers.ts
var StopOverflowItem = ({ props }) => createElement2(OrderItemStopOverflow_default, props);
var SHARED_RENDERERS = {
  items: { stopOverflow: StopOverflowItem },
  cells: SHARED_CELL_RENDERERS,
  bodies: {},
  fallbackBody: OrderCardBody_default
};

// src/shared/platform/fixturePlatform.tsx
import { Chip } from "@mui/material";

// src/__fixtures__/fixtureFormatters.ts
var capFirst = (value) => value.charAt(0).toUpperCase() + value.slice(1);
var formatVehicleLine = (move, options) => {
  const includeYear = options?.includeYear ?? true;
  const includeColor = options?.includeColor ?? false;
  const fallback = "Vehicle";
  if (!move) return fallback;
  const fields = move;
  const type = fields.consumer_type === "customer" ? "Customer " : fields.consumer_type === "loaner" ? "Loaner " : "";
  const year = fields.vehicle_year || fields.driver_vehicle_year || null;
  const make = fields.vehicle_make || fields.driver_vehicle_make || null;
  const model = fields.vehicle_model || fields.driver_vehicle_model || null;
  const color = fields.vehicle_color || fields.driver_vehicle_color || null;
  let vehicle;
  if (make && model) vehicle = `${make} ${model}`;
  else if (make) vehicle = make;
  else if (model) vehicle = model;
  else vehicle = type + fallback;
  if (includeYear && year) vehicle = `${year} ${vehicle}`;
  if (includeColor && color) vehicle = `${vehicle} (${capFirst(color)})`;
  return vehicle;
};
var formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const digits = `${phone}`.replace(/\D/g, "");
  const match = digits.match(/^(\d{1})?(\d{3})(\d{3})(\d{4})$/);
  if (!match) return phone;
  const intlCode = match[1] ? `+${match[1]} ` : "";
  return [intlCode, "(", match[2], ")\xA0", match[3], "-\u2060", match[4]].join("");
};
var PHOTO_STEP_ORDER = [
  "driver-front",
  "driver-front-wheel",
  "driver-rear",
  "driver-rear-wheel",
  "passenger-front",
  "passenger-front-wheel",
  "passenger-rear",
  "passenger-rear-wheel",
  "instrument-cluster",
  "interior-front",
  "interior-rear",
  "interior-trunk",
  "interior-vin",
  "vehicle-vin",
  "vehicle-weight-badge",
  "fuel-pump",
  "front-bumper",
  "windshield",
  "driver-side-full-view",
  "trunk-rear-bumper",
  "passenger-side-full-view",
  "engine-bay",
  "roof-photo",
  "license-plate-photo",
  "center-console",
  "backup-camera-photo",
  "vehicle-keys",
  "driver-carpet-photo",
  "third-row-photo",
  "vehicle-contents-photo",
  "exterior-damage-photos",
  "interior-damage-photos"
];
var photoOrder = (stepId) => {
  if (stepId === "damages") return 500;
  if (stepId === "signature") return 1e3;
  const index = stepId ? PHOTO_STEP_ORDER.indexOf(stepId) : -1;
  return index === -1 ? 100 : index;
};
var sortPhotosByStandardOrder = (photos) => [...photos].sort((a, b) => photoOrder(a.step_id) - photoOrder(b.step_id));
var fixtureFormatters = { formatVehicleLine, formatPhoneNumber, sortPhotosByStandardOrder };

// src/shared/platform/fixturePlatform.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var MoveTileHangtag = ({ hangtag }) => /* @__PURE__ */ jsx19(Chip, { size: "small", variant: "outlined", color: "warning", label: hangtag.rear_code ?? hangtag.type, sx: { position: "absolute", top: -8, left: "50%", bgcolor: "background.paper" } });
var fixturePlatform = {
  ...fixtureFormatters,
  copyToClipboard: () => {
  },
  buildCarouselImages: () => [],
  MoveTileHangtag
};

// src/shared/hooks/useOrderClockTick.ts
import { useEffect, useState as useState2 } from "react";
var useOrderClockTick = (intervalMs = 6e4) => {
  const [nowMs, setNowMs] = useState2(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return nowMs;
};

// src/shared/appearance/orderStatusToken.ts
var PHASE_TOKEN = {
  queued: ORDER_STATUS_TOKEN.QUEUED,
  planned: ORDER_STATUS_TOKEN.PLANNED,
  active: ORDER_STATUS_TOKEN.DRIVING,
  completed: ORDER_STATUS_TOKEN.DONE,
  cancelled: ORDER_STATUS_TOKEN.CANCELLED
};
var resolveOrderStatusToken = (phase) => PHASE_TOKEN[phase];
var isLivePhase = (phase) => phase === "active";
var tokensByTheme = /* @__PURE__ */ new WeakMap();
var resolveOrderStatusColor = (theme, token) => {
  let tokens = tokensByTheme.get(theme);
  if (!tokens) {
    tokens = resolveOrderTokens(theme);
    tokensByTheme.set(theme, tokens);
  }
  return tokens.status[token];
};

// src/OrderActionButtons/OrderActionButtons.tsx
import { Box as Box15, IconButton } from "@mui/material";
import { BiDotsVerticalRounded } from "react-icons/bi";

// src/OrderActionButtons/supportedActions.ts
var filterSupportedActions = (actions, handlers) => actions.flatMap((action) => {
  if (action.children?.length) {
    const children = filterSupportedActions(action.children, handlers);
    return children.length ? [{ ...action, children }] : [];
  }
  return Object.prototype.hasOwnProperty.call(handlers, action.key) && typeof handlers[action.key] === "function" ? [action] : [];
});

// src/OrderActionButtons/OrderActionButtons.tsx
import { jsx as jsx20, jsxs as jsxs13 } from "react/jsx-runtime";
var OrderActionButton = ({ label, hasBadge = false, onClick, children }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx20(OrderTooltip_default, { content: [{ kind: "description", text: label }], children: /* @__PURE__ */ jsxs13(
    IconButton,
    {
      "aria-label": label,
      onClick: (event) => {
        event.stopPropagation();
        onClick(event);
      },
      sx: {
        position: "relative",
        width: 32,
        height: 32,
        borderRadius: `${tokens.radii.md}px`,
        bgcolor: tokens.surfaces.chipNeutral,
        color: tokens.text.secondary,
        transition: "all 100ms ease-in-out",
        "&:hover": { bgcolor: tokens.surfaces.chipStrong, color: tokens.text.primary }
      },
      children: [
        children,
        hasBadge ? /* @__PURE__ */ jsx20(
          Box15,
          {
            sx: {
              position: "absolute",
              top: -2,
              right: -2,
              width: 9,
              height: 9,
              borderRadius: `${tokens.radii.pill}px`,
              bgcolor: tokens.semantic.error,
              // Ringed in the card color so the dot reads as separate from the button.
              border: `2px solid ${tokens.surfaces.card}`
            }
          }
        ) : null
      ]
    }
  ) });
};
var barGlyph = (descriptor) => {
  const Icon = descriptor.icon;
  return Icon ? /* @__PURE__ */ jsx20(Icon, { size: 16, "aria-hidden": true }) : null;
};
var toMenuActions = (descriptors, handlers) => descriptors.flatMap((descriptor) => {
  const Icon = descriptor.icon;
  const action = {
    label: descriptor.label,
    icon: Icon ? /* @__PURE__ */ jsx20(Icon, {}) : void 0,
    disabled: descriptor.disabled,
    isDanger: descriptor.danger,
    handler: () => handlers[descriptor.key]?.(descriptor.targetId)
  };
  return descriptor.dividerBefore ? [{}, action] : [action];
});
var OrderActionButtons = ({ actions, handlers }) => {
  const supported = filterSupportedActions(actions, handlers);
  if (!supported.length) return null;
  const bar = supported.filter((descriptor) => descriptor.placement === "bar");
  const menu = toMenuActions(
    supported.filter((descriptor) => descriptor.placement === "menu"),
    handlers
  );
  return /* @__PURE__ */ jsxs13(Box15, { sx: { display: "flex", gap: "6px", justifyContent: "flex-end" }, children: [
    bar.map(
      (descriptor) => descriptor.children?.length ? /* @__PURE__ */ jsx20(OrderActionMenu_default, { actions: toMenuActions(descriptor.children, handlers), children: ({ onClick }) => /* @__PURE__ */ jsx20(OrderActionButton, { label: descriptor.label, hasBadge: descriptor.hasBadge, onClick, children: barGlyph(descriptor) }) }, descriptor.key) : /* @__PURE__ */ jsx20(OrderActionButton, { label: descriptor.label, hasBadge: descriptor.hasBadge, onClick: () => handlers[descriptor.key]?.(descriptor.targetId), children: barGlyph(descriptor) }, descriptor.key)
    ),
    menu.length ? /* @__PURE__ */ jsx20(OrderActionMenu_default, { actions: menu, children: ({ onClick }) => /* @__PURE__ */ jsx20(OrderActionButton, { label: "More actions", onClick, children: /* @__PURE__ */ jsx20(BiDotsVerticalRounded, { size: 16, "aria-hidden": true }) }) }) : null
  ] });
};
var OrderActionButtons_default = OrderActionButtons;

// src/OrderPhotoTile/placeholderPhoto.ts
var PLACEHOLDER_PHOTO = { url: null, isPlaceholder: true, hasPickupPhotos: false, hasDeliveryPhotos: false };

// src/OrderItemStopBase/OrderItemStopBase.tsx
import { Box as Box16, Typography as Typography9 } from "@mui/material";

// src/OrderItemStopBase/stopMarker.ts
var resolveStopMarkerTone = (state, tokens) => {
  switch (state) {
    case "active":
      return { fill: tokens.status.driving, ink: tokens.text.onBadge };
    case "done":
      return { fill: tokens.status.done, ink: tokens.text.onBadge };
    case "exception":
      return { fill: tokens.status.cancelled, ink: tokens.text.onBadge };
    case "pending":
    default:
      return { fill: tokens.lines.barOff, ink: tokens.text.muted };
  }
};

// src/OrderItemStopBase/OrderItemStopBase.tsx
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var OrderItemStopBase = ({ marker, name, note, thumbnail, trailing, children, tooltip = null, density = "regular" }) => {
  const tokens = useOrderTokens();
  const tone = resolveStopMarkerTone(marker.state, tokens);
  const geometry = STOP_ROW_GEOMETRY[density];
  const isDense = density === "dense";
  const hasNote = Boolean(note);
  const hasChildren = children !== void 0 && children !== null && children !== false;
  const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  return /* @__PURE__ */ jsxs14(Box16, { sx: { display: "flex", flexDirection: "column", gap: "4px", maxWidth: geometry.maxWidth }, children: [
    /* @__PURE__ */ jsxs14(
      Box16,
      {
        sx: {
          display: "flex",
          alignItems: "stretch",
          gap: `${geometry.gap}px`,
          bgcolor: tokens.surfaces.legBackground,
          borderRadius: `${tokens.radii.md}px`,
          height: hasNote ? geometry.height : geometry.compactHeight,
          pl: `${geometry.gutter}px`,
          pr: `${geometry.paddingRight}px`
        },
        children: [
          /* @__PURE__ */ jsx21(Box16, { sx: { display: "flex", alignItems: "center", flex: "none" }, children: /* @__PURE__ */ jsx21(
            Box16,
            {
              sx: {
                width: geometry.markerSize,
                height: geometry.markerSize,
                borderRadius: `${tokens.radii.pill}px`,
                bgcolor: tone.fill,
                color: tone.ink,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isDense ? "10px" : "11px",
                fontWeight: 700,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums"
              },
              children: marker.position
            }
          ) }),
          thumbnail ? /* @__PURE__ */ jsx21(Box16, { sx: { display: "flex", flex: "none", py: "4px" }, children: thumbnail }) : null,
          isDense ? /* @__PURE__ */ jsx21(Box16, { sx: { flex: 1, minWidth: 0, display: "flex", alignItems: "center" }, children: /* @__PURE__ */ jsx21(OrderTooltip_default, { content: tooltip, children: /* @__PURE__ */ jsxs14(Typography9, { component: "span", sx: { display: "block", minWidth: 0, fontSize: "13px", lineHeight: 1, color: tokens.text.primary, ...ellipsis }, children: [
            /* @__PURE__ */ jsx21(Box16, { component: "span", sx: { fontWeight: 600 }, children: name }),
            hasNote ? /* @__PURE__ */ jsx21(Box16, { component: "span", sx: { fontSize: "12px", color: tokens.text.secondary }, children: ` \xB7 ${note}` }) : null
          ] }) }) }) : /* @__PURE__ */ jsxs14(Box16, { sx: { flex: 1, minWidth: 0, py: "6px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "4px" }, children: [
            /* @__PURE__ */ jsx21(OrderTooltip_default, { content: tooltip, children: /* @__PURE__ */ jsx21(Typography9, { component: "span", sx: { display: "block", minWidth: 0, fontSize: "14px", fontWeight: 700, lineHeight: 1, color: tokens.text.primary, ...ellipsis }, children: name }) }),
            hasNote ? /* @__PURE__ */ jsx21(Typography9, { component: "span", sx: { display: "block", minWidth: 0, fontSize: "12px", lineHeight: 1, color: tokens.text.secondary, ...ellipsis }, children: note }) : null
          ] }),
          trailing ? /* @__PURE__ */ jsx21(Box16, { sx: { display: "flex", alignItems: "center", flex: "none" }, children: trailing }) : null
        ]
      }
    ),
    hasChildren ? /* @__PURE__ */ jsx21(
      Box16,
      {
        sx: {
          // Indented to the text column, framed by a rail on the marker's axis.
          ml: `${geometry.gutter + geometry.markerSize / 2}px`,
          pl: `${geometry.gap + geometry.markerSize / 2 - 1}px`,
          borderLeft: `2px solid ${tokens.lines.divider}`,
          display: "flex",
          flexDirection: "column",
          gap: "4px"
        },
        children
      }
    ) : null
  ] });
};
var OrderItemStopBase_default = OrderItemStopBase;

// src/OrderCard/OrderCard.tsx
import React4 from "react";
import { Box as Box21, Card as Card2 } from "@mui/material";

// src/OrderCard/OrderCardActionBar.tsx
import { Box as Box17 } from "@mui/material";
import { jsx as jsx22, jsxs as jsxs15 } from "react/jsx-runtime";
var OrderCardActionBar = ({ driver, actionBar, actions, isAssigning = false }) => {
  const tokens = useOrderTokens();
  const showDriverZone = driver.showPill;
  const hasActions = filterSupportedActions(actionBar, actions).length > 0;
  if (!showDriverZone && !hasActions) return null;
  return /* @__PURE__ */ jsxs15(
    Box17,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 12px",
        borderTop: `1px solid ${tokens.lines.divider}`,
        borderRadius: `0 0 ${tokens.radii.md}px ${tokens.radii.md}px`
      },
      children: [
        showDriverZone ? /* @__PURE__ */ jsx22(
          OrderDriverPill_default,
          {
            driverName: driver.driverName,
            avatarUrl: driver.avatarUrl,
            state: typeof actions.assignDriver === "function" ? driver.state : "locked",
            isSettled: driver.isSettled,
            detail: driver.detail,
            isAssigning,
            tooltip: driver.tooltip,
            readOnly: driver.readOnly,
            onClick: () => actions.assignDriver?.()
          }
        ) : null,
        /* @__PURE__ */ jsx22(Box17, { sx: { flex: 1 } }),
        /* @__PURE__ */ jsx22(Box17, { sx: { flex: "none", display: "flex", alignItems: "center" }, children: /* @__PURE__ */ jsx22(OrderActionButtons_default, { actions: actionBar, handlers: actions }) })
      ]
    }
  );
};
var OrderCardActionBar_default = OrderCardActionBar;

// src/OrderCard/OrderCardCancelOverlay.tsx
import { Box as Box18, Typography as Typography10 } from "@mui/material";
import { jsx as jsx23, jsxs as jsxs16 } from "react/jsx-runtime";
var OrderCardCancelOverlay = ({ reason, hasFooterBelow = false }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs16(
    Box18,
    {
      sx: {
        position: "absolute",
        inset: 0,
        zIndex: 1,
        borderRadius: hasFooterBelow ? `${tokens.radii.md}px ${tokens.radii.md}px 0 0` : `${tokens.radii.md}px`,
        bgcolor: `color-mix(in srgb, ${tokens.text.primary} 72%, transparent)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        textAlign: "center",
        padding: "16px"
      },
      children: [
        /* @__PURE__ */ jsx23(Typography10, { component: "span", sx: { fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: tokens.text.onTooltipMuted }, children: "Reason:" }),
        /* @__PURE__ */ jsx23(Typography10, { component: "span", sx: { fontSize: "20px", fontWeight: 800, lineHeight: 1.25, color: tokens.text.onTooltip }, children: reason || "No reason given" })
      ]
    }
  );
};
var OrderCardCancelOverlay_default = OrderCardCancelOverlay;

// src/OrderCard/OrderCardHeaderIdentity.tsx
import { Box as Box19, Typography as Typography11 } from "@mui/material";
import { BiFingerprint as BiFingerprint2 } from "react-icons/bi";
import { jsx as jsx24, jsxs as jsxs17 } from "react/jsx-runtime";
var OrderCardHeaderIdentity = ({ displayName, ids, tooltip = null }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx24(OrderTooltip_default, { content: tooltip, children: /* @__PURE__ */ jsxs17(Box19, { sx: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }, children: [
    /* @__PURE__ */ jsx24(Typography11, { component: "span", sx: { fontSize: "13px", fontWeight: 700, lineHeight: 1.1, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: displayName }),
    /* @__PURE__ */ jsxs17(
      Typography11,
      {
        component: "span",
        sx: {
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          fontSize: "9px",
          fontWeight: 700,
          lineHeight: 1,
          color: tokens.text.labelFaded,
          // Normal tracking, because the uppercase label treatment does not suit numbers.
          letterSpacing: "normal"
        },
        children: [
          /* @__PURE__ */ jsx24(BiFingerprint2, { size: 10, style: { flex: "none" } }),
          ids.join(" \xB7 ")
        ]
      }
    )
  ] }) });
};
var OrderCardHeaderIdentity_default = OrderCardHeaderIdentity;

// src/OrderCard/OrderCardSkeleton.tsx
import { Box as Box20, Card } from "@mui/material";
import { jsx as jsx25, jsxs as jsxs18 } from "react/jsx-runtime";
var OrderSkeletonBlock = ({ width = "100%", height, isPill = false, mb }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx25(
    Box20,
    {
      sx: {
        width,
        height,
        marginBottom: mb,
        flex: "none",
        borderRadius: isPill ? `${tokens.radii.pill}px` : `${tokens.radii.sm}px`,
        bgcolor: tokens.surfaces.skeleton,
        position: "relative",
        overflow: "hidden",
        "@keyframes order-skeleton-shimmer": {
          from: { transform: "translateX(-100%)" },
          to: { transform: "translateX(100%)" }
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `linear-gradient(90deg, transparent, ${tokens.lines.shimmerLine}, transparent)`,
          animation: "order-skeleton-shimmer 1500ms linear infinite"
        }
      }
    }
  );
};
var OrderSkeletonTimeSlot = () => /* @__PURE__ */ jsxs18(Box20, { sx: { display: "flex", flexDirection: "column", gap: "3px" }, children: [
  /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 48, height: 8 }),
  /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 88, height: 12 })
] });
var OrderSkeletonBody = () => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs18(Box20, { children: [
    /* @__PURE__ */ jsxs18(Box20, { sx: { display: "flex", gap: "4px", padding: "0 8px", mb: "14px" }, children: [
      /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1 }, children: /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 7, isPill: true }) }),
      /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1 }, children: /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 7, isPill: true }) }),
      /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1 }, children: /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 7, isPill: true }) })
    ] }),
    /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 64, mb: "6px" }),
    /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 64 }),
    /* @__PURE__ */ jsxs18(Box20, { sx: { mt: "10px", mx: "-12px", padding: "8px 12px 0", borderTop: `1px solid ${tokens.lines.divider}`, display: "flex", flexDirection: "column", gap: "5px" }, children: [
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 10 }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 10 }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { height: 10, width: "72%" })
    ] }),
    /* @__PURE__ */ jsxs18(Box20, { sx: { mt: "8px", mx: "-12px", padding: "8px 12px 4px", borderTop: `1px solid ${tokens.lines.divider}`, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }, children: [
      /* @__PURE__ */ jsx25(OrderSkeletonTimeSlot, {}),
      /* @__PURE__ */ jsx25(OrderSkeletonTimeSlot, {}),
      /* @__PURE__ */ jsx25(OrderSkeletonTimeSlot, {}),
      /* @__PURE__ */ jsx25(OrderSkeletonTimeSlot, {})
    ] })
  ] });
};
var OrderCardSkeleton = ({ variant = "body" }) => {
  const tokens = useOrderTokens();
  if (variant === "body") return /* @__PURE__ */ jsx25(OrderSkeletonBody, {});
  return /* @__PURE__ */ jsxs18(Card, { sx: { position: "relative", width: "100%", minWidth: 384, maxWidth: 576, borderRadius: `${tokens.radii.md}px`, boxShadow: tokens.shadows.card, border: "none", overflow: "visible", cursor: "default" }, children: [
    /* @__PURE__ */ jsxs18(Box20, { sx: { position: "absolute", top: "1px", left: 0, right: 0, transform: "translateY(-50%)", display: "flex", alignItems: "center", pointerEvents: "none" }, children: [
      /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1, height: "2px", bgcolor: tokens.surfaces.skeleton } }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 76, height: 16, isPill: true }),
      /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1, height: "2px", bgcolor: tokens.surfaces.skeleton } })
    ] }),
    /* @__PURE__ */ jsxs18(Box20, { sx: { padding: "16px 12px 8px" }, children: [
      /* @__PURE__ */ jsxs18(Box20, { sx: { display: "flex", alignItems: "center", gap: "8px", mb: "8px" }, children: [
        /* @__PURE__ */ jsxs18(Box20, { sx: { display: "flex", flexDirection: "column", gap: "3px" }, children: [
          /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 140, height: 13 }),
          /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 90, height: 9 })
        ] }),
        /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1 } }),
        /* @__PURE__ */ jsxs18(Box20, { sx: { display: "flex", flexDirection: "column", gap: "3px", alignItems: "flex-end" }, children: [
          /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 96, height: 13 }),
          /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 60, height: 9 })
        ] })
      ] }),
      /* @__PURE__ */ jsx25(OrderSkeletonBody, {})
    ] }),
    /* @__PURE__ */ jsxs18(Box20, { sx: { display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderTop: `1px solid ${tokens.lines.divider}` }, children: [
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 22, height: 22, isPill: true }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 72, height: 10 }),
      /* @__PURE__ */ jsx25(Box20, { sx: { flex: 1 } }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 24, height: 24 }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 24, height: 24 }),
      /* @__PURE__ */ jsx25(OrderSkeletonBlock, { width: 24, height: 24 })
    ] })
  ] });
};
var OrderCardSkeleton_default = OrderCardSkeleton;

// src/OrderCard/OrderCard.tsx
import { jsx as jsx26, jsxs as jsxs19 } from "react/jsx-runtime";
var OrderCard = ({ orderKey: orderKey2, rendererKey, statusBar, tracker, header, items, times, details, driver, actionBar, actions, state, isAssigning = false, onOpenPhotos }) => {
  const tokens = useOrderTokens();
  const { bodies, fallbackBody } = useOrderRenderers();
  const Body = bodies[rendererKey] ?? fallbackBody;
  const isInDanger = state.isInDanger && !state.isSettled;
  return /* @__PURE__ */ jsxs19(
    Card2,
    {
      "data-order-key": orderKey2,
      "data-order-product": orderKey2?.split(":")[0],
      sx: {
        position: "relative",
        // A flex column hands a stretched grid row's extra height to the body, so footers
        // line up across a row.
        display: "flex",
        flexDirection: "column",
        // No padding here so the strap reaches the edges. Below 384 an item's route line has
        // nowhere to go; above 576 the header's two ends stop reading as one line.
        width: "100%",
        minWidth: 384,
        maxWidth: 576,
        borderRadius: `${tokens.radii.md}px`,
        // Cards use shadows, never borders.
        boxShadow: tokens.shadows.card,
        border: "none",
        overflow: "visible",
        transition: "all 150ms ease-in-out",
        // Slow on purpose. A faster or harder pulse strobes across a screenful of cards.
        ...isInDanger ? {
          // A keyframe because no elevation token can compose the card shadow with a spreading ring.
          "@keyframes order-danger-pulse": {
            "0%": { boxShadow: `${tokens.shadows.card}, 0 0 0 0 ${tokens.semantic.dangerRing}` },
            "70%": { boxShadow: `${tokens.shadows.card}, 0 0 0 14px ${tokens.semantic.dangerRingFaded}` },
            "100%": { boxShadow: `${tokens.shadows.card}, 0 0 0 0 ${tokens.semantic.dangerRingFaded}` }
          },
          animation: "order-danger-pulse 2s ease-out infinite"
        } : {}
      },
      children: [
        /* @__PURE__ */ jsx26(OrderStatusBar_default, { variant: "strap", status: statusBar.status, badge: statusBar.badge, isLive: statusBar.isLive, needsAttention: statusBar.needsAttention }),
        /* @__PURE__ */ jsxs19(Box21, { sx: { position: "relative", padding: "16px 12px 8px", flexGrow: 1 }, children: [
          state.isCancelDeclared ? /* @__PURE__ */ jsx26(OrderCardCancelOverlay_default, { reason: state.cancelReason, hasFooterBelow: !!actions }) : null,
          /* @__PURE__ */ jsxs19(Box21, { sx: { display: "flex", alignItems: "center", gap: "8px", mb: "8px" }, children: [
            /* @__PURE__ */ jsx26(OrderCardHeaderIdentity_default, { displayName: header.displayName, ids: header.ids, tooltip: header.tooltip }),
            /* @__PURE__ */ jsx26(OrderMilestone_default, { value: header.milestone.value, subtext: header.milestone.subtext, tooltip: header.milestone.tooltip, align: "right" })
          ] }),
          state.isHydrated ? /* @__PURE__ */ jsx26(Body, { tracker, items, details, times, onOpenPhotos }) : /* @__PURE__ */ jsx26(OrderSkeletonBody, {})
        ] }),
        actions ? /* @__PURE__ */ jsx26(OrderCardActionBar_default, { driver, actionBar, actions, isAssigning }) : null
      ]
    }
  );
};
var OrderCard_default = React4.memo(OrderCard);

// src/OrderCard/OrderCardStateShell.tsx
import { Box as Box22, Card as Card3, Typography as Typography12 } from "@mui/material";
import { BiFingerprint as BiFingerprint3 } from "react-icons/bi";
import { jsx as jsx27, jsxs as jsxs20 } from "react/jsx-runtime";
var OrderCardStateShell = ({ children }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx27(
    Card3,
    {
      sx: {
        width: "100%",
        minWidth: 384,
        maxWidth: 576,
        borderRadius: `${tokens.radii.md}px`,
        boxShadow: tokens.shadows.card,
        border: "none",
        padding: "24px 20px",
        textAlign: "center",
        cursor: "default"
      },
      children
    }
  );
};
var OrderStateIds = ({ ids }) => {
  const tokens = useOrderTokens();
  if (!ids.length) return null;
  return /* @__PURE__ */ jsxs20(Box22, { sx: { display: "inline-flex", alignItems: "center", gap: "3px", color: tokens.text.muted, mb: "8px" }, children: [
    /* @__PURE__ */ jsx27(BiFingerprint3, { size: 12 }),
    /* @__PURE__ */ jsx27(Typography12, { component: "span", sx: { fontSize: "11px", fontWeight: 500 }, children: ids.join(" \xB7 ") })
  ] });
};
var OrderStateTitle = ({ children, isMuted = false }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx27(Typography12, { sx: { fontSize: "15px", fontWeight: 700, color: isMuted ? tokens.text.secondary : tokens.text.primary }, children });
};
var OrderStateDescription = ({ children }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx27(Typography12, { sx: { fontSize: "12.5px", color: tokens.text.secondary, maxWidth: 260, margin: "4px auto 12px" }, children });
};
var OrderStateIcon = ({ children, tone }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx27(
    Box22,
    {
      sx: {
        width: 48,
        height: 48,
        borderRadius: `${tokens.radii.pill}px`,
        margin: "0 auto 8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
        // A soft wash of the tone's color, not the color itself; the glyph carries it.
        bgcolor: `color-mix(in srgb, ${tokens.semantic[tone]} 12%, ${tokens.surfaces.card})`,
        color: tokens.semantic[tone]
      },
      children
    }
  );
};
var OrderCardStateShell_default = OrderCardStateShell;

// src/OrderCard/OrderCardEmpty.tsx
import { jsx as jsx28, jsxs as jsxs21 } from "react/jsx-runtime";
var OrderCardEmpty = ({ displayName, ids }) => /* @__PURE__ */ jsxs21(OrderCardStateShell_default, { children: [
  /* @__PURE__ */ jsx28(OrderStateTitle, { isMuted: true, children: displayName }),
  /* @__PURE__ */ jsx28(OrderStateIds, { ids }),
  /* @__PURE__ */ jsx28(OrderStateDescription, { children: "Details unavailable for this order." })
] });
var OrderCardEmpty_default = OrderCardEmpty;

// src/OrderCard/OrderCardError.tsx
import { BiErrorCircle as BiErrorCircle2 } from "react-icons/bi";
import { jsx as jsx29, jsxs as jsxs22 } from "react/jsx-runtime";
var OrderCardError = ({ ids }) => /* @__PURE__ */ jsxs22(OrderCardStateShell_default, { children: [
  /* @__PURE__ */ jsx29(OrderStateIcon, { tone: "error", children: /* @__PURE__ */ jsx29(BiErrorCircle2, {}) }),
  /* @__PURE__ */ jsx29(OrderStateTitle, { children: "Couldn't load this order" }),
  /* @__PURE__ */ jsx29(OrderStateIds, { ids }),
  /* @__PURE__ */ jsx29(OrderStateDescription, { children: "Something went wrong while fetching the details. Your other orders are not affected. Please contact HopDrive if this issue continues." })
] });
var OrderCardError_default = OrderCardError;

// src/OrderRow/OrderRow.tsx
import { TableRow as TableRow2 } from "@mui/material";
import { BodyCell, ExpandToggleCell, PinnedActionsCell } from "@hopdrive/sdk-ui";

// src/OrderRow/OrderRowExpansion.tsx
import { Box as Box23, Collapse, TableCell, TableRow } from "@mui/material";
import { jsx as jsx30 } from "react/jsx-runtime";
var OrderRowExpansion = ({ colSpan, isOpen, children }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx30(TableRow, { children: /* @__PURE__ */ jsx30(
    TableCell,
    {
      colSpan,
      sx: {
        padding: 0,
        // Open, it takes the bottom rule its parent row dropped.
        borderBottom: isOpen ? `1px solid ${tokens.lines.divider}` : "none"
      },
      children: /* @__PURE__ */ jsx30(Collapse, { in: isOpen, timeout: "auto", unmountOnExit: true, children: /* @__PURE__ */ jsx30(
        Box23,
        {
          sx: {
            padding: "12px",
            bgcolor: tokens.surfaces.legBackground,
            borderTop: `1px solid ${tokens.lines.divider}`,
            // The band owns all the spacing, so the list adds none.
            "& > div": { flexDirection: "row", flexWrap: "wrap", gap: "12px", padding: 0, maxWidth: "none" },
            "& > div > *": { flex: "1 1 384px", maxWidth: 576 }
          },
          children
        }
      ) })
    }
  ) });
};
var OrderRowExpansion_default = OrderRowExpansion;

// src/OrderRow/OrderRow.tsx
import { Fragment as Fragment4, jsx as jsx31, jsxs as jsxs23 } from "react/jsx-runtime";
var ORDER_ROW_MIN_HEIGHT = 56;
function OrderRow({
  orderKey: orderKey2,
  view,
  columns: columns2,
  items,
  expanded,
  onToggleExpanded,
  onOpenPhotos
}) {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs23(Fragment4, { children: [
    /* @__PURE__ */ jsxs23(
      TableRow2,
      {
        "data-order-key": orderKey2,
        "data-order-product": orderKey2?.split(":")[0],
        hover: true,
        onClick: onToggleExpanded,
        sx: {
          cursor: "pointer",
          "& > td": {
            // A td height is a minimum, so a taller cell still grows. Sized for a three-stop route stack.
            height: ORDER_ROW_MIN_HEIGHT,
            // An open row hands its bottom rule to the expansion below it.
            ...expanded ? { borderBottom: "none" } : {}
          }
        },
        children: [
          /* @__PURE__ */ jsx31(ExpandToggleCell, { isOpen: expanded, onToggle: onToggleExpanded, ariaLabel: "Toggle order details" }),
          columns2.map((column) => {
            const content = column.render(view);
            return column.pinned === "right" ? /* @__PURE__ */ jsx31(PinnedActionsCell, { children: content }, column.name) : /* @__PURE__ */ jsx31(
              BodyCell,
              {
                sx: {
                  textAlign: column.align ?? "left",
                  minWidth: column.minWidth,
                  fontSize: 12,
                  padding: "6px 10px",
                  verticalAlign: "middle",
                  borderBottom: `1px solid ${tokens.lines.divider}`
                },
                children: content
              },
              column.name
            );
          })
        ]
      }
    ),
    /* @__PURE__ */ jsx31(OrderRowExpansion_default, { colSpan: columns2.length + 1, isOpen: expanded, children: /* @__PURE__ */ jsx31(OrderCardItems, { items, onOpenPhotos }) })
  ] });
}
var OrderRow_default = OrderRow;

// src/OrderRow/OrderRowMobileCard.tsx
import { Box as Box24, Collapse as Collapse2, Typography as Typography13 } from "@mui/material";
import { BiChevronDown } from "react-icons/bi";
import { jsx as jsx32, jsxs as jsxs24 } from "react/jsx-runtime";
function OrderRowMobileCard({ orderKey: orderKey2, view, columns: columns2, items, expanded, onToggleExpanded, onOpenPhotos }) {
  const tokens = useOrderTokens();
  const pairs = columns2.filter((column) => column.mobile === "pair");
  const barLeft = columns2.filter((column) => column.mobile === "bar" && column.pinned !== "right");
  const barRight = columns2.filter((column) => column.mobile === "bar" && column.pinned === "right");
  const hasBar = barLeft.length > 0 || barRight.length > 0;
  return /* @__PURE__ */ jsxs24(
    Box24,
    {
      "data-order-key": orderKey2,
      "data-order-product": orderKey2?.split(":")[0],
      onClick: onToggleExpanded,
      sx: {
        bgcolor: tokens.surfaces.card,
        borderRadius: `${tokens.radii.md}px`,
        boxShadow: tokens.shadows.card,
        // No bottom padding: the items band and the actions band bleed to the card's edges.
        padding: "10px 12px 0",
        cursor: "pointer",
        overflow: "hidden"
      },
      children: [
        /* @__PURE__ */ jsx32(Box24, { sx: { display: "flex", flexDirection: "column", gap: "7px" }, children: pairs.map((column) => /* @__PURE__ */ jsxs24(Box24, { sx: { display: "flex", alignItems: "center", gap: "10px" }, children: [
          /* @__PURE__ */ jsx32(Typography13, { component: "span", sx: { flex: "none", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.text.labelFaded }, children: column.label }),
          /* @__PURE__ */ jsx32(Box24, { sx: { flex: 1, minWidth: 8, borderBottom: `1px dashed ${tokens.lines.divider}`, transform: "translateY(-2px)" } }),
          /* @__PURE__ */ jsx32(
            Box24,
            {
              sx: {
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 0,
                textAlign: "right",
                "& .MuiTypography-root": { maxWidth: 200 },
                // An object of selectors, never a callback.
                ...column.mobileValueSx ?? {}
              },
              children: column.render(view)
            }
          )
        ] }, column.name)) }),
        /* @__PURE__ */ jsx32(Box24, { sx: { display: "flex", justifyContent: "center", padding: "8px 0" }, children: /* @__PURE__ */ jsx32(Box24, { component: "span", sx: { display: "inline-flex", color: tokens.text.secondary, transition: "transform 150ms ease", transform: expanded ? "rotate(180deg)" : "none" }, children: /* @__PURE__ */ jsx32(BiChevronDown, { size: 14, "aria-hidden": true }) }) }),
        /* @__PURE__ */ jsx32(Collapse2, { in: expanded, timeout: "auto", unmountOnExit: true, sx: { mx: "-12px", mt: "4px" }, children: /* @__PURE__ */ jsx32(
          Box24,
          {
            sx: {
              padding: "12px",
              bgcolor: tokens.surfaces.legBackground,
              borderTop: `1px solid ${tokens.lines.divider}`,
              "& > div": { padding: 0, maxWidth: "none" }
            },
            children: /* @__PURE__ */ jsx32(OrderCardItems, { items, onOpenPhotos })
          }
        ) }),
        hasBar ? /* @__PURE__ */ jsxs24(Box24, { sx: { display: "flex", alignItems: "center", gap: "10px", borderTop: `1px solid ${tokens.lines.divider}`, margin: "0 -12px 0", padding: "8px 12px" }, children: [
          barLeft.map((column) => /* @__PURE__ */ jsx32(Box24, { sx: { display: "flex", alignItems: "center", minWidth: 0 }, children: column.render(view) }, column.name)),
          /* @__PURE__ */ jsx32(Box24, { sx: { flex: 1 } }),
          barRight.map((column) => /* @__PURE__ */ jsx32(Box24, { sx: { display: "flex", alignItems: "center", flex: "none" }, children: column.render(view) }, column.name))
        ] }) : null
      ]
    }
  );
}
var OrderRowMobileCard_default = OrderRowMobileCard;

// src/OrderRow/OrderRowMobileCardSkeleton.tsx
import { Box as Box25, Skeleton } from "@mui/material";
import { jsx as jsx33, jsxs as jsxs25 } from "react/jsx-runtime";
var OrderRowMobileCardSkeleton = () => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx33(Box25, { sx: { bgcolor: tokens.surfaces.card, borderRadius: `${tokens.radii.md}px`, boxShadow: tokens.shadows.card, padding: "10px 12px", display: "flex", flexDirection: "column", gap: "7px" }, children: Array.from({ length: 5 }, (_, index) => /* @__PURE__ */ jsxs25(Box25, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [
    /* @__PURE__ */ jsx33(Skeleton, { variant: "text", width: 56, sx: { fontSize: 9 } }),
    /* @__PURE__ */ jsx33(Skeleton, { variant: "text", width: index % 2 ? 96 : 140, sx: { fontSize: 12 } })
  ] }, index)) });
};
var OrderRowMobileCardSkeleton_default = OrderRowMobileCardSkeleton;

// src/OrderRow/OrderRowError.tsx
import { Box as Box26, TableCell as TableCell2, TableRow as TableRow3, Typography as Typography14 } from "@mui/material";
import { BiErrorCircle as BiErrorCircle3, BiFingerprint as BiFingerprint4 } from "react-icons/bi";
import { jsx as jsx34, jsxs as jsxs26 } from "react/jsx-runtime";
var ErrorContent = ({ ids }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs26(Box26, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [
    /* @__PURE__ */ jsx34(Box26, { sx: { display: "inline-flex", color: tokens.semantic.error, flex: "none" }, children: /* @__PURE__ */ jsx34(BiErrorCircle3, { size: 18 }) }),
    /* @__PURE__ */ jsxs26(Box26, { sx: { minWidth: 0 }, children: [
      /* @__PURE__ */ jsxs26(Box26, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1 }, children: [
        /* @__PURE__ */ jsx34(Typography14, { sx: { fontSize: 13, fontWeight: 700 }, children: "Couldn't load this order" }),
        ids.length ? /* @__PURE__ */ jsxs26(Box26, { sx: { display: "inline-flex", alignItems: "center", gap: "3px", color: tokens.text.muted }, children: [
          /* @__PURE__ */ jsx34(BiFingerprint4, { size: 12 }),
          /* @__PURE__ */ jsx34(Typography14, { component: "span", sx: { fontSize: 12, fontWeight: 500 }, children: ids.join(" \xB7 ") })
        ] }) : null
      ] }),
      /* @__PURE__ */ jsx34(Typography14, { sx: { fontSize: 12, color: tokens.text.secondary }, children: "Something went wrong while fetching the details. Your other orders are not affected. Please contact HopDrive if this issue continues." })
    ] })
  ] });
};
var OrderRowError = ({ colSpan, ids }) => /* @__PURE__ */ jsx34(TableRow3, { children: /* @__PURE__ */ jsx34(TableCell2, { colSpan, sx: { height: ORDER_ROW_MIN_HEIGHT, padding: "12px 10px" }, children: /* @__PURE__ */ jsx34(ErrorContent, { ids }) }) });
var OrderRowErrorCard = ({ ids }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx34(Box26, { sx: { bgcolor: tokens.surfaces.card, borderRadius: `${tokens.radii.md}px`, boxShadow: tokens.shadows.card, padding: "12px" }, children: /* @__PURE__ */ jsx34(ErrorContent, { ids }) });
};
var OrderRowError_default = OrderRowError;

// src/OrderIndexTable/OrderIndexTable.tsx
import React5, { useCallback, useMemo as useMemo2, useState as useState3 } from "react";
import { Box as Box28, Table, TableBody, TableCell as TableCell3, TableContainer, TableHead, TableRow as TableRow4, TableSortLabel } from "@mui/material";

// src/OrderSectionHeader/OrderSectionHeader.tsx
import { Box as Box27, Typography as Typography15 } from "@mui/material";
import { jsx as jsx35, jsxs as jsxs27 } from "react/jsx-runtime";
var OrderSectionHeader = ({ title: title2, count, variant = "band" }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs27(
    Box27,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: tokens.surfaces.sectionHeader,
        ...variant === "band" ? { px: 1.5, py: 0.75, borderRadius: `${tokens.radii.sm}px` } : { px: 1.25, py: 0.25 }
      },
      children: [
        /* @__PURE__ */ jsx35(Typography15, { component: "span", sx: { fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.text.onSectionHeader }, children: title2 }),
        /* @__PURE__ */ jsx35(Typography15, { component: "span", sx: { fontSize: 11, fontWeight: 600, color: tokens.text.onSectionHeaderMuted }, children: count })
      ]
    }
  );
};
var OrderSectionHeader_default = OrderSectionHeader;

// src/OrderIndexTable/OrderIndexTable.tsx
import { DataTableLoading, DataTableMobileCards, HeaderCell, PinnedActionsCell as PinnedActionsCell2, TABLE_DEFAULTS, dataTableDesktopOnlySx } from "@hopdrive/sdk-ui";

// src/shared/feed/viewerAccess.ts
var ADMIN_VIEWER_ROLES = /* @__PURE__ */ new Set(["admin", "dealer-super-admin", "dealer-admin", "program-admin"]);
var isAdminViewerRole = (role) => !!role && ADMIN_VIEWER_ROLES.has(role);
var optionApplies = (option, isAdminViewer, viewMode) => {
  if (option.adminOnly && !isAdminViewer) return false;
  if (option.viewModes && !option.viewModes.includes(viewMode)) return false;
  return true;
};
var resolveVisibleSortOptions = ({
  options,
  isAdminViewer,
  viewMode
}) => options.filter((option) => optionApplies(option, isAdminViewer, viewMode));
var resolveEffectiveSortBy = ({
  sortBy,
  options,
  isAdminViewer,
  viewMode,
  defaultSortBy
}) => {
  const option = options.find((candidate) => candidate.key === sortBy);
  if (!option) return defaultSortBy;
  return optionApplies(option, isAdminViewer, viewMode) ? sortBy : defaultSortBy;
};
var resolveEffectiveDisplayPreferences = ({
  preferences,
  definitions,
  isAdminViewer
}) => {
  if (isAdminViewer) return preferences;
  const effective = { ...preferences };
  for (const definition of definitions) if (definition.adminOnly) effective[definition.key] = false;
  return effective;
};
var resolveVisibleColumns = ({
  columns: columns2,
  metric,
  rowPreferences
}) => columns2.filter((column) => {
  const rule = column.visibleWhen;
  if (!rule) return true;
  if (rule.metric && !rule.metric.includes(metric)) return false;
  if (rule.notMetric && rule.notMetric.includes(metric)) return false;
  if (rule.preference && !rowPreferences[rule.preference]) return false;
  return true;
});

// src/OrderIndexTable/OrderIndexTable.tsx
import { Fragment as Fragment5, jsx as jsx36, jsxs as jsxs28 } from "react/jsx-runtime";
var OrderTableRowItem = React5.memo(function OrderTableRowItem2({
  view,
  rowKey,
  columns: columns2,
  items,
  expanded,
  onToggleKey,
  onOpenPhotos,
  variant
}) {
  const onToggleExpanded = () => onToggleKey(rowKey);
  const Row = variant === "row" ? OrderRow_default : OrderRowMobileCard_default;
  return /* @__PURE__ */ jsx36(Row, { orderKey: rowKey, view, columns: columns2, items, expanded, onToggleExpanded, onOpenPhotos });
});
function OrderIndexTable({
  items,
  columns: columns2,
  metric,
  rowPreferences,
  context,
  sortBy,
  sortOrder,
  onSortingChange,
  onOpenPhotos,
  isLoading = false,
  skeletonRowCount = 8,
  pagination,
  mobilePagination
}) {
  const tokens = useOrderTokens();
  const { cells } = useOrderRenderers();
  const [openKeys, setOpenKeys] = useState3(/* @__PURE__ */ new Set());
  const visibleColumns = useMemo2(() => resolveVisibleColumns({ columns: columns2, metric, rowPreferences }), [columns2, metric, rowPreferences]);
  const colCount = visibleColumns.length + 1;
  const toggleRow = useCallback(
    (key) => setOpenKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    }),
    []
  );
  const rowColumns = useMemo2(
    () => visibleColumns.map((column) => ({
      name: column.name,
      label: column.label,
      align: column.align,
      minWidth: column.minWidth,
      pinned: column.pinned,
      mobile: column.mobile,
      mobileValueSx: column.mobileValueSx,
      render: (view) => renderOrderCell(cells, { column, view, context, tokens })
    })),
    [visibleColumns, cells, context, tokens]
  );
  return /* @__PURE__ */ jsxs28(Fragment5, { children: [
    /* @__PURE__ */ jsxs28(
      Box28,
      {
        sx: {
          borderRadius: `${tokens.radii.md}px`,
          bgcolor: tokens.surfaces.card,
          boxShadow: tokens.shadows.card,
          // Without this the header's tint squares off the rounded corners.
          overflow: "hidden",
          ...dataTableDesktopOnlySx()
        },
        children: [
          pagination,
          /* @__PURE__ */ jsx36(TableContainer, { sx: { overflowX: "auto" }, children: /* @__PURE__ */ jsxs28(Table, { ...TABLE_DEFAULTS, children: [
            /* @__PURE__ */ jsx36(TableHead, { children: /* @__PURE__ */ jsxs28(TableRow4, { children: [
              /* @__PURE__ */ jsx36(HeaderCell, { sx: { width: 28, padding: "7px 0 7px 10px" } }),
              visibleColumns.map((column) => {
                const sortKey = column.sortKey ?? column.name;
                const headerSx = { textAlign: column.align ?? "left", minWidth: column.minWidth, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", padding: "7px 10px" };
                const label = column.sortable ? /* @__PURE__ */ jsx36(TableSortLabel, { active: sortBy === sortKey, direction: sortBy === sortKey ? sortOrder : "asc", onClick: () => onSortingChange(sortKey, sortBy === sortKey && sortOrder === "asc" ? "desc" : "asc"), children: column.label }) : column.label;
                const content = column.tooltip ? /* @__PURE__ */ jsx36(OrderTooltip_default, { content: [{ kind: "description", text: column.tooltip }], children: /* @__PURE__ */ jsx36(Box28, { component: "span", sx: { display: "inline-flex" }, children: label }) }) : label;
                return column.pinned === "right" ? /* @__PURE__ */ jsx36(PinnedActionsCell2, { variant: "head", sx: headerSx, children: content }, column.name) : /* @__PURE__ */ jsx36(HeaderCell, { sx: headerSx, children: content }, column.name);
              })
            ] }) }),
            /* @__PURE__ */ jsx36(TableBody, { sx: isLoading ? { "& tr": { height: ORDER_ROW_MIN_HEIGHT } } : void 0, children: isLoading ? /* @__PURE__ */ jsx36(DataTableLoading, { colSpan: colCount, rows: skeletonRowCount }) : items.map(
              (item) => item.kind === "section" ? (
                // Section header rows never count toward pagination.
                /* @__PURE__ */ jsx36(TableRow4, { children: /* @__PURE__ */ jsx36(TableCell3, { colSpan: colCount, sx: { p: 0, border: "none" }, children: /* @__PURE__ */ jsx36(OrderSectionHeader_default, { title: item.title, count: item.count, variant: "row" }) }) }, item.key)
              ) : item.kind === "error" ? /* @__PURE__ */ jsx36(OrderRowError_default, { colSpan: colCount, ids: item.ids }, item.key) : /* @__PURE__ */ jsx36(OrderTableRowItem, { rowKey: item.key, view: item.view, columns: rowColumns, items: item.card.items, expanded: openKeys.has(item.key), onToggleKey: toggleRow, onOpenPhotos, variant: "row" }, item.key)
            ) })
          ] }) }),
          pagination
        ]
      }
    ),
    /* @__PURE__ */ jsxs28(DataTableMobileCards, { children: [
      mobilePagination,
      isLoading ? (
        // A stack of full-height placeholders would run past a phone's viewport; a few
        // hold the shape.
        Array.from({ length: Math.min(skeletonRowCount, 4) }, (_, index) => /* @__PURE__ */ jsx36(OrderRowMobileCardSkeleton_default, {}, index))
      ) : items.map(
        (item) => item.kind === "section" ? /* @__PURE__ */ jsx36(OrderSectionHeader_default, { title: item.title, count: item.count }, item.key) : item.kind === "error" ? /* @__PURE__ */ jsx36(OrderRowErrorCard, { ids: item.ids }, item.key) : /* @__PURE__ */ jsx36(OrderTableRowItem, { rowKey: item.key, view: item.view, columns: rowColumns, items: item.card.items, expanded: openKeys.has(item.key), onToggleKey: toggleRow, onOpenPhotos, variant: "mobileCard" }, item.key)
      ),
      mobilePagination
    ] })
  ] });
}
var OrderIndexTable_default = OrderIndexTable;

// src/OrderIndexGrid/OrderIndexGrid.tsx
import { Box as Box29 } from "@mui/material";
import { Fragment as Fragment6, jsx as jsx37, jsxs as jsxs29 } from "react/jsx-runtime";
function OrderIndexGrid({ items, isLoading = false, skeletonCount = 6, pagination = null, onOpenPhotos }) {
  return /* @__PURE__ */ jsx37(Fragment6, { children: /* @__PURE__ */ jsxs29(Box29, { sx: { containerType: "inline-size", display: "flex", flexDirection: "column", gap: 3 }, children: [
    pagination,
    /* @__PURE__ */ jsx37(
      Box29,
      {
        sx: {
          display: "grid",
          // A wide region gains columns instead of wider cards. The track matches the card's
          // 384px floor, since a narrower one would squeeze a card and force a horizontal scroll.
          gridTemplateColumns: "1fr",
          "@container (min-width: 800px)": { gridTemplateColumns: "repeat(auto-fill, minmax(384px, 1fr))" },
          gap: 3
          // No `alignItems`, so every card in a row stretches to the tallest and its body
          // absorbs the height, keeping the footer on the bottom edge. No inset, because the
          // gap gives the straddling status strap its room.
        },
        children: isLoading ? Array.from({ length: skeletonCount }, (_, index) => /* @__PURE__ */ jsx37(OrderCardSkeleton_default, { variant: "card" }, `skeleton-${index}`)) : items.map(
          (item) => item.kind === "section" ? (
            // A full-width band in the same grid, so sections partition the flow without restructuring it.
            /* @__PURE__ */ jsx37(Box29, { sx: { gridColumn: "1 / -1", my: 1.5 }, children: /* @__PURE__ */ jsx37(OrderSectionHeader_default, { title: item.title, count: item.count }) }, item.key)
          ) : item.kind === "error" ? /* @__PURE__ */ jsx37(OrderCardError_default, { ids: item.ids }, item.key) : /* @__PURE__ */ jsx37(OrderCard_default, { ...item.card, orderKey: item.key, actions: item.actions, isAssigning: item.isAssigning, onOpenPhotos }, item.key)
        )
      }
    ),
    pagination
  ] }) });
}
var OrderIndexGrid_default = OrderIndexGrid;

// src/OrderIndexPagination/OrderIndexPagination.tsx
import { TablePagination } from "@mui/material";
import { jsx as jsx38 } from "react/jsx-runtime";
var OrderIndexPagination = ({ totalCount, page, pageSize, pageSizeOptions, onPageChange, onPageSizeChange }) => {
  if (!totalCount) return null;
  return /* @__PURE__ */ jsx38(
    TablePagination,
    {
      component: "div",
      count: totalCount,
      page,
      rowsPerPage: pageSize,
      onPageChange: (_event, nextPage) => onPageChange(nextPage),
      onRowsPerPageChange: (event) => onPageSizeChange(Number(event.target.value)),
      rowsPerPageOptions: [...pageSizeOptions],
      sx: { "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 } }
    }
  );
};
var OrderIndexPagination_default = OrderIndexPagination;

// src/OrderIndexEmpty/OrderIndexEmpty.tsx
import { Box as Box30, Button, Typography as Typography16 } from "@mui/material";
import { BiPackage } from "react-icons/bi";
import { jsx as jsx39, jsxs as jsxs30 } from "react/jsx-runtime";
var OrderIndexEmpty = ({ isFilteredEmpty, copy, onClearAllFilters }) => {
  const tokens = useOrderTokens();
  const text = isFilteredEmpty ? copy.filtered : copy.noOrders;
  return /* @__PURE__ */ jsxs30(Box30, { sx: { py: 8, textAlign: "center" }, children: [
    /* @__PURE__ */ jsx39(Box30, { sx: { width: 96, height: 96, borderRadius: "50%", bgcolor: "secondary.lighter", color: "secondary.main", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }, children: /* @__PURE__ */ jsx39(BiPackage, { size: 48 }) }),
    /* @__PURE__ */ jsx39(Typography16, { sx: { fontSize: 20, fontWeight: 600, mb: 1 }, children: text.title }),
    text.description ? /* @__PURE__ */ jsx39(Typography16, { variant: "body2", sx: { color: tokens.text.secondary, mb: 2 }, children: text.description }) : null,
    isFilteredEmpty && /* @__PURE__ */ jsx39(Button, { variant: "text", onClick: onClearAllFilters, sx: { textTransform: "none", color: tokens.text.secondary, fontWeight: 500, fontSize: 13 }, children: "Clear all filters" })
  ] });
};
var OrderIndexEmpty_default = OrderIndexEmpty;

// src/OrderIndex/OrderIndex.tsx
import { useMemo as useMemo5, useRef as useRef3 } from "react";
import { Alert, Box as Box39, Button as Button5 } from "@mui/material";

// src/OrderIndexControls/OrderIndexControls.tsx
import { Box as Box37, Card as Card4 } from "@mui/material";

// src/OrderIndexControls/controlsPresence.ts
function hasControlPieces(model) {
  const { search, dateRange, sort, groupBy, viewMode, displayPreferences, activeChips, customer, market, createdBy, product, driver, provider } = model;
  return [search, dateRange, sort, groupBy, viewMode, displayPreferences, activeChips, customer, market, createdBy, product, driver, provider].some(Boolean);
}
function splitControlsModel(model) {
  const { viewMode, displayPreferences, sort, groupBy, metricTiles: _metricTiles, ...filters } = model;
  return { view: { viewMode, displayPreferences, sort, groupBy }, filters };
}

// src/OrderIndexControls/OrderActiveFilterChips.tsx
import { Box as Box31, Button as Button2, Chip as Chip2, Typography as Typography17 } from "@mui/material";
import { BiX as BiX2 } from "react-icons/bi";
import { jsx as jsx40, jsxs as jsxs31 } from "react/jsx-runtime";
var OrderActiveFilterChips = ({ piece, compact = false }) => {
  const tokens = useOrderTokens();
  const { chips, onRemove, onClearAll } = piece;
  if (!chips.length) return null;
  return /* @__PURE__ */ jsxs31(Box31, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: compact ? 2 : 3, py: 1.5, borderTop: "1px solid", borderColor: tokens.lines.divider }, children: [
    /* @__PURE__ */ jsxs31(Box31, { sx: { display: "flex", alignItems: "center", gap: 0.75, mr: 0.5 }, children: [
      /* @__PURE__ */ jsx40(Typography17, { variant: "body2", sx: { fontSize: 12, color: tokens.text.secondary }, children: "Filters" }),
      /* @__PURE__ */ jsx40(Box31, { sx: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 18, height: 18, px: 0.75, borderRadius: `${tokens.radii.pill}px`, bgcolor: tokens.semantic.error, fontSize: 11, fontWeight: 700, color: tokens.text.onBadge }, children: chips.length })
    ] }),
    chips.map((chip) => /* @__PURE__ */ jsx40(
      Chip2,
      {
        label: chip.label,
        size: compact ? "medium" : "small",
        onDelete: () => onRemove(chip.key),
        deleteIcon: /* @__PURE__ */ jsx40(BiX2, { style: { fontSize: 14 } }),
        sx: {
          borderRadius: `${tokens.radii.sm}px`,
          bgcolor: tokens.surfaces.chipNeutral,
          color: tokens.text.secondary,
          fontWeight: 500,
          "& .MuiChip-deleteIcon": { color: tokens.text.muted, "&:hover": { color: tokens.text.secondary } }
        }
      },
      chip.key
    )),
    /* @__PURE__ */ jsx40(Button2, { variant: "text", size: "small", onClick: onClearAll, sx: { textTransform: "none", fontSize: 12, color: tokens.text.secondary, fontWeight: 500 }, children: "Clear all" })
  ] });
};
var OrderActiveFilterChips_default = OrderActiveFilterChips;

// src/OrderIndexControls/OrderDateRangeStrip.tsx
import { useMemo as useMemo3 } from "react";
import { Box as Box32 } from "@mui/material";
import { DateRangeFilter } from "@hopdrive/sdk-ui";

// src/shared/feed/dateWindow.ts
import dayjs from "dayjs";
var resolveDateWindowFromPreset = (preset, reference = void 0) => {
  const now = dayjs(reference);
  switch (preset) {
    case "today":
      return { startIso: now.startOf("day").format(), endIso: now.endOf("day").format() };
    case "tomorrow":
      return { startIso: now.add(1, "day").startOf("day").format(), endIso: now.add(1, "day").endOf("day").format() };
    case "yesterday":
      return {
        startIso: now.subtract(1, "day").startOf("day").format(),
        endIso: now.subtract(1, "day").endOf("day").format()
      };
    case "nextweek":
      return {
        startIso: now.add(1, "week").startOf("week").format(),
        endIso: now.add(1, "week").endOf("week").format()
      };
    case "thisweek":
      return { startIso: now.startOf("week").format(), endIso: now.endOf("week").format() };
    case "lastweek":
      return {
        startIso: now.subtract(1, "week").startOf("week").format(),
        endIso: now.subtract(1, "week").endOf("week").format()
      };
    case "nextmonth":
      return {
        startIso: now.add(1, "month").startOf("month").format(),
        endIso: now.add(1, "month").endOf("month").format()
      };
    case "thismonth":
      return { startIso: now.startOf("month").format(), endIso: now.endOf("month").format() };
    case "lastmonth":
      return {
        startIso: now.subtract(1, "month").startOf("month").format(),
        endIso: now.subtract(1, "month").endOf("month").format()
      };
    case "custom":
      return null;
    default:
      return null;
  }
};
var formatDateWindowLabel = (window) => {
  const start = dayjs(window.startIso);
  const end = dayjs(window.endIso);
  const startLabel = start.format("MM/DD/YYYY");
  return start.isSame(end, "day") ? startLabel : `${startLabel} \u2013 ${end.format("MM/DD/YYYY")}`;
};
var resolveEffectiveDateWindow = ({
  preset,
  startDate,
  endDate,
  reference
}) => {
  const fallback = resolveDateWindowFromPreset("today", reference);
  if (preset !== "custom") return resolveDateWindowFromPreset(preset, reference) ?? fallback;
  if (!startDate || !endDate) return fallback;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return fallback;
  const [from, to] = start.isAfter(end) ? [end, start] : [start, end];
  return { startIso: from.startOf("day").format(), endIso: to.endOf("day").format() };
};

// src/OrderIndexControls/OrderDateRangeStrip.tsx
import { jsx as jsx41, jsxs as jsxs32 } from "react/jsx-runtime";
var toFilterPresets = (presets) => presets.filter((candidate) => candidate.key !== "custom").map((candidate) => ({
  key: candidate.key,
  label: candidate.label,
  group: String(candidate.group),
  getWindow: (reference) => {
    const window = resolveDateWindowFromPreset(candidate.key, reference);
    return [new Date(window.startIso), new Date(window.endIso)];
  }
}));
var OrderDateRangeStrip = ({ piece, compact = false, filtersSlot = null }) => {
  const tokens = useOrderTokens();
  const { presets, preset, customStartIso, customEndIso, onPresetChange, onCustomRangeChange } = piece;
  const filterPresets = useMemo3(() => toFilterPresets(presets), [presets]);
  const value = { preset: preset ?? "today", startDate: customStartIso, endDate: customEndIso };
  const handleChange = (next) => {
    if (next.preset === "custom") {
      onPresetChange("custom");
      onCustomRangeChange(next.startDate, next.endDate);
      return;
    }
    onPresetChange(next.preset);
  };
  const control = /* @__PURE__ */ jsx41(DateRangeFilter, { value, onChange: handleChange, presets: filterPresets, fullWidth: compact, "aria-label": "Date range" });
  if (compact) {
    return /* @__PURE__ */ jsxs32(Box32, { sx: { display: "flex", flexDirection: "column", gap: 1.5, px: 2, py: 1.5, borderTop: "1px solid", borderColor: tokens.lines.divider }, children: [
      control,
      filtersSlot
    ] });
  }
  return /* @__PURE__ */ jsxs32(Box32, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: 3, py: 1.5, borderTop: "1px solid", borderColor: tokens.lines.divider }, children: [
    control,
    filtersSlot && /* @__PURE__ */ jsx41(Box32, { sx: { ml: "auto" }, children: filtersSlot })
  ] });
};
var OrderDateRangeStrip_default = OrderDateRangeStrip;

// src/OrderIndexControls/OrderDisplayPreferencesMenu.tsx
import { useState as useState4 } from "react";
import { Checkbox, Divider as Divider2, IconButton as IconButton2, ListItemText, ListSubheader, Menu as Menu2, MenuItem as MenuItem2, Tooltip as Tooltip3 } from "@mui/material";
import { BiShow } from "react-icons/bi";
import { Fragment as Fragment7, jsx as jsx42, jsxs as jsxs33 } from "react/jsx-runtime";
var OrderDisplayPreferencesMenu = ({ piece, surface }) => {
  const tokens = useOrderTokens();
  const [anchorEl, setAnchorEl] = useState4(null);
  const definitions = piece.definitions.filter((definition) => definition.surface === surface);
  const preferences = surface === "card" ? piece.card : piece.row;
  const sections = [...new Set(definitions.map((definition) => definition.section ?? ""))];
  return /* @__PURE__ */ jsxs33(Fragment7, { children: [
    /* @__PURE__ */ jsx42(Tooltip3, { title: "Displayed information", children: /* @__PURE__ */ jsx42(IconButton2, { onClick: (event) => setAnchorEl(event.currentTarget), "aria-label": "Displayed information", sx: { p: 1, color: tokens.text.muted }, children: /* @__PURE__ */ jsx42(BiShow, { size: 24 }) }) }),
    /* @__PURE__ */ jsx42(Menu2, { anchorEl, open: !!anchorEl, onClose: () => setAnchorEl(null), children: sections.flatMap((section2, index) => [
      ...index ? [/* @__PURE__ */ jsx42(Divider2, {}, `divider-${section2}`)] : [],
      ...section2 ? [
        /* @__PURE__ */ jsx42(ListSubheader, { disableSticky: true, sx: { lineHeight: "30px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }, children: section2 }, `header-${section2}`)
      ] : [],
      ...definitions.filter((definition) => (definition.section ?? "") === section2).map((definition) => (
        // Stays open between toggles, so turning on three columns takes one open.
        /* @__PURE__ */ jsxs33(MenuItem2, { dense: true, onClick: () => piece.onToggle(surface, definition.key), children: [
          /* @__PURE__ */ jsx42(Checkbox, { edge: "start", size: "small", checked: preferences[definition.key] ?? definition.defaultValue, disableRipple: true }),
          /* @__PURE__ */ jsx42(ListItemText, { primary: definition.label })
        ] }, definition.key)
      ))
    ]) })
  ] });
};
var OrderDisplayPreferencesMenu_default = OrderDisplayPreferencesMenu;

// src/OrderIndexControls/OrderFilterMultiselect.tsx
import { Checkbox as Checkbox2, ListItemText as ListItemText2, MenuItem as MenuItem3, TextField } from "@mui/material";
import { jsx as jsx43, jsxs as jsxs34 } from "react/jsx-runtime";
function OrderFilterMultiselect({ piece, fullWidth = false }) {
  const tokens = useOrderTokens();
  const labelOf = (value) => piece.options.find((option) => option.value === value)?.label ?? value;
  return /* @__PURE__ */ jsx43(
    TextField,
    {
      select: true,
      size: "small",
      label: piece.label,
      value: piece.selected,
      onChange: (event) => piece.onChange(event.target.value),
      slotProps: { select: { multiple: true, renderValue: (value) => value.map(labelOf).join(", ") } },
      sx: { ...fullWidth ? { width: "100%" } : { width: 220 }, "& .MuiOutlinedInput-root": { borderRadius: `${tokens.radii.md}px` } },
      children: piece.options.map((option) => /* @__PURE__ */ jsxs34(MenuItem3, { value: option.value, dense: true, disabled: option.disabled, children: [
        /* @__PURE__ */ jsx43(Checkbox2, { edge: "start", size: "small", checked: piece.selected.includes(option.value), disableRipple: true }),
        /* @__PURE__ */ jsx43(ListItemText2, { primary: option.count === void 0 ? option.label : `${option.label} (${option.count})`, slotProps: { primary: { fontSize: 13 } } })
      ] }, option.value))
    }
  );
}
var OrderFilterMultiselect_default = OrderFilterMultiselect;

// src/OrderIndexControls/OrderDriverFilter.tsx
import { jsx as jsx44 } from "react/jsx-runtime";
var OrderDriverFilter = ({ piece, fullWidth }) => /* @__PURE__ */ jsx44(OrderFilterMultiselect_default, { piece, fullWidth });
var OrderDriverFilter_default = OrderDriverFilter;

// src/OrderIndexControls/OrderGroupControl.tsx
import { useState as useState5 } from "react";
import { Box as Box33, Button as Button3, Menu as Menu3, MenuItem as MenuItem4 } from "@mui/material";
import { Fragment as Fragment8, jsx as jsx45, jsxs as jsxs35 } from "react/jsx-runtime";
var OrderGroupControl = ({ piece, fullWidth = false }) => {
  const tokens = useOrderTokens();
  const [menuAnchor, setMenuAnchor] = useState5(null);
  const { options, groupBy, onChange } = piece;
  const activeLabel = options.find((option) => option.key === groupBy)?.label ?? groupBy;
  const handlePick = (key) => {
    onChange(key);
    setMenuAnchor(null);
  };
  return /* @__PURE__ */ jsxs35(Fragment8, { children: [
    /* @__PURE__ */ jsxs35(
      Button3,
      {
        variant: "outlined",
        size: "small",
        color: "inherit",
        onClick: (event) => setMenuAnchor(event.currentTarget),
        "aria-haspopup": "menu",
        "aria-expanded": !!menuAnchor,
        sx: {
          ...fullWidth ? { width: "100%" } : {},
          textTransform: "none",
          fontSize: 13,
          fontWeight: 400,
          color: tokens.text.secondary,
          borderColor: tokens.lines.control,
          borderRadius: `${tokens.radii.md}px`,
          "&:hover": { borderColor: tokens.lines.control, bgcolor: tokens.surfaces.chipNeutral }
        },
        children: [
          /* @__PURE__ */ jsx45(Box33, { component: "span", sx: { color: tokens.text.secondary }, children: "Group By:\xA0" }),
          /* @__PURE__ */ jsx45(Box33, { component: "span", sx: { color: tokens.text.primary }, children: activeLabel })
        ]
      }
    ),
    /* @__PURE__ */ jsx45(Menu3, { anchorEl: menuAnchor, open: !!menuAnchor, onClose: () => setMenuAnchor(null), children: options.map((option) => /* @__PURE__ */ jsx45(MenuItem4, { dense: true, selected: option.key === groupBy, onClick: () => handlePick(option.key), children: option.label }, option.key)) })
  ] });
};
var OrderGroupControl_default = OrderGroupControl;

// src/OrderIndexControls/OrderProductFilter.tsx
import { Box as Box34, Chip as Chip3 } from "@mui/material";
import { jsx as jsx46 } from "react/jsx-runtime";
var OrderProductFilter = ({ piece }) => {
  const tokens = useOrderTokens();
  const toggle = (value) => {
    const next = piece.selected.includes(value) ? piece.selected.filter((selected) => selected !== value) : [...piece.selected, value];
    piece.onChange(next);
  };
  return /* @__PURE__ */ jsx46(Box34, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }, role: "group", "aria-label": piece.label, children: piece.options.map((option) => {
    const isSelected = piece.selected.includes(option.value);
    return /* @__PURE__ */ jsx46(
      Chip3,
      {
        label: option.count === void 0 ? option.label : `${option.label} \xB7 ${option.count}`,
        size: "small",
        clickable: true,
        disabled: option.disabled,
        "aria-pressed": isSelected,
        onClick: () => toggle(option.value),
        sx: {
          borderRadius: `${tokens.radii.pill}px`,
          fontWeight: 600,
          bgcolor: isSelected ? tokens.text.primary : tokens.surfaces.chipNeutral,
          color: isSelected ? tokens.text.onBadge : tokens.text.secondary,
          "&:hover": { bgcolor: isSelected ? tokens.text.primary : tokens.surfaces.chipStrong }
        }
      },
      option.value
    );
  }) });
};
var OrderProductFilter_default = OrderProductFilter;

// src/OrderIndexControls/OrderProviderFilter.tsx
import { jsx as jsx47 } from "react/jsx-runtime";
var OrderProviderFilter = ({ piece, fullWidth }) => /* @__PURE__ */ jsx47(OrderFilterMultiselect_default, { piece, fullWidth });
var OrderProviderFilter_default = OrderProviderFilter;

// src/OrderIndexControls/OrderSearch.tsx
import { useEffect as useEffect3, useState as useState6 } from "react";
import { IconButton as IconButton3, InputAdornment, TextField as TextField2 } from "@mui/material";
import { BiSearch, BiX as BiX3 } from "react-icons/bi";

// src/shared/hooks/useDebouncedCallback.ts
import { useCallback as useCallback2, useEffect as useEffect2, useMemo as useMemo4, useRef } from "react";

// src/shared/controls/debounce.ts
var createTrailingDebounce = (fn, delayMs) => {
  let timer = null;
  return {
    call: (value) => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn(value);
      }, delayMs);
    },
    cancel: () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    }
  };
};

// src/shared/hooks/useDebouncedCallback.ts
var useDebouncedCallback = (callback, delayMs) => {
  const callbackRef = useRef(callback);
  useEffect2(() => {
    callbackRef.current = callback;
  });
  const debounceRef = useRef(null);
  useEffect2(() => {
    const debounce = createTrailingDebounce((value) => callbackRef.current(value), delayMs);
    debounceRef.current = debounce;
    return () => {
      debounce.cancel();
      debounceRef.current = null;
    };
  }, [delayMs]);
  const call = useCallback2((value) => debounceRef.current?.call(value), []);
  const cancel = useCallback2(() => debounceRef.current?.cancel(), []);
  return useMemo4(() => ({ call, cancel }), [call, cancel]);
};

// src/OrderIndexControls/OrderSearch.tsx
import { jsx as jsx48 } from "react/jsx-runtime";
var SEARCH_DEBOUNCE_MS = 1e3;
var OrderSearch = ({ piece, fullWidth = false }) => {
  const tokens = useOrderTokens();
  const { value: search, placeholder, onChange, clearSignal = 0 } = piece;
  const [value, setValue] = useState6(search);
  const debouncedSearchChange = useDebouncedCallback(onChange, SEARCH_DEBOUNCE_MS);
  const [prevSearch, setPrevSearch] = useState6(search);
  if (search !== prevSearch) {
    setPrevSearch(search);
    setValue(search);
  }
  const [prevClearSignal, setPrevClearSignal] = useState6(clearSignal);
  if (clearSignal !== prevClearSignal) {
    setPrevClearSignal(clearSignal);
    setValue("");
  }
  useEffect3(() => {
    debouncedSearchChange.cancel();
  }, [search, clearSignal, debouncedSearchChange]);
  const handleChange = (next) => {
    setValue(next);
    debouncedSearchChange.call(next);
  };
  const handleClear = () => {
    debouncedSearchChange.cancel();
    setValue("");
    onChange("");
  };
  return /* @__PURE__ */ jsx48(
    TextField2,
    {
      size: "small",
      placeholder,
      value,
      onChange: (event) => handleChange(event.target.value),
      slotProps: {
        input: {
          startAdornment: /* @__PURE__ */ jsx48(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx48(BiSearch, { style: { fontSize: 18, color: "inherit" } }) }),
          endAdornment: value ? /* @__PURE__ */ jsx48(InputAdornment, { position: "end", children: /* @__PURE__ */ jsx48(IconButton3, { size: "small", edge: "end", "aria-label": "Clear search", onClick: handleClear, children: /* @__PURE__ */ jsx48(BiX3, { style: { fontSize: 16 } }) }) }) : void 0
        }
      },
      sx: { ...fullWidth ? { width: "100%" } : { flex: 1, minWidth: 200, maxWidth: 360 }, "& .MuiOutlinedInput-root": { borderRadius: `${tokens.radii.md}px` } }
    }
  );
};
var OrderSearch_default = OrderSearch;

// src/OrderIndexControls/OrderSortControl.tsx
import { useState as useState7 } from "react";
import { Box as Box35, Button as Button4, ButtonGroup, Menu as Menu4, MenuItem as MenuItem5, Tooltip as Tooltip4 } from "@mui/material";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { Fragment as Fragment9, jsx as jsx49, jsxs as jsxs36 } from "react/jsx-runtime";
var OrderSortControl = ({ piece, fullWidth = false }) => {
  const tokens = useOrderTokens();
  const [menuAnchor, setMenuAnchor] = useState7(null);
  const { options, sortBy, sortOrder, onChange } = piece;
  const activeLabel = options.find((option) => option.key === sortBy)?.label ?? sortBy;
  const handleKeyPick = (key) => {
    onChange(key, sortOrder);
    setMenuAnchor(null);
  };
  return /* @__PURE__ */ jsxs36(Fragment9, { children: [
    /* @__PURE__ */ jsxs36(
      ButtonGroup,
      {
        variant: "outlined",
        size: "small",
        color: "inherit",
        sx: {
          ...fullWidth ? { width: "100%" } : {},
          "& .MuiButton-root": {
            textTransform: "none",
            fontSize: 13,
            fontWeight: 400,
            color: tokens.text.secondary,
            borderColor: tokens.lines.control,
            "&:hover": { borderColor: tokens.lines.control, bgcolor: tokens.surfaces.chipNeutral }
          },
          "& .MuiButton-root:first-of-type": {
            borderTopLeftRadius: `${tokens.radii.md}px`,
            borderBottomLeftRadius: `${tokens.radii.md}px`,
            // The label segment carries the stretch; the direction toggle keeps its size.
            ...fullWidth ? { flex: 1 } : {}
          },
          // An opaque line, because an alpha line composites differently here and never matches the border.
          "& .MuiButtonGroup-firstButton": { borderRightColor: tokens.lines.control },
          "& .MuiButton-root:last-of-type": { borderTopRightRadius: `${tokens.radii.md}px`, borderBottomRightRadius: `${tokens.radii.md}px` }
        },
        children: [
          /* @__PURE__ */ jsxs36(Button4, { onClick: (event) => setMenuAnchor(event.currentTarget), "aria-haspopup": "menu", "aria-expanded": !!menuAnchor, children: [
            /* @__PURE__ */ jsx49(Box35, { component: "span", sx: { color: tokens.text.secondary }, children: "Sort By:\xA0" }),
            /* @__PURE__ */ jsx49(Box35, { component: "span", sx: { color: tokens.text.primary }, children: activeLabel })
          ] }),
          /* @__PURE__ */ jsx49(Tooltip4, { title: sortOrder === "asc" ? "Ascending" : "Descending", children: /* @__PURE__ */ jsx49(Button4, { onClick: () => onChange(sortBy, sortOrder === "asc" ? "desc" : "asc"), "aria-pressed": sortOrder === "desc", "aria-label": `Sort ${sortOrder === "asc" ? "descending" : "ascending"}`, sx: { px: 1 }, children: sortOrder === "asc" ? /* @__PURE__ */ jsx49(BiSortUp, { style: { fontSize: 16 } }) : /* @__PURE__ */ jsx49(BiSortDown, { style: { fontSize: 16 } }) }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx49(Menu4, { anchorEl: menuAnchor, open: !!menuAnchor, onClose: () => setMenuAnchor(null), children: options.map((option) => /* @__PURE__ */ jsx49(MenuItem5, { dense: true, selected: option.key === sortBy, onClick: () => handleKeyPick(option.key), children: option.label }, option.key)) })
  ] });
};
var OrderSortControl_default = OrderSortControl;

// src/OrderIndexControls/OrderViewModeToggle.tsx
import { Box as Box36, ButtonBase, Typography as Typography18 } from "@mui/material";
import { BiGridAlt, BiListUl, BiMap } from "react-icons/bi";
import { jsx as jsx50, jsxs as jsxs37 } from "react/jsx-runtime";
var MODE_DEFINITIONS = {
  cards: { label: "Grid", icon: /* @__PURE__ */ jsx50(BiGridAlt, { style: { fontSize: 16 } }) },
  table: { label: "Table", icon: /* @__PURE__ */ jsx50(BiListUl, { style: { fontSize: 16 } }) },
  map: { label: "Map", icon: /* @__PURE__ */ jsx50(BiMap, { style: { fontSize: 16 } }) }
};
var OrderViewModeToggle = ({ piece, fullWidth = false }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx50(Box36, { sx: { display: "flex", gap: "2px", p: "2px", borderRadius: `${tokens.radii.md}px`, bgcolor: tokens.surfaces.chipNeutral, ...fullWidth ? { width: "100%" } : {} }, children: piece.modes.map((mode) => {
    const definition = MODE_DEFINITIONS[mode];
    const isActive = mode === piece.value;
    return /* @__PURE__ */ jsxs37(
      ButtonBase,
      {
        "aria-label": definition.label,
        "aria-pressed": isActive,
        onClick: () => piece.onChange(mode),
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          // With the track's 2px padding this matches the search field's 40px height.
          minHeight: 36,
          ...fullWidth ? { flex: 1 } : {},
          borderRadius: `${tokens.radii.sm}px`,
          bgcolor: isActive ? tokens.surfaces.card : "transparent",
          color: isActive ? tokens.text.primary : tokens.text.secondary,
          boxShadow: isActive ? tokens.shadows.card : "none",
          "&:hover": { bgcolor: isActive ? tokens.surfaces.card : tokens.surfaces.chipStrong }
        },
        children: [
          definition.icon,
          /* @__PURE__ */ jsx50(Typography18, { component: "span", sx: { fontSize: 13, fontWeight: 600, lineHeight: 1 }, children: definition.label })
        ]
      },
      mode
    );
  }) });
};
var OrderViewModeToggle_default = OrderViewModeToggle;

// src/OrderIndexControls/OrderIndexControls.tsx
import { jsx as jsx51, jsxs as jsxs38 } from "react/jsx-runtime";
var OrderIndexControls = ({ model, compact = false }) => {
  const tokens = useOrderTokens();
  const { search, dateRange, sort, groupBy, viewMode, displayPreferences, activeChips, customer, market, createdBy, offerStatus, product, driver, provider } = model;
  const hasToolbar = !!(viewMode || displayPreferences || groupBy || sort || search);
  const standingPieces = [customer, market, createdBy, offerStatus, driver, provider, product].filter(Boolean).length;
  if (!hasControlPieces(model)) return null;
  const preferencesMenu = displayPreferences ? /* @__PURE__ */ jsx51(OrderDisplayPreferencesMenu_default, { piece: displayPreferences, surface: viewMode?.value === "table" ? "row" : "card" }) : null;
  const standingFilters = standingPieces ? /* @__PURE__ */ jsxs38(Box37, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: compact ? 1.5 : 2 }, children: [
    customer ? /* @__PURE__ */ jsx51(Box37, { sx: compact ? { flex: 1, minWidth: 0 } : void 0, children: /* @__PURE__ */ jsx51(OrderFilterMultiselect_default, { piece: customer, fullWidth: compact }) }) : null,
    market ? /* @__PURE__ */ jsx51(Box37, { sx: compact ? { flex: 1, minWidth: 0 } : void 0, children: /* @__PURE__ */ jsx51(OrderFilterMultiselect_default, { piece: market, fullWidth: compact }) }) : null,
    offerStatus ? /* @__PURE__ */ jsx51(Box37, { sx: compact ? { flex: 1, minWidth: 0 } : void 0, children: /* @__PURE__ */ jsx51(OrderFilterMultiselect_default, { piece: offerStatus, fullWidth: compact }) }) : null,
    createdBy ? /* @__PURE__ */ jsx51(Box37, { sx: compact ? { flex: 1, minWidth: 0 } : void 0, children: /* @__PURE__ */ jsx51(OrderFilterMultiselect_default, { piece: createdBy, fullWidth: compact }) }) : null,
    driver ? /* @__PURE__ */ jsx51(Box37, { sx: compact ? { flex: 1, minWidth: 0 } : void 0, children: /* @__PURE__ */ jsx51(OrderDriverFilter_default, { piece: driver, fullWidth: compact }) }) : null,
    provider ? /* @__PURE__ */ jsx51(Box37, { sx: compact ? { flex: 1, minWidth: 0 } : void 0, children: /* @__PURE__ */ jsx51(OrderProviderFilter_default, { piece: provider, fullWidth: compact }) }) : null,
    product ? /* @__PURE__ */ jsx51(OrderProductFilter_default, { piece: product }) : null
  ] }) : null;
  return /* @__PURE__ */ jsxs38(
    Card4,
    {
      component: "section",
      "aria-label": "Order filters",
      sx: {
        mb: 2.25,
        bgcolor: tokens.surfaces.card,
        // The cards' shadow token, so this surface and the cards sit at one elevation.
        borderRadius: `${tokens.radii.lg}px`,
        boxShadow: tokens.shadows.card,
        border: "none",
        overflow: "visible"
      },
      children: [
        hasToolbar ? compact ? (
          /* The stack keeps the desktop's left-to-right order, so a resize never rearranges the controls. */
          /* @__PURE__ */ jsxs38(Box37, { sx: { display: "flex", flexDirection: "column", gap: 1.5, px: 2, py: 2 }, children: [
            viewMode || preferencesMenu ? /* @__PURE__ */ jsxs38(Box37, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
              viewMode ? /* @__PURE__ */ jsx51(Box37, { sx: { flex: 1, minWidth: 0 }, children: /* @__PURE__ */ jsx51(OrderViewModeToggle_default, { piece: viewMode, fullWidth: true }) }) : null,
              preferencesMenu
            ] }) : null,
            groupBy || sort ? /* @__PURE__ */ jsxs38(Box37, { sx: { display: "flex", gap: 1 }, children: [
              groupBy ? /* @__PURE__ */ jsx51(Box37, { sx: { flex: 1, minWidth: 0 }, children: /* @__PURE__ */ jsx51(OrderGroupControl_default, { piece: groupBy, fullWidth: true }) }) : null,
              sort ? /* @__PURE__ */ jsx51(Box37, { sx: { flex: 1, minWidth: 0 }, children: /* @__PURE__ */ jsx51(OrderSortControl_default, { piece: sort, fullWidth: true }) }) : null
            ] }) : null,
            search ? /* @__PURE__ */ jsx51(OrderSearch_default, { piece: search, fullWidth: true }) : null
          ] })
        ) : /* @__PURE__ */ jsxs38(Box37, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: 3, py: 2 }, children: [
          viewMode || preferencesMenu ? /* @__PURE__ */ jsxs38(Box37, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
            viewMode ? /* @__PURE__ */ jsx51(OrderViewModeToggle_default, { piece: viewMode }) : null,
            preferencesMenu
          ] }) : null,
          groupBy || sort ? /* @__PURE__ */ jsxs38(Box37, { sx: { ml: "auto", display: "flex", alignItems: "center", gap: 1 }, children: [
            groupBy ? /* @__PURE__ */ jsx51(OrderGroupControl_default, { piece: groupBy }) : null,
            sort ? /* @__PURE__ */ jsx51(OrderSortControl_default, { piece: sort }) : null
          ] }) : null,
          search ? /* @__PURE__ */ jsx51(OrderSearch_default, { piece: search }) : null
        ] }) : null,
        dateRange ? /* @__PURE__ */ jsx51(OrderDateRangeStrip_default, { piece: dateRange, compact, filtersSlot: standingFilters }) : standingFilters ? /* @__PURE__ */ jsx51(Box37, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: compact ? 2 : 3, py: 1.5, ...hasToolbar ? { borderTop: "1px solid", borderColor: tokens.lines.divider } : {} }, children: standingFilters }) : null,
        activeChips ? /* @__PURE__ */ jsx51(OrderActiveFilterChips_default, { piece: activeChips, compact }) : null
      ]
    }
  );
};
var OrderIndexControls_default = OrderIndexControls;

// src/OrderIndexControls/OrderMetricTiles.tsx
import { Box as Box38 } from "@mui/material";
import { KpiCard } from "@hopdrive/sdk-ui";
import { jsx as jsx52 } from "react/jsx-runtime";
var OrderMetricTiles = ({ piece }) => {
  const { tiles, counts, selected, onSelect, loading = false } = piece;
  const visible = tiles.filter((tile) => {
    if (tile.hideWhenZero) return (counts[tile.key] ?? 0) > 0 || selected === tile.key;
    return true;
  });
  return /* @__PURE__ */ jsx52(Box38, { sx: { display: "flex", flexWrap: "wrap", gap: 2.5, mb: 2.25 }, children: visible.map((tile) => /* @__PURE__ */ jsx52(Box38, { sx: { flex: "1 1 180px", minWidth: 160 }, children: /* @__PURE__ */ jsx52(
    KpiCard,
    {
      title: tile.label,
      value: counts[tile.key] ?? 0,
      loading,
      selected: selected === tile.key,
      highlight: tile.highlight,
      "aria-label": `${tile.label}: ${counts[tile.key] ?? 0}`,
      onClick: () => onSelect(selected === tile.key ? "all" : tile.key)
    }
  ) }, tile.key)) });
};
var OrderMetricTiles_default = OrderMetricTiles;

// src/shared/feed/renderGuards.ts
var pagedRowCache = /* @__PURE__ */ new WeakMap();
var reusePagedEntry = ({
  indexEntry,
  hydratedOrder,
  build
}) => {
  const key = hydratedOrder ?? indexEntry;
  const cached = pagedRowCache.get(key);
  const next = build();
  if (cached && cached.indexEntry === indexEntry && cached.row.isHydrated === next.isHydrated && // Part of the comparison, not optional: a row going unhydrated -> FAILED keeps
  // `isHydrated: false`, so without this the cache would hand back the pending row and
  // the card would pulse as a skeleton forever even though the page knows the query died.
  !!cached.row.failedToHydrate === !!next.failedToHydrate && cached.row.hasUnreadMessages === next.hasUnreadMessages) {
    return cached.row;
  }
  pagedRowCache.set(key, { indexEntry, row: next });
  return next;
};
var createRenderValueCache = () => {
  let inputs = [];
  let values = /* @__PURE__ */ new WeakMap();
  return {
    get(key, sharedInputs, build) {
      if (inputs.length !== sharedInputs.length || inputs.some((input, index) => input !== sharedInputs[index])) {
        inputs = sharedInputs;
        values = /* @__PURE__ */ new WeakMap();
      }
      const cached = values.get(key);
      if (cached !== void 0) return cached;
      const value = build();
      values.set(key, value);
      return value;
    }
  };
};

// src/shared/hooks/useCompactRegion.ts
import { useEffect as useEffect4, useRef as useRef2, useState as useState8 } from "react";
var COMPACT_REGION_WIDTH = 900;
var useCompactRegion = (threshold = COMPACT_REGION_WIDTH) => {
  const ref = useRef2(null);
  const [compact, setCompact] = useState8(false);
  useEffect4(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const measure = () => setCompact(element.getBoundingClientRect().width < threshold);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, compact };
};

// src/OrderIndex/CollapsibleRegion.tsx
import { Accordion, AccordionDetails, AccordionSummary, Typography as Typography19 } from "@mui/material";
import { BiChevronDown as BiChevronDown2 } from "react-icons/bi";
import { Fragment as Fragment10, jsx as jsx53, jsxs as jsxs39 } from "react/jsx-runtime";
var CollapsibleRegion = ({ title: title2, collapsible, children }) => {
  if (!collapsible) return /* @__PURE__ */ jsx53(Fragment10, { children });
  return /* @__PURE__ */ jsxs39(
    Accordion,
    {
      disableGutters: true,
      elevation: 0,
      sx: { mb: 2.25, bgcolor: "transparent", border: 1, borderColor: "divider", borderRadius: 2, "&:before": { display: "none" } },
      children: [
        /* @__PURE__ */ jsx53(AccordionSummary, { expandIcon: /* @__PURE__ */ jsx53(BiChevronDown2, { size: 20 }), sx: { minHeight: 48, "&.Mui-expanded": { minHeight: 48 } }, children: /* @__PURE__ */ jsx53(Typography19, { variant: "subtitle2", sx: { fontWeight: 600 }, children: title2 }) }),
        /* @__PURE__ */ jsx53(AccordionDetails, { sx: { pt: 0 }, children })
      ]
    }
  );
};
var CollapsibleRegion_default = CollapsibleRegion;

// src/OrderIndex/orderIndexRegions.ts
var resolveOrderIndexRegions = ({ loading, entriesCount, windowCount, activeChipCount, viewMode, capReached, hasError }) => {
  const isFirstPaint = loading && windowCount === 0;
  const isEmpty = !loading && entriesCount === 0;
  const emptyState = !isEmpty || isFirstPaint ? "none" : windowCount > 0 || activeChipCount > 0 ? "filtered" : "noOrders";
  return {
    isFirstPaint,
    emptyState,
    showPagination: entriesCount > 0,
    view: viewMode === "table" ? "table" : viewMode === "map" ? "map" : "grid",
    showCapNotice: capReached,
    showErrorNotice: hasError
  };
};

// src/OrderIndex/writeControlsAllowedFor.ts
function writeControlsAllowedFor(setting, product) {
  if (setting === void 0) return true;
  if (typeof setting === "boolean") return setting;
  return setting[product] ?? true;
}

// src/OrderIndex/OrderIndex.tsx
import { Fragment as Fragment11, jsx as jsx54, jsxs as jsxs40 } from "react/jsx-runtime";
var MAP_PLACEHOLDER_COPY = { title: "Map view coming soon", description: "Switch to Grid or Table to see these orders." };
var WRITE_ACTION_KEYS = /* @__PURE__ */ new Set([
  "assignDriver",
  "cancelMove",
  "changePromiseTime",
  "createDuplicate",
  "createInverse"
]);
function gateWriteControls(card, writeControlsAllowed) {
  if (writeControlsAllowed) return card;
  return {
    ...card,
    actionBar: card.actionBar.filter((descriptor) => !WRITE_ACTION_KEYS.has(descriptor.key)),
    driver: { ...card.driver, showPill: false }
  };
}
function OrderIndex({ feed, controls, viewMode, resolved, preferences, nowMs, pageSize, pageSizes, resolveCardActions, isAssigning, onOpenPhotos, onPageChange, onPageSizeChange, onRetry, compact: compactOverride, hidePagination, writeControlsAllowed, collapsibleControls, slots }) {
  const platform = useOrderIndexPlatform();
  const region = useCompactRegion();
  const compact = compactOverride ?? region.compact;
  const viewCache = useRef3(createRenderValueCache());
  const cardCache = useRef3(createRenderValueCache());
  const modelsByView = useRef3(/* @__PURE__ */ new WeakMap());
  const activePreferences = viewMode === "table" ? preferences.row : preferences.card;
  const items = useMemo5(() => {
    const shared = [resolved, platform, nowMs];
    return feed.pagedEntries.flatMap((entry) => {
      if (entry.kind === "error") return [{ kind: "error", key: entry.key, ids: entry.ids }];
      const { model } = entry;
      if (model.hydrationFailed) return [{ kind: "error", key: model.key, ids: [model.id] }];
      const view = viewCache.current.get(model, shared, () => resolved.toView(model, { platform, nowMs }));
      if (view === null) return [{ kind: "error", key: model.key, ids: [model.id] }];
      modelsByView.current.set(view, model);
      const built = cardCache.current.get(model, [...shared, activePreferences], () => resolved.toCardProps(view, activePreferences));
      const card = gateWriteControls(built, writeControlsAllowedFor(writeControlsAllowed, model.product));
      return [{ kind: "order", key: model.key, view, card, actions: resolveCardActions?.(model, view), isAssigning: isAssigning?.(model) ?? false }];
    });
  }, [feed.pagedEntries, resolved, platform, nowMs, activePreferences, resolveCardActions, isAssigning, writeControlsAllowed]);
  const listItems = useMemo5(() => {
    if (!feed.pageSections) return items;
    const byKey = new Map(items.map((item) => [item.key, item]));
    return feed.pageSections.flatMap((section2) => [
      { kind: "section", key: `section:${section2.key}`, title: section2.title, count: section2.count },
      ...section2.entries.map((entry) => byKey.get(entry.kind === "order" ? entry.model.key : entry.key)).filter((item) => !!item)
    ]);
  }, [items, feed.pageSections]);
  const context = useMemo5(
    () => ({
      onOpenPhotos,
      renderActions: (view) => {
        const model = modelsByView.current.get(view);
        const actions = model ? resolveCardActions?.(model, view) : void 0;
        if (!model || !actions) return null;
        const built = cardCache.current.get(model, [resolved, platform, nowMs, activePreferences], () => resolved.toCardProps(view, activePreferences));
        const card = gateWriteControls(built, writeControlsAllowedFor(writeControlsAllowed, model.product));
        return /* @__PURE__ */ jsx54(OrderActionButtons_default, { actions: card.actionBar, handlers: actions });
      },
      onAssignDriver: (view) => {
        const model = modelsByView.current.get(view);
        if (!model || !writeControlsAllowedFor(writeControlsAllowed, model.product)) return;
        resolveCardActions?.(model, view)?.assignDriver?.();
      },
      isOrderAssigning: (view) => {
        const model = modelsByView.current.get(view);
        return model ? isAssigning?.(model) ?? false : false;
      }
    }),
    [onOpenPhotos, resolveCardActions, isAssigning, resolved, platform, nowMs, activePreferences, writeControlsAllowed]
  );
  const splitControls = useMemo5(() => splitControlsModel(controls), [controls]);
  const regions = resolveOrderIndexRegions({
    loading: feed.loading,
    entriesCount: feed.entries.length,
    windowCount: feed.metricCounts.all ?? feed.entries.length,
    activeChipCount: controls.activeChips?.chips.length ?? 0,
    viewMode,
    capReached: feed.capReached,
    hasError: feed.error !== null
  });
  const pagination = regions.showPagination && !hidePagination ? /* @__PURE__ */ jsx54(OrderIndexPagination_default, { totalCount: feed.totalCount, page: feed.page, pageSize, pageSizeOptions: pageSizes, onPageChange, onPageSizeChange }) : null;
  const onClearAll = controls.activeChips?.onClearAll ?? (() => {
  });
  const skeletonCount = Math.min(pageSize, 6);
  const skeletonRowCount = Math.min(pageSize, 10);
  return /* @__PURE__ */ jsxs40(Box39, { ref: region.ref, children: [
    slots?.aboveTiles,
    controls.metricTiles ? /* @__PURE__ */ jsx54(CollapsibleRegion_default, { title: "Metrics", collapsible: collapsibleControls === true, children: /* @__PURE__ */ jsx54(OrderMetricTiles_default, { piece: { ...controls.metricTiles, loading: controls.metricTiles.loading ?? regions.isFirstPaint } }) }) : null,
    regions.showCapNotice ? /* @__PURE__ */ jsx54(Alert, { severity: "warning", sx: { mb: 2.25 }, children: resolved.copy.capNotice }) : null,
    regions.showErrorNotice ? /* @__PURE__ */ jsxs40(Alert, { severity: "error", sx: { mb: 2.25 }, children: [
      "Couldn't load ",
      resolved.copy.unit.plural,
      ". ",
      feed.error?.message
    ] }) : null,
    feed.pageError ? /* @__PURE__ */ jsxs40(
      Alert,
      {
        severity: "error",
        sx: { mb: 2.25 },
        action: onRetry ? /* @__PURE__ */ jsx54(Button5, { color: "inherit", size: "small", onClick: onRetry, children: "Retry" }) : void 0,
        children: [
          "Couldn't load ",
          resolved.copy.unit.singular,
          " details. The list below shows what we know from the summary; open an ",
          resolved.copy.unit.singular,
          " to see the rest. ",
          feed.pageError.message
        ]
      }
    ) : null,
    collapsibleControls ? /* @__PURE__ */ jsxs40(Fragment11, { children: [
      hasControlPieces(splitControls.view) ? /* @__PURE__ */ jsx54(OrderIndexControls_default, { model: splitControls.view, compact }) : null,
      hasControlPieces(splitControls.filters) ? /* @__PURE__ */ jsx54(CollapsibleRegion_default, { title: "Filters", collapsible: true, children: /* @__PURE__ */ jsx54(OrderIndexControls_default, { model: splitControls.filters, compact }) }) : null
    ] }) : /* @__PURE__ */ jsx54(OrderIndexControls_default, { model: controls, compact }),
    slots?.belowControls,
    regions.emptyState !== "none" ? /* @__PURE__ */ jsx54(OrderIndexEmpty_default, { isFilteredEmpty: regions.emptyState === "filtered", copy: resolved.copy.empty, onClearAllFilters: onClearAll }) : regions.view === "map" ? /* @__PURE__ */ jsx54(OrderIndexEmpty_default, { isFilteredEmpty: false, copy: { noOrders: MAP_PLACEHOLDER_COPY, filtered: MAP_PLACEHOLDER_COPY }, onClearAllFilters: onClearAll }) : regions.view === "table" ? /* @__PURE__ */ jsx54(
      OrderIndexTable_default,
      {
        items: listItems,
        columns: resolved.columns,
        metric: controls.metricTiles?.selected ?? "all",
        rowPreferences: preferences.row,
        context,
        sortBy: controls.sort?.sortBy ?? resolved.defaultSort.sortBy,
        sortOrder: controls.sort?.sortOrder ?? resolved.defaultSort.sortOrder,
        onSortingChange: (sortBy, sortOrder) => controls.sort?.onChange(sortBy, sortOrder),
        onOpenPhotos,
        isLoading: regions.isFirstPaint,
        skeletonRowCount,
        pagination,
        mobilePagination: pagination
      }
    ) : /* @__PURE__ */ jsx54(OrderIndexGrid_default, { items: listItems, isLoading: regions.isFirstPaint, skeletonCount, pagination, onOpenPhotos })
  ] });
}
var OrderIndex_default = OrderIndex;

// src/products/valet/queries/valetOrderIndex.query.generated.ts
import { resolveExecutor } from "@hopdrive/sdk-core";
var ValetOrderIndexDocument = { "__text": 'query valetOrderIndex($scope: moves_bool_exp!, $window: moves_bool_exp!, $limit: Int!) {\n  moves(\n    where: {active: {_eq: 1}, move_type: {_eq: "drive"}, _and: [$scope, $window]}\n    order_by: [{ready_by: asc}, {id: asc}]\n    limit: $limit\n  ) {\n    id\n    customer_id\n    scenariotomoves(\n      where: {scenario: {status: {_neq: "deprecated"}}}\n      order_by: {scenario: {createdat: desc}}\n      limit: 1\n    ) {\n      id\n      cardinality\n      scenario {\n        id\n        scenario_key\n        bundletoscenarios(\n          where: {bundle: {status: {_in: ["ready", "dispatching", "dispatched"]}}}\n          order_by: {id: desc}\n        ) {\n          id\n          bundle {\n            id\n            offers(\n              where: {status: {_in: ["offered", "accepted", "assigned"]}}\n              order_by: {id: desc}\n            ) {\n              id\n              status\n              driver {\n                id\n              }\n            }\n          }\n        }\n        moves(order_by: {cardinality: asc}) {\n          id\n          cardinality\n          move {\n            id\n            updatedat\n            move_type\n            active\n            status\n            cancel_status\n            lite\n            effective_config\n            ready_by\n            target_pickup_time\n            consumer_name\n            consumer_type\n            createdBy\n            driver_name\n            driver_id\n            hangtags(limit: 1) {\n              id\n            }\n            workflowset {\n              id\n              name\n            }\n            lane {\n              id\n              distance_miles\n            }\n            customer {\n              id\n              name\n            }\n          }\n        }\n      }\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "valetOrderIndex" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "scope" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "window" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "move_type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "drive", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_and" }, "value": { "kind": "ListValue", "values": [{ "kind": "Variable", "name": { "kind": "Name", "value": "scope" } }, { "kind": "Variable", "name": { "kind": "Name", "value": "window" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "ready_by" }, "value": { "kind": "EnumValue", "value": "asc" } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "asc" } }] }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenariotomoves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenario" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_neq" }, "value": { "kind": "StringValue", "value": "deprecated", "block": false } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenario" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cardinality" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundletoscenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "ready", "block": false }, { "kind": "StringValue", "value": "dispatching", "block": false }, { "kind": "StringValue", "value": "dispatched", "block": false }] } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offers" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "offered", "block": false }, { "kind": "StringValue", "value": "accepted", "block": false }, { "kind": "StringValue", "value": "assigned", "block": false }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "cardinality" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cardinality" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ready_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "target_pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdBy" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "hangtags" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflowset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "lane" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }] } }] } }] } }] } }] } }] } }] };

// src/products/valet/queries/valetOrderPage.query.generated.ts
import { resolveExecutor as resolveExecutor2 } from "@hopdrive/sdk-core";
var ValetOrderPageDocument = { "__text": 'query valetOrderPage($scenarioIds: [bigint!]!) {\n  scenarios(where: {id: {_in: $scenarioIds}}) {\n    id\n    scenario_key\n    config\n    moves(order_by: {cardinality: asc}) {\n      id\n      cardinality\n      move {\n        ...ValetLegFields\n      }\n    }\n    confirmationrequests(order_by: {trigger_time: desc}) {\n      id\n      status\n      escalated\n      expiration_time\n      workflow {\n        id\n        name\n        label\n      }\n      confirmationset {\n        id\n        pickup_workflow_id\n        delivery_workflow_id\n        confirmation_flow\n      }\n    }\n    bundletoscenarios(\n      where: {bundle: {status: {_in: ["ready", "dispatching", "dispatched"]}}}\n      order_by: {id: desc}\n    ) {\n      id\n      bundle {\n        id\n        offers(\n          where: {status: {_in: ["offered", "accepted", "assigned"]}}\n          order_by: {id: desc}\n        ) {\n          id\n          status\n          offered_at\n          offered_by\n          accepted_at\n          accepted_by\n          createdat\n          createdby\n          updatedat\n          updatedby\n          driver {\n            id\n            user {\n              id\n              display_name\n              avatar_url\n            }\n          }\n        }\n      }\n    }\n  }\n}\n\nfragment ValetLegFields on moves {\n  id\n  updatedat\n  move_type\n  lite\n  reference_num\n  status\n  cancel_status\n  cancel_reason\n  canceled_at\n  active\n  consumer_name\n  consumer_phone\n  consumer_pickup\n  consumer_at_pickup\n  consumer_type\n  ready_by\n  target_pickup_time\n  pickup_time\n  delivery_time\n  pickup_started\n  pickup_arrived\n  pickup_successful\n  delivery_started\n  delivery_arrived\n  delivery_successful\n  driver_id\n  driver_name\n  driver {\n    id\n    user {\n      id\n      display_name\n      avatar_url\n    }\n  }\n  vehicle_year\n  vehicle_make\n  vehicle_model\n  vehicle_color\n  vehicle_stock\n  vehicle_vin\n  effective_config\n  createdBy\n  eventlogs(where: {actiontype_id: {_eq: 1}}) {\n    id\n    user\n  }\n  customer {\n    id\n    name\n    tier\n  }\n  workflowset {\n    id\n    name\n    confirmationset {\n      id\n      confirmation_flow\n    }\n    pickupWorkflow {\n      id\n      steps\n    }\n    deliveryWorkflow {\n      id\n      steps\n    }\n  }\n  hangtags(order_by: {updated_at: desc}) {\n    id\n    hash_id\n    rear_code\n    status\n    type\n    updated_at\n    workflow_data\n  }\n  vehiclephotos {\n    id\n    name\n    status\n    step_id\n    url\n    workflow_id\n    createdat\n    workflow {\n      id\n      type\n    }\n  }\n  lane {\n    id\n    description\n    distance_miles\n    duration_sec\n    pickup_inspection_sec\n    delivery_inspection_sec\n    return_ride_wait_sec\n    status\n    pickup {\n      id\n      name\n      address\n      latitude\n      longitude\n      timezone\n      type\n      customer_id\n    }\n    delivery {\n      id\n      name\n      address\n      latitude\n      longitude\n      timezone\n      type\n      customer_id\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "valetOrderPage" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "scenarioIds" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "scenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "scenarioIds" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "cardinality" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cardinality" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "FragmentSpread", "name": { "kind": "Name", "value": "ValetLegFields" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationrequests" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "trigger_time" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "escalated" } }, { "kind": "Field", "name": { "kind": "Name", "value": "expiration_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "label" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmation_flow" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundletoscenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "ready", "block": false }, { "kind": "StringValue", "value": "dispatching", "block": false }, { "kind": "StringValue", "value": "dispatched", "block": false }] } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offers" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "offered", "block": false }, { "kind": "StringValue", "value": "accepted", "block": false }, { "kind": "StringValue", "value": "assigned", "block": false }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offered_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offered_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdby" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedby" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }] } }] } }] } }] } }] } }, { "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "ValetLegFields" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "reference_num" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_reason" } }, { "kind": "Field", "name": { "kind": "Name", "value": "canceled_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_at_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ready_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "target_pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_color" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_stock" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_vin" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdBy" } }, { "kind": "Field", "name": { "kind": "Name", "value": "eventlogs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "actiontype_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tier" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflowset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmation_flow" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickupWorkflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "steps" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "deliveryWorkflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "steps" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "hangtags" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "updated_at" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "hash_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "rear_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updated_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow_data" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehiclephotos" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "step_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "url" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "lane" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "return_ride_wait_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }] } }] } }] } }] };

// src/products/valet/queries/valetMovesStream.subscription.generated.ts
var ValetMovesStreamDocument = { "__text": "subscription valetMovesStream($cursor: [moves_stream_cursor_input]!, $where: moves_bool_exp!) {\n  moves_stream(cursor: $cursor, batch_size: 50, where: $where) {\n    id\n    active\n    updatedat\n    status\n    cancel_status\n    cancel_reason\n    canceled_at\n    driver_id\n    driver_name\n    ready_by\n    target_pickup_time\n    pickup_time\n    delivery_time\n    pickup_started\n    pickup_arrived\n    pickup_successful\n    delivery_started\n    delivery_arrived\n    delivery_successful\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "subscription", "name": { "kind": "Name", "value": "valetMovesStream" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "cursor" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves_stream_cursor_input" } } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves_bool_exp" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "moves_stream" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "cursor" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "cursor" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "batch_size" }, "value": { "kind": "IntValue", "value": "50" } }, { "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_reason" } }, { "kind": "Field", "name": { "kind": "Name", "value": "canceled_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ready_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "target_pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_successful" } }] } }] } }] };

// src/products/valet/queries/valetOrderStandalone.query.generated.ts
import { resolveExecutor as resolveExecutor3 } from "@hopdrive/sdk-core";
var ValetOrderStandaloneDocument = { "__text": 'query valetOrderStandalone($identity: [scenarios_bool_exp!]!) {\n  scenarios(\n    where: {_and: [{status: {_neq: "deprecated"}}, {_and: $identity}]}\n    order_by: {createdat: desc}\n    limit: 1\n  ) {\n    id\n    scenario_key\n    config\n    moves(order_by: {cardinality: asc}) {\n      id\n      cardinality\n      move {\n        ...ValetLegFields\n      }\n    }\n    confirmationrequests(order_by: {trigger_time: desc}) {\n      id\n      status\n      escalated\n      expiration_time\n      workflow {\n        id\n        name\n        label\n      }\n      confirmationset {\n        id\n        pickup_workflow_id\n        delivery_workflow_id\n        confirmation_flow\n      }\n    }\n    bundletoscenarios(\n      where: {bundle: {status: {_in: ["ready", "dispatching", "dispatched"]}}}\n      order_by: {id: desc}\n    ) {\n      id\n      bundle {\n        id\n        offers(\n          where: {status: {_in: ["offered", "accepted", "assigned"]}}\n          order_by: {id: desc}\n        ) {\n          id\n          status\n          offered_at\n          offered_by\n          accepted_at\n          accepted_by\n          createdat\n          createdby\n          updatedat\n          updatedby\n          driver {\n            id\n            user {\n              id\n              display_name\n              avatar_url\n            }\n          }\n        }\n      }\n    }\n  }\n}\n\nfragment ValetLegFields on moves {\n  id\n  updatedat\n  move_type\n  lite\n  reference_num\n  status\n  cancel_status\n  cancel_reason\n  canceled_at\n  active\n  consumer_name\n  consumer_phone\n  consumer_pickup\n  consumer_at_pickup\n  consumer_type\n  ready_by\n  target_pickup_time\n  pickup_time\n  delivery_time\n  pickup_started\n  pickup_arrived\n  pickup_successful\n  delivery_started\n  delivery_arrived\n  delivery_successful\n  driver_id\n  driver_name\n  driver {\n    id\n    user {\n      id\n      display_name\n      avatar_url\n    }\n  }\n  vehicle_year\n  vehicle_make\n  vehicle_model\n  vehicle_color\n  vehicle_stock\n  vehicle_vin\n  effective_config\n  createdBy\n  eventlogs(where: {actiontype_id: {_eq: 1}}) {\n    id\n    user\n  }\n  customer {\n    id\n    name\n    tier\n  }\n  workflowset {\n    id\n    name\n    confirmationset {\n      id\n      confirmation_flow\n    }\n    pickupWorkflow {\n      id\n      steps\n    }\n    deliveryWorkflow {\n      id\n      steps\n    }\n  }\n  hangtags(order_by: {updated_at: desc}) {\n    id\n    hash_id\n    rear_code\n    status\n    type\n    updated_at\n    workflow_data\n  }\n  vehiclephotos {\n    id\n    name\n    status\n    step_id\n    url\n    workflow_id\n    createdat\n    workflow {\n      id\n      type\n    }\n  }\n  lane {\n    id\n    description\n    distance_miles\n    duration_sec\n    pickup_inspection_sec\n    delivery_inspection_sec\n    return_ride_wait_sec\n    status\n    pickup {\n      id\n      name\n      address\n      latitude\n      longitude\n      timezone\n      type\n      customer_id\n    }\n    delivery {\n      id\n      name\n      address\n      latitude\n      longitude\n      timezone\n      type\n      customer_id\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "valetOrderStandalone" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "identity" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "scenarios_bool_exp" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "scenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_and" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_neq" }, "value": { "kind": "StringValue", "value": "deprecated", "block": false } }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_and" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "identity" } } }] }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "cardinality" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cardinality" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "FragmentSpread", "name": { "kind": "Name", "value": "ValetLegFields" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationrequests" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "trigger_time" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "escalated" } }, { "kind": "Field", "name": { "kind": "Name", "value": "expiration_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "label" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmation_flow" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundletoscenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "ready", "block": false }, { "kind": "StringValue", "value": "dispatching", "block": false }, { "kind": "StringValue", "value": "dispatched", "block": false }] } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offers" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "offered", "block": false }, { "kind": "StringValue", "value": "accepted", "block": false }, { "kind": "StringValue", "value": "assigned", "block": false }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offered_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offered_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdby" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedby" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }] } }] } }] } }] } }] } }, { "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "ValetLegFields" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "reference_num" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_reason" } }, { "kind": "Field", "name": { "kind": "Name", "value": "canceled_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_at_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ready_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "target_pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_color" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_stock" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_vin" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdBy" } }, { "kind": "Field", "name": { "kind": "Name", "value": "eventlogs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "actiontype_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tier" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflowset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmation_flow" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickupWorkflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "steps" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "deliveryWorkflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "steps" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "hangtags" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "updated_at" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "hash_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "rear_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updated_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow_data" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehiclephotos" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "step_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "url" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "lane" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "return_ride_wait_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }] } }] } }] } }] };

// src/products/valet/queries/valetFilterPools.query.generated.ts
import { resolveExecutor as resolveExecutor4 } from "@hopdrive/sdk-core";
var ValetFilterPoolsDocument = { "__text": 'query valetFilterPools($customerId: bigint!, $start: timestamptz!, $end: timestamptz!) {\n  moves(\n    where: {active: {_eq: 1}, move_type: {_eq: "drive"}, _and: [{_or: [{customer_id: {_eq: $customerId}}, {payer_id: {_eq: $customerId}}]}, {_or: [{pickup_time: {_gte: $start, _lte: $end}}, {ready_by: {_gte: $start, _lte: $end}}]}]}\n  ) {\n    id\n    createdBy\n    customer {\n      id\n      name\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "valetFilterPools" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "start" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "end" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "move_type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "drive", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_and" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_or" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "payer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_or" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_time" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "start" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_lte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "end" } } }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "ready_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "start" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_lte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "end" } } }] } }] }] } }] }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdBy" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }] } }] } }] };

// src/products/valet/queries/valetLegFields.fragment.generated.ts
var ValetLegFieldsFragmentDoc = { "kind": "Document", "definitions": [{ "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "ValetLegFields" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "reference_num" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_reason" } }, { "kind": "Field", "name": { "kind": "Name", "value": "canceled_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_at_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ready_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "target_pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_started" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_arrived" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_color" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_stock" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_vin" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdBy" } }, { "kind": "Field", "name": { "kind": "Name", "value": "eventlogs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "actiontype_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tier" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflowset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmationset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "confirmation_flow" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickupWorkflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "steps" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "deliveryWorkflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "steps" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "hangtags" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "updated_at" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "hash_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "rear_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updated_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow_data" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehiclephotos" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "step_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "url" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflow" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "lane" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "return_ride_wait_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }] } }] } }] } }] };

// src/products/valet/queries/valetStreamRecognition.fragment.generated.ts
var ValetStreamRecognitionFragmentDoc = { "kind": "Document", "definitions": [{ "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "ValetStreamRecognition" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "moves" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenariotomoves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenario" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_neq" }, "value": { "kind": "StringValue", "value": "deprecated", "block": false } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenario" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] };

// src/products/valet/__fixtures__/valetQueryFixtures.ts
var valetQueryFixtures_exports = {};
__export(valetQueryFixtures_exports, {
  buildIndexQuery: () => buildIndexQuery,
  buildIndexRow: () => buildIndexRow,
  buildMember: () => buildMember,
  buildMemberMove: () => buildMemberMove,
  buildPageQuery: () => buildPageQuery,
  buildPageRow: () => buildPageRow,
  buildRawIndexQuery: () => buildRawIndexQuery,
  buildRawPageQuery: () => buildRawPageQuery
});
var buildMemberMove = (overrides = {}) => ({
  id: 900,
  updatedat: null,
  move_type: "drive",
  active: 1,
  status: null,
  cancel_status: null,
  lite: false,
  effective_config: null,
  ready_by: "2026-07-28T14:00:00Z",
  target_pickup_time: null,
  consumer_name: null,
  consumer_type: null,
  createdBy: null,
  driver_name: null,
  driver_id: null,
  hangtags: [],
  workflowset: null,
  lane: null,
  customer: null,
  ...overrides
});
var buildMember = (id, cardinality, move = buildMemberMove()) => ({
  id,
  cardinality,
  move
});
var buildIndexRow = (id, scenariotomoves, customerId = null) => ({ id, customer_id: customerId, scenariotomoves });
var buildIndexQuery = (moves) => ({ moves });
var buildPageRow = (overrides = {}) => ({
  id: 5e3,
  scenario_key: "concierge-pu",
  config: null,
  bundletoscenarios: [],
  moves: [
    {
      id: 10,
      cardinality: 0,
      move: {
        id: 900,
        move_type: "drive",
        active: 1,
        status: "pickup started",
        vehiclephotos: [],
        ...overrides.move
      }
    }
  ],
  ...overrides
});
var buildPageQuery = (rows) => ({ scenarios: rows });
var toRawMemberMove = (move) => ({
  id: move.id,
  updatedat: null,
  move_type: move.move_type,
  active: move.active,
  status: move.status,
  cancel_status: move.cancel_status,
  lite: move.lite ?? false,
  effective_config: move.effective_config ?? null,
  ready_by: move.ready_by,
  target_pickup_time: move.target_pickup_time,
  consumer_name: move.consumer_name ?? null,
  consumer_type: move.consumer_type ?? null,
  createdBy: move.createdBy ?? null,
  driver_name: move.driver_name ?? null,
  driver_id: move.driver_id ?? null,
  hangtags: (move.hangtags ?? []).map((hangtag) => ({ id: hangtag.id })),
  workflowset: move.workflowset?.name ? { id: 1, name: move.workflowset.name } : null,
  lane: move.lane ? { id: move.lane.id, distance_miles: move.lane.distance_miles ?? null } : null,
  customer: move.customer ? { id: move.customer.id, name: move.customer.name ?? null } : null
});
var buildRawIndexQuery = (rows, customerId = 77) => ({
  moves: rows.map((row2) => {
    const leg = row2.order?.legs.find((candidate) => candidate.move.id === row2.moveId);
    return {
      id: row2.moveId,
      customer_id: customerId,
      scenariotomoves: row2.order && leg ? [
        {
          id: leg.id,
          cardinality: leg.cardinality,
          scenario: {
            id: row2.order.scenarioId,
            scenario_key: row2.order.scenarioKey,
            bundletoscenarios: [],
            moves: row2.order.legs.map((member) => ({ id: member.id, cardinality: member.cardinality, move: toRawMemberMove(member.move) }))
          }
        }
      ] : []
    };
  })
});
var buildRawPageQuery = (orders) => ({
  scenarios: orders.map(
    (order) => ({
      id: order.scenarioId,
      scenario_key: order.scenarioKey,
      config: order.config ?? null,
      moves: order.legs.map((leg) => ({
        id: leg.id,
        cardinality: leg.cardinality,
        move: {
          ...leg.move,
          driver: leg.move.driver ? { id: leg.move.driver.id, user: { id: leg.move.driver.id, display_name: leg.move.driver.display_name, avatar_url: leg.move.driver.avatar_url } } : null
        }
      })),
      confirmationrequests: (order.confirmationRequests ?? []).map((request) => ({ ...request })),
      bundletoscenarios: []
    })
  )
});

// src/shared/model/orderModel.ts
var orderKey = (product, id) => `${product}:${id}`;
var ORDER_PHASE_ORDER = ["queued", "planned", "active", "completed", "cancelled"];
var ORDER_PHASE_PRECEDENCE = ["cancelled", "completed", "active", "planned", "queued"];
var metricForPhase = (phase) => {
  switch (phase) {
    case "queued":
    case "planned":
      return "upcoming";
    case "active":
      return "active";
    case "completed":
      return "completed";
    case "cancelled":
      return "cancelled";
  }
};
var isOrderEntry = (entry) => entry.kind === "order";
var anchorOf = (entry) => entry.kind === "order" ? entry.model.anchorMs : entry.anchorMs;
var keyOf = (entry) => entry.kind === "order" ? entry.model.key : entry.key;

// src/shared/model/filterState.ts
var EMPTY_ORDER_FILTER_STATE = {
  search: "",
  market: [],
  createdBy: [],
  driver: [],
  provider: [],
  product: [],
  offerStatus: []
};
var isEmptyFilterState = (state) => state.search.trim() === "" && state.market.length === 0 && state.createdBy.length === 0 && state.driver.length === 0 && state.provider.length === 0 && state.product.length === 0 && state.offerStatus.length === 0;

// src/shared/model/offerStatus.ts
var ORDER_OFFER_STATUS_OPTIONS = [
  { value: "unassigned", label: "Unassigned" },
  { value: "offered", label: "Pending Acceptance" },
  { value: "accepted", label: "Accepted" }
];
var ORDER_OFFER_STATUS_LABELS = Object.fromEntries(
  ORDER_OFFER_STATUS_OPTIONS.map(({ value, label }) => [value, label])
);
var NO_OFFER_STATUS = [];
var resolveActiveOfferStatus = (selection, isSelfManaged) => isSelfManaged ? selection : NO_OFFER_STATUS;
var countOfferStatuses = (entries) => {
  const counts = { unassigned: 0, offered: 0, accepted: 0 };
  for (const entry of entries) {
    if (entry.kind !== "order") continue;
    const key = entry.model.flags.offerStatus;
    if (key) counts[key] += 1;
  }
  return counts;
};
var filterByOfferStatus = (entries, selection) => selection.length ? entries.filter((entry) => entry.kind === "order" && !!entry.model.flags.offerStatus && selection.includes(entry.model.flags.offerStatus)) : entries;

// src/shared/feed/comparator.ts
var compareNullness = (a, b) => {
  const aMissing = a === null || a === void 0 || a === "";
  const bMissing = b === null || b === void 0 || b === "";
  if (aMissing === bMissing) return 0;
  return aMissing ? 1 : -1;
};
var compareAnchors = (a, b) => {
  const nullness = compareNullness(a, b);
  if (nullness !== 0) return nullness;
  if (a === null || b === null) return 0;
  return a - b;
};
var compareKeys = (a, b) => {
  const [aKind, ...aRest] = a.split(":");
  const [bKind, ...bRest] = b.split(":");
  if (aKind !== bKind) return aKind.localeCompare(bKind);
  const aId = aRest.join(":");
  const bId = bRest.join(":");
  const aNumber = Number(aId);
  const bNumber = Number(bId);
  if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) return aNumber - bNumber;
  return aId.localeCompare(bId);
};
var sortValueOf = (entry, sortBy, byAnchor) => {
  if (byAnchor) return anchorOf(entry);
  return entry.kind === "order" ? entry.model.sortValues[sortBy] ?? null : null;
};
var buildOrderComparator = ({
  sortBy,
  sortOrder,
  options
}) => {
  const direction = sortOrder === "asc" ? 1 : -1;
  const option = options.find((candidate) => candidate.key === sortBy);
  const byAnchor = !!option?.anchor;
  const missingIsHighest = option?.missingValue === "highest";
  return (a, b) => {
    const left = sortValueOf(a, sortBy, byAnchor);
    const right = sortValueOf(b, sortBy, byAnchor);
    const nullness = compareNullness(left, right);
    if (nullness !== 0) return missingIsHighest ? nullness * direction : nullness;
    let primary = 0;
    if (typeof left === "number" && typeof right === "number") primary = left - right;
    else if (left !== null && right !== null) primary = String(left).localeCompare(String(right));
    if (primary !== 0) return primary * direction;
    const anchorOrder = compareAnchors(anchorOf(a), anchorOf(b));
    if (anchorOrder !== 0) return anchorOrder;
    return compareKeys(keyOf(a), keyOf(b));
  };
};

// src/shared/feed/grouping.ts
var isGrouping = (option) => !!option && option.key !== ORDER_GROUP_NONE;
var resolveGroupSection = (entry, option) => {
  if (!isGrouping(option)) return null;
  const value = entry.kind === "order" ? entry.model.groupKeys[option.key] : null;
  return value ? { title: value, rank: 0 } : { title: option.emptyTitle ?? "Everything Else", rank: 1 };
};
var resolveAvailableGroupOptions = ({
  entries,
  options,
  isAdminViewer
}) => {
  const orders = entries.flatMap((entry) => entry.kind === "order" ? [entry.model] : []);
  const customerIds = new Set(orders.flatMap((model) => model.customerId === null ? [] : [model.customerId]));
  return options.filter((option) => {
    if (option.adminOnly && !isAdminViewer) return false;
    switch (option.availability) {
      case "anyValue":
        return orders.some((model) => !!model.groupKeys[option.key]);
      case "multipleCustomers":
        return customerIds.size > 1;
      default:
        return true;
    }
  });
};
var resolveEffectiveGroupBy = (stored, available) => available.some((option) => option.key === stored) ? stored : ORDER_GROUP_NONE;
var buildGroupedComparator = ({
  option,
  sortBy,
  sortOrder,
  sortOptions
}) => {
  const base = buildOrderComparator({ sortBy, sortOrder, options: sortOptions });
  if (!isGrouping(option)) return base;
  return (a, b) => {
    const sectionA = resolveGroupSection(a, option);
    const sectionB = resolveGroupSection(b, option);
    if (sectionA.rank !== sectionB.rank) return sectionA.rank - sectionB.rank;
    const byTitle = sectionA.title.localeCompare(sectionB.title);
    if (byTitle !== 0) return byTitle;
    return base(a, b);
  };
};
var countGroupSections = (entries, option) => {
  const counts = /* @__PURE__ */ new Map();
  if (!isGrouping(option)) return counts;
  for (const entry of entries) {
    const section2 = resolveGroupSection(entry, option);
    counts.set(section2.title, (counts.get(section2.title) ?? 0) + 1);
  }
  return counts;
};
var buildPageSections = ({
  pageEntries,
  option,
  counts
}) => {
  if (!isGrouping(option)) return null;
  const sections = [];
  for (const entry of pageEntries) {
    const section2 = resolveGroupSection(entry, option);
    const last = sections[sections.length - 1];
    if (last && last.title === section2.title) {
      last.entries.push(entry);
    } else {
      sections.push({
        key: section2.title,
        title: section2.title,
        count: counts.get(section2.title) ?? 0,
        entries: [entry]
      });
    }
  }
  return sections;
};

// src/shared/feed/paging.ts
var sliceToPage = ({ items, page, pageSize }) => {
  const totalCount = items.length;
  const size = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(totalCount / size));
  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  const start = safePage * size;
  return { items: items.slice(start, start + size), page: safePage, pageCount, totalCount };
};
var resequenceHydrated = (sliceOrder, hydrated, idOf) => {
  const byId = new Map(hydrated.map((row2) => [idOf(row2), row2]));
  return sliceOrder.map((id) => byId.get(id)).filter((row2) => row2 !== void 0);
};

// src/shared/feed/metrics.ts
var entryMatchesTile = (entry, tile) => {
  if (tile.key === "all") return true;
  if (entry.kind !== "order") return false;
  if (tile.flag) return entry.model.flags[tile.flag] === true;
  return entry.model.metric === tile.key;
};
var countMetrics = (entries, tiles) => {
  const counts = {};
  const excludedFromAll = new Set(tiles.filter((tile) => !tile.inAll && !tile.flag && tile.key !== "all").map((tile) => tile.key));
  for (const tile of tiles) {
    counts[tile.key] = tile.key === "all" ? entries.filter((entry) => entry.kind !== "order" || !excludedFromAll.has(entry.model.metric)).length : entries.filter((entry) => entryMatchesTile(entry, tile)).length;
  }
  return counts;
};
var filterByMetric = (entries, metric, tiles) => {
  const tile = tiles.find((candidate) => candidate.key === metric);
  if (!tile || tile.key === "all") return entries;
  return entries.filter((entry) => entryMatchesTile(entry, tile));
};

// src/shared/feed/merge.ts
var mergeAdapterResults = (results) => ({
  entries: results.flatMap((result) => result.entries),
  capReached: results.some((result) => result.capReached)
});

// src/shared/feed/streamEffects.ts
var classifyStreamBatch = ({
  rows,
  isRecognized,
  hasDeparted,
  attemptedUnrecognized
}) => {
  let refetchIndex = false;
  const changedIds = [];
  for (const row2 of rows) {
    if (!isRecognized(row2.id)) {
      if (!attemptedUnrecognized.has(row2.id)) {
        attemptedUnrecognized.add(row2.id);
        refetchIndex = true;
      }
      continue;
    }
    if (hasDeparted(row2)) {
      refetchIndex = true;
      continue;
    }
    changedIds.push(row2.id);
  }
  return { refetchIndex, changedIds };
};

// src/shared/feed/deriveFeed.ts
var idsByProduct = (entries, keep) => {
  var _a;
  const ids = {};
  for (const entry of entries) {
    if (entry.kind !== "order" || !keep(entry)) continue;
    (ids[_a = entry.model.product] ?? (ids[_a] = [])).push(entry.model.id);
  }
  return ids;
};
var deriveFeed = ({
  entries,
  state,
  resolved,
  isAdminViewer
}) => {
  const metricFiltered = filterByMetric(entries, state.metric, resolved.metricTiles);
  const offerStatusCounts = countOfferStatuses(metricFiltered);
  const filtered = filterByOfferStatus(metricFiltered, state.offerStatus ?? []);
  const visibleSortOptions = resolveVisibleSortOptions({ options: resolved.sortOptions, isAdminViewer, viewMode: state.viewMode });
  const sortBy = resolveEffectiveSortBy({
    sortBy: state.sortBy,
    options: resolved.sortOptions,
    isAdminViewer,
    viewMode: state.viewMode,
    defaultSortBy: resolved.defaultSort.sortBy
  });
  const availableGroupOptions = resolveAvailableGroupOptions({ entries, options: resolved.groupOptions, isAdminViewer });
  const groupBy = resolveEffectiveGroupBy(state.groupBy, availableGroupOptions);
  const groupOption = groupBy === ORDER_GROUP_NONE ? null : availableGroupOptions.find((option) => option.key === groupBy) ?? null;
  const sorted = [...filtered].sort(
    buildGroupedComparator({ option: groupOption, sortBy, sortOrder: state.sortOrder, sortOptions: resolved.sortOptions })
  );
  const sectionCounts = countGroupSections(sorted, groupOption);
  const slice = sliceToPage({ items: sorted, page: state.page, pageSize: state.pageSize });
  return {
    entries: sorted,
    pagedEntries: slice.items,
    pageSections: buildPageSections({ pageEntries: slice.items, option: groupOption, counts: sectionCounts }),
    metricCounts: countMetrics(entries, resolved.metricTiles),
    offerStatusCounts,
    page: slice.page,
    pageCount: slice.pageCount,
    totalCount: slice.totalCount,
    hydrateIds: idsByProduct(slice.items, (entry) => entry.kind === "order" && !entry.model.isHydrated),
    filteredIds: idsByProduct(sorted, () => true),
    sortBy,
    groupBy,
    visibleSortOptions,
    availableGroupOptions
  };
};

// src/shared/controls/viewModes.ts
var resolveViewModes = (mapEnabled) => mapEnabled ? ["cards", "table", "map"] : ["cards", "table"];
var resolveViewMode = (stored, mapEnabled) => {
  const allowed = resolveViewModes(mapEnabled);
  return allowed.includes(stored) ? stored : "cards";
};

// src/shared/controls/controlsModel.ts
var ADMIN_ONLY_PIECES = /* @__PURE__ */ new Set(["market"]);
var resolveEnabledPieces = (defaults, overrides, isAdminViewer) => {
  const enabled = /* @__PURE__ */ new Set([...defaults.enabled, ...overrides?.enable ?? []]);
  for (const key of overrides?.disable ?? []) enabled.delete(key);
  if (!isAdminViewer) for (const key of ADMIN_ONLY_PIECES) enabled.delete(key);
  return enabled;
};
var buildActiveChips = ({ preset, filters, metric, tiles, productOptions = [] }) => {
  const chips = [];
  if (preset === "custom") chips.push({ key: "dateRange", label: "Custom date range" });
  if (filters.search.trim()) chips.push({ key: "search", label: `Search: ${filters.search.trim()}` });
  for (const product of filters.product) {
    chips.push({ key: `product:${product}`, label: `Product: ${productOptions.find((option) => option.value === product)?.label ?? product}` });
  }
  for (const creator of filters.createdBy) chips.push({ key: `createdBy:${creator}`, label: `Created by ${creator}` });
  for (const market of filters.market) chips.push({ key: `market:${market}`, label: `Market: ${market}` });
  for (const status of filters.offerStatus) chips.push({ key: `offerStatus:${status}`, label: `Offer status: ${ORDER_OFFER_STATUS_LABELS[status]}` });
  for (const driver of filters.driver) chips.push({ key: `driver:${driver}`, label: `Driver: ${driver}` });
  for (const provider of filters.provider) chips.push({ key: `provider:${provider}`, label: `Provider: ${provider}` });
  if (metric !== "all") chips.push({ key: "metric", label: tiles.find((tile) => tile.key === metric)?.label ?? metric });
  return chips;
};
var FILTER_FIELDS = /* @__PURE__ */ new Set(["product", "createdBy", "market", "driver", "provider", "offerStatus"]);
var resolveChipRemoval = (key) => {
  if (key === "dateRange") return { kind: "dateRange" };
  if (key === "search") return { kind: "search" };
  if (key === "metric") return { kind: "metric" };
  const separator = key.indexOf(":");
  if (separator > 0) {
    const field = key.slice(0, separator);
    if (FILTER_FIELDS.has(field)) return { kind: "filter", field, value: key.slice(separator + 1) };
  }
  return { kind: "unknown" };
};
var visibleDefinitions = (definitions, isAdminViewer) => definitions.filter((definition) => isAdminViewer || !definition.adminOnly);
var resolveControlsModel = ({ resolved, overrides, viewer, state, options = {}, counts, handlers, loading = false }) => {
  const enabled = resolveEnabledPieces(resolved.controlsDefaults, overrides, viewer.isAdminViewer);
  const model = {};
  if (enabled.has("search")) model.search = { value: state.filters.search, placeholder: resolved.copy.searchPlaceholder, onChange: handlers.onSearchChange, clearSignal: state.searchClearSignal };
  if (enabled.has("dateRange")) {
    model.dateRange = {
      presets: resolved.dateRangePresets,
      preset: state.dateRange.preset,
      customStartIso: state.dateRange.startDate || null,
      customEndIso: state.dateRange.endDate || null,
      onPresetChange: (key) => handlers.onDateRangePresetChange(key),
      onCustomRangeChange: handlers.onDateRangeCustomChange
    };
  }
  if (enabled.has("metricTiles")) {
    model.metricTiles = {
      tiles: resolved.metricTiles.filter((tile) => viewer.isSelfManaged || !tile.saasOnly),
      counts,
      selected: state.metric,
      onSelect: handlers.onMetricChange,
      loading
    };
  }
  if (enabled.has("sort")) {
    model.sort = {
      options: resolveVisibleSortOptions({ options: resolved.sortOptions, isAdminViewer: viewer.isAdminViewer, viewMode: state.viewMode }),
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      onChange: handlers.onSortingChange
    };
  }
  if (enabled.has("groupBy")) model.groupBy = { options: options.groupOptions ?? resolved.groupOptions, groupBy: state.groupBy, onChange: handlers.onGroupByChange };
  if (enabled.has("viewMode")) model.viewMode = { modes: options.viewModes ?? resolveViewModes(false), value: state.viewMode, onChange: handlers.onViewModeChange };
  if (enabled.has("displayPreferences")) {
    model.displayPreferences = {
      definitions: visibleDefinitions(resolved.displayPreferences, viewer.isAdminViewer),
      card: state.cardPreferences,
      row: state.rowPreferences,
      onToggle: handlers.onPreferenceToggle
    };
  }
  if (enabled.has("activeChips")) {
    model.activeChips = {
      chips: buildActiveChips({ preset: state.dateRange.preset, filters: state.filters, metric: state.metric, tiles: resolved.metricTiles, productOptions: options.product }),
      onRemove: handlers.onClearFilter,
      onClearAll: handlers.onClearAll
    };
  }
  const multiselect = (field, label) => ({
    label,
    options: options[field] ?? [],
    selected: state.filters[field],
    onChange: (values) => handlers.onFilterChange(field, values)
  });
  if (enabled.has("market")) model.market = multiselect("market", "Market");
  if (enabled.has("createdBy")) model.createdBy = multiselect("createdBy", "Created By");
  if (enabled.has("offerStatus")) {
    model.offerStatus = {
      label: "Offer Status",
      options: options.offerStatus ?? [],
      selected: state.filters.offerStatus,
      onChange: (values) => handlers.onFilterChange("offerStatus", values)
    };
  }
  if (enabled.has("driver")) model.driver = multiselect("driver", "Driver");
  if (enabled.has("provider")) model.provider = multiselect("provider", "Provider");
  if (enabled.has("product")) {
    model.product = {
      label: "Product",
      options: options.product ?? [],
      selected: state.filters.product,
      onChange: (values) => handlers.onFilterChange("product", values)
    };
  }
  return model;
};

// src/shared/store/createOrderIndexStore.ts
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
var readPersistedFilters = (stored, current) => ({
  ...current,
  ...stored?.market ? { market: stored.market } : stored?.markets ? { market: stored.markets } : {},
  ...stored?.createdBy ? { createdBy: stored.createdBy } : stored?.creators ? { createdBy: stored.creators } : {},
  ...stored?.driver ? { driver: stored.driver } : {},
  ...stored?.provider ? { provider: stored.provider } : {},
  ...stored?.product ? { product: stored.product } : {},
  search: ""
});
var readPersistedDateRange = (storedRange, storedFilters, current) => {
  if (storedRange?.preset) return { ...current, ...storedRange };
  if (storedFilters?.dateRange) return { preset: storedFilters.dateRange, startDate: storedFilters.startDate ?? "", endDate: storedFilters.endDate ?? "" };
  return current;
};
var createOrderIndexStore = ({ storageKey, defaults, mapEnabled = false, storage }) => {
  const initial = () => ({
    filters: EMPTY_ORDER_FILTER_STATE,
    dateRange: { preset: defaults.dateRangePreset ?? "today", startDate: "", endDate: "" },
    metric: "all",
    sortBy: defaults.sortBy,
    sortOrder: defaults.sortOrder,
    groupBy: defaults.groupBy ?? "none",
    page: 0,
    pageSize: defaults.pageSize,
    viewMode: defaults.viewMode ?? "cards",
    cardPreferences: defaults.cardPreferences,
    rowPreferences: defaults.rowPreferences,
    searchClearSignal: 0
  });
  const reducers = (set) => ({
    // Changing which orders are listed returns to page 0, since the old page no longer refers to the same set.
    setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters }, page: 0 })),
    setDateRange: (dateRange) => set((state) => ({ dateRange: { ...state.dateRange, ...dateRange }, page: 0 })),
    resetDateRange: () => set({ dateRange: { preset: defaults.dateRangePreset ?? "today", startDate: "", endDate: "" }, page: 0 }),
    // A selected metric tile narrows the list like a filter, so "Clear all" clears it too.
    resetFilters: () => set((state) => ({
      filters: EMPTY_ORDER_FILTER_STATE,
      dateRange: { preset: defaults.dateRangePreset ?? "today", startDate: "", endDate: "" },
      metric: "all",
      page: 0,
      searchClearSignal: state.searchClearSignal + 1
    })),
    setMetric: (metric) => set({ metric, page: 0 }),
    resetMetric: () => set({ metric: "all", page: 0 }),
    setSorting: (sortBy, sortOrder) => set({ sortBy, sortOrder, page: 0 }),
    setGroupBy: (groupBy) => set({ groupBy, page: 0 }),
    setPage: (page) => set({ page }),
    setPageSize: (pageSize) => set({ pageSize, page: 0 }),
    setViewMode: (viewMode) => set({ viewMode }),
    setCardPreference: (key, value) => set((state) => ({ cardPreferences: { ...state.cardPreferences, [key]: value } })),
    setRowPreference: (key, value) => set((state) => ({ rowPreferences: { ...state.rowPreferences, [key]: value } }))
  });
  if (storage === null) {
    return create()((set) => ({ ...initial(), ...reducers(set) }));
  }
  return create()(
    persist((set) => ({ ...initial(), ...reducers(set) }), {
      name: storageKey,
      ...storage ? { storage: createJSONStorage(() => storage) } : {},
      partialize: (state) => ({
        metric: state.metric,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        groupBy: state.groupBy,
        pageSize: state.pageSize,
        viewMode: state.viewMode,
        cardPreferences: state.cardPreferences,
        rowPreferences: state.rowPreferences,
        filters: {
          ...state.filters,
          search: "",
          creators: state.filters.createdBy,
          markets: state.filters.market,
          dateRange: state.dateRange.preset,
          startDate: state.dateRange.startDate,
          endDate: state.dateRange.endDate
        },
        dateRange: state.dateRange
      }),
      // Persisted blobs outlive the shape that wrote them. Nested slices merge over their defaults so a new
      // key is never undefined, and keys from retired shapes are dropped.
      merge: (persisted, current) => {
        const stored = persisted ?? {};
        const { displayPreferences: _displayPreferences, filtersOpen: _filtersOpen, filters: storedFilters, dateRange: storedRange, ...rest } = stored;
        return {
          ...current,
          ...rest,
          viewMode: resolveViewMode(stored.viewMode, mapEnabled),
          filters: readPersistedFilters(storedFilters, current.filters),
          dateRange: readPersistedDateRange(storedRange, storedFilters, current.dateRange),
          cardPreferences: { ...current.cardPreferences, ...stored.cardPreferences ?? {} },
          rowPreferences: { ...current.rowPreferences, ...stored.rowPreferences ?? {} }
        };
      }
    })
  );
};

// src/shared/store/storeDefaults.ts
var resolveStoreDefaults = (resolved) => ({
  sortBy: resolved.defaultSort.sortBy,
  sortOrder: resolved.defaultSort.sortOrder,
  pageSize: resolved.defaultPageSize,
  cardPreferences: Object.fromEntries(resolved.displayPreferences.filter((definition) => definition.surface === "card").map((definition) => [definition.key, definition.defaultValue])),
  rowPreferences: Object.fromEntries(resolved.displayPreferences.filter((definition) => definition.surface === "row").map((definition) => [definition.key, definition.defaultValue]))
});

// src/products/valet/renderers.ts
import { createElement as createElement3 } from "react";

// src/products/valet/items/OrderItemLeg.tsx
import { Box as Box41, Typography as Typography20 } from "@mui/material";
import { BiSolidArrowFromBottom, BiSolidCar, BiSolidKey, BiSolidUser, BiUndo } from "react-icons/bi";

// src/products/valet/items/OrderItemLegIdentifierChips.tsx
import { Box as Box40 } from "@mui/material";
import { BiBarcode, BiFingerprint as BiFingerprint5, BiHash, BiPurchaseTag } from "react-icons/bi";
import { jsx as jsx55, jsxs as jsxs41 } from "react/jsx-runtime";
var CHIP_ICONS = {
  moveId: BiFingerprint5,
  reference: BiHash,
  vin: BiBarcode,
  stock: BiPurchaseTag
};
var OrderItemLegIdentifierChips = ({ chips }) => {
  const tokens = useOrderTokens();
  const { copyToClipboard } = useOrderIndexPlatform();
  if (!chips.length) return null;
  return /* @__PURE__ */ jsx55(
    Box40,
    {
      sx: {
        display: "flex",
        flex: "none",
        flexWrap: "nowrap",
        gap: "4px",
        mt: "3px",
        // Scrolls rather than wraps, because the leg block is a fixed height.
        overflowX: "auto",
        overflowY: "hidden",
        // A visible scrollbar would take height from the fixed 64px leg block.
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
        // Keeps a dragged or wheel-scrolled row from also scrolling the page.
        overscrollBehaviorX: "contain"
      },
      children: chips.map((chip) => {
        const Icon = CHIP_ICONS[chip.key] ?? BiHash;
        return /* @__PURE__ */ jsx55(
          OrderTooltip_default,
          {
            content: [
              { kind: "title", text: chip.label },
              { kind: "description", text: "Click to copy" }
            ],
            children: /* @__PURE__ */ jsxs41(
              Box40,
              {
                component: "button",
                type: "button",
                onClick: (event) => {
                  event.stopPropagation();
                  copyToClipboard(chip.value);
                },
                sx: {
                  flex: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  bgcolor: tokens.surfaces.chipStrong,
                  color: tokens.text.primary,
                  borderRadius: `${tokens.radii.pill}px`,
                  padding: "3px 8px 3px 6px",
                  fontSize: "10px",
                  fontWeight: 500,
                  lineHeight: 1,
                  letterSpacing: "0.02em",
                  whiteSpace: "nowrap",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 100ms ease-in-out",
                  // Mixed rather than alpha-layered so it darkens on any surface.
                  "&:hover": { bgcolor: `color-mix(in srgb, ${tokens.surfaces.chipStrong} 85%, ${tokens.text.primary})` },
                  "&:focus-visible": { outline: `2px solid ${tokens.semantic.info}`, outlineOffset: "1px" }
                },
                children: [
                  /* @__PURE__ */ jsx55(Icon, { size: 10, "aria-hidden": true }),
                  chip.value
                ]
              }
            )
          },
          chip.key
        );
      })
    }
  );
};
var OrderItemLegIdentifierChips_default = OrderItemLegIdentifierChips;

// src/products/valet/items/OrderItemLeg.tsx
import { jsx as jsx56, jsxs as jsxs42 } from "react/jsx-runtime";
var TYPE_CHIP = {
  pickup: { label: "Pickup", icon: BiSolidArrowFromBottom, tone: "concierge" },
  return: { label: "Return", icon: BiUndo, tone: "concierge" },
  loaner: { label: "Loaner", icon: BiSolidKey, tone: "loaner" },
  operational: { label: "Operational", icon: BiSolidCar, tone: "operational" },
  ride: { label: "Ride", icon: BiSolidUser, tone: "auto" }
};
var ROUTE_ARROW = "\u2192";
var OrderItemLeg = ({ moveId, isRide, vehicleLine: vehicleLine2, typeIdentity, typeTooltip, route, laneTooltip, laneSummary, photo, identifierChips, hangtag, onOpenPhotos }) => {
  const tokens = useOrderTokens();
  const { MoveTileHangtag: MoveTileHangtag2 } = useOrderIndexPlatform();
  const chip = TYPE_CHIP[typeIdentity];
  const hasChips = identifierChips.length > 0;
  return /* @__PURE__ */ jsxs42(
    Box41,
    {
      sx: {
        display: "flex",
        alignItems: "stretch",
        // Visible on purpose, because the hangtag hangs 8px above the top edge. The photo
        // clips its own square and every text line has its own ellipsis.
        overflow: "visible",
        position: "relative",
        bgcolor: tokens.surfaces.legBackground,
        borderRadius: `${tokens.radii.md}px`,
        maxWidth: 480,
        height: hasChips ? 64 : 48,
        transition: "background 100ms ease-in-out"
      },
      children: [
        photo ? /* @__PURE__ */ jsx56(OrderPhotoTile_default, { photo, onOpenPhase: onOpenPhotos ? (phase) => onOpenPhotos(moveId, phase) : void 0 }) : null,
        /* @__PURE__ */ jsxs42(Box41, { sx: { flex: 1, minWidth: 0, p: "6px", display: "flex", flexDirection: "column", justifyContent: "center" }, children: [
          /* @__PURE__ */ jsxs42(Box41, { sx: { display: "flex", flex: "none", justifyContent: "space-between", gap: "8px" }, children: [
            /* @__PURE__ */ jsx56(
              Typography20,
              {
                component: "span",
                sx: { flex: 1, minWidth: 0, fontSize: "14px", fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: tokens.text.primary },
                children: vehicleLine2
              }
            ),
            /* @__PURE__ */ jsx56(OrderTooltip_default, { content: typeTooltip, children: /* @__PURE__ */ jsxs42(
              Box41,
              {
                sx: {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                  borderRadius: `${tokens.radii.pill}px`,
                  padding: "4px 8px",
                  fontSize: "10px",
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: "0.02em",
                  bgcolor: tokens.service[chip.tone],
                  color: tokens.text.onTooltip,
                  flex: "none"
                },
                children: [
                  /* @__PURE__ */ jsx56(chip.icon, { size: 10, "aria-hidden": true }),
                  chip.label
                ]
              }
            ) })
          ] }),
          /* @__PURE__ */ jsxs42(Box41, { sx: { display: "flex", flex: "none", justifyContent: "space-between", gap: "8px" }, children: [
            /* @__PURE__ */ jsx56(OrderTooltip_default, { content: laneTooltip, children: /* @__PURE__ */ jsx56(Typography20, { component: "span", sx: { flex: 1, minWidth: 0, fontSize: "12px", color: tokens.text.secondary, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: `${route.pickupName} ${ROUTE_ARROW} ${route.deliveryName}` }) }),
            laneSummary ? /* @__PURE__ */ jsx56(Typography20, { component: "span", sx: { flex: "none", mt: "4px", fontSize: "10px", lineHeight: 1, color: tokens.text.muted, whiteSpace: "nowrap", pr: "3px" }, children: laneSummary }) : null
          ] }),
          hasChips ? /* @__PURE__ */ jsx56(OrderItemLegIdentifierChips_default, { chips: identifierChips }) : null
        ] }),
        !isRide && hangtag ? /* @__PURE__ */ jsx56(Box41, { sx: { position: "absolute", top: 2, left: 4, width: 0, height: 0, transform: "scale(0.72)", transformOrigin: "0 0" }, children: /* @__PURE__ */ jsx56(MoveTileHangtag2, { hangtag }) }) : null
      ]
    }
  );
};
var OrderItemLeg_default = OrderItemLeg;

// src/products/valet/renderers.ts
var LegItem = ({ props, onOpenPhotos }) => createElement3(OrderItemLeg_default, { ...props, onOpenPhotos });
var valetRenderers = {
  items: { leg: LegItem }
};

// src/products/valet/resolveValetOrders.ts
import { PiCarFill } from "react-icons/pi";
import { BiAddToQueue, BiBlock, BiChat, BiLinkExternal, BiTime as BiTime2, BiTransfer } from "react-icons/bi";

// src/shared/format/formatDistanceMiles.ts
var formatDistanceMiles = (miles) => miles == null ? null : `${miles.toFixed(1)} mi`;

// src/products/valet/constants/scenarioStatusConstants.ts
var RAW_STATUS = {
  UNCONFIRMED: "unconfirmed",
  NONE: "none",
  DISPATCHED: "dispatched",
  PICKUP_STARTED: "pickup started",
  PICKUP_ARRIVED: "pickup arrived",
  PICKUP_SUCCESSFUL: "pickup successful",
  DELIVERY_STARTED: "delivery started",
  DELIVERY_ARRIVED: "delivery arrived",
  DELIVERY_SUCCESSFUL: "delivery successful"
};
var KNOWN_RAW_STATUSES = new Set(Object.values(RAW_STATUS));
var resolveRawStatusKey = (status) => status != null && KNOWN_RAW_STATUSES.has(status) ? status : RAW_STATUS.NONE;
var RIDE_STATUS = {
  PENDING: "pending",
  READY_TO_BOOK: "ready_to_book",
  BOOKED: "booked",
  DRIVER_ARRIVED: "driver_arrived",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  FAILED: "failed"
};
var CANCEL_STATUS = {
  CANCELLED_BEFORE_START: "cancelled",
  CANCELLED_AFTER_START: "started",
  PENDING: "pending"
};
var FINALIZED_CANCEL_STATUSES = [
  CANCEL_STATUS.CANCELLED_BEFORE_START,
  CANCEL_STATUS.CANCELLED_AFTER_START
];
var SCENARIO_PHASE = {
  UNCONFIRMED: "unconfirmed",
  QUEUED: "queued",
  PLANNED: "planned",
  DRIVING: "driving",
  ON_SITE: "onSite",
  DONE: "done",
  CANCEL_PENDING: "cancelPending",
  CANCELLED: "cancelled"
};
var SCENARIO_PHASE_ORDER = [
  SCENARIO_PHASE.UNCONFIRMED,
  SCENARIO_PHASE.QUEUED,
  SCENARIO_PHASE.PLANNED,
  SCENARIO_PHASE.DRIVING,
  SCENARIO_PHASE.ON_SITE,
  SCENARIO_PHASE.DONE,
  SCENARIO_PHASE.CANCEL_PENDING,
  SCENARIO_PHASE.CANCELLED
];
var SCENARIO_RIDE_PHASE = {
  WAITING: "waiting",
  RIDING: "riding"
};
var SCENARIO_METRIC = {
  ALL: "all",
  UPCOMING: "upcoming",
  ACTIVE: "active",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  UNCONFIRMED: "unconfirmed",
  UNREAD: "unread"
};

// src/products/valet/platform/portalShapes.ts
var buildPortalHangtag = (hangtag) => ({
  type: "yard",
  status: hangtag.status,
  rear_code: hangtag.rearCode,
  hash_id: hangtag.hashId,
  workflow_data: hangtag.workflowData
});

// src/products/valet/configs/saasOverlayConfig.ts
var SAAS_OVERLAY_WILDCARD_KEY = "*";
var saasOverlayConfig = {
  [SAAS_OVERLAY_WILDCARD_KEY]: {
    times: { rightColumn: ["started", "completionEta"] },
    // SaaS-only because Full Service drivers are HopDrive's, and the delta would only
    // arm a dealer against HopDrive's own operation.
    milestone: { scheduleDelta: { amberBehindMinutes: 5, redBehindMinutes: 15 } },
    actions: { chat: true, assignDriver: true },
    mustLeaveBy: { enabled: true, prepBufferSource: "pickup_inspection_sec" },
    danger: { enabled: true, graceMinutes: 10 },
    rideLegs: { enabled: "rideshareCalling" },
    status: {
      ride: {
        [SCENARIO_RIDE_PHASE.WAITING]: { badge: "Waiting", tracker: "Waiting for ride", live: true },
        [SCENARIO_RIDE_PHASE.RIDING]: { badge: "Riding", tracker: "Riding to {deliveryStop}", live: true }
      }
    }
  },
  // Ready-by orders promise a start, not an arrival, so danger has nothing to measure.
  // Every key below is ready-by.
  "concierge-ret": { danger: { enabled: false, graceMinutes: 10 } },
  "concierge-ret-no-rideshare": { danger: { enabled: false, graceMinutes: 10 } },
  "concierge-loaner-ret": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-rideshare-before": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-rideshare-after": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-rideshare-both": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-flex": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-flex-rideshare-before": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-flex-rideshare-after": { danger: { enabled: false, graceMinutes: 10 } },
  "round-trip-flex-rideshare-both": { danger: { enabled: false, graceMinutes: 10 } },
  "stranded-no-rideshare": { danger: { enabled: false, graceMinutes: 10 } },
  "stranded-rideshare-before": { danger: { enabled: false, graceMinutes: 10 } },
  "stranded-rideshare-after": { danger: { enabled: false, graceMinutes: 10 } },
  "stranded-rideshare-both": { danger: { enabled: false, graceMinutes: 10 } },
  "stranded-shuttle-after": { danger: { enabled: false, graceMinutes: 10 } }
};

// src/products/valet/configs/scenarioConfigBase.ts
var DEFAULT_STATUS_MAP = {
  // Confirmation is not tier behavior, so the SaaS overlay carries nothing for it.
  [RAW_STATUS.UNCONFIRMED]: { badge: "Unconfirmed", tracker: "Awaiting confirmation", live: false },
  [RAW_STATUS.NONE]: { badge: "Upcoming", tracker: "Queued", live: false },
  [RAW_STATUS.DISPATCHED]: { badge: "Dispatched", tracker: "Queued", live: false },
  [RAW_STATUS.PICKUP_STARTED]: { badge: "Driving", tracker: "Driving to {pickupStop}", live: true },
  [RAW_STATUS.PICKUP_ARRIVED]: { badge: "Inspecting", tracker: "Inspecting at {pickupStop}", live: true },
  [RAW_STATUS.PICKUP_SUCCESSFUL]: { badge: "Inspecting", tracker: "Inspecting at {pickupStop}", live: true },
  [RAW_STATUS.DELIVERY_STARTED]: { badge: "Driving", tracker: "Driving to {deliveryStop}", live: true },
  [RAW_STATUS.DELIVERY_ARRIVED]: { badge: "Inspecting", tracker: "Inspecting at {deliveryStop}", live: true },
  [RAW_STATUS.DELIVERY_SUCCESSFUL]: { badge: "Completed", tracker: "Completed", live: false }
};
var DEFAULT_CANCEL_MAP = {
  cancelPending: { badge: "Cancel Pending", tracker: "Cancel Pending", live: false },
  cancelled: { badge: "Canceled", tracker: "Canceled", live: false }
};
var BASE_CONFIG = {
  isGuestFacing: false,
  legSequence: "fixed",
  promise: { label: "Ready By" },
  milestone: { humanizeWithinMinutes: 60 },
  status: { default: DEFAULT_STATUS_MAP, cancel: DEFAULT_CANCEL_MAP },
  times: { leftColumn: ["promise"], rightColumn: ["completionEta"] },
  route: { hideOrderingRooftopStop: false },
  legs: { showIdentifierChips: true, showVehiclePhotos: true },
  details: { showGuestInfo: false },
  actions: {
    openMove: true,
    createDuplicate: true,
    createInverse: true,
    changePromiseTime: true,
    chat: false,
    assignDriver: false,
    cancelMove: true
  },
  mustLeaveBy: { enabled: false, prepBufferSource: "pickup_inspection_sec" },
  danger: { enabled: false, graceMinutes: 10 },
  rideLegs: { enabled: false, vehicleLabel: "Ride Vehicle", showIdentifierChips: false }
};
var createScenarioConfig = (init) => ({
  ...BASE_CONFIG,
  ...init
});

// src/products/valet/configs/conciergeLoanerPickupConfig.ts
var conciergeLoanerPickupConfig = createScenarioConfig({
  scenarioKey: "concierge-loaner-pu",
  displayName: "Valet + Loaner Pickup",
  rendererKey: "conciergeLoaner",
  isGuestFacing: true,
  promise: { label: "Appointment" },
  times: { leftColumn: ["promise", "arrivalEta"], rightColumn: ["completionEta"] },
  route: { hideOrderingRooftopStop: true },
  details: { showGuestInfo: true }
});

// src/products/valet/configs/conciergeLoanerReturnConfig.ts
var conciergeLoanerReturnConfig = createScenarioConfig({
  scenarioKey: "concierge-loaner-ret",
  displayName: "Valet + Loaner Return",
  rendererKey: "conciergeLoaner",
  isGuestFacing: true,
  times: { leftColumn: ["promise", "arrivalEta"], rightColumn: ["completionEta"] },
  route: { hideOrderingRooftopStop: true },
  details: { showGuestInfo: true }
});

// src/products/valet/configs/conciergePickupConfig.ts
var conciergePickupConfig = createScenarioConfig({
  scenarioKey: "concierge-pu",
  displayName: "Valet Pickup",
  rendererKey: "concierge",
  isGuestFacing: true,
  promise: { label: "Appointment" },
  times: { leftColumn: ["promise", "arrivalEta"], rightColumn: ["completionEta"] },
  route: { hideOrderingRooftopStop: true },
  details: { showGuestInfo: true }
});

// src/products/valet/configs/conciergeReturnConfig.ts
var conciergeReturnConfig = createScenarioConfig({
  scenarioKey: "concierge-ret",
  displayName: "Valet Return",
  rendererKey: "concierge",
  isGuestFacing: true,
  times: { leftColumn: ["promise", "arrivalEta"], rightColumn: ["completionEta"] },
  route: { hideOrderingRooftopStop: true },
  details: { showGuestInfo: true }
});

// src/products/valet/configs/roundTripConfig.ts
var roundTripConfig = createScenarioConfig({
  scenarioKey: "round-trip",
  displayName: "Round-Trip",
  rendererKey: "operational"
});

// src/products/valet/configs/roundTripFlexConfig.ts
var roundTripFlexConfig = createScenarioConfig({
  scenarioKey: "round-trip-flex",
  displayName: "Round-Trip",
  rendererKey: "operational",
  legSequence: "flexible"
});

// src/products/valet/configs/strandedConfig.ts
var strandedConfig = createScenarioConfig({
  scenarioKey: "stranded",
  displayName: "One-Way",
  rendererKey: "operational"
});

// src/products/valet/configs/scenarioConfigRegistry.ts
var scenarioConfigRegistry = {
  "concierge-pu": conciergePickupConfig,
  "concierge-pu-no-rideshare": conciergePickupConfig,
  "concierge-ret": conciergeReturnConfig,
  "concierge-ret-no-rideshare": conciergeReturnConfig,
  "concierge-loaner-pu": conciergeLoanerPickupConfig,
  "concierge-loaner-ret": conciergeLoanerReturnConfig,
  "round-trip": roundTripConfig,
  "round-trip-rideshare-before": roundTripConfig,
  "round-trip-rideshare-after": roundTripConfig,
  "round-trip-rideshare-both": roundTripConfig,
  "round-trip-flex": roundTripFlexConfig,
  "round-trip-flex-rideshare-before": roundTripFlexConfig,
  "round-trip-flex-rideshare-after": roundTripFlexConfig,
  "round-trip-flex-rideshare-both": roundTripFlexConfig,
  "stranded-no-rideshare": strandedConfig,
  "stranded-rideshare-before": strandedConfig,
  "stranded-rideshare-after": strandedConfig,
  "stranded-rideshare-both": strandedConfig,
  "stranded-shuttle-after": strandedConfig
};
var DEALER_FACING_SCENARIO_KEYS = Object.keys(scenarioConfigRegistry);

// src/products/valet/scenarioConfigService.ts
var deliveryModelForOperationalModel = (model) => model === "self-managed" ? "saas" : "fullService";
var resolveOrderDeliveryModel = ({
  rooftopOperationalModel,
  firstDriveLegIsLite
}) => {
  if (firstDriveLegIsLite != null) return firstDriveLegIsLite ? "saas" : "fullService";
  return deliveryModelForOperationalModel(rooftopOperationalModel);
};
var resolveRooftopDeliveryModel = (rooftop) => deliveryModelForOperationalModel(rooftop?.operationalModel);
var resolveCustomerConfigGate = (gate, move) => {
  const config = move?.effective_config;
  if (!config) return false;
  switch (gate) {
    case "rideshareCalling":
      return config.rideshare_calling_enabled === true;
    default:
      return false;
  }
};
var isPlainObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var deepMerge = (base, overlay) => {
  if (!isPlainObject(base) || !isPlainObject(overlay)) return overlay ?? base;
  const merged = { ...base };
  for (const [key, overlayValue] of Object.entries(overlay)) {
    const baseValue = merged[key];
    merged[key] = isPlainObject(baseValue) && isPlainObject(overlayValue) ? deepMerge(baseValue, overlayValue) : overlayValue;
  }
  return merged;
};
var resolveScenarioConfig = ({
  scenarioKey,
  deliveryModel
}) => {
  if (!scenarioKey) return null;
  const base = scenarioConfigRegistry[scenarioKey];
  if (!base) return null;
  const resolved = { ...base, scenarioKey };
  if (deliveryModel !== "saas") return resolved;
  const withWildcard = deepMerge(resolved, saasOverlayConfig[SAAS_OVERLAY_WILDCARD_KEY] ?? {});
  return deepMerge(withWildcard, saasOverlayConfig[scenarioKey] ?? {});
};
var isRideLifecycleEnabled = (config, move) => {
  const { enabled } = config.rideLegs;
  return typeof enabled === "boolean" ? enabled : resolveCustomerConfigGate(enabled, move);
};

// src/products/valet/scenarioStatusService.ts
var resolveLegPhase = (move) => {
  switch (resolveRawStatusKey(move.status)) {
    case RAW_STATUS.UNCONFIRMED:
      return SCENARIO_PHASE.UNCONFIRMED;
    case RAW_STATUS.NONE:
      return SCENARIO_PHASE.QUEUED;
    case RAW_STATUS.DISPATCHED:
      return SCENARIO_PHASE.PLANNED;
    case RAW_STATUS.PICKUP_STARTED:
    case RAW_STATUS.DELIVERY_STARTED:
      return SCENARIO_PHASE.DRIVING;
    case RAW_STATUS.PICKUP_ARRIVED:
    case RAW_STATUS.PICKUP_SUCCESSFUL:
    case RAW_STATUS.DELIVERY_ARRIVED:
      return SCENARIO_PHASE.ON_SITE;
    case RAW_STATUS.DELIVERY_SUCCESSFUL:
      return SCENARIO_PHASE.DONE;
    default:
      return SCENARIO_PHASE.QUEUED;
  }
};
var isInProgressPhase = (phase) => phase === SCENARIO_PHASE.DRIVING || phase === SCENARIO_PHASE.ON_SITE;
var isDriveLeg = (leg) => leg.move.move_type === "drive";
var isRideLeg = (leg) => leg.move.move_type === "ride";
var resolveRideLegPhase = (move) => {
  const status = move.status;
  if (status === RIDE_STATUS.COMPLETED || status === RIDE_STATUS.CANCELLED || status === RIDE_STATUS.FAILED || !!move.delivery_successful || !!move.delivery_arrived) {
    return SCENARIO_PHASE.DONE;
  }
  if (status === RIDE_STATUS.IN_PROGRESS || !!move.pickup_successful || !!move.delivery_started) {
    return SCENARIO_PHASE.DRIVING;
  }
  return SCENARIO_PHASE.QUEUED;
};
var resolveOrderCancelState = (driveLegs) => {
  let sawPending = false;
  for (const leg of driveLegs) {
    const cancelStatus = leg.move.cancel_status;
    if (!cancelStatus) continue;
    if (FINALIZED_CANCEL_STATUSES.includes(cancelStatus)) return "cancelled";
    sawPending = true;
  }
  return sawPending ? "cancelPending" : null;
};
var resolveLatestActualMs = (move) => {
  const actuals = [
    move.delivery_successful,
    move.delivery_arrived,
    move.delivery_started,
    move.pickup_successful,
    move.pickup_arrived,
    move.pickup_started
  ];
  for (const actual of actuals) {
    if (!actual) continue;
    const ms = Date.parse(actual);
    if (!Number.isNaN(ms)) return ms;
  }
  return null;
};
var resolveAuthoritativeLeg = (driveLegs, legSequence) => {
  if (!driveLegs.length) return null;
  const byCardinality = [...driveLegs].sort((a, b) => a.cardinality - b.cardinality);
  if (legSequence === "flexible") {
    const withActuals = byCardinality.map((leg) => ({ leg, ms: resolveLatestActualMs(leg.move) })).filter((candidate) => candidate.ms !== null);
    if (withActuals.length) {
      return withActuals.reduce((latest, candidate) => candidate.ms >= latest.ms ? candidate : latest).leg;
    }
  }
  const inProgress = byCardinality.filter((leg) => isInProgressPhase(resolveLegPhase(leg.move)));
  if (inProgress.length) return inProgress[inProgress.length - 1];
  const dispatched = byCardinality.find((leg) => resolveRawStatusKey(leg.move.status) === RAW_STATUS.DISPATCHED);
  if (dispatched) return dispatched;
  const confirmed = byCardinality.find((leg) => resolveLegPhase(leg.move) !== SCENARIO_PHASE.UNCONFIRMED);
  return confirmed ?? byCardinality[0];
};
var interpolateStopTokens = (template, leg) => {
  const pickup = leg?.move.lane?.pickup?.name || "pickup";
  const delivery = leg?.move.lane?.delivery?.name || "delivery";
  return template.replace("{pickupStop}", pickup).replace("{deliveryStop}", delivery);
};
var resolveStatusDisplay = (config, leg, rawStatus) => {
  const override = config.status.byCardinality?.[String(leg.cardinality)]?.[rawStatus];
  return override ?? config.status.default[rawStatus] ?? config.status.default[RAW_STATUS.NONE];
};
var resolveRidePhase = ({
  legs,
  driveLegs
}) => {
  const rideLegs = legs.filter(isRideLeg);
  if (!rideLegs.length || !driveLegs.length) return null;
  const firstDrive = driveLegs[0];
  const lastDrive = driveLegs[driveLegs.length - 1];
  const leadingRide = rideLegs.find((ride) => ride.cardinality < firstDrive.cardinality);
  if (leadingRide && resolveRawStatusKey(firstDrive.move.status) === RAW_STATUS.PICKUP_STARTED) {
    return { phase: SCENARIO_RIDE_PHASE.RIDING, leg: leadingRide };
  }
  const trailingRide = rideLegs.find((ride) => ride.cardinality > lastDrive.cardinality);
  if (trailingRide && resolveLegPhase(lastDrive.move) === SCENARIO_PHASE.DONE) {
    const ridePhase = resolveRideLegPhase(trailingRide.move);
    if (ridePhase === SCENARIO_PHASE.DONE) return null;
    return {
      phase: ridePhase === SCENARIO_PHASE.DRIVING ? SCENARIO_RIDE_PHASE.RIDING : SCENARIO_RIDE_PHASE.WAITING,
      leg: trailingRide
    };
  }
  return null;
};
var deriveScenarioStatus = ({
  legs,
  config,
  rideLifecycleEnabled = false
}) => {
  const driveLegs = legs.filter(isDriveLeg);
  const authoritativeLeg = resolveAuthoritativeLeg(driveLegs, config.legSequence);
  const authoritativePoint = authoritativeLeg ? { leg: authoritativeLeg, rawStatus: resolveRawStatusKey(authoritativeLeg.move.status) } : null;
  const toStatus = (display, phase, leg) => ({
    phase,
    badge: display.badge,
    trackerLine: interpolateStopTokens(display.tracker, leg),
    isLive: display.live,
    authoritativePoint
  });
  const cancelState = resolveOrderCancelState(driveLegs);
  if (cancelState) {
    const phase = cancelState === "cancelled" ? SCENARIO_PHASE.CANCELLED : SCENARIO_PHASE.CANCEL_PENDING;
    return toStatus(config.status.cancel[cancelState], phase, authoritativeLeg);
  }
  if (!driveLegs.length || !authoritativeLeg) {
    return toStatus(config.status.default[RAW_STATUS.NONE], SCENARIO_PHASE.QUEUED, null);
  }
  if (rideLifecycleEnabled && config.status.ride) {
    const ridePhase = resolveRidePhase({ legs, driveLegs });
    if (ridePhase) {
      const display = config.status.ride[ridePhase.phase];
      const phase = ridePhase.phase === SCENARIO_RIDE_PHASE.WAITING ? SCENARIO_PHASE.ON_SITE : SCENARIO_PHASE.DRIVING;
      return toStatus(display, phase, ridePhase.leg);
    }
  }
  const lastDrive = [...driveLegs].sort((a, b) => a.cardinality - b.cardinality).slice(-1)[0];
  if (resolveLegPhase(lastDrive.move) === SCENARIO_PHASE.DONE) {
    return toStatus(
      resolveStatusDisplay(config, lastDrive, RAW_STATUS.DELIVERY_SUCCESSFUL),
      SCENARIO_PHASE.DONE,
      lastDrive
    );
  }
  const rawStatus = resolveRawStatusKey(authoritativeLeg.move.status);
  return toStatus(
    resolveStatusDisplay(config, authoritativeLeg, rawStatus),
    resolveLegPhase(authoritativeLeg.move),
    authoritativeLeg
  );
};
var resolveOrderMetric = ({ phase }) => {
  switch (phase) {
    case SCENARIO_PHASE.UNCONFIRMED:
    case SCENARIO_PHASE.QUEUED:
    case SCENARIO_PHASE.PLANNED:
      return "upcoming";
    case SCENARIO_PHASE.DRIVING:
    case SCENARIO_PHASE.ON_SITE:
      return "active";
    case SCENARIO_PHASE.DONE:
      return "completed";
    case SCENARIO_PHASE.CANCEL_PENDING:
    case SCENARIO_PHASE.CANCELLED:
      return "cancelled";
  }
};
var SCENARIO_STATUS_TOKEN = {
  [SCENARIO_PHASE.UNCONFIRMED]: ORDER_STATUS_TOKEN.QUEUED,
  [SCENARIO_PHASE.QUEUED]: ORDER_STATUS_TOKEN.QUEUED,
  [SCENARIO_PHASE.PLANNED]: ORDER_STATUS_TOKEN.PLANNED,
  [SCENARIO_PHASE.DRIVING]: ORDER_STATUS_TOKEN.DRIVING,
  [SCENARIO_PHASE.ON_SITE]: ORDER_STATUS_TOKEN.ON_SITE,
  [SCENARIO_PHASE.DONE]: ORDER_STATUS_TOKEN.DONE,
  [SCENARIO_PHASE.CANCEL_PENDING]: ORDER_STATUS_TOKEN.CANCEL_PENDING,
  [SCENARIO_PHASE.CANCELLED]: ORDER_STATUS_TOKEN.CANCELLED
};
var resolveScenarioStatusToken = (phase) => SCENARIO_STATUS_TOKEN[phase];
var needsScenarioAttention = (phase) => phase === SCENARIO_PHASE.UNCONFIRMED;

// src/products/valet/scenarioTimeService.ts
var WALK_EVENTS = [
  "pickupStarted",
  "pickupArrived",
  "pickupSuccessful",
  "deliveryStarted",
  "deliveryArrived",
  "deliverySuccessful"
];
var ACTUAL_COLUMN = {
  pickupStarted: "pickup_started",
  pickupArrived: "pickup_arrived",
  pickupSuccessful: "pickup_successful",
  deliveryStarted: "delivery_started",
  deliveryArrived: "delivery_arrived",
  deliverySuccessful: "delivery_successful"
};
var toMs = (value) => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};
var seconds = (value) => value ?? 0;
var addSeconds = (ms, secs) => ms === null || secs === null ? null : ms + secs * 1e3;
var laterOf = (a, b) => {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
};
var resolveLaneDriveSeconds = (lane) => {
  if (lane?.duration_sec == null) return null;
  return lane.duration_sec - seconds(lane.pickup_inspection_sec) - seconds(lane.delivery_inspection_sec);
};
var resolveRideSeconds = (lane) => lane?.duration_sec ?? null;
var isLaneEnriched = (lane) => lane?.duration_sec != null;
var sortByCardinality = (legs) => [...legs].sort((a, b) => a.cardinality - b.cardinality);
var resolveDriveLegs = (legs) => sortByCardinality(legs.filter(isDriveLeg));
var resolveLeadingRide = (legs) => {
  const driveLegs = resolveDriveLegs(legs);
  if (!driveLegs.length) return null;
  return sortByCardinality(legs).find((leg) => isRideLeg(leg) && leg.cardinality < driveLegs[0].cardinality) ?? null;
};
var resolveTrailingRide = (legs) => {
  const driveLegs = resolveDriveLegs(legs);
  if (!driveLegs.length) return null;
  const lastCardinality = driveLegs[driveLegs.length - 1].cardinality;
  return sortByCardinality(legs).find((leg) => isRideLeg(leg) && leg.cardinality > lastCardinality) ?? null;
};
var resolveEarliestReadyByMs = (legs) => {
  const timestamps = legs.map((leg) => toMs(leg.move.ready_by)).filter((ms) => ms !== null);
  return timestamps.length ? Math.min(...timestamps) : null;
};
var resolveOrderPromiseMs = (legs) => resolveEarliestReadyByMs(resolveDriveLegs(legs));
var isAppointmentStyleOrder = (legs) => resolveDriveLegs(legs).some((leg) => leg.move.target_pickup_time != null);
var resolveEventGaps = (leg, approachSeconds) => {
  const lane = leg.move.lane;
  return {
    pickupStarted: 0,
    pickupArrived: approachSeconds ?? 0,
    pickupSuccessful: seconds(lane?.pickup_inspection_sec),
    deliveryStarted: 0,
    deliveryArrived: resolveLaneDriveSeconds(lane),
    deliverySuccessful: seconds(lane?.delivery_inspection_sec)
  };
};
var emptyLegWalk = (leg) => ({
  moveId: leg.move.id,
  cardinality: leg.cardinality,
  pickupStarted: null,
  pickupArrived: null,
  pickupSuccessful: null,
  deliveryStarted: null,
  deliveryArrived: null,
  deliverySuccessful: null
});
var readActual = (leg, event) => toMs(leg.move[ACTUAL_COLUMN[event]]);
var buildPlannedWalk = (legs) => {
  const driveLegs = resolveDriveLegs(legs);
  if (!driveLegs.length) return null;
  if (driveLegs.some((leg) => !isLaneEnriched(leg.move.lane))) return null;
  const approachSeconds = resolveRideSeconds(resolveLeadingRide(legs)?.move.lane);
  let previousCompletion = null;
  return driveLegs.map((leg, index) => {
    const scheduled = toMs(leg.move.pickup_time) ?? (index === 0 ? toMs(leg.move.ready_by) : null);
    const anchor = laterOf(scheduled, previousCompletion);
    const gaps = resolveEventGaps(leg, index === 0 ? approachSeconds : 0);
    const pickupArrived = anchor;
    const pickupStarted = index === 0 && approachSeconds !== null ? addSeconds(anchor, -approachSeconds) : anchor;
    const pickupSuccessful = addSeconds(pickupArrived, gaps.pickupSuccessful);
    const deliveryStarted = pickupSuccessful;
    const deliveryArrived = addSeconds(deliveryStarted, gaps.deliveryArrived);
    const deliverySuccessful = addSeconds(deliveryArrived, gaps.deliverySuccessful);
    previousCompletion = deliverySuccessful;
    return {
      ...emptyLegWalk(leg),
      pickupStarted,
      pickupArrived,
      pickupSuccessful,
      deliveryStarted,
      deliveryArrived,
      deliverySuccessful
    };
  });
};
var walkSequence = (legCount) => Array.from({ length: legCount }, (_, legIndex) => WALK_EVENTS.map((event) => ({ legIndex, event }))).flat();
var buildLiveWalk = (legs) => {
  const planned = buildPlannedWalk(legs);
  if (!planned) return null;
  const driveLegs = resolveDriveLegs(legs);
  const sequence = walkSequence(driveLegs.length);
  const lastHitIndex = sequence.reduce(
    (last, step, index) => readActual(driveLegs[step.legIndex], step.event) !== null ? index : last,
    -1
  );
  if (lastHitIndex === -1) return planned;
  const live = planned.map((legWalk) => ({ ...legWalk }));
  const approachSeconds = resolveRideSeconds(resolveLeadingRide(legs)?.move.lane);
  let cursor = null;
  sequence.forEach((step, index) => {
    const leg = driveLegs[step.legIndex];
    const actual = readActual(leg, step.event);
    if (index <= lastHitIndex) {
      if (actual !== null) {
        live[step.legIndex][step.event] = actual;
        cursor = actual;
      }
      return;
    }
    const gaps = resolveEventGaps(leg, step.legIndex === 0 ? approachSeconds : 0);
    const gap = step.event === "pickupStarted" ? 0 : gaps[step.event];
    cursor = addSeconds(cursor, gap);
    live[step.legIndex][step.event] = cursor;
  });
  return live;
};
var hasOrderStarted = (legs) => resolveDriveLegs(legs).some(
  (leg) => resolveLegPhase(leg.move) !== SCENARIO_PHASE.QUEUED && leg.move.status !== "dispatched"
);
var isApproachingFirstPickup = (legs) => {
  const firstDrive = resolveDriveLegs(legs)[0];
  return !!firstDrive && firstDrive.move.status === "pickup started" && !firstDrive.move.pickup_arrived && !resolveLeadingRide(legs);
};
var isTrailingRideStillOut = ({
  legs,
  rideLifecycleEnabled = false
}) => {
  if (!rideLifecycleEnabled) return false;
  const trailingRide = resolveTrailingRide(legs);
  if (!trailingRide) return false;
  const status = trailingRide.move.status;
  if (status === RIDE_STATUS.CANCELLED || status === RIDE_STATUS.FAILED) return false;
  return resolveRideLegPhase(trailingRide.move) !== SCENARIO_PHASE.DONE;
};
var resolveOrderCompletedMs = ({
  legs,
  rideLifecycleEnabled = false
}) => {
  const trailingRide = rideLifecycleEnabled ? resolveTrailingRide(legs) : null;
  if (trailingRide && trailingRide.move.status !== RIDE_STATUS.CANCELLED && trailingRide.move.status !== RIDE_STATUS.FAILED && resolveRideLegPhase(trailingRide.move) === SCENARIO_PHASE.DONE) {
    return readActual(trailingRide, "deliverySuccessful") ?? readActual(trailingRide, "deliveryArrived");
  }
  const lastDrive = resolveDriveLegs(legs).slice(-1)[0];
  return lastDrive ? toMs(lastDrive.move.delivery_successful) : null;
};
var isGuestFacingLeg = (leg) => leg.move.consumer_pickup === true && !!leg.move.consumer_name && !!leg.move.consumer_phone;
var resolveGuestArrivalEvent = (leg) => leg.move.consumer_at_pickup === 1 ? "pickupArrived" : "deliveryArrived";
var resolveGuestArrivalMs = (legs) => {
  const walk = buildLiveWalk(legs);
  if (!walk) return null;
  const driveLegs = resolveDriveLegs(legs);
  const arrivals = driveLegs.map((leg, index) => isGuestFacingLeg(leg) ? walk[index][resolveGuestArrivalEvent(leg)] : null).filter((ms) => ms !== null);
  return arrivals.length ? Math.min(...arrivals) : null;
};
var resolveCompletionEtaMs = ({
  legs,
  rideLifecycleEnabled = false
}) => {
  const walk = buildLiveWalk(legs);
  if (!walk) return null;
  const finalDelivery = walk[walk.length - 1]?.deliverySuccessful ?? null;
  if (!rideLifecycleEnabled) return finalDelivery;
  const trailingRide = resolveTrailingRide(legs);
  if (!trailingRide) return finalDelivery;
  const rideStatus = trailingRide.move.status;
  if (rideStatus === RIDE_STATUS.CANCELLED || rideStatus === RIDE_STATUS.FAILED) {
    return finalDelivery;
  }
  const rideSeconds = resolveRideSeconds(trailingRide.move.lane);
  const rideCompleted = readActual(trailingRide, "deliverySuccessful") ?? readActual(trailingRide, "deliveryArrived");
  if (rideCompleted !== null) return rideCompleted;
  const rideBoarded = readActual(trailingRide, "pickupSuccessful") ?? readActual(trailingRide, "deliveryStarted");
  if (rideBoarded !== null) return addSeconds(rideBoarded, rideSeconds);
  const waitSeconds = seconds(trailingRide.move.lane?.return_ride_wait_sec);
  return addSeconds(addSeconds(finalDelivery, waitSeconds), rideSeconds);
};
var resolveScheduleDeltaMinutes = (legs) => {
  const planned = buildPlannedWalk(legs);
  if (!planned) return null;
  const driveLegs = resolveDriveLegs(legs);
  const sequence = walkSequence(driveLegs.length);
  let latest = null;
  for (const step of sequence) {
    const actual = readActual(driveLegs[step.legIndex], step.event);
    if (actual !== null) latest = { actual, plannedMs: planned[step.legIndex][step.event] };
  }
  if (!latest || latest.plannedMs === null) return null;
  return Math.round((latest.actual - latest.plannedMs) / 6e4);
};
var resolveMustLeaveByMs = ({
  legs,
  config
}) => {
  const promiseMs2 = resolveOrderPromiseMs(legs);
  if (promiseMs2 === null) return null;
  const leadingRide = resolveLeadingRide(legs);
  if (leadingRide) {
    const rideSeconds = resolveRideSeconds(leadingRide.move.lane);
    return rideSeconds === null ? null : addSeconds(promiseMs2, -rideSeconds);
  }
  const firstDrive = resolveDriveLegs(legs)[0];
  if (!firstDrive) return null;
  const driveSeconds = resolveLaneDriveSeconds(firstDrive.move.lane);
  if (driveSeconds === null) return null;
  const prepSeconds = config.mustLeaveBy.prepBufferSource === "pickup_inspection_sec" ? seconds(firstDrive.move.lane?.pickup_inspection_sec) : 0;
  return addSeconds(promiseMs2, -(driveSeconds + prepSeconds));
};
var isOrderInDanger = ({
  legs,
  config,
  nowMs,
  rideLifecycleEnabled = false
}) => {
  if (!config.danger.enabled) return false;
  const driveLegs = resolveDriveLegs(legs);
  if (!driveLegs.length) return false;
  if (resolveOrderCancelState(driveLegs)) return false;
  if (resolveLegPhase(driveLegs[driveLegs.length - 1].move) === SCENARIO_PHASE.DONE) return false;
  const promiseMs2 = resolveOrderPromiseMs(legs);
  if (promiseMs2 === null) return false;
  const hasStarted = hasOrderStarted(legs);
  if (!hasStarted) {
    const isDispatched = driveLegs.some((leg) => leg.move.status === "dispatched");
    if (!isDispatched || !isAppointmentStyleOrder(legs)) return false;
    const mustLeaveByMs = resolveMustLeaveByMs({ legs, config });
    return mustLeaveByMs !== null && nowMs > mustLeaveByMs;
  }
  const gateMs = (config.isGuestFacing ? resolveGuestArrivalMs(legs) : null) ?? resolveCompletionEtaMs({ legs, rideLifecycleEnabled });
  if (gateMs === null) return false;
  return gateMs > promiseMs2 + config.danger.graceMinutes * 6e4;
};
var hasDisconnectedTrackerStops = (legs) => {
  const driveLegs = resolveDriveLegs(legs);
  return driveLegs.some((leg, index) => {
    const nextLeg = driveLegs[index + 1];
    if (!nextLeg) return false;
    const deliveryId = leg.move.lane?.delivery?.id;
    const pickupId = nextLeg.move.lane?.pickup?.id;
    return deliveryId != null && pickupId != null && deliveryId !== pickupId;
  });
};
var buildTrackerBars = ({
  legs,
  deliveryModel,
  rideLifecycleEnabled = false
}) => {
  const driveLegs = resolveDriveLegs(legs);
  if (!driveLegs.length) return [];
  const leadingRideSeconds = resolveRideSeconds(resolveLeadingRide(legs)?.move.lane);
  const bars = [];
  bars.push({
    key: `pickup:${driveLegs[0].move.id}`,
    role: "firstPickup",
    isReached: readActual(driveLegs[0], "pickupArrived") !== null,
    isDrivingToward: resolveLegPhase(driveLegs[0].move) === SCENARIO_PHASE.DRIVING && readActual(driveLegs[0], "pickupArrived") === null,
    expectedSpanSeconds: leadingRideSeconds,
    slipSeconds: null
  });
  driveLegs.forEach((leg, index) => {
    const spanSeconds = (() => {
      const inspection = seconds(leg.move.lane?.pickup_inspection_sec);
      const drive = resolveLaneDriveSeconds(leg.move.lane);
      return drive === null ? null : inspection + drive;
    })();
    bars.push({
      key: `delivery:${leg.move.id}`,
      role: driveLegs[index + 1] ? "combined" : "delivery",
      isReached: readActual(leg, "deliveryArrived") !== null,
      isDrivingToward: resolveRawStatusKeyIsDeliveryDriving(leg) && readActual(leg, "deliveryArrived") === null,
      expectedSpanSeconds: spanSeconds,
      slipSeconds: null
    });
  });
  if (rideLifecycleEnabled) {
    const trailingRide = resolveTrailingRide(legs);
    if (trailingRide) {
      const rideSeconds = resolveRideSeconds(trailingRide.move.lane);
      const waitSeconds = seconds(trailingRide.move.lane?.return_ride_wait_sec);
      const rideDone = readActual(trailingRide, "deliverySuccessful") ?? readActual(trailingRide, "deliveryArrived");
      const rideBoarded = readActual(trailingRide, "pickupSuccessful") ?? readActual(trailingRide, "deliveryStarted");
      bars.push({
        key: `return:${trailingRide.move.id}`,
        role: "rideReturn",
        isReached: rideDone !== null,
        isDrivingToward: rideBoarded !== null && rideDone === null,
        expectedSpanSeconds: rideSeconds === null ? null : waitSeconds + rideSeconds,
        slipSeconds: null
      });
    }
  }
  if (deliveryModel !== "saas") return bars;
  const planned = buildPlannedWalk(legs);
  const live = buildLiveWalk(legs);
  return attachSlipToFirstUnreachedBar({ bars, driveLegs, planned, live });
};
var resolveRawStatusKeyIsDeliveryDriving = (leg) => leg.move.status === "delivery started";
var attachSlipToFirstUnreachedBar = ({
  bars,
  driveLegs,
  planned,
  live
}) => {
  if (!planned || !live) return bars;
  const firstUnreachedIndex = bars.findIndex((bar2) => !bar2.isReached);
  if (firstUnreachedIndex === -1) return bars;
  const bar = bars[firstUnreachedIndex];
  const legIndex = bar.role === "firstPickup" ? 0 : firstUnreachedIndex - 1;
  const event = bar.role === "firstPickup" ? "pickupArrived" : "deliveryArrived";
  if (legIndex < 0 || legIndex >= driveLegs.length) return bars;
  const plannedMs = planned[legIndex]?.[event] ?? null;
  const liveMs = live[legIndex]?.[event] ?? null;
  if (plannedMs === null || liveMs === null) return bars;
  const slip = Math.round((liveMs - plannedMs) / 1e3);
  return bars.map(
    (candidate, index) => index === firstUnreachedIndex ? { ...candidate, slipSeconds: Math.max(0, slip) } : candidate
  );
};
var MIN_SLIP_DENOMINATOR_SECONDS = 5 * 60;
var resolveTrackerSlipFraction = (bar) => {
  if (bar.slipSeconds === null || bar.slipSeconds <= 0) return 0;
  if (bar.expectedSpanSeconds === null) return 0;
  const denominator = Math.max(bar.expectedSpanSeconds, MIN_SLIP_DENOMINATOR_SECONDS);
  return Math.min(1, bar.slipSeconds / denominator);
};

// src/products/valet/scenarioMilestoneService.ts
var MINUTE_MS = 6e4;
var DEFAULT_HUMANIZE_WITHIN_MINUTES = 60;
var clockParts = (ms, timeZone) => new Intl.DateTimeFormat("en-US", {
  timeZone,
  hour: "numeric",
  minute: "2-digit",
  hour12: true
}).format(ms);
var formatClockTime = (ms, timeZone) => ms === null ? "N/A" : clockParts(ms, timeZone);
var formatDatedClockTime = (ms, timeZone) => {
  if (ms === null) return "N/A";
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "numeric",
    day: "numeric"
  }).format(ms);
  return `${date}, ${clockParts(ms, timeZone)}`;
};
var formatDuration = (totalMinutes, withinMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  if (totalMinutes < withinMinutes || hours === 0) return `in ${totalMinutes} min`;
  const remainder = totalMinutes % 60;
  return remainder === 0 ? `in ${hours} hr` : `in ${hours} hr ${remainder} min`;
};
var humanizeRemainingTime = ({
  targetMs,
  nowMs,
  withinMinutes = DEFAULT_HUMANIZE_WITHIN_MINUTES,
  asDeadline = false
}) => {
  if (targetMs === null) return "N/A";
  const remainingMinutes = Math.round((targetMs - nowMs) / MINUTE_MS);
  if (remainingMinutes > 0) return formatDuration(remainingMinutes, withinMinutes);
  return asDeadline ? `${Math.abs(remainingMinutes)} min overdue` : "soon";
};
var describeScheduleDelta = (deltaMinutes, thresholds) => {
  if (deltaMinutes === null) return null;
  const { amberBehindMinutes, redBehindMinutes } = thresholds;
  if (deltaMinutes >= amberBehindMinutes) {
    return {
      text: `${deltaMinutes} min behind`,
      tone: deltaMinutes >= redBehindMinutes ? "red" : "amber"
    };
  }
  if (deltaMinutes <= -amberBehindMinutes) {
    return { text: `${Math.abs(deltaMinutes)} min ahead`, tone: "green" };
  }
  return { text: "on schedule", tone: "neutral" };
};
var tooltipInput = (label, targetMs, legs, meaning) => ({
  label,
  targetMs,
  laneTimezone: resolveDriveLegs(legs)[0]?.move.lane?.pickup?.timezone ?? null,
  meaning
});
var resolveLiveSubtext = (legs, config) => {
  const thresholds = config.milestone.scheduleDelta;
  if (!thresholds) return { text: "In Progress", tone: "neutral" };
  const delta = describeScheduleDelta(resolveScheduleDeltaMinutes(legs), thresholds);
  return delta ?? { text: "In Progress", tone: "neutral" };
};
var resolvePromiseMeaning = (config) => {
  if (!config.isGuestFacing) return "When the vehicle is ready to move";
  return config.promise.label === "Appointment" ? "When the guest expects to meet the driver" : "When the guest's vehicle is ready to be returned";
};
var resolveScenarioMilestone = ({
  legs,
  config,
  nowMs,
  timeZone,
  rideLifecycleEnabled = false
}) => {
  const driveLegs = resolveDriveLegs(legs);
  const promiseMs2 = resolveOrderPromiseMs(legs);
  const humanizeWithin = config.milestone.humanizeWithinMinutes;
  const cancelState = resolveOrderCancelState(driveLegs);
  if (cancelState) {
    const cancelledAt = driveLegs.map((leg) => leg.move.canceled_at).find(Boolean) ?? null;
    const cancelledMs = cancelledAt ? Date.parse(cancelledAt) : null;
    const hasTime = cancelledMs !== null && !Number.isNaN(cancelledMs);
    return {
      kind: "cancelled",
      value: hasTime ? formatDatedClockTime(cancelledMs, timeZone) : "\u2014",
      subtext: { text: "Canceled", tone: "neutral" },
      tooltipInput: tooltipInput("Canceled", hasTime ? cancelledMs : null, legs, "When the order was canceled")
    };
  }
  const lastDrive = driveLegs[driveLegs.length - 1];
  if (lastDrive && resolveLegPhase(lastDrive.move) === SCENARIO_PHASE.DONE && !isTrailingRideStillOut({ legs, rideLifecycleEnabled })) {
    const completedMs = resolveOrderCompletedMs({ legs, rideLifecycleEnabled });
    return {
      kind: "completed",
      value: formatDatedClockTime(completedMs, timeZone),
      subtext: { text: "Completed", tone: "neutral" },
      tooltipInput: tooltipInput("Completed", completedMs, legs, "When the order was completed")
    };
  }
  const hasStarted = hasOrderStarted(legs);
  const isDispatched = driveLegs.some((leg) => leg.move.status === "dispatched");
  if (config.mustLeaveBy.enabled && isAppointmentStyleOrder(legs) && isDispatched && !hasStarted) {
    const mustLeaveByMs = resolveMustLeaveByMs({ legs, config });
    if (mustLeaveByMs !== null) {
      const isOverdue = nowMs > mustLeaveByMs;
      return {
        kind: "departure",
        value: `Leave by ${formatClockTime(mustLeaveByMs, timeZone)}`,
        subtext: isOverdue ? { text: humanizeRemainingTime({ targetMs: mustLeaveByMs, nowMs, asDeadline: true }), tone: "red" } : { text: "Driver Departure", tone: "neutral" },
        tooltipInput: tooltipInput(
          "Driver Departure",
          mustLeaveByMs,
          legs,
          "When the driver must leave to make the appointment"
        )
      };
    }
  }
  if (isApproachingFirstPickup(legs)) {
    return {
      kind: "starting",
      value: "Starting",
      subtext: resolveLiveSubtext(legs, config),
      tooltipInput: tooltipInput(
        "Starting",
        null,
        legs,
        "No estimate exists until the driver reaches the first pickup"
      )
    };
  }
  if (hasStarted) {
    const guestArrivalMs = config.isGuestFacing ? resolveGuestArrivalMs(legs) : null;
    const guestReached = driveLegs.some(
      (leg) => isGuestFacingLeg(leg) && (leg.move.consumer_at_pickup === 1 ? !!leg.move.pickup_arrived : !!leg.move.delivery_arrived)
    );
    if (guestArrivalMs !== null && !guestReached) {
      return {
        kind: "live",
        value: `Arriving ${humanizeRemainingTime({ targetMs: guestArrivalMs, nowMs, withinMinutes: humanizeWithin })}`,
        subtext: resolveLiveSubtext(legs, config),
        tooltipInput: tooltipInput("Arrival ETA", guestArrivalMs, legs, "When the driver should reach the guest")
      };
    }
    const completionMs = resolveCompletionEtaMs({ legs, rideLifecycleEnabled });
    return {
      kind: "live",
      // A bare N/A with no lane yet; a gerund would dress "unknown" up as a reading.
      value: completionMs === null ? "N/A" : `Completing ${humanizeRemainingTime({ targetMs: completionMs, nowMs, withinMinutes: humanizeWithin })}`,
      subtext: resolveLiveSubtext(legs, config),
      tooltipInput: tooltipInput("Completion ETA", completionMs, legs, "When the order should complete")
    };
  }
  const meaning = resolvePromiseMeaning(config);
  return {
    kind: "promise",
    value: formatDatedClockTime(promiseMs2, timeZone),
    subtext: { text: config.promise.label, tone: "neutral" },
    tooltipInput: tooltipInput(config.promise.label, promiseMs2, legs, meaning)
  };
};

// src/products/valet/scenarioGroupingService.ts
var isResolvedOrderEntry = (entry) => entry.kind === "order";
var isErrorEntry = (entry) => entry.kind === "error";
var collectEntryMoveIds = (entry) => isResolvedOrderEntry(entry) ? entry.order.legs.map((leg) => leg.move.id) : entry.moveIds;
var toTimestampMs = (value) => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};
var isDriveLeg2 = (leg) => leg.move.move_type === "drive";
var buildLegList = (order) => order.legs.filter((leg) => leg.move.active !== 0).sort((a, b) => a.cardinality - b.cardinality);
var resolveAnchorMs = (legs) => {
  const timestamps = legs.map((leg) => toTimestampMs(leg.move.ready_by)).filter((ms) => ms !== null);
  return timestamps.length ? Math.min(...timestamps) : null;
};
var resolveZonedDay = (ms, timeZone) => new Intl.DateTimeFormat("en-CA", {
  timeZone,
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
}).format(new Date(ms));
var resolveHasCrossDayLegs = (legs, timeZone) => {
  const days = new Set(
    legs.filter(isDriveLeg2).map((leg) => toTimestampMs(leg.move.ready_by)).filter((ms) => ms !== null).map((ms) => resolveZonedDay(ms, timeZone))
  );
  return days.size > 1;
};
var buildErrorEntry = (key, reason, moveIds, anchorMs) => ({ kind: "error", key, anchorMs, reason, moveIds });
var buildEntryForScenario = (order, timeZone) => {
  const key = `scenario:${order.scenarioId}`;
  const legs = buildLegList(order);
  const driveLegs = legs.filter(isDriveLeg2);
  if (!driveLegs.length) {
    return buildErrorEntry(
      key,
      "noDriveLegs",
      legs.map((leg) => leg.move.id),
      resolveAnchorMs(legs)
    );
  }
  return {
    kind: "order",
    key,
    anchorMs: resolveAnchorMs(driveLegs),
    order: { ...order, legs },
    hasCrossDayLegs: resolveHasCrossDayLegs(legs, timeZone)
  };
};
var scenarioEntryCache = /* @__PURE__ */ new WeakMap();
var buildEntryForScenarioCached = (order, timeZone) => {
  const cached = scenarioEntryCache.get(order);
  if (cached && cached.timeZone === timeZone) return cached.entry;
  const entry = buildEntryForScenario(order, timeZone);
  scenarioEntryCache.set(order, { timeZone, entry });
  return entry;
};
var faultEntryCache = /* @__PURE__ */ new WeakMap();
var buildFaultEntryCached = (row2, build) => {
  const cached = faultEntryCache.get(row2);
  if (cached) return cached;
  const entry = build();
  faultEntryCache.set(row2, entry);
  return entry;
};
var compareEntryKeys = (a, b) => {
  const [aKind, aId] = a.split(":");
  const [bKind, bId] = b.split(":");
  if (aKind !== bKind) return aKind.localeCompare(bKind);
  return Number(aId) - Number(bId);
};
var compareEntries = (a, b) => {
  if (a.anchorMs !== b.anchorMs) {
    if (a.anchorMs === null) return 1;
    if (b.anchorMs === null) return -1;
    return a.anchorMs - b.anchorMs;
  }
  return compareEntryKeys(a.key, b.key);
};
var buildScenarioOrdersFromIndex = (rows, { timeZone = "UTC" } = {}) => {
  const entriesByKey = /* @__PURE__ */ new Map();
  const unlinkedEntries = [];
  for (const row2 of rows) {
    if (!row2.order) {
      unlinkedEntries.push(buildFaultEntryCached(row2, () => buildErrorEntry(`move:${row2.moveId}`, "noScenarioLink", [row2.moveId], null)));
      continue;
    }
    const key = `scenario:${row2.order.scenarioId}`;
    if (!entriesByKey.has(key)) {
      entriesByKey.set(key, buildEntryForScenarioCached(row2.order, timeZone));
    }
  }
  const entries = [...entriesByKey.values(), ...unlinkedEntries];
  const coveredMoveIds = new Set(entries.flatMap(collectEntryMoveIds));
  for (const row2 of rows) {
    if (!coveredMoveIds.has(row2.moveId)) {
      entries.push(buildFaultEntryCached(row2, () => buildErrorEntry(`move:${row2.moveId}`, "moveMissingFromScenario", [row2.moveId], null)));
      coveredMoveIds.add(row2.moveId);
    }
  }
  return entries.sort(compareEntries);
};

// src/products/valet/scenarioTooltipService.ts
import { formatOfferActor } from "@hopdrive/sdk-dispatch";
var ABSENT = "\u2014";
var row = (key, value) => ({ kind: "row", key, value });
var section = (text) => ({ kind: "section", text });
var divider = () => ({ kind: "divider" });
var description = (text) => ({ kind: "description", text });
var title = (text) => ({ kind: "title", text });
var formatZonedStamp = (ms, timeZone) => new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
  ...timeZone ? { timeZone } : {}
}).format(new Date(ms));
var identityContent = (displayName, rows, referenceLabel) => {
  const content = [title(displayName)];
  rows.forEach((identity, index) => {
    if (index > 0) content.push(divider());
    content.push(section(`Move #${identity.moveId}`));
    content.push(row(referenceLabel, identity.reference ?? ABSENT));
    content.push(row("VIN", identity.vin ?? ABSENT));
    content.push(row("Stock", identity.stock ?? ABSENT));
  });
  return content;
};
var buildOrderIdentityTooltipFromView = (view) => identityContent(
  view.identity.displayName,
  view.legs.filter((leg) => leg.moveType === "drive").map((leg) => ({
    moveId: leg.moveId,
    reference: leg.identifiers.reference,
    vin: leg.identifiers.vin,
    stock: leg.identifiers.stock
  })),
  view.display.referenceLabel
);
var buildTimeTooltip = (input) => {
  const content = [title(input.label)];
  if (input.targetMs !== null) {
    if (input.laneTimezone) content.push(row("Region", formatZonedStamp(input.targetMs, input.laneTimezone)));
    content.push(row("Local", formatZonedStamp(input.targetMs)));
    content.push(row("UTC", formatZonedStamp(input.targetMs, "UTC")));
  }
  content.push(description(input.meaning));
  return content;
};
var locationRows = (location) => {
  const content = [];
  if (location?.address) content.push(row("Address", location.address));
  if (location?.latitude != null && location?.longitude != null) {
    content.push(row("Lat / Lng", `${location.latitude}, ${location.longitude}`));
  }
  return content;
};
var laneEnd = (label, location) => [
  section(`${label} \u2014 ${location?.name ?? ""}`.trim()),
  ...locationRows(location)
];
var buildLaneTooltip = (lane) => {
  const content = [
    ...laneEnd("Pickup", lane?.pickup),
    divider(),
    ...laneEnd("Delivery", lane?.delivery)
  ];
  if (lane?.distanceMiles != null && lane?.durationSec != null) {
    content.push(divider());
    content.push(row("Distance", formatDistanceMiles(lane.distanceMiles) ?? ""));
    content.push(row("Duration", `${Math.round(lane.durationSec / 60)} min`));
  }
  return content;
};
var buildRouteChainTooltip = (stops) => {
  if (!stops.length) return null;
  const content = [];
  stops.forEach((stop, index) => {
    if (index > 0) content.push(divider());
    content.push(section(stop.name), ...locationRows(stop));
  });
  return content;
};
var buildMoveTypeTooltip = (identity, loanerDirection = null) => {
  switch (identity) {
    case "pickup":
      return [description("This guest's vehicle will be picked up")];
    case "return":
      return [description("This guest's vehicle will be returned")];
    case "loaner":
      return [
        description(
          loanerDirection === "returned" ? "This loaner vehicle will be returned" : "This loaner vehicle will be lent out"
        )
      ];
    case "operational":
      return [description("This vehicle will be moved")];
    case "ride":
      return null;
    default:
      return null;
  }
};
var TRACKER_BAR_BEHAVIOUR = {
  firstPickup: "Flashes while driving, solid while on site",
  delivery: "Flashes while driving, solid through inspection to completed",
  combined: "First delivery and second pickup are the same stop \u2014 flashes while driving, solid on site",
  rideReturn: "Flashes while riding, solid once returned"
};
var resolveTrackerBarLabel = ({
  bar,
  isLastBar,
  isMultiLeg
}) => {
  if (bar.role === "rideReturn") return "Driver return ride";
  if (bar.role === "combined") return "Combined stop";
  if (bar.role === "firstPickup") return isMultiLeg ? "First pickup" : "Pickup";
  return isMultiLeg && isLastBar ? "Final delivery" : "Delivery";
};
var buildTrackerBarTooltip = ({
  bars,
  index,
  isMultiLeg
}) => {
  const bar = bars[index];
  if (!bar) return null;
  const content = [
    title(resolveTrackerBarLabel({ bar, isLastBar: index === bars.length - 1, isMultiLeg }))
  ];
  content.push(
    row("Span", bar.expectedSpanSeconds === null ? ABSENT : `~${Math.round(bar.expectedSpanSeconds / 60)} min`)
  );
  if (bar.slipSeconds !== null && bar.slipSeconds > 0) {
    content.push(row("Behind", `+${Math.round(bar.slipSeconds / 60)} min`));
  }
  content.push(description(TRACKER_BAR_BEHAVIOUR[bar.role]));
  return content;
};
var formatAuditTimestamp = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
};
var buildDriverPillTooltip = ({
  state,
  driverName,
  offeredAt,
  offeredBy,
  acceptedAt,
  acceptedBy
}) => {
  const name = driverName || "A driver";
  if (state === "unassigned") {
    return [title("Assign a driver"), description("Choose a driver for this order.")];
  }
  if (state === "offered") {
    const content = [title(`Offered to ${name}`)];
    const formattedOfferedAt = formatAuditTimestamp(offeredAt);
    if (formattedOfferedAt) content.push(row("Offered", formattedOfferedAt));
    const offeredActor = formatOfferActor(offeredBy);
    if (offeredActor !== "\u2014") content.push(description(`by ${offeredActor}`));
    return content;
  }
  if (state === "accepted" || state === "assigned") {
    const content = [title(`Accepted by ${name}`)];
    const formattedOfferedAt = formatAuditTimestamp(offeredAt);
    const formattedAcceptedAt = formatAuditTimestamp(acceptedAt);
    if (formattedOfferedAt) content.push(row("Offered", formattedOfferedAt));
    if (formattedAcceptedAt) content.push(row("Accepted", formattedAcceptedAt));
    const acceptedActor = formatOfferActor(acceptedBy);
    if (acceptedActor !== "\u2014") content.push(description(`accepted by ${acceptedActor}`));
    return content;
  }
  if (state === "locked") {
    const content = [title(driverName ? `${name} is assigned` : "Assignment locked")];
    const formattedOfferedAt = formatAuditTimestamp(offeredAt);
    const formattedAcceptedAt = formatAuditTimestamp(acceptedAt);
    if (formattedOfferedAt) content.push(row("Offered", formattedOfferedAt));
    if (formattedAcceptedAt) content.push(row("Accepted", formattedAcceptedAt));
    content.push(description("Assignment is locked once the order is in progress."));
    return content;
  }
  return [title(`${name} is assigned`)];
};

// src/products/valet/scenarioViewService.ts
import dayjs2 from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { resolveDriverAssignment } from "@hopdrive/sdk-dispatch";
import {
  isOrderAssignable,
  isOfferAccepted,
  resolveOfferAuditFields,
  resolveDriverAssignment as resolveDriverAssignment2
} from "@hopdrive/sdk-dispatch";
dayjs2.extend(relativeTime);
var resolveAssignmentDetail = (assignment, nowMs) => {
  const isAccepted = assignment.state === "accepted" || assignment.state === "assigned";
  if (isAccepted && assignment.acceptedAt) return `Accepted ${dayjs2(assignment.acceptedAt).format("M/D")}`;
  if (assignment.state === "offered" && assignment.offeredAt) return `Offered ${dayjs2(assignment.offeredAt).from(dayjs2(nowMs), true)} ago`;
  return null;
};
var resolveMoveTypeIdentity = (move) => {
  if (move.move_type === "ride") return "ride";
  if (move.consumer_type === "loaner") return "loaner";
  if (move.consumer_type === "customer") return move.consumer_at_pickup === 1 ? "pickup" : "return";
  return "operational";
};
var isCapturedPhoto = (photo) => photo.status === "done";
var findHeroStepId = (steps) => {
  if (!steps) return null;
  for (const step of steps) {
    if (step?.type === "photo" && step.config?.is_hero === true && step.id) return step.id;
    const nested = findHeroStepId(step?.steps);
    if (nested) return nested;
  }
  return null;
};
var resolveLegPhoto = (leg, platform) => {
  const captured = (leg.move.vehiclephotos ?? []).filter(isCapturedPhoto);
  const hasPhase = (type) => captured.some((photo) => photo.workflow?.type === type);
  const phases = { hasPickupPhotos: hasPhase("pickup"), hasDeliveryPhotos: hasPhase("delivery") };
  if (!captured.length) return { url: null, isPlaceholder: true, ...phases };
  const groups = /* @__PURE__ */ new Map();
  for (const photo of captured) {
    const key = String(photo.workflow_id ?? "ungrouped");
    groups.set(key, [...groups.get(key) ?? [], photo]);
  }
  const earliestOf = (photos) => Math.min(...photos.map((photo) => photo.createdat ? Date.parse(photo.createdat) : Number.MAX_SAFE_INTEGER));
  const ordered = [...groups.values()].sort((a, b) => earliestOf(a) - earliestOf(b));
  const workflowset = leg.move.workflowset;
  const groupForWorkflow = (id) => id == null ? void 0 : ordered.find((group) => group.some((photo) => photo.workflow_id === id));
  const pickupGroup = groupForWorkflow(workflowset?.pickupWorkflow?.id) ?? ordered[0];
  const deliveryGroup = groupForWorkflow(workflowset?.deliveryWorkflow?.id) ?? (ordered.length > 1 ? ordered[ordered.length - 1] : void 0);
  const heroIn = (group, steps) => {
    if (!group?.length) return void 0;
    const byStep = (stepId) => stepId ? group.find((photo) => photo.step_id === stepId) : void 0;
    return byStep(findHeroStepId(steps)) ?? byStep("driver-front");
  };
  const hero = heroIn(deliveryGroup, workflowset?.deliveryWorkflow?.steps) ?? heroIn(pickupGroup, workflowset?.pickupWorkflow?.steps);
  if (hero?.url) return { url: hero.url, isPlaceholder: false, ...phases };
  const fallbackGroup = deliveryGroup ?? pickupGroup;
  const preferred = platform.sortPhotosByStandardOrder(fallbackGroup)[0] ?? fallbackGroup[0];
  return { url: preferred?.url ?? null, isPlaceholder: !preferred?.url, ...phases };
};
var resolveReferenceLabel = (configured) => configured || "Reference ID";
var resolveVisibleLegIdentifiers = ({
  identifiers,
  displayPreferences
}) => {
  const chips = [
    { key: "moveId", label: "HopDrive Move ID", value: String(identifiers.moveId), enabled: displayPreferences.moveId },
    {
      key: "reference",
      label: identifiers.referenceLabel,
      value: identifiers.reference,
      enabled: displayPreferences.reference
    },
    { key: "vin", label: "VIN", value: identifiers.vin, enabled: displayPreferences.vin },
    { key: "stock", label: "Stock Number", value: identifiers.stock, enabled: displayPreferences.stock }
  ];
  return chips.flatMap(
    (chip) => chip.enabled && chip.value ? [{ key: chip.key, label: chip.label, value: chip.value }] : []
  );
};
var resolveVehicleLine = (leg, config, platform) => isRideLeg(leg) ? config.rideLegs.vehicleLabel : platform.formatVehicleLine(leg.move) || "Vehicle";
var resolveLegHangtag = (move) => {
  const newest = move.hangtags?.[0];
  if (!newest || newest.type !== "yard") return null;
  return {
    rearCode: newest.rear_code,
    hashId: newest.hash_id,
    status: newest.status,
    workflowData: newest.workflow_data
  };
};
var buildLegView = ({
  leg,
  config,
  referenceLabel,
  platform
}) => {
  const lane = leg.move.lane;
  const isRide = isRideLeg(leg);
  const typeIdentity = resolveMoveTypeIdentity(leg.move);
  return {
    moveId: leg.move.id,
    cardinality: leg.cardinality,
    moveType: leg.move.move_type,
    typeIdentity,
    vehicleLine: resolveVehicleLine(leg, config, platform),
    identifiers: {
      moveId: leg.move.id,
      reference: isRide ? null : leg.move.reference_num ?? null,
      vin: isRide ? null : leg.move.vehicle_vin ?? null,
      stock: isRide ? null : leg.move.vehicle_stock ?? null,
      referenceLabel
    },
    lane: {
      distanceMiles: lane?.distance_miles ?? null,
      durationSec: lane?.duration_sec ?? null,
      // Enrichment lands within seconds, so a lane with no duration is still arriving.
      isAwaitingEnrichment: !!lane && lane.duration_sec == null,
      pickup: toRouteStop(lane?.pickup ?? null),
      delivery: toRouteStop(lane?.delivery ?? null)
    },
    loanerDirection: typeIdentity === "loaner" ? leg.move.consumer_at_pickup === 1 ? "returned" : "lent" : null,
    routeEnds: {
      pickupName: lane?.pickup?.name ?? "Pickup",
      deliveryName: lane?.delivery?.name ?? "Delivery"
    },
    photo: isRide ? { url: null, isPlaceholder: true, hasPickupPhotos: false, hasDeliveryPhotos: false } : resolveLegPhoto(leg, platform),
    phase: resolveLegPhase(leg.move),
    hangtag: isRide ? null : resolveLegHangtag(leg.move)
  };
};
var resolveOrderConfirmation = ({
  order,
  driveLegs,
  phase
}) => {
  if (phase !== SCENARIO_PHASE.UNCONFIRMED) return null;
  const requests = order.confirmationRequests ?? [];
  const latest = requests[0] ?? null;
  const flowLeg = driveLegs.find(isGuestFacingLeg) ?? driveLegs[0] ?? null;
  return {
    status: latest?.status ?? "pending",
    isEscalated: latest?.escalated ?? false,
    expirationTime: latest?.expiration_time ?? null,
    confirmationFlow: flowLeg?.move.workflowset?.confirmationset?.confirmation_flow ?? null,
    requests
  };
};
var toRouteStop = (location) => location ? {
  locationId: location.id,
  name: location.name ?? "Stop",
  address: location.address ?? null,
  latitude: location.latitude ?? null,
  longitude: location.longitude ?? null
} : null;
var resolveRouteStops = ({
  legs,
  config,
  rooftopLocationId
}) => {
  const stops = [];
  for (const leg of legs) {
    for (const end of [leg.move.lane?.pickup, leg.move.lane?.delivery]) {
      const stop = toRouteStop(end ?? null);
      if (!stop) continue;
      if (config.route.hideOrderingRooftopStop && rooftopLocationId !== null && end?.id === rooftopLocationId) {
        continue;
      }
      if (stops[stops.length - 1]?.locationId === stop.locationId) continue;
      stops.push(stop);
    }
  }
  return stops;
};
var isOrderAssignmentSettled = ({
  legs,
  offers = [],
  action,
  driverId
}) => {
  const driveLegs = resolveDriveLegs(legs);
  const hasAssignedLeg = driveLegs.some((leg) => leg.move.driver_id != null);
  if (action === "unassign") return !hasAssignedLeg && !offers.length;
  if (driverId === null) return false;
  return driveLegs.some((leg) => leg.move.driver_id === driverId) || offers[0]?.driver?.id === driverId;
};
var ACTION_ICONS = {
  openMove: "open_in_new",
  createDuplicate: "content_copy",
  createInverse: "swap_horiz",
  changePromiseTime: "schedule",
  chat: "chat",
  assignDriver: "person_add",
  cancelMove: "cancel"
};
var buildScenarioActionMenuItems = ({
  config,
  legs
}) => {
  const driveLegs = resolveDriveLegs(legs);
  const hasStarted = driveLegs.some((leg) => resolveLegPhase(leg.move) !== SCENARIO_PHASE.QUEUED);
  const isCancelled = driveLegs.some((leg) => leg.move.cancel_status != null);
  const lastLegStatus = driveLegs[driveLegs.length - 1]?.move.status ?? null;
  const isPastCancelPoint = lastLegStatus === RAW_STATUS.DELIVERY_ARRIVED || lastLegStatus === RAW_STATUS.DELIVERY_SUCCESSFUL;
  const items = [];
  const add2 = (key, label, extra = {}) => {
    if (!config.actions[key]) return;
    items.push({ key, label, icon: ACTION_ICONS[key], disabled: false, ...extra });
  };
  add2("createDuplicate", "Create Duplicate");
  add2("createInverse", "Create Inverse");
  add2("changePromiseTime", `Change ${config.promise.label}`, {
    dividerBefore: items.length > 0,
    disabled: hasStarted
  });
  add2("cancelMove", "Cancel Order", {
    dividerBefore: items.length > 0,
    disabled: isCancelled || isPastCancelPoint,
    isDanger: true
  });
  return items;
};
var SLOT_META = {
  arrivalEta: {
    estimateLabel: "Arrival ETA",
    actualLabel: "Arrived",
    estimateMeaning: "When the driver should reach the guest",
    actualMeaning: "When the driver arrived at the guest",
    pendingMeaning: "Estimates once the leg starts"
  },
  started: {
    estimateLabel: "Started",
    actualLabel: "Started",
    estimateMeaning: "Fills when the order's first move begins",
    actualMeaning: "When the order's first move began",
    pendingMeaning: "Fills when the order's first move begins"
  },
  completionEta: {
    estimateLabel: "Completion ETA",
    actualLabel: "Completed",
    estimateMeaning: "When the order should complete",
    actualMeaning: "When the order was completed",
    pendingMeaning: "Estimates once the order starts"
  }
};
var buildTimeSlot = ({
  slot,
  legs,
  config,
  timeZone,
  promiseMs: promiseMs2,
  guestArrivalMs,
  completionMs,
  completedActualMs,
  canShowEtaEstimates
}) => {
  const driveLegs = resolveDriveLegs(legs);
  const laneTimezone = driveLegs[0]?.move.lane?.pickup?.timezone ?? null;
  if (slot === "promise") {
    return {
      key: slot,
      label: config.promise.label,
      value: formatClockTime(promiseMs2, timeZone),
      isEstimate: false,
      hasValue: promiseMs2 !== null,
      tooltipInput: {
        label: config.promise.label,
        targetMs: promiseMs2,
        laneTimezone,
        meaning: resolvePromiseMeaning(config)
      }
    };
  }
  const meta = SLOT_META[slot];
  const actualMs = (() => {
    if (slot === "started") {
      const started = driveLegs[0]?.move.pickup_started;
      return started ? Date.parse(started) : null;
    }
    if (slot === "completionEta") {
      return completedActualMs;
    }
    for (const leg of driveLegs) {
      if (!isGuestFacingLeg(leg)) continue;
      const value = leg.move.consumer_at_pickup === 1 ? leg.move.pickup_arrived : leg.move.delivery_arrived;
      if (value) return Date.parse(value);
    }
    return null;
  })();
  if (actualMs !== null) {
    return {
      key: slot,
      label: meta.actualLabel,
      value: formatClockTime(actualMs, timeZone),
      isEstimate: false,
      hasValue: true,
      tooltipInput: { label: meta.actualLabel, targetMs: actualMs, laneTimezone, meaning: meta.actualMeaning }
    };
  }
  const estimateMs = canShowEtaEstimates ? slot === "completionEta" ? completionMs : slot === "arrivalEta" ? guestArrivalMs : null : null;
  const hasEstimate = estimateMs !== null;
  return {
    key: slot,
    label: meta.estimateLabel,
    value: hasEstimate ? `~${formatClockTime(estimateMs, timeZone)}` : "N/A",
    isEstimate: hasEstimate,
    hasValue: hasEstimate,
    tooltipInput: {
      label: meta.estimateLabel,
      targetMs: estimateMs,
      laneTimezone,
      meaning: hasEstimate ? meta.estimateMeaning : meta.pendingMeaning
    }
  };
};
var resolveOrderCreatedBy = (move) => move?.createdBy || move?.eventlogs?.[0]?.user || null;
var buildDetailRows = ({
  legs,
  config,
  platform
}) => {
  const driveLegs = resolveDriveLegs(legs);
  const rows = [];
  const guestLeg = driveLegs.find((leg) => !!leg.move.consumer_name);
  if (config.details.showGuestInfo && guestLeg?.move.consumer_name) {
    const phone = platform.formatPhoneNumber(guestLeg.move.consumer_phone);
    rows.push({
      key: "guest",
      label: "Guest Info",
      value: phone ? `${guestLeg.move.consumer_name} \xB7 ${phone}` : guestLeg.move.consumer_name,
      isAccented: true
    });
  }
  if (driveLegs[0]?.move.customer?.name) {
    rows.push({ key: "market", label: "Market", value: driveLegs[0].move.customer.name });
  }
  const createdBy = resolveOrderCreatedBy(driveLegs[0]?.move);
  if (createdBy) {
    rows.push({ key: "createdBy", label: "Created By", value: createdBy });
  }
  if (driveLegs[0]?.move.workflowset?.name) {
    rows.push({ key: "workflow", label: "Workflow", value: driveLegs[0].move.workflowset.name });
  }
  return rows;
};
var DETAIL_ROW_PREFERENCE_KEYS = {
  market: "market",
  createdBy: "createdBy",
  workflow: "workflow"
};
var resolveVisibleDetailRows = ({
  details,
  displayPreferences
}) => details.filter((row2) => {
  const flag = DETAIL_ROW_PREFERENCE_KEYS[row2.key];
  return flag ? displayPreferences[flag] : true;
});
var deriveScenarioDistanceMiles = (legs) => {
  const contributing = resolveDriveLegs(legs).filter((leg) => resolveMoveTypeIdentity(leg.move) !== "loaner");
  const distances = contributing.flatMap(
    (leg) => leg.move.lane?.distance_miles != null ? [leg.move.lane.distance_miles] : []
  );
  if (!distances.length) return null;
  return distances.reduce((total, miles) => total + miles, 0);
};
var buildScenarioView = ({
  entry,
  rooftop,
  nowMs,
  isHydrated,
  unreadMoveIds,
  referenceLabel: configuredReferenceLabel,
  platform
}) => {
  const { order } = entry;
  const legs = order.legs;
  const driveLegs = resolveDriveLegs(legs);
  const firstDrive = driveLegs[0];
  const deliveryModel = resolveOrderDeliveryModel({
    rooftopOperationalModel: rooftop.operationalModel,
    firstDriveLegIsLite: firstDrive?.move.lite ?? null
  });
  const config = resolveScenarioConfig({ scenarioKey: order.scenarioKey, deliveryModel });
  if (!config) return null;
  const rideLifecycleEnabled = isRideLifecycleEnabled(config, firstDrive?.move);
  const visibleLegs = rideLifecycleEnabled ? [...legs].sort((a, b) => a.cardinality - b.cardinality) : driveLegs;
  const timeZone = firstDrive?.move.lane?.pickup?.timezone ?? rooftop.timezone ?? "UTC";
  const status = deriveScenarioStatus({ legs, config, rideLifecycleEnabled });
  const cancelState = resolveOrderCancelState(driveLegs);
  const promiseMs2 = resolveOrderPromiseMs(legs);
  const guestArrivalMs = config.isGuestFacing ? resolveGuestArrivalMs(legs) : null;
  const completionMs = resolveCompletionEtaMs({ legs, rideLifecycleEnabled });
  const lastDrive = driveLegs[driveLegs.length - 1];
  const completedActualMs = lastDrive && resolveLegPhase(lastDrive.move) === SCENARIO_PHASE.DONE && !isTrailingRideStillOut({ legs, rideLifecycleEnabled }) ? resolveOrderCompletedMs({ legs, rideLifecycleEnabled }) : null;
  const canShowEtaEstimates = hasOrderStarted(legs) && !isApproachingFirstPickup(legs);
  const guestLeg = driveLegs.find((leg) => !!leg.move.consumer_name);
  const guestFacingLegs = driveLegs.filter(isGuestFacingLeg);
  const chatLeg = guestFacingLegs.find((leg) => !!unreadMoveIds?.has(leg.move.id)) ?? guestFacingLegs.find((leg) => leg.move.consumer_type === "customer") ?? guestFacingLegs[0] ?? driveLegs.find((leg) => !!leg.move.consumer_name && !!leg.move.consumer_phone);
  const referenceLabel = resolveReferenceLabel(configuredReferenceLabel);
  const assignment = resolveDriverAssignment({ legs, offers: order.offers ?? [] });
  const slotArgs = {
    legs,
    config,
    timeZone,
    promiseMs: promiseMs2,
    guestArrivalMs,
    completionMs,
    completedActualMs,
    canShowEtaEstimates
  };
  return {
    scenarioId: order.scenarioId,
    scenarioKey: order.scenarioKey,
    deliveryModel,
    display: {
      rendererKey: config.rendererKey,
      showIdentifierChips: config.legs.showIdentifierChips,
      showVehiclePhotos: config.legs.showVehiclePhotos,
      referenceLabel
    },
    timezone: timeZone,
    anchorMs: entry.anchorMs,
    identity: {
      displayName: config.displayName,
      driveMoveIds: driveLegs.map((leg) => leg.move.id)
    },
    status: {
      phase: status.phase,
      badge: status.badge,
      trackerLine: status.trackerLine,
      isLive: status.isLive,
      metric: resolveOrderMetric(status)
    },
    milestone: resolveScenarioMilestone({ legs, config, nowMs, timeZone, rideLifecycleEnabled }),
    // A provably broken chain gets the tracker's error state, not a wrong stop model.
    tracker: hasDisconnectedTrackerStops(legs) ? { bars: [], isChainBroken: true } : { bars: buildTrackerBars({ legs, deliveryModel, rideLifecycleEnabled }), isChainBroken: false },
    legs: visibleLegs.map((leg) => buildLegView({ leg, config, referenceLabel, platform })),
    confirmation: resolveOrderConfirmation({ order, driveLegs, phase: status.phase }),
    guest: config.details.showGuestInfo && guestLeg?.move.consumer_name ? { name: guestLeg.move.consumer_name, phone: platform.formatPhoneNumber(guestLeg.move.consumer_phone) } : null,
    route: resolveRouteStops({
      legs: visibleLegs,
      config,
      rooftopLocationId: rooftop.locationId ?? null
    }),
    times: {
      leftColumn: config.times.leftColumn.map((slot) => buildTimeSlot({ slot, ...slotArgs })),
      rightColumn: config.times.rightColumn.map((slot) => buildTimeSlot({ slot, ...slotArgs }))
    },
    details: buildDetailRows({ legs, config, platform }),
    distance: deriveScenarioDistanceMiles(legs),
    driver: {
      assignment,
      // The order's model decides eligibility, not the rooftop's. The pill waits for
      // hydration because `lite` and leg statuses arrive with the page query, and an
      // unhydrated order would flash an inert locked pill.
      showPill: config.actions.assignDriver && isHydrated,
      detail: resolveAssignmentDetail(assignment, nowMs)
    },
    actions: {
      menuItems: buildScenarioActionMenuItems({ config, legs }),
      openTargets: driveLegs.map((leg) => leg.move.id),
      canChat: config.actions.chat && !!chatLeg,
      hasUnreadMessages: !!unreadMoveIds && driveLegs.some((leg) => unreadMoveIds.has(leg.move.id)),
      chatMoveId: chatLeg?.move.id ?? null
    },
    state: {
      isHydrated,
      isSettled: status.phase === SCENARIO_PHASE.DONE || !!cancelState,
      isCancelDeclared: cancelState !== null,
      cancelReason: driveLegs.map((leg) => leg.move.cancel_reason).find(Boolean) ?? null,
      isInDanger: isOrderInDanger({ legs, config, nowMs, rideLifecycleEnabled }),
      hasCrossDayLegs: entry.hasCrossDayLegs
    }
  };
};

// src/products/valet/resolveValetOrders.ts
var MENU_ICONS = {
  createDuplicate: BiAddToQueue,
  createInverse: BiTransfer,
  changePromiseTime: BiTime2,
  cancelMove: BiBlock
};
var VALET_METRIC_TILES = [
  { key: SCENARIO_METRIC.ALL, label: "All Orders", inAll: true },
  { key: SCENARIO_METRIC.UPCOMING, label: "Upcoming Orders", inAll: true },
  { key: SCENARIO_METRIC.ACTIVE, label: "Active Orders", inAll: true },
  { key: SCENARIO_METRIC.COMPLETED, label: "Completed Orders", inAll: true },
  { key: SCENARIO_METRIC.CANCELLED, label: "Canceled Orders", inAll: true },
  // Unconfirmed orders also count in Upcoming; this shortcut finds them by flag. An attention tile with nothing to attend to is noise, so it earns its slot.
  { key: SCENARIO_METRIC.UNCONFIRMED, label: "Awaiting Confirmation", inAll: true, highlight: true, hideWhenZero: true, flag: "isUnconfirmed" },
  // Unread is a self-managed concern: full-service dealers have no guest chat.
  { key: SCENARIO_METRIC.UNREAD, label: "Unread Messages", inAll: true, saasOnly: true, flag: "hasUnreadMessages" }
];
var VALET_SORT_OPTIONS = [
  { key: "promise", label: "Promise Time", anchor: true },
  // A fault has no rank and sits past the end of the lifecycle, so a descending status sort leads with the faults.
  { key: "status", label: "Status", missingValue: "highest" },
  { key: "order", label: "Move ID" },
  { key: "guest", label: "Guest Name" },
  { key: "market", label: "Market Name", adminOnly: true },
  { key: "createdBy", label: "Created By" },
  { key: "workflow", label: "Workflow Name", adminOnly: true },
  { key: "distance", label: "Distance", viewModes: ["table"] }
];
var groupOptions = (isSelfManaged) => [
  { key: "none", label: "None", availability: "always" },
  { key: "guest", label: "Guest Name", availability: "anyValue", emptyTitle: "No Guest" },
  { key: "market", label: "Market", availability: "multipleCustomers", adminOnly: true, emptyTitle: "No Market" },
  ...isSelfManaged ? [{ key: "driver", label: "Driver", availability: "always", emptyTitle: "No Driver" }] : [],
  { key: "createdBy", label: "Created By", availability: "always", emptyTitle: "Unknown" },
  { key: "hangtags", label: "Hangtags", availability: "anyValue", emptyTitle: "Everything Else" },
  ...isSelfManaged ? [{ key: "unread", label: "Unread Messages", availability: "always", emptyTitle: "Everything Else" }] : []
];
var VALET_DATE_RANGE_PRESETS = [
  { key: "tomorrow", label: "Tomorrow", group: 0 },
  { key: "today", label: "Today", group: 0 },
  { key: "yesterday", label: "Yesterday", group: 0 },
  { key: "nextweek", label: "Next Week", group: 1 },
  { key: "thisweek", label: "This Week", group: 1 },
  { key: "lastweek", label: "Last Week", group: 1 },
  { key: "nextmonth", label: "Next Month", group: 2 },
  { key: "thismonth", label: "This Month", group: 2 },
  { key: "lastmonth", label: "Last Month", group: 2 },
  { key: "custom", label: "Custom", group: 3 }
];
var displayPreferenceDefinitions = (referenceLabel) => {
  const shared = (surface) => {
    const info = surface === "card" ? "Info" : "Columns";
    return [
      { surface, section: "IDs", key: "moveId", label: "HopDrive move ID", defaultValue: true },
      { surface, section: "IDs", key: "reference", label: referenceLabel, defaultValue: true },
      { surface, section: "IDs", key: "vin", label: "VIN", defaultValue: true },
      { surface, section: "IDs", key: "stock", label: "Stock number", defaultValue: false },
      { surface, section: info, key: "market", label: "Market", defaultValue: false, adminOnly: true },
      { surface, section: info, key: "createdBy", label: "Created by", defaultValue: false },
      { surface, section: info, key: "workflow", label: "Workflow", defaultValue: false, adminOnly: true }
    ];
  };
  return [...shared("card"), ...shared("row"), { surface: "row", section: "Columns", key: "distance", label: "Distance", defaultValue: false }];
};
var VALET_SEARCH_FIELDS = [
  "consumer_name",
  "consumer_phone",
  "customer.name",
  "lane.pickup.name",
  "lane.pickup.address",
  "lane.delivery.name",
  "lane.delivery.address",
  "lane.description",
  "reference_num",
  "vehicle_make",
  "vehicle_model",
  "vehicle_stock",
  "vehicle_vin",
  "vehicle_year",
  "id"
];
var resolvePromiseSlot = (view) => [...view.times.leftColumn, ...view.times.rightColumn].find((slot) => slot.key === "promise") ?? null;
var detailValue = (view, key) => view.details.find((row2) => row2.key === key)?.value ?? null;
var columns = (isSelfManaged) => {
  const all = [
    {
      name: "market",
      label: "Market",
      minWidth: 130,
      sortable: true,
      sortValue: (view) => detailValue(view, "market"),
      cell: "text",
      text: (view) => ({ value: detailValue(view, "market") }),
      mobile: "pair",
      visibleWhen: { preference: "market" }
    },
    {
      name: "status",
      label: "Status",
      tooltip: "Where the order is in its lifecycle",
      minWidth: 110,
      sortable: true,
      // Lifecycle order, not the badge word.
      sortValue: (view) => view.status.phase,
      cell: "status",
      cellProps: (view) => ({ statusBar: toStatusBarGroup(view), tracker: toTrackerGroup(view) }),
      mobile: "pair",
      // The badge hangs from the pair's right edge; the tracker keeps the full width under it.
      mobileValueSx: { "& > div": { alignItems: "flex-end" }, "& > div > :last-child": { alignSelf: "stretch" } }
    },
    {
      name: "cancelReason",
      label: "Cancel Reason",
      minWidth: 170,
      cell: "text",
      text: (view) => ({ value: view.state.cancelReason, isBold: true }),
      mobile: "pair",
      visibleWhen: { metric: [SCENARIO_METRIC.CANCELLED] }
    },
    {
      name: "order",
      label: "Order",
      tooltip: "The order and the moves it contains",
      minWidth: 150,
      sortable: true,
      // The first drive leg's move id, the number a dealer reads off the order.
      sortValue: (view) => view.identity.driveMoveIds[0] ?? null,
      cell: "identity",
      // The first leg's own photo, never the order's open target, which is a navigation concept.
      cellProps: (view) => ({
        displayName: view.identity.displayName,
        ids: view.identity.driveMoveIds,
        tooltip: buildOrderIdentityTooltipFromView(view),
        photo: view.legs[0]?.photo ?? PLACEHOLDER_PHOTO,
        photoItemId: view.legs[0]?.moveId ?? null
      }),
      mobile: "pair",
      mobileValueSx: { "& > div": { flexDirection: "row-reverse" }, "& > div > div": { alignItems: "flex-end" } }
    },
    {
      name: "guest",
      label: "Guest",
      minWidth: 140,
      sortable: true,
      sortValue: (view) => view.guest?.name ?? null,
      cell: "guest",
      cellProps: (view) => view.guest,
      mobile: "pair"
    },
    {
      name: "route",
      label: "Route / Address",
      tooltip: "Every stop in the order, start to finish",
      minWidth: 200,
      cell: "route",
      cellProps: (view) => view.route.length ? { stops: view.route.map((stop) => ({ key: String(stop.locationId), name: stop.name })), tooltip: buildRouteChainTooltip(view.route) } : null,
      mobile: "pair",
      // Each stop line hangs from the pair's right edge.
      mobileValueSx: { "& > div": { alignItems: "flex-end" } }
    },
    {
      name: "promise",
      label: "Appt / Ready By",
      tooltip: "The appointment an order promises, or the time its vehicle is ready",
      minWidth: 150,
      sortable: true,
      sortValue: (view) => resolvePromiseSlot(view)?.tooltipInput.targetMs ?? null,
      cell: "promise",
      // Dated, unlike the card's grid, because a row is read down a list spanning the whole window.
      cellProps: (view) => {
        const slot = resolvePromiseSlot(view);
        if (!slot || slot.tooltipInput.targetMs === null) return null;
        return { value: formatDatedClockTime(slot.tooltipInput.targetMs, view.timezone), tooltip: buildTimeTooltip(slot.tooltipInput) };
      },
      mobile: "pair"
    },
    // Milestone, Completed, and Cancelled share one slot; exactly one occupies it.
    {
      name: "milestone",
      label: "Milestone",
      tooltip: "What happens next, and when",
      minWidth: 150,
      cell: "milestone",
      // A promise-kind milestone is blank here, because the promise has its own column.
      cellProps: (view) => view.milestone.kind === "promise" ? null : toMilestoneGroup(view),
      mobile: "pair",
      mobileValueSx: { "& > div": { alignItems: "flex-end", textAlign: "right" } },
      visibleWhen: { notMetric: [SCENARIO_METRIC.COMPLETED, SCENARIO_METRIC.CANCELLED] }
    },
    {
      name: "completed",
      label: "Completed",
      minWidth: 150,
      cell: "text",
      text: (view) => ({ value: view.milestone.value, isBold: true }),
      mobile: "pair",
      visibleWhen: { metric: [SCENARIO_METRIC.COMPLETED] }
    },
    {
      name: "cancelled",
      label: "Canceled",
      minWidth: 150,
      cell: "text",
      text: (view) => ({ value: view.milestone.value, isBold: true }),
      mobile: "pair",
      visibleWhen: { metric: [SCENARIO_METRIC.CANCELLED] }
    },
    // Not a pair: the driver is the actions band's left zone, mirroring the card's bar.
    { name: "driver", label: "Driver", minWidth: 150, cell: "driver", cellProps: (view) => toDriverGroup(view), mobile: "bar" },
    {
      name: "createdBy",
      label: "Created By",
      minWidth: 150,
      sortable: true,
      sortValue: (view) => detailValue(view, "createdBy"),
      cell: "text",
      text: (view) => ({ value: detailValue(view, "createdBy") }),
      mobile: "pair",
      visibleWhen: { preference: "createdBy" }
    },
    {
      name: "workflow",
      label: "Workflow",
      minWidth: 150,
      sortable: true,
      sortValue: (view) => detailValue(view, "workflow"),
      cell: "text",
      text: (view) => ({ value: detailValue(view, "workflow") }),
      mobile: "pair",
      visibleWhen: { preference: "workflow" }
    },
    {
      name: "distance",
      label: "Distance",
      minWidth: 90,
      sortable: true,
      // Compare by the derived miles, never the rendered `12.4 mi` text.
      sortValue: (view) => view.distance,
      cell: "text",
      text: (view) => ({ value: formatDistanceMiles(view.distance) }),
      mobile: "pair",
      visibleWhen: { preference: "distance" }
    },
    { name: "actions", label: "Actions", align: "right", pinned: "right", mobile: "bar", cell: "actions" }
  ];
  return isSelfManaged ? all : all.filter((column) => column.name !== "driver");
};
var buildValetActionDescriptors = (view) => {
  const descriptors = [];
  const openTargets = view.actions.openTargets;
  if (openTargets.length === 1) {
    descriptors.push({ key: "openMove", label: "View move details", icon: BiLinkExternal, placement: "bar", targetId: openTargets[0] });
  } else if (openTargets.length > 1) {
    descriptors.push({
      key: "openMove",
      label: "View move details",
      icon: BiLinkExternal,
      placement: "bar",
      children: openTargets.map((moveId) => ({ key: "openMove", label: `Open Move #${moveId}`, icon: BiLinkExternal, placement: "menu", targetId: moveId }))
    });
  }
  if (view.actions.canChat) {
    descriptors.push({ key: "openChat", label: "Chat with guest", icon: BiChat, placement: "bar", hasBadge: view.actions.hasUnreadMessages });
  }
  for (const item of view.actions.menuItems) {
    descriptors.push({
      key: item.key,
      label: item.label,
      icon: MENU_ICONS[item.key],
      placement: "menu",
      disabled: item.disabled,
      danger: item.isDanger,
      dividerBefore: item.dividerBefore
    });
  }
  return descriptors;
};
var toSharedPreferences = (preferences) => ({
  moveId: preferences.moveId ?? false,
  reference: preferences.reference ?? false,
  vin: preferences.vin ?? false,
  stock: preferences.stock ?? false,
  market: preferences.market ?? false,
  createdBy: preferences.createdBy ?? false,
  workflow: preferences.workflow ?? false
});
var toTrackerBars = (view) => {
  const isMultiLeg = view.identity.driveMoveIds.length > 1;
  return view.tracker.bars.map((bar, index) => ({
    key: bar.key,
    isReached: bar.isReached,
    isDrivingToward: bar.isDrivingToward,
    slipFraction: resolveTrackerSlipFraction(bar),
    tooltip: buildTrackerBarTooltip({ bars: view.tracker.bars, index, isMultiLeg })
  }));
};
var toStatusBarGroup = (view) => ({
  status: resolveScenarioStatusToken(view.status.phase),
  badge: view.status.badge,
  isLive: view.status.isLive,
  needsAttention: needsScenarioAttention(view.status.phase)
});
var toTrackerGroup = (view) => ({
  bars: toTrackerBars(view),
  status: resolveScenarioStatusToken(view.status.phase),
  statusLine: view.status.trackerLine,
  isLive: view.status.isLive,
  isChainBroken: view.tracker.isChainBroken
});
var toMilestoneGroup = (view) => ({ value: view.milestone.value, subtext: view.milestone.subtext, tooltip: buildTimeTooltip(view.milestone.tooltipInput) });
var toDriverGroup = (view) => {
  const { assignment, detail } = view.driver;
  const driverName = assignment.driver?.display_name ?? null;
  const audit = {
    offeredAt: assignment.offeredAt,
    offeredBy: assignment.offeredBy,
    acceptedAt: assignment.acceptedAt,
    acceptedBy: assignment.acceptedBy
  };
  return {
    showPill: view.driver.showPill,
    driverName,
    avatarUrl: assignment.driver?.avatar_url ?? null,
    state: assignment.state,
    isSettled: view.state.isSettled,
    detail,
    // No driver on the order is two different situations: `unassigned` invites a click, while
    // `locked` with no driver is a dead order whose tooltip must say so rather than invite one.
    tooltip: driverName ? buildDriverPillTooltip({ state: assignment.state, driverName, ...audit }) : buildDriverPillTooltip({ state: assignment.state === "locked" ? "locked" : "unassigned" })
  };
};
var toTimeSlot = (slot) => ({
  key: slot.key,
  label: slot.label,
  value: slot.value,
  treatment: !slot.hasValue ? "pending" : slot.isEstimate ? "estimate" : "actual",
  tooltip: buildTimeTooltip(slot.tooltipInput)
});
var toValetLegItemProps = (leg, view, preferences) => {
  const isRide = leg.moveType === "ride";
  const hasLaneMeta = leg.lane.distanceMiles != null && leg.lane.durationSec != null;
  return {
    moveId: leg.moveId,
    isRide,
    vehicleLine: leg.vehicleLine,
    typeIdentity: leg.typeIdentity,
    typeTooltip: buildMoveTypeTooltip(leg.typeIdentity, leg.loanerDirection),
    route: { pickupName: leg.routeEnds.pickupName, deliveryName: leg.routeEnds.deliveryName },
    laneTooltip: buildLaneTooltip({ pickup: leg.lane.pickup, delivery: leg.lane.delivery, distanceMiles: leg.lane.distanceMiles, durationSec: leg.lane.durationSec }),
    // The lane's total, inspections included.
    laneSummary: hasLaneMeta ? `${formatDistanceMiles(leg.lane.distanceMiles)} \xB7 ${Math.round((leg.lane.durationSec ?? 0) / 60)} min` : null,
    // A ride is informational, not managed, so it has no identifiers or photo.
    photo: !isRide && view.display.showVehiclePhotos ? leg.photo : null,
    identifierChips: !isRide && view.display.showIdentifierChips ? resolveVisibleLegIdentifiers({ identifiers: leg.identifiers, displayPreferences: preferences }) : [],
    hangtag: !isRide && leg.hangtag ? buildPortalHangtag(leg.hangtag) : null
  };
};
var toValetCardProps = (view, displayPreferences) => {
  const preferences = toSharedPreferences(displayPreferences);
  return {
    rendererKey: view.display.rendererKey,
    statusBar: toStatusBarGroup(view),
    tracker: toTrackerGroup(view),
    header: {
      displayName: view.identity.displayName,
      ids: view.identity.driveMoveIds,
      tooltip: buildOrderIdentityTooltipFromView(view),
      milestone: toMilestoneGroup(view)
    },
    items: view.legs.map((leg) => ({ kind: "leg", key: `leg:${leg.moveId}`, props: toValetLegItemProps(leg, view, preferences) })),
    times: { leftColumn: view.times.leftColumn.map(toTimeSlot), rightColumn: view.times.rightColumn.map(toTimeSlot) },
    // The view's details are not gated by preference because the table reads them too.
    details: { rows: resolveVisibleDetailRows({ details: view.details, displayPreferences: preferences }) },
    driver: toDriverGroup(view),
    actionBar: buildValetActionDescriptors(view),
    state: {
      isHydrated: view.state.isHydrated,
      isSettled: view.state.isSettled,
      isCancelDeclared: view.state.isCancelDeclared,
      cancelReason: view.state.cancelReason,
      isInDanger: view.state.isInDanger
    }
  };
};
var toRooftopInput = (context) => ({
  id: context.rooftop.id,
  name: context.rooftop.name ?? void 0,
  operationalModel: context.rooftop.operationalModel,
  timezone: context.rooftop.timezone ?? void 0,
  locationId: context.rooftop.locationId ?? void 0,
  effective_config: context.rooftop.effectiveConfig ?? void 0
});
var resolveValetOrders = (context) => {
  const rooftop = toRooftopInput(context);
  const isSelfManaged = resolveRooftopDeliveryModel(rooftop) === "saas";
  const referenceLabel = resolveReferenceLabel(context.referenceNumLabel);
  return {
    identity: { key: "moves", label: "Valet", icon: PiCarFill },
    operationalModel: isSelfManaged ? "self-managed" : "hopdrive-managed",
    copy: {
      unit: { singular: "order", plural: "orders" },
      referenceLabel,
      searchPlaceholder: "Search orders\u2026",
      capNotice: "This range holds more orders than the dashboard shows at once. Narrow the date range to see them all.",
      empty: {
        noOrders: { title: "No orders in this range", description: "Widen the date range to see orders from other days." },
        filtered: { title: "No orders match your filters", description: "Try adjusting your search or filter criteria." }
      }
    },
    rendererKeys: ["concierge", "conciergeLoaner", "operational"],
    itemKinds: ["leg"],
    toView(model, { platform, nowMs }) {
      const source = model.source;
      if (!isResolvedOrderEntry(source.entry)) return null;
      try {
        return buildScenarioView({
          entry: source.entry,
          rooftop,
          nowMs,
          isHydrated: model.isHydrated,
          // The viewer's unread drive moves on this order, so the chat opens the leg holding the thread.
          unreadMoveIds: source.unreadMoveIds.length > 0 ? new Set(source.unreadMoveIds) : void 0,
          referenceLabel: context.referenceNumLabel,
          platform
        });
      } catch {
        return null;
      }
    },
    toCardProps: toValetCardProps,
    columns: columns(isSelfManaged),
    controlsDefaults: {
      enabled: [
        "search",
        "dateRange",
        "metricTiles",
        "sort",
        "groupBy",
        "viewMode",
        "displayPreferences",
        "activeChips",
        "createdBy",
        ...context.isAdminViewer ? ["market"] : [],
        // Self-managed only: HopDrive-managed work never enters the offer lifecycle, so every one of
        // its orders reads as the same bucket and the axis says nothing about that board.
        ...isSelfManaged ? ["offerStatus"] : []
      ]
    },
    metricTiles: VALET_METRIC_TILES,
    sortOptions: VALET_SORT_OPTIONS,
    defaultSort: { sortBy: "promise", sortOrder: "asc" },
    groupOptions: groupOptions(isSelfManaged),
    dateRangePresets: VALET_DATE_RANGE_PRESETS,
    displayPreferences: displayPreferenceDefinitions(referenceLabel),
    defaultPageSize: 25,
    searchFields: VALET_SEARCH_FIELDS
  };
};

// src/products/valet/adapters/scenarioGraphAdapter.ts
var toMoveType = (moveType) => moveType === "ride" ? "ride" : "drive";
var toScenarioMoveInput = (move) => ({
  id: move.id,
  move_type: toMoveType(move.move_type),
  active: move.active,
  status: move.status ?? null,
  cancel_status: move.cancel_status ?? null,
  // Bucketing runs at index depth, so a gate the index cannot resolve is a tile count gone wrong.
  lite: move.lite,
  effective_config: move.effective_config ?? null,
  ready_by: move.ready_by ?? null,
  target_pickup_time: move.target_pickup_time ?? null,
  // Sorting and grouping run over the whole window before the page query hydrates
  // anything, so the driver name and hangtag presence must be here.
  consumer_name: move.consumer_name ?? null,
  consumer_type: move.consumer_type ?? null,
  createdBy: move.createdBy ?? null,
  driver_name: move.driver_name ?? null,
  // Carried at index depth for the Offer Status classification: `resolveDriverAssignment`
  // reads the id to tell an assigned/committed leg from an unassigned one. `driver_name`
  // above serves the sort; this serves the derivation.
  driver_id: toNumberOrNull(move.driver_id),
  hangtags: (move.hangtags ?? []).map((hangtag) => ({
    id: hangtag.id,
    hash_id: null,
    rear_code: null,
    status: null,
    type: null,
    updated_at: null,
    workflow_data: null
  })),
  workflowset: move.workflowset ? { name: move.workflowset.name ?? null } : null,
  lane: move.lane ? { id: move.lane.id, distance_miles: toNumberOrNull(move.lane.distance_miles) } : null,
  customer: move.customer ? { id: move.customer.id, name: move.customer.name ?? null } : null
});
var toScenarioLegInput = (member) => ({
  id: member.id,
  cardinality: member.cardinality,
  move: toScenarioMoveInput(member.move)
});
var toIndexOfferInputs = (scenario) => {
  for (const link of scenario.bundletoscenarios ?? []) {
    const offers = link.bundle?.offers ?? [];
    if (offers.length) {
      return offers.map((offer) => ({
        id: offer.id,
        status: offer.status ?? null,
        driver: offer.driver ? { id: offer.driver.id, display_name: null, avatar_url: null } : null
      }));
    }
  }
  return [];
};
var toScenarioOrderInput = (link) => ({
  scenarioId: link.scenario.id,
  scenarioKey: link.scenario.scenario_key,
  config: null,
  legs: link.scenario.moves.map(toScenarioLegInput),
  offers: toIndexOfferInputs(link.scenario)
});
var resolveCurrentLink = (move) => move.scenariotomoves[0] ?? null;
var toScenarioIndexRow = (move) => {
  const link = resolveCurrentLink(move);
  return {
    moveId: move.id,
    order: link ? toScenarioOrderInput(link) : null
  };
};
var indexRowCache = /* @__PURE__ */ new WeakMap();
var adaptScenarioOrderIndex = (data) => (data?.moves ?? []).map((move) => {
  const cached = indexRowCache.get(move);
  if (cached) return cached;
  const row2 = toScenarioIndexRow(move);
  indexRowCache.set(move, row2);
  return row2;
});
var toNumberOrNull = (value) => {
  if (value === null || value === void 0) return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};
var toLocationInput = (location) => location ? {
  id: location.id,
  name: location.name ?? null,
  address: location.address ?? null,
  latitude: toNumberOrNull(location.latitude),
  longitude: toNumberOrNull(location.longitude),
  timezone: location.timezone ?? null,
  type: location.type ?? null,
  customer_id: toNumberOrNull(location.customer_id)
} : null;
var toLaneInput = (lane) => lane ? {
  id: lane.id,
  distance_miles: toNumberOrNull(lane.distance_miles),
  duration_sec: toNumberOrNull(lane.duration_sec),
  pickup_inspection_sec: toNumberOrNull(lane.pickup_inspection_sec),
  delivery_inspection_sec: toNumberOrNull(lane.delivery_inspection_sec),
  return_ride_wait_sec: toNumberOrNull(lane.return_ride_wait_sec),
  status: lane.status ?? null,
  pickup: toLocationInput(lane.pickup),
  delivery: toLocationInput(lane.delivery)
} : null;
var toDriverInput = (driver) => driver ? {
  id: driver.id,
  display_name: driver.user?.display_name ?? null,
  avatar_url: driver.user?.avatar_url ?? null
} : null;
var toHangtagInput = (hangtag) => ({
  id: hangtag.id,
  hash_id: hangtag.hash_id ?? null,
  rear_code: hangtag.rear_code ?? null,
  status: hangtag.status ?? null,
  type: hangtag.type ?? null,
  updated_at: hangtag.updated_at ?? null,
  workflow_data: hangtag.workflow_data ?? null
});
var toVehiclePhotoInput = (photo) => ({
  id: photo.id,
  name: photo.name ?? null,
  status: photo.status ?? null,
  step_id: photo.step_id ?? null,
  url: photo.url ?? null,
  workflow_id: toNumberOrNull(photo.workflow_id),
  workflow: photo.workflow ? { id: photo.workflow.id, type: photo.workflow.type ?? null } : null,
  createdat: photo.createdat ?? null
});
var toHydratedMoveInput = (move) => ({
  id: move.id,
  move_type: toMoveType(move.move_type),
  active: move.active,
  status: move.status ?? null,
  cancel_status: move.cancel_status ?? null,
  ready_by: move.ready_by ?? null,
  target_pickup_time: move.target_pickup_time ?? null,
  lite: move.lite ?? null,
  reference_num: move.reference_num ?? null,
  cancel_reason: move.cancel_reason ?? null,
  canceled_at: move.canceled_at ?? null,
  pickup_time: move.pickup_time ?? null,
  delivery_time: move.delivery_time ?? null,
  pickup_started: move.pickup_started ?? null,
  pickup_arrived: move.pickup_arrived ?? null,
  pickup_successful: move.pickup_successful ?? null,
  delivery_started: move.delivery_started ?? null,
  delivery_arrived: move.delivery_arrived ?? null,
  delivery_successful: move.delivery_successful ?? null,
  consumer_name: move.consumer_name ?? null,
  consumer_phone: move.consumer_phone ?? null,
  consumer_pickup: move.consumer_pickup ?? null,
  consumer_at_pickup: toNumberOrNull(move.consumer_at_pickup),
  consumer_type: move.consumer_type ?? null,
  vehicle_year: move.vehicle_year ?? null,
  vehicle_make: move.vehicle_make ?? null,
  vehicle_model: move.vehicle_model ?? null,
  vehicle_color: move.vehicle_color ?? null,
  vehicle_stock: move.vehicle_stock ?? null,
  vehicle_vin: move.vehicle_vin ?? null,
  driver_id: toNumberOrNull(move.driver_id),
  driver_name: move.driver_name ?? null,
  driver: toDriverInput(move.driver),
  effective_config: move.effective_config ?? null,
  customer: move.customer ? { id: move.customer.id, name: move.customer.name ?? null, tier: move.customer.tier ?? null } : null,
  createdBy: move.createdBy ?? null,
  eventlogs: (move.eventlogs ?? []).map((log) => ({ id: log.id, user: log.user ?? null })),
  workflowset: move.workflowset ? {
    name: move.workflowset.name ?? null,
    confirmationset: move.workflowset.confirmationset ? { confirmation_flow: move.workflowset.confirmationset.confirmation_flow ?? null } : null,
    pickupWorkflow: move.workflowset.pickupWorkflow ? {
      id: move.workflowset.pickupWorkflow.id ?? null,
      steps: move.workflowset.pickupWorkflow.steps ?? null
    } : null,
    deliveryWorkflow: move.workflowset.deliveryWorkflow ? {
      id: move.workflowset.deliveryWorkflow.id ?? null,
      steps: move.workflowset.deliveryWorkflow.steps ?? null
    } : null
  } : null,
  vehiclephotos: (move.vehiclephotos ?? []).map(toVehiclePhotoInput),
  hangtags: (move.hangtags ?? []).map(toHangtagInput),
  lane: toLaneInput(move.lane)
});
var toOfferInputs = (row2) => {
  for (const link of row2.bundletoscenarios ?? []) {
    const offers = link.bundle?.offers ?? [];
    if (offers.length) {
      return offers.map((offer) => ({
        id: offer.id,
        status: offer.status ?? null,
        driver: toDriverInput(offer.driver),
        offered_at: offer.offered_at ?? null,
        offered_by: offer.offered_by ?? null,
        accepted_at: offer.accepted_at ?? null,
        accepted_by: offer.accepted_by ?? null,
        createdat: offer.createdat ?? null,
        createdby: offer.createdby ?? null,
        updatedat: offer.updatedat ?? null,
        updatedby: offer.updatedby ?? null
      }));
    }
  }
  return [];
};
var toConfirmationRequestInput = (request) => ({
  id: request.id,
  status: request.status ?? null,
  escalated: request.escalated ?? null,
  expiration_time: request.expiration_time ?? null,
  workflow: request.workflow ? { id: request.workflow.id, name: request.workflow.name ?? null, label: request.workflow.label ?? null } : null,
  confirmationset: request.confirmationset ? {
    id: request.confirmationset.id,
    pickup_workflow_id: toNumberOrNull(request.confirmationset.pickup_workflow_id),
    delivery_workflow_id: toNumberOrNull(request.confirmationset.delivery_workflow_id),
    confirmation_flow: request.confirmationset.confirmation_flow ?? null
  } : null
});
var buildOrderInput = (row2) => ({
  scenarioId: row2.id,
  scenarioKey: row2.scenario_key,
  config: row2.config ?? null,
  legs: row2.moves.map((member) => ({
    id: member.id,
    cardinality: member.cardinality,
    move: toHydratedMoveInput(member.move)
  })),
  offers: toOfferInputs(row2),
  confirmationRequests: (row2.confirmationrequests ?? []).map(toConfirmationRequestInput)
});
var orderCache = /* @__PURE__ */ new WeakMap();
var toOrderInput = (row2) => {
  const cached = orderCache.get(row2);
  if (cached) return cached;
  const order = buildOrderInput(row2);
  orderCache.set(row2, order);
  return order;
};
var adaptScenarioOrderPage = (data) => (data?.scenarios ?? []).map(toOrderInput);
var adaptScenarioOrderStandalone = (data) => {
  const row2 = data?.scenarios?.[0];
  return row2 ? toOrderInput(row2) : null;
};

// src/products/valet/feed/scenarioFilters.ts
var SCENARIO_API_CREATOR_OPTION = "API";
var dedupeSortedOptions = (values, selected) => (
  // Selected values stay pickable after the pool drops them, or a window change would
  // strand a filter the picker cannot clear.
  [.../* @__PURE__ */ new Set([...values, ...selected])].sort((a, b) => a.localeCompare(b))
);
var deriveCreatorOptions = (rows, selected) => {
  const creators = rows.flatMap((row2) => {
    if (!row2.createdBy) return [];
    return [row2.createdBy.startsWith("API-") ? SCENARIO_API_CREATOR_OPTION : row2.createdBy];
  });
  return dedupeSortedOptions(creators, selected);
};
var deriveMarketOptions = (rows, selected) => {
  const names = rows.flatMap((row2) => row2.customer?.name ? [row2.customer.name] : []);
  return dedupeSortedOptions(names, selected);
};
var poolSpansMultipleCustomers = (rows) => new Set(rows.flatMap((row2) => row2.customer ? [row2.customer.id] : [])).size > 1;

// src/products/valet/adapters/valetIndexPredicates.ts
var buildScenarioSearchFilters = (search) => search.split(/\s+/).filter(Boolean).map((term) => buildScenarioSearchTermFilter(term));
var buildScenarioSearchTermFilter = (rawTerm) => {
  const term = `%${rawTerm}%`;
  const conditions = [
    { consumer_name: { _ilike: term } },
    { consumer_phone: { _ilike: term } },
    { customer: { name: { _ilike: term } } },
    { lane: { pickup: { name: { _ilike: term } } } },
    { lane: { pickup: { address: { _ilike: term } } } },
    { lane: { delivery: { name: { _ilike: term } } } },
    { lane: { delivery: { address: { _ilike: term } } } },
    { lane: { description: { _ilike: term } } },
    { reference_num: { _ilike: term } },
    { vehicle_make: { _ilike: term } },
    { vehicle_model: { _ilike: term } },
    { vehicle_stock: { _ilike: term } },
    { vehicle_vin: { _ilike: term } },
    { vehicle_year: { _ilike: term } }
  ];
  const asNumber = Number.parseInt(rawTerm, 10);
  if (!Number.isNaN(asNumber) && String(asNumber) === rawTerm) {
    conditions.push({ id: { _eq: asNumber } });
  }
  return { _or: conditions };
};
var buildScenarioCreatorFilters = (usernames) => {
  if (!usernames.length) return [];
  const exact = usernames.filter((username) => username !== SCENARIO_API_CREATOR_OPTION);
  const arms = [];
  if (exact.length) arms.push({ createdBy: { _in: exact } });
  if (exact.length !== usernames.length) arms.push({ createdBy: { _like: "API-%" } });
  return arms.length > 1 ? [{ _or: arms }] : arms;
};
var buildScenarioMarketFilters = (markets) => markets.length ? [{ customer: { name: { _in: markets } } }] : [];

// src/products/valet/feed/scenarioRenderGuards.ts
var indexedWrapperCache = /* @__PURE__ */ new WeakMap();
var sameMoveIds = (a, b) => a.length === b.length && a.every((id, index) => id === b[index]);
var sameAssignment = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.state === b.state && (a.driver?.id ?? null) === (b.driver?.id ?? null) && (a.offeredAt ?? null) === (b.offeredAt ?? null) && (a.offeredBy ?? null) === (b.offeredBy ?? null) && (a.acceptedAt ?? null) === (b.acceptedAt ?? null) && (a.acceptedBy ?? null) === (b.acceptedBy ?? null);
};
var reuseScenarioIndexedEntry = (next) => {
  const cached = indexedWrapperCache.get(next.entry);
  if (cached && cached.phase === next.phase && cached.metric === next.metric && cached.hasUnreadMessages === next.hasUnreadMessages && sameMoveIds(cached.unreadMoveIds, next.unreadMoveIds) && sameAssignment(cached.assignment, next.assignment)) {
    return cached;
  }
  indexedWrapperCache.set(next.entry, next);
  return next;
};
var derivationFaultCache = /* @__PURE__ */ new WeakMap();
var faultResolvedOrderEntry = (entry) => {
  const cached = derivationFaultCache.get(entry);
  if (cached) return cached;
  const fault = {
    kind: "error",
    key: entry.key,
    anchorMs: entry.anchorMs,
    reason: "derivationFailed",
    moveIds: (entry.order?.legs ?? []).map((leg) => leg?.move?.id).filter((id) => typeof id === "number")
  };
  derivationFaultCache.set(entry, fault);
  return fault;
};

// src/products/valet/feed/scenarioIndexDerivation.ts
var deriveScenarioIndex = (grouped, { rooftopOperationalModel, unreadMoveIds, onError }) => {
  const entries = grouped.map((entry) => {
    if (!isResolvedOrderEntry(entry)) {
      return reuseScenarioIndexedEntry({ entry, phase: null, metric: null, hasUnreadMessages: false, unreadMoveIds: [], assignment: null });
    }
    try {
      const driveLegs = entry.order.legs.filter((leg) => leg.move.move_type === "drive");
      const deliveryModel = resolveOrderDeliveryModel({
        rooftopOperationalModel,
        firstDriveLegIsLite: driveLegs[0]?.move.lite ?? null
      });
      const config = resolveScenarioConfig({ scenarioKey: entry.order.scenarioKey, deliveryModel });
      if (!config) return reuseScenarioIndexedEntry({ entry, phase: null, metric: null, hasUnreadMessages: false, unreadMoveIds: [], assignment: null });
      const rideLifecycleEnabled = isRideLifecycleEnabled(config, driveLegs[0]?.move);
      const status = deriveScenarioStatus({ legs: entry.order.legs, config, rideLifecycleEnabled });
      const unreadLegIds = unreadMoveIds ? driveLegs.filter((leg) => unreadMoveIds.has(leg.move.id)).map((leg) => leg.move.id) : [];
      return reuseScenarioIndexedEntry({
        entry,
        phase: status.phase,
        metric: resolveOrderMetric(status),
        hasUnreadMessages: unreadLegIds.length > 0,
        unreadMoveIds: unreadLegIds,
        // The SAME classifier the pill runs, fed the index's now-present offers — one
        // implementation of the offered/accepted precedence, at both depths.
        assignment: resolveDriverAssignment2({ legs: entry.order.legs, offers: entry.order.offers ?? [] })
      });
    } catch (error) {
      onError?.(entry, error);
      return reuseScenarioIndexedEntry({
        entry: faultResolvedOrderEntry(entry),
        phase: null,
        metric: null,
        hasUnreadMessages: false,
        unreadMoveIds: [],
        assignment: null
      });
    }
  });
  const metricCounts = {
    [SCENARIO_METRIC.ALL]: entries.length,
    [SCENARIO_METRIC.UPCOMING]: 0,
    [SCENARIO_METRIC.ACTIVE]: 0,
    [SCENARIO_METRIC.COMPLETED]: 0,
    [SCENARIO_METRIC.CANCELLED]: 0,
    [SCENARIO_METRIC.UNCONFIRMED]: 0,
    [SCENARIO_METRIC.UNREAD]: 0
  };
  for (const item of entries) {
    if (item.metric) metricCounts[item.metric] += 1;
    if (item.phase === SCENARIO_PHASE.UNCONFIRMED) metricCounts[SCENARIO_METRIC.UNCONFIRMED] += 1;
    if (item.hasUnreadMessages) metricCounts[SCENARIO_METRIC.UNREAD] += 1;
  }
  return { entries, metricCounts };
};

// src/products/valet/feed/offerStatusFilter.ts
var mapAssignmentToOfferFilterKey = (assignment) => {
  if (!assignment) return null;
  switch (assignment.state) {
    case "unassigned":
      return "unassigned";
    case "offered":
      return "offered";
    case "accepted":
    case "assigned":
      return "accepted";
    case "locked":
      return assignment.driver ? "accepted" : null;
    default:
      return null;
  }
};

// src/products/valet/valetOrderAdapter.ts
var VALET_INDEX_CAP = 2e3;
var VALET_PAGE_SIZES = [10, 25, 50, 100];
var VALET_PAGE_POLL_INTERVAL_MS = 6e4;
var customerScope = (customerIds, includePayer) => includePayer ? { _or: [{ customer_id: { _in: customerIds } }, { payer_id: { _in: customerIds } }] } : { customer_id: { _in: customerIds } };
var windowScope = (window) => window ? {
  _or: [
    { pickup_time: { _gte: window.startIso, _lte: window.endIso } },
    { ready_by: { _gte: window.startIso, _lte: window.endIso } }
  ]
} : {};
var buildValetScope = ({ customerIds, includePayer, filters }) => {
  const arms = [
    customerScope(customerIds, includePayer),
    ...buildScenarioCreatorFilters(filters.createdBy),
    ...buildScenarioMarketFilters(filters.market),
    ...buildScenarioSearchFilters(filters.search)
  ];
  return arms.length === 1 ? arms[0] : { _and: arms };
};
var buildIndexVariables = ({ customerIds, includePayer, window, filters, limit }) => ({
  scope: buildValetScope({ customerIds, includePayer, filters }),
  window: windowScope(window),
  limit
});
var buildPageVariables = (ids) => ({ scenarioIds: ids });
var buildValetStandaloneVariables = (orderId) => ({ identity: [{ id: { _eq: orderId } }] });
var buildStreamVariables = ({ customerIds, includePayer, window, cursor }) => ({
  cursor: [{ initial_value: { updatedat: cursor }, ordering: "ASC" }],
  where: { _and: window ? [customerScope(customerIds, includePayer), windowScope(window)] : [customerScope(customerIds, includePayer)] }
});
var toOrderPhase = (phase) => {
  switch (phase) {
    case SCENARIO_PHASE.UNCONFIRMED:
    case SCENARIO_PHASE.QUEUED:
      return "queued";
    case SCENARIO_PHASE.PLANNED:
      return "planned";
    case SCENARIO_PHASE.DRIVING:
    case SCENARIO_PHASE.ON_SITE:
      return "active";
    case SCENARIO_PHASE.DONE:
      return "completed";
    case SCENARIO_PHASE.CANCEL_PENDING:
    case SCENARIO_PHASE.CANCELLED:
      return "cancelled";
  }
};
var driveLegsByCardinality = (order) => [...order.legs].sort((a, b) => a.cardinality - b.cardinality).filter((leg) => leg.move.move_type === "drive");
var VALET_PRODUCT = "moves";
var faultKey = (key) => orderKey(VALET_PRODUCT, key.replace(":", "-"));
var modelCache = /* @__PURE__ */ new WeakMap();
var toFeedEntry = (indexed, customerId, facts) => {
  const { entry, phase, metric, hasUnreadMessages, assignment } = indexed;
  if (!isResolvedOrderEntry(entry)) {
    return { kind: "error", key: faultKey(entry.key), anchorMs: entry.anchorMs, reason: entry.reason, ids: entry.moveIds };
  }
  if (phase === null || metric === null) {
    return { kind: "error", key: faultKey(entry.key), anchorMs: entry.anchorMs, reason: "unresolvedConfig", ids: collectEntryMoveIds(entry) };
  }
  const order = entry.order;
  const driveLegs = driveLegsByCardinality(order);
  const first = driveLegs[0];
  const deliveryModel = resolveOrderDeliveryModel({ rooftopOperationalModel: facts.operationalModel, firstDriveLegIsLite: first?.move.lite ?? null });
  const config = resolveScenarioConfig({ scenarioKey: order.scenarioKey, deliveryModel });
  const rideLifecycleEnabled = config ? isRideLifecycleEnabled(config, first?.move) : false;
  const status = config ? deriveScenarioStatus({ legs: order.legs, config, rideLifecycleEnabled }) : null;
  const cached = modelCache.get(indexed);
  if (cached && cached.customerId === customerId) return { kind: "order", model: cached.model };
  const createdBy = first?.move.createdBy ?? null;
  const model = {
    key: orderKey("moves", order.scenarioId),
    product: VALET_PRODUCT,
    id: order.scenarioId,
    customerId,
    rendererKey: config?.rendererKey ?? "concierge",
    phase: toOrderPhase(phase),
    metric,
    anchorMs: entry.anchorMs,
    promiseMs: resolveOrderPromiseMs(order.legs),
    operationalModel: deliveryModel === "saas" ? "self-managed" : "hopdrive-managed",
    isLive: status?.isLive ?? false,
    isHydrated: false,
    // The SAME classifier the pill runs, fed the index's offers, so the Offer Status filter can never disagree with the pill.
    flags: { isUnconfirmed: phase === SCENARIO_PHASE.UNCONFIRMED, hasUnreadMessages, offerStatus: mapAssignmentToOfferFilterKey(assignment) },
    sortValues: {
      status: SCENARIO_PHASE_ORDER.indexOf(phase),
      order: first?.move.id ?? null,
      guest: first?.move.consumer_name ?? null,
      market: first?.move.customer?.name ?? null,
      createdBy,
      workflow: first?.move.workflowset?.name ?? null,
      distance: deriveScenarioDistanceMiles(order.legs)
    },
    // Null files the order under the option's empty title.
    groupKeys: {
      guest: driveLegs.find((leg) => leg.move.consumer_name)?.move.consumer_name ?? null,
      market: first?.move.customer?.name ?? null,
      driver: driveLegs.find((leg) => leg.move.driver_name)?.move.driver_name ?? null,
      createdBy: createdBy ? createdBy.startsWith("API-") ? SCENARIO_API_CREATOR_OPTION : createdBy : null,
      hangtags: order.legs.some((leg) => leg.move.hangtags?.length) ? "With Hangtags" : null,
      unread: hasUnreadMessages ? "Needs Response" : null
    },
    source: indexed
  };
  modelCache.set(indexed, { customerId, model });
  return { kind: "order", model };
};
var rooftopFor = (customerId, rooftops) => rooftops.find((rooftop) => rooftop.id === customerId) ?? rooftops[0] ?? NO_ROOFTOP_FACTS;
var adaptIndexRows = (rows, context) => {
  const groups = /* @__PURE__ */ new Map();
  const customerIdByMoveId = /* @__PURE__ */ new Map();
  for (const item of rows) {
    const facts = rooftopFor(item.customerId, context.rooftops);
    groups.set(facts, [...groups.get(facts) ?? [], item]);
    customerIdByMoveId.set(item.row.moveId, item.customerId);
  }
  const entries = [];
  for (const [facts, groupRows] of groups) {
    const grouped = buildScenarioOrdersFromIndex(
      groupRows.map((item) => item.row),
      { timeZone: facts.timezone ?? "UTC" }
    );
    const { entries: indexed } = deriveScenarioIndex(grouped, { rooftopOperationalModel: facts.operationalModel, unreadMoveIds: context.extras?.unreadMoveIds });
    for (const item of indexed) {
      const firstMoveId = collectEntryMoveIds(item.entry)[0];
      const customerId = firstMoveId === void 0 ? null : customerIdByMoveId.get(firstMoveId) ?? null;
      entries.push(toFeedEntry(item, customerId, facts));
    }
  }
  return entries;
};
var adaptIndex = (data, context) => {
  const rows = adaptScenarioOrderIndex(data);
  const rawRows = data?.moves ?? [];
  const capReached = rows.length > VALET_INDEX_CAP;
  const capped = (capReached ? rows.slice(0, VALET_INDEX_CAP) : rows).map((row2, index) => ({
    row: row2,
    customerId: rawRows[index]?.customer_id ?? null
  }));
  return { entries: adaptIndexRows(capped, context), capReached };
};
var adaptPage = (data) => adaptScenarioOrderPage(data);
var mergePage = (entries, page) => {
  const byScenarioId = new Map(page.map((order) => [order.scenarioId, order]));
  return entries.map((entry) => {
    if (entry.kind !== "order") return entry;
    const model = entry.model;
    const hydratedOrder = byScenarioId.get(model.id);
    if (!hydratedOrder || !isResolvedOrderEntry(model.source.entry)) return entry;
    const merged = reusePagedEntry({
      // The indexed wrapper, not the bare entry: its identity also moves when the viewer's
      // unread legs change, which the chat target reads.
      indexEntry: model.source,
      hydratedOrder,
      build: () => ({
        isHydrated: true,
        hasUnreadMessages: model.flags.hasUnreadMessages,
        model: {
          ...model,
          isHydrated: true,
          source: { ...model.source, entry: { ...model.source.entry, order: hydratedOrder } }
        }
      })
    });
    return { kind: "order", model: merged.model };
  });
};
var adaptValetStandalone = (data, context) => {
  const order = adaptScenarioOrderStandalone(data);
  if (!order) return null;
  const customerId = driveLegsByCardinality(order)[0]?.move.customer?.id ?? order.legs[0]?.move.customer?.id ?? null;
  const rows = order.legs.map((leg) => ({ row: { moveId: leg.move.id, order }, customerId }));
  const [entry] = mergePage(adaptIndexRows(rows, context), [order]);
  return entry ?? null;
};
var valetEntryMoveIds = (entry) => {
  if (entry.kind === "error") return entry.key.startsWith(`${VALET_PRODUCT}:`) ? entry.ids : [];
  if (entry.model.product !== "moves") return [];
  return collectEntryMoveIds(entry.model.source.entry);
};
var valetOrderAdapter = {
  product: VALET_PRODUCT,
  dayColumns: ["ready_by", "pickup_time"],
  indexCap: VALET_INDEX_CAP,
  pageSizes: VALET_PAGE_SIZES,
  pollMs: VALET_PAGE_POLL_INTERVAL_MS,
  live: "stream",
  documents: { index: ValetOrderIndexDocument, page: ValetOrderPageDocument, stream: ValetMovesStreamDocument },
  buildIndexVariables,
  buildPageVariables,
  buildStreamVariables,
  adaptIndex,
  adaptPage,
  mergePage
};

// src/products/valet/index.ts
var valetPlugin = {
  product: "moves",
  adapter: valetOrderAdapter,
  resolve: resolveValetOrders,
  renderers: valetRenderers
};

// src/shared/mobility/mobilityRunStatus.ts
var MOBILITY_RUN_STATUS = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  DISPATCHED: "dispatched",
  CLAIMED: "claimed",
  IN_TRANSIT: "in_transit",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  CANCELED: "canceled"
};
var formatStatusWord = (status) => status.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
var RUN_VOCABULARY = {
  draft: { phase: "queued", badge: "Draft" },
  scheduled: { phase: "planned", badge: "Scheduled" },
  dispatched: { phase: "planned", badge: "Dispatched" },
  claimed: { phase: "planned", badge: "Dispatched" },
  in_transit: { phase: "active", badge: "In Transit" },
  completed: { phase: "completed", badge: "Completed" },
  cancelled: { phase: "cancelled", badge: "Canceled" },
  canceled: { phase: "cancelled", badge: "Canceled" }
};
var vocabularyOf = (status) => RUN_VOCABULARY[status];
var resolveMobilityRunPhase = (status) => vocabularyOf(status)?.phase ?? "queued";
var resolveMobilityRunStatusToken = (status) => resolveOrderStatusToken(resolveMobilityRunPhase(status));
var isMobilityRunLive = (status) => isLivePhase(resolveMobilityRunPhase(status));
var resolveMobilityRunBadge = (status) => vocabularyOf(status)?.badge ?? formatStatusWord(status);
var resolveMobilityRunChip = (status) => ({
  label: resolveMobilityRunBadge(status),
  token: resolveMobilityRunStatusToken(status),
  bucket: metricForPhase(resolveMobilityRunPhase(status))
});
var statusesForPhases = (phases) => Object.keys(RUN_VOCABULARY).filter((status) => phases.includes(RUN_VOCABULARY[status].phase));
var MOBILITY_RUN_ACTIVE_STATUSES = statusesForPhases(["active"]);
var MOBILITY_RUN_UPCOMING_STATUSES = statusesForPhases(["queued", "planned"]);
var MOBILITY_RUN_TILE_STATUSES = {
  upcoming: MOBILITY_RUN_UPCOMING_STATUSES,
  active: MOBILITY_RUN_ACTIVE_STATUSES,
  completed: statusesForPhases(["completed"]),
  cancelled: statusesForPhases(["cancelled"])
};

// src/products/mobile-service/queries/mobileServiceRunIndex.query.generated.ts
import { resolveExecutor as resolveExecutor5 } from "@hopdrive/sdk-core";
var MobileServiceRunIndexDocument = { "__text": 'query mobileServiceRunIndex($scope: mobility_runs_bool_exp!, $window: mobility_runs_bool_exp!, $limit: Int!) {\n  mobility_runs(\n    where: {product_type: {_eq: "mobile_service"}, _and: [$scope, $window]}\n    order_by: [{departure_time: asc}, {id: asc}]\n    limit: $limit\n  ) {\n    id\n    customer_id\n    status\n    departure_time\n    dispatched_at\n    departed_at\n    completed_at\n    created_at\n    estimated_distance_miles\n    driver {\n      id\n      user {\n        id\n        display_name\n        avatar_url\n      }\n    }\n    vehicle {\n      id\n      make\n      model\n      year\n      license_plate\n    }\n    stops_aggregate {\n      aggregate {\n        count\n      }\n    }\n    stops(order_by: {stop_order: asc}) {\n      id\n      stop_order\n      status\n      service_stop_detail {\n        customer_name\n        estimated_service_minutes\n      }\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "mobileServiceRunIndex" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "scope" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "mobility_runs_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "window" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "mobility_runs_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "mobility_runs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "product_type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "mobile_service", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_and" }, "value": { "kind": "ListValue", "values": [{ "kind": "Variable", "name": { "kind": "Name", "value": "scope" } }, { "kind": "Variable", "name": { "kind": "Name", "value": "window" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "departure_time" }, "value": { "kind": "EnumValue", "value": "asc" } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "asc" } }] }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departure_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dispatched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "completed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "estimated_distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "license_plate" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "stops_aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "count" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "stops" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "stop_order" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "stop_order" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "service_stop_detail" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "customer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "estimated_service_minutes" } }] } }] } }] } }] } }] };

// src/products/mobile-service/queries/mobileServiceRunPage.query.generated.ts
import { resolveExecutor as resolveExecutor6 } from "@hopdrive/sdk-core";
var MobileServiceRunPageDocument = { "__text": 'query mobileServiceRunPage($runIds: [bigint!]!) {\n  mobility_runs(where: {id: {_in: $runIds}}) {\n    id\n    customer_id\n    status\n    departure_time\n    dispatched_at\n    departed_at\n    completed_at\n    estimated_distance_miles\n    driver {\n      id\n      user {\n        id\n        display_name\n        avatar_url\n      }\n    }\n    vehicle {\n      id\n      make\n      model\n      year\n    }\n    scenario {\n      id\n      bundletoscenarios(\n        order_by: {bundle: {createdat: desc}}\n        where: {bundle: {status: {_neq: "deprecated"}}}\n        limit: 1\n      ) {\n        id\n        bundle {\n          id\n          offers(\n            where: {status: {_in: ["new", "offered", "accepted", "assigned"]}}\n            order_by: {createdat: desc}\n            limit: 1\n          ) {\n            id\n            driver {\n              id\n              user {\n                id\n                display_name\n                avatar_url\n              }\n            }\n          }\n        }\n      }\n    }\n    stops(order_by: {stop_order: asc}) {\n      id\n      stop_order\n      status\n      geofence_arrived_at\n      geofence_departed_at\n      service_stop_detail {\n        customer_name\n        customer_phone\n        service_description\n        estimated_service_minutes\n        ro_reference\n        is_fleet_stop\n        fleet_vehicle_count\n        skip_reason_code\n        skip_notes\n      }\n      service_orders(order_by: {id: asc}) {\n        id\n        vehicle_make\n        vehicle_model\n        vehicle_year\n        service_description\n        estimated_minutes\n        status\n        vehiclephotos(order_by: {createdat: asc}) {\n          id\n          url\n          step_id\n        }\n      }\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "mobileServiceRunPage" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "runIds" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "mobility_runs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "runIds" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departure_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dispatched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "completed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "estimated_distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "year" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundletoscenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_neq" }, "value": { "kind": "StringValue", "value": "deprecated", "block": false } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offers" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "new", "block": false }, { "kind": "StringValue", "value": "offered", "block": false }, { "kind": "StringValue", "value": "accepted", "block": false }, { "kind": "StringValue", "value": "assigned", "block": false }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "avatar_url" } }] } }] } }] } }] } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "stops" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "stop_order" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "stop_order" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "geofence_arrived_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "geofence_departed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "service_stop_detail" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "customer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "service_description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "estimated_service_minutes" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ro_reference" } }, { "kind": "Field", "name": { "kind": "Name", "value": "is_fleet_stop" } }, { "kind": "Field", "name": { "kind": "Name", "value": "fleet_vehicle_count" } }, { "kind": "Field", "name": { "kind": "Name", "value": "skip_reason_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "skip_notes" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "service_orders" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "service_description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "estimated_minutes" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehiclephotos" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "url" } }, { "kind": "Field", "name": { "kind": "Name", "value": "step_id" } }] } }] } }] } }] } }] } }] };

// src/shared/model/stopWindow.ts
var placeWindow = (stops, activeIndex, size) => {
  const centeredStart = activeIndex < 0 ? 0 : activeIndex - Math.floor(size / 2);
  const start = Math.min(Math.max(centeredStart, 0), stops.length - size);
  return { before: start, visible: stops.slice(start, start + size), after: stops.length - start - size };
};
var overflowRows = (window) => (window.before > 0 ? 1 : 0) + (window.after > 0 ? 1 : 0);
var windowStops = (stops, activePosition, cap) => {
  const total = stops.length;
  if (cap <= 0 || total <= cap) return { before: 0, visible: [...stops], after: 0 };
  const activeIndex = activePosition === null ? -1 : stops.findIndex((stop) => stop.position === activePosition);
  const oneOverflow = placeWindow(stops, activeIndex, cap - 1);
  return overflowRows(oneOverflow) + oneOverflow.visible.length <= cap ? oneOverflow : placeWindow(stops, activeIndex, cap - 2);
};

// src/products/mobile-service/constants/mobileServiceStatus.ts
var MOBILE_SERVICE_STOP_STATUS = {
  PENDING_CONFIRMATION: "pending_confirmation",
  CONFIRMED: "confirmed",
  ASSIGNED: "assigned",
  IN_TRANSIT_TO: "in_transit_to",
  ARRIVED: "arrived",
  IN_SERVICE: "in_service",
  SERVICE_IN_PROGRESS: "service_in_progress",
  COMPLETED: "completed",
  SKIPPED: "skipped"
};
var MOBILE_SERVICE_ORDER_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  SKIPPED: "skipped"
};
var STOP_MARKER_STATE = {
  pending_confirmation: "pending",
  confirmed: "pending",
  assigned: "pending",
  in_transit_to: "active",
  arrived: "active",
  in_service: "active",
  service_in_progress: "active",
  completed: "done",
  skipped: "exception"
};
var resolveStopMarkerState = (status) => STOP_MARKER_STATE[status] ?? "pending";
var STOP_BADGE = {
  pending_confirmation: "Awaiting Confirmation",
  in_transit_to: "In Transit"
};
var resolveStopBadge = (status) => STOP_BADGE[status] ?? formatStatusWord(status);
var resolveOrderBadge = (status) => formatStatusWord(status);
var MOBILE_SERVICE_CARD_STOP_CAP = 6;
var MOBILE_SERVICE_ROUTE_CHAIN_CAP = 3;
var MOBILE_SERVICE_SHOW_FLEET_VEHICLES = "showFleetVehicles";
var MOBILE_SERVICE_DISPLAY_PREFERENCES = [
  { surface: "card", key: MOBILE_SERVICE_SHOW_FLEET_VEHICLES, label: "Show fleet vehicles", section: "Stops", defaultValue: false }
];

// src/products/mobile-service/items/serviceStopText.ts
var formatMinutes = (minutes) => minutes === null ? null : `${minutes} min`;
var formatDurationMinutes = (total) => {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
};
var formatFleetCount = (stop) => {
  const count = stop.fleetVehicleCount ?? stop.orders.length;
  return `${count} ${count === 1 ? "vehicle" : "vehicles"}`;
};
var buildStopNote = (stop) => {
  if (stop.status === MOBILE_SERVICE_STOP_STATUS.SKIPPED) return stop.skipReason ? `Skipped \xB7 ${stop.skipReason}` : "Skipped";
  const parts = [];
  if (stop.isFleet) parts.push(formatFleetCount(stop));
  if (stop.serviceDescription) parts.push(stop.serviceDescription);
  const minutes = formatMinutes(stop.estimatedMinutes);
  if (minutes) parts.push(minutes);
  return parts.length ? parts.join(" \xB7 ") : void 0;
};
var buildOrderRowNote = (order) => {
  const minutes = formatMinutes(order.estimatedMinutes);
  return minutes ? `${order.serviceDescription} \xB7 ${minutes}` : order.serviceDescription;
};
var buildStopTooltip = (stop) => {
  const content = [{ kind: "title", text: stop.customerName }];
  if (stop.customerPhone) content.push({ kind: "row", key: "Phone", value: stop.customerPhone });
  if (stop.serviceDescription) content.push({ kind: "row", key: "Service", value: stop.serviceDescription });
  const minutes = formatMinutes(stop.estimatedMinutes);
  if (minutes) content.push({ kind: "row", key: "Estimate", value: minutes });
  if (stop.isFleet) content.push({ kind: "row", key: "Vehicles", value: formatFleetCount(stop) });
  content.push({ kind: "row", key: "Status", value: resolveStopBadge(stop.status) });
  if (stop.status === MOBILE_SERVICE_STOP_STATUS.SKIPPED && stop.skipReason) content.push({ kind: "description", text: stop.skipReason });
  return content;
};

// src/products/mobile-service/slots.ts
var { DRAFT, SCHEDULED, DISPATCHED, CLAIMED, IN_TRANSIT, COMPLETED, CANCELLED, CANCELED } = MOBILITY_RUN_STATUS;
var isRunLive = (view) => isMobilityRunLive(view.status);
var isRunSettled = (view) => {
  const phase = resolveMobilityRunPhase(view.status);
  return phase === "completed" || phase === "cancelled";
};
var toStatusBarGroup2 = (view) => ({
  status: resolveMobilityRunStatusToken(view.status),
  badge: resolveMobilityRunBadge(view.status),
  isLive: isRunLive(view)
});
var isStopReached = (stop) => stop.status === MOBILE_SERVICE_STOP_STATUS.COMPLETED || stop.status === MOBILE_SERVICE_STOP_STATUS.SKIPPED;
var toTrackerBar = (stop, activePosition) => ({
  key: `stop:${stop.id}`,
  isReached: isStopReached(stop),
  isDrivingToward: stop.position === activePosition,
  slipFraction: 0,
  tooltip: [
    { kind: "title", text: stop.customerName },
    { kind: "row", key: "Status", value: resolveStopBadge(stop.status) }
  ]
});
var buildStatusLine = (view) => {
  const active = view.activeStopPosition === null ? void 0 : view.stops.find((stop) => stop.position === view.activeStopPosition);
  if (active && isRunLive(view)) {
    return active.status === MOBILE_SERVICE_STOP_STATUS.IN_TRANSIT_TO ? `Driving to ${active.customerName}` : `Servicing at ${active.customerName}`;
  }
  return resolveMobilityRunBadge(view.status);
};
var toTrackerGroup2 = (view) => ({
  bars: view.stops.map((stop) => toTrackerBar(stop, view.activeStopPosition)),
  status: resolveMobilityRunStatusToken(view.status),
  statusLine: buildStatusLine(view),
  isLive: isRunLive(view),
  isChainBroken: false
});
var RUN_DISPLAY_NAME = "Service Run";
var buildIdentityTooltip = (view) => [
  { kind: "title", text: `${RUN_DISPLAY_NAME} ${view.id}` },
  { kind: "row", key: "Technician", value: view.technician?.name ?? view.offeredDriverName ?? "Auto-assign" },
  { kind: "row", key: "Vehicle", value: view.vehicleLine ?? "Unassigned" },
  { kind: "row", key: "Stops", value: String(view.customerStopCount) },
  { kind: "row", key: "Est. service", value: formatDurationMinutes(view.estimatedTotalServiceMinutes) }
];
var toIdentityPhoto = (view) => view.stops.find((stop) => stop.photo)?.photo ?? PLACEHOLDER_PHOTO;
var toIdentityGroup = (view) => ({
  displayName: RUN_DISPLAY_NAME,
  ids: [view.id],
  tooltip: buildIdentityTooltip(view)
});
var TIME_PENDING_MARK = "N/A";
var TIME_ABSENT_MARK = "\u2014";
var toMilestoneGroup2 = (view) => {
  switch (view.status) {
    case DRAFT:
    case SCHEDULED:
      return { value: view.departure.display ?? TIME_PENDING_MARK, subtext: { text: "Departure", tone: "neutral" }, tooltip: null };
    case DISPATCHED:
    case CLAIMED:
    case IN_TRANSIT:
      return view.departed.display ? { value: view.departed.display, subtext: { text: "Departed", tone: "neutral" }, tooltip: null } : { value: view.dispatched.display ?? view.departure.display ?? TIME_PENDING_MARK, subtext: { text: view.dispatched.display ? "Dispatched" : "Departure", tone: "neutral" }, tooltip: null };
    case COMPLETED:
      return { value: view.completed.display ?? TIME_PENDING_MARK, subtext: { text: "Completed", tone: "neutral" }, tooltip: null };
    case CANCELLED:
    case CANCELED:
    default:
      return { value: TIME_ABSENT_MARK, subtext: { text: resolveMobilityRunBadge(view.status), tone: "neutral" }, tooltip: null };
  }
};
var toDriverGroup2 = (view) => {
  const isSettled = isRunSettled(view);
  if (view.technician) {
    return { showPill: true, driverName: view.technician.name, avatarUrl: view.technician.avatarUrl, state: "assigned", isSettled, tooltip: [{ kind: "description", text: `${view.technician.name} is the technician on this run` }] };
  }
  if (view.offeredDriverName) {
    return { showPill: true, driverName: view.offeredDriverName, avatarUrl: null, state: "offered", isSettled, tooltip: [{ kind: "description", text: `Offered to ${view.offeredDriverName}, awaiting acceptance` }] };
  }
  return { showPill: true, driverName: null, avatarUrl: null, state: "unassigned", isSettled, tooltip: [{ kind: "description", text: "Auto-assign: a technician is assigned when the run is dispatched" }] };
};

// src/products/mobile-service/cardProps.ts
var MOBILE_SERVICE_RENDERER_KEY = "mobileServiceRun";
var defaultMobileServiceCardPreferences = () => Object.fromEntries(MOBILE_SERVICE_DISPLAY_PREFERENCES.filter((definition) => definition.surface === "card").map((definition) => [definition.key, definition.defaultValue]));
var toTimeSlot2 = (key, label, time) => ({
  key,
  label,
  value: time.display ?? TIME_PENDING_MARK,
  treatment: time.display ? "actual" : "pending",
  tooltip: null
});
var toTimesGroup = (view) => ({
  leftColumn: [toTimeSlot2("departure", "Departure", view.departure), toTimeSlot2("dispatched", "Dispatched", view.dispatched)],
  rightColumn: [toTimeSlot2("departed", "Departed", view.departed), toTimeSlot2("completed", "Completed", view.completed)]
});
var toDetailRows = (view) => {
  const rows = [
    { key: "stops", label: "Stops", value: String(view.customerStopCount) },
    { key: "serviceTime", label: "Est. service time", value: formatDurationMinutes(view.estimatedTotalServiceMinutes) }
  ];
  if (view.estimatedDistanceMiles !== null) rows.push({ key: "distance", label: "Est. distance", value: `${view.estimatedDistanceMiles} mi` });
  if (view.referenceNums.length) rows.push({ key: "reference", label: "Reference", value: view.referenceNums.join(" \xB7 ") });
  return rows;
};
var toStopItems = (view, showOrders, cap = MOBILE_SERVICE_CARD_STOP_CAP) => {
  const window = windowStops(view.stops, view.activeStopPosition, cap);
  const overflow = (count, position) => count > 0 ? [{ kind: "stopOverflow", key: `overflow:${position}`, props: { count, position } }] : [];
  return [
    ...overflow(window.before, "before"),
    ...window.visible.map((stop) => ({ kind: "serviceStop", key: `stop:${stop.id}`, props: { stop, showOrders } })),
    ...overflow(window.after, "after")
  ];
};
var toMobileServiceCardProps = (view, preferences) => ({
  rendererKey: MOBILE_SERVICE_RENDERER_KEY,
  statusBar: toStatusBarGroup2(view),
  tracker: toTrackerGroup2(view),
  header: { ...toIdentityGroup(view), milestone: toMilestoneGroup2(view) },
  items: toStopItems(view, preferences[MOBILE_SERVICE_SHOW_FLEET_VEHICLES] === true),
  times: toTimesGroup(view),
  details: { rows: toDetailRows(view) },
  driver: toDriverGroup2(view),
  actionBar: [],
  state: {
    isHydrated: view.isHydrated,
    isSettled: isRunSettled(view),
    isCancelDeclared: resolveMobilityRunPhase(view.status) === "cancelled",
    cancelReason: null,
    isInDanger: false
  }
});

// src/products/mobile-service/mobileServiceOrderAdapter.ts
var MOBILE_SERVICE_INDEX_CAP = 2e3;
var MOBILE_SERVICE_PAGE_SIZES = [10, 25, 50, 100];
var MOBILE_SERVICE_PAGE_POLL_INTERVAL_MS = 6e4;
var customerScope2 = (customerIds) => ({ customer_id: { _in: customerIds } });
var windowScope2 = (window) => window ? { departure_time: { _gte: window.startIso, _lte: window.endIso } } : {};
var buildIndexVariables2 = ({ customerIds, window, limit }) => ({
  scope: customerScope2(customerIds),
  window: windowScope2(window),
  limit
});
var buildPageVariables2 = (ids) => ({ runIds: ids });
var runAnchorMs = (departureTime) => {
  if (!departureTime) return null;
  const ms = Date.parse(departureTime);
  return Number.isNaN(ms) ? null : ms;
};
var isRunStatusLive = (status) => isMobilityRunLive(status);
var toModel = (row2) => {
  const phase = resolveMobilityRunPhase(row2.status);
  const anchorMs = runAnchorMs(row2.departure_time);
  const technicianName = row2.driver?.user?.display_name ?? null;
  return {
    key: orderKey("mobile-service", row2.id),
    product: "mobile-service",
    id: row2.id,
    customerId: row2.customer_id,
    rendererKey: MOBILE_SERVICE_RENDERER_KEY,
    phase,
    metric: metricForPhase(phase),
    anchorMs,
    promiseMs: anchorMs,
    operationalModel: "self-managed",
    isLive: isRunStatusLive(row2.status),
    isHydrated: false,
    flags: { isUnconfirmed: false, hasUnreadMessages: false },
    sortValues: {
      // The lifecycle rank, never the badge word.
      status: ORDER_PHASE_ORDER.indexOf(phase),
      order: row2.id,
      technician: technicianName
    },
    groupKeys: { technician: technicianName },
    source: { index: row2, detail: null }
  };
};
var adaptIndex2 = (data, _context) => {
  const rows = data?.mobility_runs ?? [];
  const capReached = rows.length > MOBILE_SERVICE_INDEX_CAP;
  const capped = capReached ? rows.slice(0, MOBILE_SERVICE_INDEX_CAP) : rows;
  return { entries: capped.map((row2) => ({ kind: "order", model: toModel(row2) })), capReached };
};
var adaptPage2 = (data) => data?.mobility_runs ?? [];
var mergePage2 = (entries, page) => {
  const byId = new Map(page.map((run) => [run.id, run]));
  return entries.map((entry) => {
    if (entry.kind !== "order" || entry.model.product !== "mobile-service") return entry;
    const model = entry.model;
    const detail = byId.get(model.id);
    if (!detail) return entry;
    return { kind: "order", model: { ...model, isHydrated: true, source: { ...model.source, detail } } };
  });
};
var mobileServiceOrderAdapter = {
  product: "mobile-service",
  dayColumns: ["departure_time"],
  indexCap: MOBILE_SERVICE_INDEX_CAP,
  pageSizes: MOBILE_SERVICE_PAGE_SIZES,
  pollMs: MOBILE_SERVICE_PAGE_POLL_INTERVAL_MS,
  live: "poll",
  documents: { index: MobileServiceRunIndexDocument, page: MobileServiceRunPageDocument },
  buildIndexVariables: buildIndexVariables2,
  buildPageVariables: buildPageVariables2,
  adaptIndex: adaptIndex2,
  adaptPage: adaptPage2,
  mergePage: mergePage2
};

// src/products/mobile-service/resolveMobileServiceOrders.ts
import { PiWrenchFill } from "react-icons/pi";

// src/products/mobile-service/columns.ts
var buildRouteTooltip = (stops) => [
  { kind: "title", text: `${stops.length} ${stops.length === 1 ? "stop" : "stops"}` },
  ...stops.map((stop) => ({ kind: "row", key: String(stop.position), value: `${stop.customerName} \xB7 ${resolveStopBadge(stop.status)}` }))
];
var buildRouteChainStops = (stops, cap = MOBILE_SERVICE_ROUTE_CHAIN_CAP) => {
  if (stops.length <= cap) return stops.map((stop) => ({ key: String(stop.id), name: stop.customerName }));
  const named = stops.slice(0, Math.max(cap - 1, 0)).map((stop) => ({ key: String(stop.id), name: stop.customerName }));
  const hidden = stops.length - named.length;
  return [...named, { key: "more", name: `+${hidden} more ${hidden === 1 ? "stop" : "stops"}` }];
};
var departureMs = (view) => view.departure.iso ? Date.parse(view.departure.iso) : null;
var mobileServiceColumns = [
  {
    name: "status",
    label: "Status",
    tooltip: "Where the run is in its lifecycle",
    minWidth: 110,
    sortable: true,
    // The lifecycle rank, never the badge word.
    sortValue: (view) => ORDER_PHASE_ORDER.indexOf(view.phase),
    cell: "status",
    cellProps: (view) => ({ statusBar: toStatusBarGroup2(view), tracker: toTrackerGroup2(view) }),
    mobile: "pair",
    // The badge hangs from the pair's right edge; the tracker keeps the full width under it.
    mobileValueSx: { "& > div": { alignItems: "flex-end" }, "& > div > :last-child": { alignSelf: "stretch" } }
  },
  {
    name: "run",
    label: "Run",
    tooltip: "The run and the technician, vehicle, and stops on it",
    minWidth: 150,
    sortable: true,
    sortValue: (view) => view.id,
    cell: "identity",
    // A run has no photo of its own, so the tile borrows the first stop's capture.
    cellProps: (view) => ({ ...toIdentityGroup(view), photo: toIdentityPhoto(view), photoItemId: null }),
    mobile: "pair",
    mobileValueSx: { "& > div": { flexDirection: "row-reverse" }, "& > div > div": { alignItems: "flex-end" } }
  },
  {
    name: "technician",
    label: "Technician",
    minWidth: 150,
    sortable: true,
    sortValue: (view) => view.technician?.name ?? view.offeredDriverName ?? null,
    cell: "driver",
    cellProps: (view) => toDriverGroup2(view),
    mobile: "bar"
  },
  {
    name: "departure",
    label: "Departure",
    tooltip: "When the run is scheduled to leave",
    minWidth: 140,
    sortable: true,
    sortValue: departureMs,
    cell: "promise",
    cellProps: (view) => view.departure.display ? { value: view.departure.display, tooltip: null } : null,
    mobile: "pair"
  },
  {
    name: "stops",
    label: "Stops",
    align: "right",
    minWidth: 70,
    sortable: true,
    sortValue: (view) => view.customerStopCount,
    cell: "text",
    text: (view) => ({ value: String(view.customerStopCount), isBold: true }),
    mobile: "pair"
  },
  {
    name: "serviceTime",
    label: "Est. Time",
    tooltip: "The sum of the stops\u2019 estimated service minutes",
    align: "right",
    minWidth: 100,
    sortable: true,
    sortValue: (view) => view.estimatedTotalServiceMinutes,
    cell: "text",
    text: (view) => ({ value: formatDurationMinutes(view.estimatedTotalServiceMinutes) }),
    mobile: "pair"
  },
  {
    name: "route",
    label: "Route",
    tooltip: "Every customer stop on the run, in order",
    minWidth: 200,
    cell: "route",
    cellProps: (view) => view.stops.length ? { stops: buildRouteChainStops(view.stops), tooltip: buildRouteTooltip(view.stops) } : null
  }
];

// src/products/mobile-service/resolveMobileServiceOrders.ts
var clockParts2 = (ms, timeZone) => new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", hour12: true }).format(ms);
var formatRunClock = (iso, timeZone) => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const date = new Intl.DateTimeFormat("en-US", { timeZone, month: "numeric", day: "numeric" }).format(ms);
  return `${date}, ${clockParts2(ms, timeZone)}`;
};
var toTime = (iso, timeZone) => ({ iso, display: formatRunClock(iso, timeZone) });
var toVehicleLine = (vehicle) => {
  if (!vehicle) return null;
  const line = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ");
  return line || null;
};
var toTechnician = (driver) => driver?.user ? { name: driver.user.display_name, avatarUrl: driver.user.avatar_url } : null;
var confirmationFor = (status) => status === MOBILE_SERVICE_STOP_STATUS.PENDING_CONFIRMATION ? "awaiting" : "confirmed";
var sumMinutes = (stops) => stops.reduce((total, stop) => total + (stop.estimatedMinutes ?? 0), 0);
var activeStopPosition = (stops) => stops.find((stop) => resolveStopMarkerState(stop.status) === "active")?.position ?? null;
var indexCustomerStops = (row2) => row2.stops.filter((stop) => stop.service_stop_detail[0]?.customer_name != null);
var toIndexStopView = (stop, position) => ({
  id: stop.id,
  position,
  status: stop.status,
  customerName: stop.service_stop_detail[0]?.customer_name ?? "",
  customerPhone: null,
  serviceDescription: null,
  estimatedMinutes: stop.service_stop_detail[0]?.estimated_service_minutes ?? null,
  confirmation: confirmationFor(stop.status),
  isFleet: false,
  fleetVehicleCount: null,
  orders: [],
  photo: null,
  skipReason: null,
  arrivedIso: null,
  departedIso: null
});
var buildIndexView = (row2, timeZone) => {
  const stops = indexCustomerStops(row2).map((stop, index) => toIndexStopView(stop, index + 1));
  return {
    id: row2.id,
    status: row2.status,
    phase: resolveMobilityRunPhase(row2.status),
    technician: toTechnician(row2.driver),
    offeredDriverName: null,
    vehicleLine: toVehicleLine(row2.vehicle),
    departure: toTime(row2.departure_time, timeZone),
    dispatched: toTime(row2.dispatched_at, timeZone),
    departed: toTime(row2.departed_at, timeZone),
    completed: toTime(row2.completed_at, timeZone),
    estimatedTotalServiceMinutes: sumMinutes(stops),
    estimatedDistanceMiles: row2.estimated_distance_miles,
    customerStopCount: stops.length,
    stops,
    activeStopPosition: activeStopPosition(stops),
    referenceNums: [],
    isHydrated: false
  };
};
var detailCustomerStops = (row2) => row2.stops.filter((stop) => stop.service_stop_detail[0]?.customer_name != null);
var toOrderView = (order) => ({
  id: order.id,
  vehicleLine: [order.vehicle_year, order.vehicle_make, order.vehicle_model].filter(Boolean).join(" "),
  serviceDescription: order.service_description ?? "",
  estimatedMinutes: order.estimated_minutes,
  status: order.status
});
var toStopPhoto = (stop) => {
  const url = stop.service_orders.flatMap((order) => order.vehiclephotos).find((photo) => photo.url)?.url ?? null;
  return url ? { url, isPlaceholder: false, hasPickupPhotos: false, hasDeliveryPhotos: false } : null;
};
var toDetailStopView = (stop, position) => {
  const detail = stop.service_stop_detail[0];
  const isSkipped = stop.status === MOBILE_SERVICE_STOP_STATUS.SKIPPED;
  return {
    id: stop.id,
    position,
    status: stop.status,
    customerName: detail?.customer_name ?? "",
    customerPhone: detail?.customer_phone ?? null,
    serviceDescription: detail?.service_description ?? null,
    estimatedMinutes: detail?.estimated_service_minutes ?? null,
    confirmation: confirmationFor(stop.status),
    isFleet: detail?.is_fleet_stop ?? false,
    fleetVehicleCount: detail?.fleet_vehicle_count ?? null,
    orders: stop.service_orders.map(toOrderView),
    photo: toStopPhoto(stop),
    skipReason: isSkipped ? detail?.skip_notes ?? detail?.skip_reason_code ?? null : null,
    arrivedIso: stop.geofence_arrived_at,
    departedIso: stop.geofence_departed_at
  };
};
var offeredDriverName = (row2) => row2.scenario?.bundletoscenarios[0]?.bundle.offers[0]?.driver.user?.display_name ?? null;
var buildDetailView = (row2, timeZone) => {
  const stops = detailCustomerStops(row2).map((stop, index) => toDetailStopView(stop, index + 1));
  const technician = toTechnician(row2.driver);
  return {
    id: row2.id,
    status: row2.status,
    phase: resolveMobilityRunPhase(row2.status),
    technician,
    offeredDriverName: technician ? null : offeredDriverName(row2),
    vehicleLine: toVehicleLine(row2.vehicle),
    departure: toTime(row2.departure_time, timeZone),
    dispatched: toTime(row2.dispatched_at, timeZone),
    departed: toTime(row2.departed_at, timeZone),
    completed: toTime(row2.completed_at, timeZone),
    estimatedTotalServiceMinutes: sumMinutes(stops),
    estimatedDistanceMiles: row2.estimated_distance_miles,
    customerStopCount: stops.length,
    stops,
    activeStopPosition: activeStopPosition(stops),
    referenceNums: stops.map((_, index) => detailCustomerStops(row2)[index]?.service_stop_detail[0]?.ro_reference ?? "").filter(Boolean),
    isHydrated: true
  };
};
var MOBILE_SERVICE_METRIC_TILES = [
  { key: "all", label: "All Runs", inAll: true },
  { key: "upcoming", label: "Upcoming", inAll: true },
  { key: "active", label: "Active", inAll: true },
  { key: "completed", label: "Completed", inAll: true },
  { key: "cancelled", label: "Canceled", inAll: true }
];
var MOBILE_SERVICE_SORT_OPTIONS = [
  { key: "departure", label: "Departure Time", anchor: true },
  { key: "status", label: "Status", missingValue: "highest" },
  { key: "order", label: "Run ID" },
  { key: "technician", label: "Technician" }
];
var MOBILE_SERVICE_DATE_RANGE_PRESETS = [
  { key: "tomorrow", label: "Tomorrow", group: 0 },
  { key: "today", label: "Today", group: 0 },
  { key: "yesterday", label: "Yesterday", group: 0 },
  { key: "nextweek", label: "Next Week", group: 1 },
  { key: "thisweek", label: "This Week", group: 1 },
  { key: "lastweek", label: "Last Week", group: 1 },
  { key: "nextmonth", label: "Next Month", group: 2 },
  { key: "thismonth", label: "This Month", group: 2 },
  { key: "lastmonth", label: "Last Month", group: 2 },
  { key: "custom", label: "Custom", group: 3 }
];
var MOBILE_SERVICE_SEARCH_FIELDS = ["id", "driver.user.display_name", "vehicle.make", "vehicle.model", "stops.service_stop_detail.customer_name", "stops.service_stop_detail.ro_reference"];
var resolveMobileServiceOrders = (context) => {
  const timeZone = context.rooftop.timezone ?? "UTC";
  const referenceLabel = context.referenceNumLabel ?? "RO Reference";
  return {
    identity: { key: "mobile-service", label: "Mobile Service", icon: PiWrenchFill },
    operationalModel: "self-managed",
    copy: {
      unit: { singular: "run", plural: "runs" },
      referenceLabel,
      searchPlaceholder: "Search runs\u2026",
      capNotice: "This range holds more runs than the display shows at once. Narrow the date range to see them all.",
      empty: {
        noOrders: { title: "No runs in this range", description: "Widen the date range to see runs from other days." },
        filtered: { title: "No runs match your filters", description: "Try adjusting your search or filter criteria." }
      }
    },
    rendererKeys: [MOBILE_SERVICE_RENDERER_KEY],
    itemKinds: ["serviceStop", "stopOverflow"],
    toView(model) {
      const source = model.source;
      try {
        return source.detail ? buildDetailView(source.detail, timeZone) : buildIndexView(source.index, timeZone);
      } catch {
        return null;
      }
    },
    toCardProps: toMobileServiceCardProps,
    columns: mobileServiceColumns,
    controlsDefaults: { enabled: ["search", "dateRange", "metricTiles", "sort", "groupBy", "viewMode", "displayPreferences", "activeChips"] },
    metricTiles: MOBILE_SERVICE_METRIC_TILES,
    sortOptions: MOBILE_SERVICE_SORT_OPTIONS,
    defaultSort: { sortBy: "departure", sortOrder: "asc" },
    groupOptions: [
      { key: "none", label: "None", availability: "always" },
      { key: "technician", label: "Technician", availability: "always", emptyTitle: "Unassigned" }
    ],
    dateRangePresets: MOBILE_SERVICE_DATE_RANGE_PRESETS,
    displayPreferences: MOBILE_SERVICE_DISPLAY_PREFERENCES,
    defaultPageSize: 25,
    searchFields: MOBILE_SERVICE_SEARCH_FIELDS
  };
};

// src/products/mobile-service/renderers.ts
import { createElement as createElement4 } from "react";

// src/products/mobile-service/items/ServiceOrderRow.tsx
import { Box as Box42, Typography as Typography21 } from "@mui/material";
import { jsx as jsx57, jsxs as jsxs43 } from "react/jsx-runtime";
var ServiceOrderRow = ({ order }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs43(Box42, { sx: { display: "flex", alignItems: "baseline", gap: "8px", minWidth: 0 }, children: [
    /* @__PURE__ */ jsxs43(Typography21, { component: "span", sx: { flex: 1, minWidth: 0, fontSize: "12px", lineHeight: 1.25, color: tokens.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: [
      /* @__PURE__ */ jsx57(Box42, { component: "span", sx: { color: tokens.text.primary, fontWeight: 600 }, children: order.vehicleLine }),
      ` \xB7 ${buildOrderRowNote(order)}`
    ] }),
    /* @__PURE__ */ jsx57(Typography21, { component: "span", sx: { flex: "none", fontSize: "10px", lineHeight: 1, color: tokens.text.muted, whiteSpace: "nowrap" }, children: resolveOrderBadge(order.status) })
  ] });
};
var ServiceOrderRow_default = ServiceOrderRow;

// src/products/mobile-service/items/ServiceStopConfirmationChip.tsx
import { Box as Box43 } from "@mui/material";
import { jsx as jsx58 } from "react/jsx-runtime";
var ServiceStopConfirmationChip = () => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx58(
    Box43,
    {
      component: "span",
      sx: {
        display: "inline-flex",
        alignItems: "center",
        borderRadius: `${tokens.radii.pill}px`,
        px: "6px",
        py: "3px",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        bgcolor: tokens.semantic.warning,
        color: tokens.text.onBadge
      },
      children: "Awaiting confirmation"
    }
  );
};
var ServiceStopConfirmationChip_default = ServiceStopConfirmationChip;

// src/products/mobile-service/items/OrderItemServiceStop.tsx
import { jsx as jsx59 } from "react/jsx-runtime";
var OrderItemServiceStop = ({ stop, showOrders }) => {
  const showInnerList = showOrders && stop.isFleet && stop.orders.length > 0;
  return /* @__PURE__ */ jsx59(
    OrderItemStopBase_default,
    {
      density: "dense",
      marker: { state: resolveStopMarkerState(stop.status), position: stop.position },
      name: stop.customerName,
      note: buildStopNote(stop),
      tooltip: buildStopTooltip(stop),
      thumbnail: stop.photo ? /* @__PURE__ */ jsx59(OrderPhotoTile_default, { photo: stop.photo }) : void 0,
      trailing: stop.confirmation === "awaiting" ? /* @__PURE__ */ jsx59(ServiceStopConfirmationChip_default, {}) : void 0,
      children: showInnerList ? stop.orders.map((order) => /* @__PURE__ */ jsx59(ServiceOrderRow_default, { order }, order.id)) : null
    }
  );
};
var OrderItemServiceStop_default = OrderItemServiceStop;

// src/products/mobile-service/renderers.ts
var ServiceStopItem = ({ props }) => createElement4(OrderItemServiceStop_default, props);
var mobileServiceRenderers = {
  items: { serviceStop: ServiceStopItem }
};

// src/products/mobile-service/index.ts
var mobileServicePlugin = {
  product: "mobile-service",
  adapter: mobileServiceOrderAdapter,
  resolve: resolveMobileServiceOrders,
  renderers: mobileServiceRenderers
};

// src/products/guest-rides/constants/guestRideStatus.ts
var GUEST_RIDE_STATUS = {
  NEW: "new",
  PENDING: "pending",
  BOOKED: "booked",
  DRIVER_ENROUTE: "driver_enroute",
  DRIVER_ARRIVED: "driver_arrived",
  RIDING: "riding",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  FAILED: "failed",
  DELETED: "deleted"
};
var STATUS_PHASE = {
  new: "queued",
  failed: "queued",
  pending: "active",
  booked: "active",
  driver_enroute: "active",
  driver_arrived: "active",
  riding: "active",
  completed: "completed",
  cancelled: "cancelled",
  deleted: "cancelled"
};
var resolveRidePhase2 = (status) => STATUS_PHASE[status] ?? "queued";
var statusesInPhase = (phase) => Object.values(GUEST_RIDE_STATUS).filter((status) => STATUS_PHASE[status] === phase);
var GUEST_RIDE_UPCOMING_STATUSES = statusesInPhase("queued");
var GUEST_RIDE_ACTIVE_STATUSES = statusesInPhase("active");
var GUEST_RIDE_HISTORY_STATUSES = [GUEST_RIDE_STATUS.COMPLETED, GUEST_RIDE_STATUS.CANCELLED];
var GUEST_RIDE_DELETED_STATUSES = [GUEST_RIDE_STATUS.DELETED];
var isRideAtOrPastPickup = (status) => status === GUEST_RIDE_STATUS.RIDING || status === GUEST_RIDE_STATUS.COMPLETED || status === GUEST_RIDE_STATUS.CANCELLED || status === GUEST_RIDE_STATUS.FAILED;
var resolveRideStatusToken = (status) => status === GUEST_RIDE_STATUS.FAILED ? ORDER_STATUS_TOKEN.CANCEL_PENDING : resolveOrderStatusToken(resolveRidePhase2(status));
var STATUS_BADGE = {
  new: "Ready to be Called",
  pending: "Pending",
  booked: "Booked",
  driver_enroute: "Driver Enroute",
  driver_arrived: "Driver Arrived",
  riding: "Riding",
  completed: "Completed",
  cancelled: "Canceled",
  failed: "Failed",
  deleted: "Deleted"
};
var resolveRideBadge = (status) => STATUS_BADGE[status] ?? formatStatusWord(status);
var STATUS_STAGE = {
  new: 0,
  pending: 0,
  failed: 0,
  deleted: 0,
  cancelled: 0,
  booked: 1,
  driver_enroute: 2,
  driver_arrived: 3,
  riding: 4,
  completed: 5
};
var resolveRideStage = (status) => STATUS_STAGE[status] ?? 0;
var RIDE_PROVIDER = {
  UBER: "uber",
  LYFT: "lyft",
  AUTO: "auto"
};
var PROVIDER_LABEL = {
  uber: "Uber",
  lyft: "Lyft",
  auto: "Auto"
};
var resolveProviderLabel = (provider) => PROVIDER_LABEL[provider] ?? formatStatusWord(provider);
var GUEST_RIDE_ACTION = {
  CALL_RIDE: "callRide",
  RETRY_RIDE: "retryRide",
  CANCEL_RIDE: "cancelRide",
  SHARE_LINK: "shareLink",
  VIEW_DETAILS: "viewDetails",
  EDIT_RIDE: "editRide",
  DELETE_RIDE: "deleteRide",
  REVERT_RIDE: "revertRide"
};

// src/products/guest-rides/actionDescriptors.ts
import { BiLink, BiLinkExternal as BiLinkExternal2, BiPencil, BiPhoneCall, BiTrash, BiUndo as BiUndo2, BiXCircle } from "react-icons/bi";
var buildGuestRideActionDescriptors = (view) => {
  const { status } = view;
  const isDeleted = status === GUEST_RIDE_STATUS.DELETED;
  const descriptors = [];
  if (status === GUEST_RIDE_STATUS.NEW) descriptors.push({ key: GUEST_RIDE_ACTION.CALL_RIDE, label: "Call Ride", icon: BiPhoneCall, placement: "bar" });
  if (status === GUEST_RIDE_STATUS.FAILED) descriptors.push({ key: GUEST_RIDE_ACTION.RETRY_RIDE, label: "Retry Ride", icon: BiPhoneCall, placement: "bar" });
  const canCancel = !isDeleted && status !== GUEST_RIDE_STATUS.NEW && !isRideAtOrPastPickup(status) && Boolean(view.ridehail?.externalId);
  if (canCancel) descriptors.push({ key: GUEST_RIDE_ACTION.CANCEL_RIDE, label: "Cancel Ride", icon: BiXCircle, placement: "bar", danger: true });
  if (!isDeleted) descriptors.push({ key: GUEST_RIDE_ACTION.SHARE_LINK, label: "Share Guest Link", icon: BiLink, placement: "menu" });
  descriptors.push({ key: GUEST_RIDE_ACTION.VIEW_DETAILS, label: "View Details", icon: BiLinkExternal2, placement: "menu" });
  if (!isDeleted && (status === GUEST_RIDE_STATUS.NEW || status === GUEST_RIDE_STATUS.FAILED)) descriptors.push({ key: GUEST_RIDE_ACTION.EDIT_RIDE, label: "Edit Ride", icon: BiPencil, placement: "menu" });
  if (status === GUEST_RIDE_STATUS.NEW) descriptors.push({ key: GUEST_RIDE_ACTION.DELETE_RIDE, label: "Delete Ride", icon: BiTrash, placement: "menu", danger: true, dividerBefore: true });
  if (isDeleted) descriptors.push({ key: GUEST_RIDE_ACTION.REVERT_RIDE, label: "Revert Ride", icon: BiUndo2, placement: "menu" });
  return descriptors;
};

// src/products/guest-rides/items/rideText.ts
var ROUTE_ARROW2 = "\u2192";
var buildRouteLine = (ride) => `${ride.pickupAddress} ${ROUTE_ARROW2} ${ride.dropoffAddress}`;
var buildDriverLine = (ride) => {
  const driver = ride.ridehail?.driver;
  if (!driver) return "No driver assigned";
  return [driver.name, ride.ridehail?.vehicleLine, ride.ridehail?.licensePlate].filter(Boolean).join(" \xB7 ");
};
var formatRideDuration = (minutes) => minutes === null ? null : `${minutes} min`;
var buildRouteTooltip2 = (ride) => [
  { kind: "row", key: "Pickup", value: ride.pickupAddress },
  { kind: "row", key: "Dropoff", value: ride.dropoffAddress }
];

// src/products/guest-rides/slots.ts
var TIME_PENDING_MARK2 = "N/A";
var TIME_ABSENT_MARK2 = "\u2014";
var { NEW, PENDING, BOOKED, DRIVER_ENROUTE, DRIVER_ARRIVED, RIDING, COMPLETED: COMPLETED2, CANCELLED: CANCELLED2, DELETED } = GUEST_RIDE_STATUS;
var isRideLive = (view) => isLivePhase(resolveRidePhase2(view.status));
var isRideSettled = (view) => view.status === COMPLETED2 || view.status === CANCELLED2 || view.status === DELETED;
var toStatusBarGroup3 = (view) => ({
  status: resolveRideStatusToken(view.status),
  badge: resolveRideBadge(view.status),
  isLive: isRideLive(view)
});
var STAGE_BARS = [
  { key: "booked", label: "Booked", stage: 1 },
  { key: "enroute", label: "En route", stage: 2 },
  { key: "arrived", label: "Arrived", stage: 3 },
  { key: "riding", label: "Riding", stage: 4 }
];
var toTrackerGroup3 = (view) => {
  const stage = resolveRideStage(view.status);
  const live = isRideLive(view);
  const bars = STAGE_BARS.map((bar) => ({
    key: bar.key,
    isReached: stage >= bar.stage,
    isDrivingToward: live && bar.stage === stage + 1,
    slipFraction: 0,
    tooltip: [{ kind: "title", text: bar.label }]
  }));
  return { bars, status: resolveRideStatusToken(view.status), statusLine: resolveRideBadge(view.status), isLive: live, isChainBroken: false };
};
var providerLabelFor = (view) => resolveProviderLabel(view.ridehail?.provider ?? view.requestedProvider);
var buildIdentityTooltip2 = (view) => {
  const content = [{ kind: "title", text: view.guestName }];
  content.push({ kind: "row", key: "Phone", value: view.guestPhone });
  content.push({ kind: "row", key: "Provider", value: providerLabelFor(view) });
  if (view.rideType) content.push({ kind: "row", key: "Ride type", value: view.rideType });
  if (view.referenceNum) content.push({ kind: "row", key: "Reference", value: view.referenceNum });
  return content;
};
var toIdentityGroup2 = (view) => ({
  displayName: view.guestName,
  ids: [view.id],
  tooltip: buildIdentityTooltip2(view)
});
var toMilestoneGroup3 = (view) => {
  const ride = view.ridehail;
  switch (view.status) {
    case NEW:
      return { value: view.requested.display ?? TIME_PENDING_MARK2, subtext: { text: "Requested", tone: "neutral" }, tooltip: null };
    case PENDING:
    case BOOKED:
    case DRIVER_ENROUTE:
    case DRIVER_ARRIVED:
      return ride?.eta.display ? { value: ride.eta.display, subtext: { text: "ETA", tone: "neutral" }, tooltip: null } : { value: ride?.called.display ?? TIME_PENDING_MARK2, subtext: { text: "Called", tone: "neutral" }, tooltip: null };
    case RIDING:
      return { value: ride?.pickup.display ?? TIME_PENDING_MARK2, subtext: { text: "Picked up", tone: "neutral" }, tooltip: null };
    case COMPLETED2:
      return { value: ride?.dropoff.display ?? TIME_PENDING_MARK2, subtext: { text: "Dropped off", tone: "neutral" }, tooltip: null };
    case CANCELLED2:
    case DELETED:
    default:
      return { value: TIME_ABSENT_MARK2, subtext: { text: resolveRideBadge(view.status), tone: "neutral" }, tooltip: null };
  }
};
var toDriverGroup3 = (view) => {
  const isSettled = isRideSettled(view);
  const driver = view.ridehail?.driver;
  if (!driver) return { showPill: false, driverName: null, avatarUrl: null, state: "locked", isSettled, tooltip: null };
  return { showPill: true, driverName: driver.name, avatarUrl: driver.photoUrl, state: "locked", isSettled, tooltip: [{ kind: "description", text: `${driver.name} \xB7 ${providerLabelFor(view)} driver` }] };
};

// src/products/guest-rides/cardProps.ts
var GUEST_RIDE_RENDERER_KEY = "guestRide";
var NO_TIME = { iso: null, display: null };
var toTimeSlot3 = (key, label, time) => ({
  key,
  label,
  value: time.display ?? TIME_PENDING_MARK2,
  treatment: time.display ? "actual" : "pending",
  tooltip: null
});
var toTimesGroup2 = (view) => ({
  leftColumn: [toTimeSlot3("requested", "Requested", view.requested), toTimeSlot3("called", "Called", view.ridehail?.called ?? NO_TIME)],
  rightColumn: [toTimeSlot3("pickup", "Picked up", view.ridehail?.pickup ?? NO_TIME), toTimeSlot3("dropoff", "Dropped off", view.ridehail?.dropoff ?? NO_TIME)]
});
var toDetailRows2 = (view) => {
  const rows = [];
  if (view.rideType) rows.push({ key: "rideType", label: "Ride type", value: view.rideType });
  if (view.referenceNum) rows.push({ key: "reference", label: "Reference", value: view.referenceNum });
  rows.push({ key: "provider", label: "Provider", value: resolveProviderLabel(view.ridehail?.provider ?? view.requestedProvider) });
  const duration = formatRideDuration(view.durationMinutes);
  if (duration) rows.push({ key: "duration", label: "Duration", value: duration });
  return rows;
};
var toRideItems = (view) => [{ kind: "ride", key: `ride:${view.id}`, props: { ride: view } }];
var toGuestRideCardProps = (view) => ({
  rendererKey: GUEST_RIDE_RENDERER_KEY,
  statusBar: toStatusBarGroup3(view),
  tracker: toTrackerGroup3(view),
  header: { ...toIdentityGroup2(view), milestone: toMilestoneGroup3(view) },
  items: toRideItems(view),
  times: toTimesGroup2(view),
  details: { rows: toDetailRows2(view) },
  driver: toDriverGroup3(view),
  actionBar: buildGuestRideActionDescriptors(view),
  state: {
    isHydrated: view.isHydrated,
    isSettled: isRideSettled(view),
    isCancelDeclared: view.status === GUEST_RIDE_STATUS.CANCELLED,
    cancelReason: null,
    isInDanger: false
  }
});

// src/products/guest-rides/queries/guestRideIndex.query.generated.ts
import { resolveExecutor as resolveExecutor7 } from "@hopdrive/sdk-core";
var GuestRideIndexDocument = { "__text": "query guestRideIndex($where: guestrides_bool_exp!, $limit: Int!) {\n  guestrides(\n    where: $where\n    order_by: [{created_at: desc}, {id: desc}]\n    limit: $limit\n  ) {\n    id\n    status\n    guest_name\n    guest_phone\n    pickup_address\n    dropoff_address\n    provider\n    config\n    customer_id\n    created_at\n    updated_at\n    ridehails(order_by: {created_at: desc}, limit: 1) {\n      id\n      status\n      provider\n      driver_name\n      pickup_at\n      dropoff_at\n      created_at\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "guestRideIndex" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "guestrides_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "guestrides" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "created_at" }, "value": { "kind": "EnumValue", "value": "desc" } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "desc" } }] }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guest_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guest_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dropoff_address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "provider" } }, { "kind": "Field", "name": { "kind": "Name", "value": "config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updated_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ridehails" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "created_at" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "provider" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dropoff_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }] } }] } }] } }] };

// src/products/guest-rides/queries/guestRidePage.query.generated.ts
import { resolveExecutor as resolveExecutor8 } from "@hopdrive/sdk-core";
var GuestRidePageDocument = { "__text": "query guestRidePage($rideIds: [bigint!]!) {\n  guestrides(where: {id: {_in: $rideIds}}) {\n    id\n    status\n    guest_name\n    guest_phone\n    pickup_address\n    dropoff_address\n    provider\n    config\n    customer_id\n    created_at\n    updated_at\n    ridehails(order_by: {created_at: desc}, limit: 1) {\n      id\n      status\n      provider\n      external_id\n      driver_name\n      driver_phone\n      driver_photo_url\n      vehicle_make\n      vehicle_model\n      vehicle_year\n      vehicle_license_plate\n      pickup_at\n      dropoff_at\n      created_at\n      tracking_link\n      quality_rejection_reason\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "guestRidePage" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "rideIds" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "guestrides" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "rideIds" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guest_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "guest_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dropoff_address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "provider" } }, { "kind": "Field", "name": { "kind": "Name", "value": "config" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updated_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ridehails" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "created_at" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "provider" } }, { "kind": "Field", "name": { "kind": "Name", "value": "external_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_photo_url" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_license_plate" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dropoff_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tracking_link" } }, { "kind": "Field", "name": { "kind": "Name", "value": "quality_rejection_reason" } }] } }] } }] } }] };

// src/products/guest-rides/queries/guestRideStream.subscription.generated.ts
var GuestRideStreamDocument = { "__text": "subscription guestRideStream($cursor: [guestrides_stream_cursor_input]!, $where: guestrides_bool_exp!) {\n  guestrides_stream(cursor: $cursor, batch_size: 50, where: $where) {\n    id\n    status\n    customer_id\n    created_at\n    updated_at\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "subscription", "name": { "kind": "Name", "value": "guestRideStream" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "cursor" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "guestrides_stream_cursor_input" } } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "guestrides_bool_exp" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "guestrides_stream" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "cursor" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "cursor" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "batch_size" }, "value": { "kind": "IntValue", "value": "50" } }, { "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updated_at" } }] } }] } }] };

// src/products/guest-rides/guestRideOrderAdapter.ts
var GUEST_RIDE_INDEX_CAP = 2e3;
var GUEST_RIDE_PAGE_SIZES = [10, 25, 50, 100];
var GUEST_RIDE_PAGE_POLL_INTERVAL_MS = 6e4;
var buildWhere = (customerIds, window) => {
  const arms = [{ customer_id: { _in: customerIds } }, { status: { _nin: [...GUEST_RIDE_DELETED_STATUSES] } }];
  if (window) {
    arms.push({
      _or: [{ created_at: { _gte: window.startIso, _lte: window.endIso } }, { ridehails: { created_at: { _gte: window.startIso, _lte: window.endIso } } }]
    });
  }
  return { _and: arms };
};
var buildIndexVariables3 = ({ customerIds, window, limit }) => ({
  where: buildWhere(customerIds, window),
  limit
});
var buildPageVariables3 = (ids) => ({ rideIds: ids });
var buildStreamVariables2 = ({ customerIds, window, cursor }) => ({
  cursor: [{ initial_value: { updated_at: cursor }, ordering: "ASC" }],
  where: buildWhere(customerIds, window)
});
var rideAnchorMs = (createdAt) => {
  if (!createdAt) return null;
  const ms = Date.parse(createdAt);
  return Number.isNaN(ms) ? null : ms;
};
var isRideStatusLive = (status) => isLivePhase(resolveRidePhase2(status));
var toModel2 = (row2) => {
  const phase = resolveRidePhase2(row2.status);
  const anchorMs = rideAnchorMs(row2.created_at);
  return {
    key: orderKey("guest-rides", row2.id),
    product: "guest-rides",
    id: row2.id,
    customerId: row2.customer_id,
    rendererKey: GUEST_RIDE_RENDERER_KEY,
    phase,
    metric: metricForPhase(phase),
    anchorMs,
    promiseMs: anchorMs,
    operationalModel: "hopdrive-managed",
    isLive: isRideStatusLive(row2.status),
    isHydrated: false,
    flags: { isUnconfirmed: false, hasUnreadMessages: false },
    sortValues: { status: ORDER_PHASE_ORDER.indexOf(phase), order: row2.id, guest: row2.guest_name },
    groupKeys: { guest: row2.guest_name },
    source: { index: row2, detail: null }
  };
};
var adaptIndex3 = (data, _context) => {
  const rows = data?.guestrides ?? [];
  const capReached = rows.length > GUEST_RIDE_INDEX_CAP;
  const capped = capReached ? rows.slice(0, GUEST_RIDE_INDEX_CAP) : rows;
  return { entries: capped.map((row2) => ({ kind: "order", model: toModel2(row2) })), capReached };
};
var adaptPage3 = (data) => data?.guestrides ?? [];
var mergePage3 = (entries, page) => {
  const byId = new Map(page.map((ride) => [ride.id, ride]));
  return entries.map((entry) => {
    if (entry.kind !== "order" || entry.model.product !== "guest-rides") return entry;
    const model = entry.model;
    const detail = byId.get(model.id);
    if (!detail) return entry;
    return { kind: "order", model: { ...model, isHydrated: true, source: { ...model.source, detail } } };
  });
};
var guestRideOrderAdapter = {
  product: "guest-rides",
  dayColumns: ["created_at"],
  indexCap: GUEST_RIDE_INDEX_CAP,
  pageSizes: GUEST_RIDE_PAGE_SIZES,
  pollMs: GUEST_RIDE_PAGE_POLL_INTERVAL_MS,
  live: "stream",
  documents: { index: GuestRideIndexDocument, page: GuestRidePageDocument, stream: GuestRideStreamDocument },
  buildIndexVariables: buildIndexVariables3,
  buildPageVariables: buildPageVariables3,
  buildStreamVariables: buildStreamVariables2,
  adaptIndex: adaptIndex3,
  adaptPage: adaptPage3,
  mergePage: mergePage3
};

// src/products/guest-rides/resolveGuestRideOrders.ts
import { PiHandWavingFill } from "react-icons/pi";

// src/products/guest-rides/columns.ts
var requestedMs = (view) => view.requested.iso ? Date.parse(view.requested.iso) : null;
var providerCellProps = (view) => {
  const provider = view.ridehail?.provider ?? view.requestedProvider;
  return { provider, label: resolveProviderLabel(provider) };
};
var guestRidesColumns = [
  {
    name: "guest",
    label: "Guest",
    tooltip: "The guest and their phone",
    minWidth: 150,
    sortable: true,
    sortValue: (view) => view.guestName,
    cell: "guest",
    cellProps: (view) => ({ name: view.guestName, phone: view.guestPhone }),
    mobile: "pair"
  },
  {
    name: "route",
    label: "Route",
    tooltip: "The pickup and dropoff",
    minWidth: 200,
    cell: "route",
    cellProps: (view) => ({
      stops: [
        { key: "pickup", name: view.pickupAddress },
        { key: "dropoff", name: view.dropoffAddress }
      ],
      tooltip: buildRouteTooltip2(view)
    }),
    mobile: "pair"
  },
  {
    name: "date",
    label: "Date",
    tooltip: "When the ride was requested",
    minWidth: 130,
    sortable: true,
    sortValue: requestedMs,
    cell: "promise",
    cellProps: (view) => view.requested.display ? { value: view.requested.display, tooltip: null } : null,
    mobile: "pair"
  },
  {
    name: "status",
    label: "Status",
    tooltip: "Where the ride is in its lifecycle",
    minWidth: 110,
    sortable: true,
    // Lifecycle rank, not the badge word.
    sortValue: (view) => ORDER_PHASE_ORDER.indexOf(view.phase),
    cell: "status",
    cellProps: (view) => ({ statusBar: toStatusBarGroup3(view), tracker: toTrackerGroup3(view) }),
    mobile: "pair",
    mobileValueSx: { "& > div": { alignItems: "flex-end" }, "& > div > :last-child": { alignSelf: "stretch" } }
  },
  {
    name: "provider",
    label: "Provider",
    tooltip: "The ridehail provider",
    minWidth: 110,
    cell: "providerMark",
    cellProps: providerCellProps,
    mobile: "pair"
  },
  {
    name: "duration",
    label: "Duration",
    align: "right",
    minWidth: 90,
    sortable: true,
    sortValue: (view) => view.durationMinutes,
    cell: "text",
    text: (view) => ({ value: formatRideDuration(view.durationMinutes) ?? "" }),
    mobile: "pair"
  },
  { name: "actions", label: "Actions", align: "right", pinned: "right", mobile: "bar", cell: "actions" }
];

// src/products/guest-rides/resolveGuestRideOrders.ts
var clockParts3 = (ms, timeZone) => new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", hour12: true }).format(ms);
var formatRideClock = (iso, timeZone) => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const date = new Intl.DateTimeFormat("en-US", { timeZone, month: "numeric", day: "numeric" }).format(ms);
  return `${date}, ${clockParts3(ms, timeZone)}`;
};
var toTime2 = (iso, timeZone) => ({ iso, display: formatRideClock(iso, timeZone) });
var NO_TIME2 = { iso: null, display: null };
var toRequestedProvider = (provider) => provider === "lyft" ? "lyft" : provider === "uber" ? "uber" : "auto";
var toLegProvider = (provider) => provider === "lyft" ? "lyft" : "uber";
var vehicleLine = (year, make, model) => [year, make, model].filter(Boolean).join(" ") || null;
var toIndexLeg = (rh, timeZone) => ({
  provider: toLegProvider(rh.provider),
  externalId: null,
  driver: rh.driver_name ? { name: rh.driver_name, phone: null, photoUrl: null } : null,
  vehicleLine: null,
  licensePlate: null,
  called: toTime2(rh.created_at, timeZone),
  eta: NO_TIME2,
  pickup: toTime2(rh.pickup_at, timeZone),
  dropoff: toTime2(rh.dropoff_at, timeZone),
  trackingUrl: null,
  rejectionReason: null
});
var buildIndexView2 = (row2, timeZone) => {
  const rh = row2.ridehails[0] ?? null;
  return {
    id: row2.id,
    status: row2.status,
    phase: resolveRidePhase2(row2.status),
    guestName: row2.guest_name,
    guestPhone: row2.guest_phone,
    pickupAddress: row2.pickup_address ?? "",
    dropoffAddress: row2.dropoff_address ?? "",
    requestedProvider: toRequestedProvider(row2.provider),
    rideType: null,
    referenceNum: null,
    requested: toTime2(row2.created_at, timeZone),
    ridehail: rh ? toIndexLeg(rh, timeZone) : null,
    durationMinutes: null,
    isHydrated: false
  };
};
var toDetailLeg = (rh, timeZone) => ({
  provider: toLegProvider(rh.provider),
  externalId: rh.external_id,
  driver: rh.driver_name ? { name: rh.driver_name, phone: rh.driver_phone, photoUrl: rh.driver_photo_url } : null,
  vehicleLine: vehicleLine(rh.vehicle_year, rh.vehicle_make, rh.vehicle_model),
  licensePlate: rh.vehicle_license_plate,
  called: toTime2(rh.created_at, timeZone),
  eta: NO_TIME2,
  pickup: toTime2(rh.pickup_at, timeZone),
  dropoff: toTime2(rh.dropoff_at, timeZone),
  trackingUrl: rh.tracking_link,
  rejectionReason: rh.quality_rejection_reason
});
var buildDetailView2 = (row2, timeZone) => {
  const rh = row2.ridehails[0] ?? null;
  return {
    id: row2.id,
    status: row2.status,
    phase: resolveRidePhase2(row2.status),
    guestName: row2.guest_name,
    guestPhone: row2.guest_phone,
    pickupAddress: row2.pickup_address ?? "",
    dropoffAddress: row2.dropoff_address ?? "",
    requestedProvider: toRequestedProvider(row2.provider),
    rideType: null,
    referenceNum: null,
    requested: toTime2(row2.created_at, timeZone),
    ridehail: rh ? toDetailLeg(rh, timeZone) : null,
    durationMinutes: null,
    isHydrated: true
  };
};
var GUEST_RIDE_METRIC_TILES = [
  { key: "all", label: "All Rides", inAll: true },
  { key: "upcoming", label: "Upcoming", inAll: true },
  { key: "active", label: "Active", inAll: true },
  { key: "completed", label: "Completed", inAll: true },
  { key: "cancelled", label: "Canceled", inAll: true }
];
var GUEST_RIDE_SORT_OPTIONS = [
  { key: "requested", label: "Requested", anchor: true },
  { key: "status", label: "Status", missingValue: "highest" },
  { key: "order", label: "Ride ID" },
  { key: "guest", label: "Guest" }
];
var GUEST_RIDE_DATE_RANGE_PRESETS = [
  { key: "today", label: "Today", group: 0 },
  { key: "yesterday", label: "Yesterday", group: 0 },
  { key: "thisweek", label: "This Week", group: 1 },
  { key: "lastweek", label: "Last Week", group: 1 },
  { key: "thismonth", label: "This Month", group: 2 },
  { key: "lastmonth", label: "Last Month", group: 2 },
  { key: "custom", label: "Custom", group: 3 }
];
var GUEST_RIDE_SEARCH_FIELDS = ["id", "guest_name", "guest_phone", "pickup_address", "dropoff_address"];
var resolveGuestRideOrders = (context) => {
  const timeZone = context.rooftop.timezone ?? "UTC";
  const referenceLabel = context.referenceNumLabel ?? "Reference";
  return {
    identity: { key: "guest-rides", label: "Guest Rides", icon: PiHandWavingFill },
    operationalModel: "hopdrive-managed",
    copy: {
      unit: { singular: "ride", plural: "rides" },
      referenceLabel,
      searchPlaceholder: "Search rides\u2026",
      capNotice: "This range holds more rides than the display shows at once. Narrow the date range to see them all.",
      empty: {
        noOrders: { title: "No rides in this range", description: "Widen the date range to see rides from other days." },
        filtered: { title: "No rides match your filters", description: "Try adjusting your search or filter criteria." }
      }
    },
    rendererKeys: [GUEST_RIDE_RENDERER_KEY],
    itemKinds: ["ride"],
    toView(model) {
      const source = model.source;
      try {
        return source.detail ? buildDetailView2(source.detail, timeZone) : buildIndexView2(source.index, timeZone);
      } catch {
        return null;
      }
    },
    toCardProps: toGuestRideCardProps,
    columns: guestRidesColumns,
    controlsDefaults: { enabled: ["search", "dateRange", "metricTiles", "sort", "viewMode", "activeChips"] },
    metricTiles: GUEST_RIDE_METRIC_TILES,
    sortOptions: GUEST_RIDE_SORT_OPTIONS,
    defaultSort: { sortBy: "requested", sortOrder: "desc" },
    groupOptions: [{ key: "none", label: "None", availability: "always" }],
    dateRangePresets: GUEST_RIDE_DATE_RANGE_PRESETS,
    displayPreferences: [],
    defaultPageSize: 25,
    searchFields: GUEST_RIDE_SEARCH_FIELDS
  };
};

// src/products/guest-rides/renderers.ts
import { createElement as createElement5 } from "react";

// src/products/guest-rides/cells.tsx
import { Box as Box45 } from "@mui/material";

// src/products/guest-rides/items/ProviderMark.tsx
import { Box as Box44 } from "@mui/material";
import { jsx as jsx60 } from "react/jsx-runtime";
var UBER_VIEWBOX = "0 0 250 86.86";
var UBER_PATHS = [
  "M77.24,85.35V.11H89.38V30.86c8.77-7.95,18.51-10.69,29.54-8.23A31,31,0,0,1,137.83,35,32.47,32.47,0,0,1,89.4,77.92v7.43H77.24Zm33.42-52.48c-11.41-.45-21.53,9.3-21.48,21.43a21.35,21.35,0,1,0,42.69.06C132,42.29,121.81,32.47,110.66,32.87Z",
  "M.15.09H12.87V2.47q0,25.29,0,50.58c0,6.14,1.79,11.71,6.13,16.23,10.41,10.84,29.52,6.56,34.16-7.74a30.17,30.17,0,0,0,1.48-9c.14-16.66.07-33.33.07-50V0c4.07,0,8,0,11.93,0,.28,0,.7.5.79.83a7.47,7.47,0,0,1,.06,1.71V82.86c0,2.49,0,2.49-2.5,2.49-2.68,0-5.37-.09-8,0-1.63.08-2.34-.33-2.18-2.1s0-3.42,0-5.47c-1.72,1.34-3.15,2.56-4.7,3.61-8,5.41-16.92,6.64-26.17,4.35C13.31,83.14,5.85,76.55,2.27,66.12A38.57,38.57,0,0,1,.14,54.34C-.09,36.72,0,19.09,0,1.46,0,1.09.1.71.15.09Z",
  "M212.29,58.29H161.42c1.68,7.9,5.82,13.57,13.15,16.19,10,3.58,18.5.69,25.42-7.63l8.82,6.48a32.7,32.7,0,0,1-12,10.07c-12.66,6.33-29.44,3.37-39.18-6.89C141.27,59.24,149,30,171.8,23.21c18-5.33,36.28,5.17,39.55,24.43C211.93,51.05,212,54.56,212.29,58.29Zm-13-9.9a18.74,18.74,0,0,0-19.13-15.68c-9.29.42-18.45,8.42-18.12,15.68Z",
  "M231.17,85.24H218.93V23.16h12v6.67l.42.29c4.58-6.65,11.14-8,18.64-7.4V33.89c-2.43.15-4.84.15-7.21.47-5.42.72-8.88,4-10.51,9a24.7,24.7,0,0,0-1.06,7.32c-.11,10.72-.05,21.44-.05,32.17Z"
];
var LYFT_VIEWBOX = "0 0 250 175.94";
var LYFT_PATHS = [
  "M137.25,92.15c0-13.44-.09-26.87,0-40.31.09-10,2.09-19.46,7.54-28C153.47,10.28,165.6,2.26,181.73.37c15.05-1.76,28.07,2.79,39.13,12.73a45.24,45.24,0,0,1,14.28,23.58c.47,2,1.3,2.89,3.46,2.74,2.66-.19,5.34,0,8-.06,1.56,0,2.24.53,2.23,2.15q0,16.16,0,32.31c0,1.78-.72,2.34-2.39,2.28a63.28,63.28,0,0,0-7.42.07c-.75.06-2,1-2,1.58a75.44,75.44,0,0,0,.4,11.94c.83,5.69,4.54,9.1,10.11,10.21,2,.39,2.48,1.17,2.47,3,0,10.38-.07,20.77,0,31.15,0,2.17-.72,2.88-2.82,2.71-18.31-1.47-31.88-10.31-40.89-26.24-4.48-7.91-6-16.58-6.12-25.52-.11-11.72,0-23.44,0-35.16,0-8.15-4.63-13.06-12.64-13.46-7.12-.36-12.6,4.39-13.37,12-.37,3.59-.09,7.24-.16,10.86,0,1.77,1.05,1.94,2.43,1.93,4.57,0,9.15.09,13.72-.05,2.17-.07,2.77.74,2.75,2.82q-.12,15.57,0,31.16c0,2.06-.57,2.9-2.74,2.83-4.38-.13-8.77.07-13.14-.09-2.41-.09-3.16.78-3.06,3.12a37.76,37.76,0,0,1-5.76,22c-6.58,10.54-16.55,14.62-28.64,14.33-2.23-.06-2.29-1.45-2.29-3.11Q137.27,113.16,137.25,92.15Z",
  "M56.05,139.55c5.46,1.07,10.51,2.46,15.66,3,7.72.8,15.08-.51,20.6-6.66,1.33-1.48,2-3.58,2.94-5.4l-.78-.49a9.32,9.32,0,0,0-1.51.66c-10.36,6.95-21.3,6.43-32.15,1.82-10-4.24-15-12.75-15.51-23.2-.73-16-.52-32-.66-48-.06-6.48,0-13,0-19.44,0-1.85.59-2.53,2.47-2.52,10.57.06,21.15.09,31.73,0,2.37,0,2.57,1.13,2.57,3q-.06,25.31,0,50.61c0,4.31,1.93,6.71,5.49,7.07,4.24.42,7-2,7-6.28q0-25.59,0-51.18c0-3.7,0-3.7,3.79-3.7,9.91,0,19.82.06,29.73,0,2.4,0,3.35.51,3.34,3.14-.12,29.55.28,59.1-.31,88.64-.3,15.06-6.6,27.87-19.3,36.79-7.33,5.15-15.73,7.41-24.58,8.29-9.2.91-18.19-.27-27.14-2.31-2.41-.55-3.4-1.59-3.35-4.25C56.15,159.26,56.05,149.46,56.05,139.55Z",
  "M50,134.47C35.73,144.33,10.2,140.74,3,118.7A58,58,0,0,1,.16,101.35C-.1,69.72,0,38.09,0,6.46c0-3.69,0-3.69,3.8-3.69,10.09,0,20.19.1,30.28-.07,2.75,0,3.35.91,3.35,3.45q-.11,48.15,0,96.32c0,8.54,1,17,6.09,24.24C45.41,129.42,47.77,131.79,50,134.47Z"
];
var MARK = {
  uber: { viewBox: UBER_VIEWBOX, paths: UBER_PATHS, height: 14 },
  lyft: { viewBox: LYFT_VIEWBOX, paths: LYFT_PATHS, height: 18 }
};
var ProviderMark = ({ provider }) => {
  const tokens = useOrderTokens();
  if (provider !== RIDE_PROVIDER.UBER && provider !== RIDE_PROVIDER.LYFT) return null;
  const mark = MARK[provider];
  return /* @__PURE__ */ jsx60(
    Box44,
    {
      component: "svg",
      viewBox: mark.viewBox,
      role: "img",
      "aria-label": resolveProviderLabel(provider),
      sx: { display: "block", height: mark.height, width: "auto", color: tokens.text.primary, flex: "none" },
      children: mark.paths.map((d, index) => /* @__PURE__ */ jsx60("path", { d, fill: "currentColor" }, index))
    }
  );
};
var ProviderMark_default = ProviderMark;

// src/products/guest-rides/cells.tsx
import { jsx as jsx61, jsxs as jsxs44 } from "react/jsx-runtime";
var providerMarkCell = ({ column, view, tokens }) => {
  const props = column.cellProps?.(view);
  if (!props) return /* @__PURE__ */ jsx61(CellText, { color: tokens.text.labelFaded, children: CELL_EMPTY });
  return /* @__PURE__ */ jsxs44(Box45, { sx: { display: "inline-flex", alignItems: "center", gap: "6px", minWidth: 0 }, children: [
    /* @__PURE__ */ jsx61(ProviderMark_default, { provider: props.provider }),
    /* @__PURE__ */ jsx61(CellText, { children: props.label })
  ] });
};

// src/products/guest-rides/items/OrderItemRide.tsx
import { Box as Box46, Typography as Typography22 } from "@mui/material";
import { BiLinkExternal as BiLinkExternal3 } from "react-icons/bi";
import { jsx as jsx62, jsxs as jsxs45 } from "react/jsx-runtime";
var OrderItemRide = ({ ride }) => {
  const tokens = useOrderTokens();
  const provider = ride.ridehail?.provider ?? null;
  const hasMark = provider === RIDE_PROVIDER.UBER || provider === RIDE_PROVIDER.LYFT;
  const rejection = ride.ridehail?.rejectionReason ?? null;
  const trackingUrl = ride.ridehail?.trackingUrl ?? null;
  return /* @__PURE__ */ jsxs45(
    Box46,
    {
      sx: {
        display: "flex",
        alignItems: "stretch",
        overflow: "hidden",
        bgcolor: tokens.surfaces.legBackground,
        borderRadius: `${tokens.radii.md}px`,
        maxWidth: 480,
        height: 64
      },
      children: [
        hasMark ? /* @__PURE__ */ jsx62(Box46, { sx: { flex: "none", width: 52, display: "flex", alignItems: "center", justifyContent: "center" }, children: /* @__PURE__ */ jsx62(ProviderMark_default, { provider }) }) : null,
        /* @__PURE__ */ jsxs45(Box46, { sx: { flex: 1, minWidth: 0, p: "6px 8px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "4px", ...hasMark ? {} : { pl: "12px" } }, children: [
          /* @__PURE__ */ jsx62(OrderTooltip_default, { content: buildRouteTooltip2(ride), children: /* @__PURE__ */ jsx62(Typography22, { component: "span", sx: { display: "block", minWidth: 0, fontSize: "14px", fontWeight: 700, lineHeight: 1.1, color: tokens.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: buildRouteLine(ride) }) }),
          /* @__PURE__ */ jsx62(
            Typography22,
            {
              component: "span",
              sx: { display: "block", minWidth: 0, fontSize: "12px", lineHeight: 1.1, color: rejection ? tokens.semantic.error : tokens.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
              children: rejection ?? buildDriverLine(ride)
            }
          )
        ] }),
        trackingUrl ? /* @__PURE__ */ jsx62(Box46, { sx: { flex: "none", display: "flex", alignItems: "center", pr: "10px" }, children: /* @__PURE__ */ jsx62(
          Box46,
          {
            component: "a",
            href: trackingUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            "aria-label": "Open ride tracking",
            sx: {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: `${tokens.radii.pill}px`,
              color: tokens.text.secondary,
              textDecoration: "none",
              "&:hover": { color: tokens.text.primary, bgcolor: tokens.surfaces.chipNeutral }
            },
            children: /* @__PURE__ */ jsx62(BiLinkExternal3, { size: 15, "aria-hidden": true })
          }
        ) }) : null
      ]
    }
  );
};
var OrderItemRide_default = OrderItemRide;

// src/products/guest-rides/renderers.ts
var RideItem = ({ props }) => createElement5(OrderItemRide_default, props);
var guestRidesRenderers = {
  items: { ride: RideItem },
  cells: { providerMark: providerMarkCell }
};

// src/products/guest-rides/index.ts
var guestRidePlugin = {
  product: "guest-rides",
  adapter: guestRideOrderAdapter,
  resolve: resolveGuestRideOrders,
  renderers: guestRidesRenderers
};

// src/products/parts-delivery/queries/partsRunIndex.query.generated.ts
import { resolveExecutor as resolveExecutor9 } from "@hopdrive/sdk-core";
var PartsRunIndexDocument = { "__text": 'query partsRunIndex($scope: mobility_runs_bool_exp!, $window: mobility_runs_bool_exp!, $limit: Int!) {\n  mobility_runs(\n    where: {product_type: {_eq: "parts_delivery"}, _and: [$scope, $window]}\n    order_by: [{departure_time: asc}, {id: asc}]\n    limit: $limit\n  ) {\n    id\n    customer_id\n    status\n    fulfillment_type\n    departure_time\n    expected_return_time\n    dispatched_at\n    departed_at\n    completed_at\n    driver {\n      id\n      user {\n        id\n        display_name\n      }\n    }\n    vehicle {\n      id\n      year\n      make\n      model\n      license_plate\n    }\n    parts_delivery {\n      id\n      vendor\n      orchestration_status\n      delivery_status\n      dropoff_eta\n    }\n    stops_aggregate {\n      aggregate {\n        count\n      }\n    }\n    stops(order_by: {stop_order: asc}) {\n      id\n      stop_order\n      status\n      delivery_orders {\n        id\n        quantity\n        delivery_status\n      }\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "partsRunIndex" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "scope" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "mobility_runs_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "window" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "mobility_runs_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "mobility_runs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "product_type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "parts_delivery", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_and" }, "value": { "kind": "ListValue", "values": [{ "kind": "Variable", "name": { "kind": "Name", "value": "scope" } }, { "kind": "Variable", "name": { "kind": "Name", "value": "window" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "departure_time" }, "value": { "kind": "EnumValue", "value": "asc" } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "EnumValue", "value": "asc" } }] }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "limit" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "fulfillment_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departure_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "expected_return_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dispatched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "completed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "license_plate" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "parts_delivery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vendor" } }, { "kind": "Field", "name": { "kind": "Name", "value": "orchestration_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dropoff_eta" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "stops_aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "count" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "stops" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "stop_order" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "stop_order" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_orders" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "quantity" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_status" } }] } }] } }] } }] } }] };

// src/products/parts-delivery/queries/partsRunPage.query.generated.ts
import { resolveExecutor as resolveExecutor10 } from "@hopdrive/sdk-core";
var PartsRunPageDocument = { "__text": 'query partsRunPage($runIds: [bigint!]!) {\n  mobility_runs(where: {id: {_in: $runIds}}) {\n    id\n    customer_id\n    status\n    fulfillment_type\n    departure_time\n    expected_return_time\n    dispatched_at\n    departed_at\n    completed_at\n    driver {\n      id\n      user {\n        id\n        display_name\n      }\n    }\n    vehicle {\n      id\n      year\n      make\n      model\n      license_plate\n    }\n    scenario {\n      id\n      bundletoscenarios(\n        order_by: {bundle: {createdat: desc}}\n        where: {bundle: {status: {_neq: "deprecated"}}}\n        limit: 1\n      ) {\n        id\n        bundle {\n          id\n          offers(\n            where: {status: {_in: ["new", "offered", "accepted", "assigned"]}}\n            order_by: {createdat: desc}\n            limit: 1\n          ) {\n            id\n            driver {\n              id\n              user {\n                id\n                display_name\n              }\n            }\n          }\n        }\n      }\n    }\n    parts_delivery {\n      id\n      vendor\n      orchestration_status\n      delivery_status\n      dropoff_eta\n      tracking_url\n      driver\n      booked_at\n      completed_at\n      cancelation_reason\n    }\n    stops(order_by: {stop_order: asc}) {\n      id\n      stop_order\n      status\n      address\n      geofence_arrived_at\n      geofence_departed_at\n      delivery_orders(order_by: {order_number: asc}) {\n        id\n        order_number\n        recipient_business\n        quantity\n        status\n        delivery_status\n      }\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "partsRunPage" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "runIds" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "mobility_runs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "runIds" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "fulfillment_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departure_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "expected_return_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dispatched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "completed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "license_plate" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "scenario" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundletoscenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_neq" }, "value": { "kind": "StringValue", "value": "deprecated", "block": false } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundle" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offers" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "ListValue", "values": [{ "kind": "StringValue", "value": "new", "block": false }, { "kind": "StringValue", "value": "offered", "block": false }, { "kind": "StringValue", "value": "accepted", "block": false }, { "kind": "StringValue", "value": "assigned", "block": false }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "EnumValue", "value": "desc" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "user" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "display_name" } }] } }] } }] } }] } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "parts_delivery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vendor" } }, { "kind": "Field", "name": { "kind": "Name", "value": "orchestration_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "dropoff_eta" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tracking_url" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver" } }, { "kind": "Field", "name": { "kind": "Name", "value": "booked_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "completed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancelation_reason" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "stops" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "stop_order" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "stop_order" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "geofence_arrived_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "geofence_departed_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_orders" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "order_number" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "order_number" } }, { "kind": "Field", "name": { "kind": "Name", "value": "recipient_business" } }, { "kind": "Field", "name": { "kind": "Name", "value": "quantity" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_status" } }] } }] } }] } }] } }] };

// src/products/parts-delivery/constants/partsDeliveryStatus.ts
var PARTS_STOP_STATUS = {
  PENDING: "pending",
  IN_TRANSIT_TO: "in_transit_to",
  ARRIVED: "arrived",
  COMPLETED: "completed",
  FAILED: "failed"
};
var PARTS_ORDER_STATUS = {
  PENDING: "pending",
  ASSIGNED: "assigned",
  LOADED: "loaded",
  IN_TRANSIT: "in_transit",
  DELIVERED: "delivered",
  PARTIAL: "partial",
  REJECTED: "rejected"
};
var PARTS_ORDER_OUTCOME = {
  DELIVERED: "delivered",
  PARTIAL: "partial",
  REJECTED: "rejected"
};
var COURIER_VENDOR = {
  UBER: "uber",
  DOORDASH: "doordash"
};
var VENDOR_LABEL = {
  uber: "Uber Direct",
  doordash: "DoorDash Drive"
};
var resolveVendorLabel = (vendor) => VENDOR_LABEL[vendor] ?? formatStatusWord(vendor);
var resolveStopMarkerState2 = (stop) => {
  if (stop.status === PARTS_STOP_STATUS.FAILED) return "exception";
  if (stop.status === PARTS_STOP_STATUS.COMPLETED) return stop.outcome === PARTS_ORDER_OUTCOME.PARTIAL || stop.outcome === PARTS_ORDER_OUTCOME.REJECTED ? "exception" : "done";
  if (stop.status === PARTS_STOP_STATUS.IN_TRANSIT_TO || stop.status === PARTS_STOP_STATUS.ARRIVED) return "active";
  return "pending";
};
var resolveStopBadge2 = (status) => formatStatusWord(status);
var OUTCOME_BADGE = {
  delivered: "Delivered",
  partial: "Partial",
  rejected: "Rejected"
};
var resolveOutcomeBadge = (outcome) => OUTCOME_BADGE[outcome] ?? formatStatusWord(outcome);
var resolveOrderBadge2 = (status) => formatStatusWord(status);
var COURIER_STEPS = ["Booked", "Assigned", "Picked up", "In transit", "Delivered"];
var PARTS_DELIVERY_CARD_STOP_CAP = 6;
var PARTS_DELIVERY_ROUTE_CHAIN_CAP = 3;
var PARTS_DELIVERY_SHOW_ORDERS = "showPartOrders";
var PARTS_DELIVERY_DISPLAY_PREFERENCES = [
  { surface: "card", key: PARTS_DELIVERY_SHOW_ORDERS, label: "Show part orders", section: "Stops", defaultValue: false }
];

// src/products/parts-delivery/items/partStopText.ts
var formatPackages = (count) => `${count} pkg`;
var buildStopName = (stop) => stop.recipientBusiness ?? stop.address ?? `Stop ${stop.position}`;
var buildStopNote2 = (stop) => {
  if (stop.isCheckIn) return "Check-in stop";
  const orders = stop.orders.length;
  let note = `${orders} ${orders === 1 ? "order" : "orders"} \xB7 ${formatPackages(stop.packageCount)}`;
  if (stop.outcome === PARTS_ORDER_OUTCOME.REJECTED) note += ` \xB7 ${resolveOutcomeBadge(PARTS_ORDER_OUTCOME.REJECTED)}`;
  else if (stop.outcome === PARTS_ORDER_OUTCOME.PARTIAL) note += ` \xB7 ${resolveOutcomeBadge(PARTS_ORDER_OUTCOME.PARTIAL)}`;
  return note;
};
var buildStopTooltip2 = (stop) => {
  const content = [{ kind: "title", text: buildStopName(stop) }];
  if (stop.recipientName) content.push({ kind: "row", key: "Recipient", value: stop.recipientName });
  if (stop.address) content.push({ kind: "row", key: "Address", value: stop.address });
  if (stop.orders.length) content.push({ kind: "row", key: stop.orders.length === 1 ? "Order" : "Orders", value: stop.orders.map((order) => order.orderNumber).join(", ") });
  content.push({ kind: "row", key: "Packages", value: formatPackages(stop.packageCount) });
  content.push({ kind: "row", key: "Status", value: resolveStopBadge2(stop.status) });
  if (stop.isCheckIn) content.push({ kind: "description", text: "Check-in stop \u2014 no delivery orders" });
  return content;
};
var buildOrderRowNote2 = (order) => {
  const descriptor = order.partNumber ?? order.description;
  const packages = formatPackages(order.quantity);
  return descriptor ? `${descriptor} \xB7 ${packages}` : packages;
};

// src/products/parts-delivery/slots.ts
var { DRAFT: DRAFT2, SCHEDULED: SCHEDULED2, DISPATCHED: DISPATCHED2, CLAIMED: CLAIMED2, IN_TRANSIT: IN_TRANSIT2, COMPLETED: COMPLETED3, CANCELLED: CANCELLED3, CANCELED: CANCELED2 } = MOBILITY_RUN_STATUS;
var RUN_DISPLAY_NAME2 = "Delivery Run";
var COURIER_DISPLAY_NAME = "Courier Delivery";
var TIME_PENDING_MARK3 = "N/A";
var TIME_ABSENT_MARK3 = "\u2014";
var isCourier = (view) => view.fulfillment === "courier" && view.courier !== null;
var isRunLive2 = (view) => isCourier(view) ? isLivePhase(view.courier.status.phase) : isMobilityRunLive(view.status);
var isRunSettled2 = (view) => {
  if (isCourier(view) && view.courier.status.isTerminal) return true;
  const phase = resolveMobilityRunPhase(view.status);
  return phase === "completed" || phase === "cancelled";
};
var displayNameFor = (view) => isCourier(view) ? COURIER_DISPLAY_NAME : RUN_DISPLAY_NAME2;
var toStatusBarGroup4 = (view) => {
  if (isCourier(view)) return { status: resolveOrderStatusToken(view.courier.status.phase), badge: view.courier.status.label, isLive: isRunLive2(view) };
  return { status: resolveMobilityRunStatusToken(view.status), badge: resolveMobilityRunBadge(view.status), isLive: isRunLive2(view) };
};
var isStopReached2 = (stop) => stop.status === PARTS_STOP_STATUS.COMPLETED || stop.status === PARTS_STOP_STATUS.FAILED;
var toStopTrackerBar = (stop, activePosition) => ({
  key: `stop:${stop.id}`,
  isReached: isStopReached2(stop),
  isDrivingToward: stop.position === activePosition,
  slipFraction: 0,
  tooltip: [
    { kind: "title", text: buildStopName(stop) },
    { kind: "row", key: "Status", value: resolveStopBadge2(stop.status) }
  ]
});
var toCourierTrackerBars = (courier) => COURIER_STEPS.map((label, index) => ({
  key: `step:${index}`,
  isReached: courier.status.phase === "completed" || index < courier.status.stepIndex,
  isDrivingToward: !courier.status.isTerminal && index === courier.status.stepIndex,
  slipFraction: 0,
  tooltip: [{ kind: "title", text: label }]
}));
var buildStatusLine2 = (view) => {
  if (isCourier(view)) return view.courier.status.label;
  const active = view.activeStopPosition === null ? void 0 : view.stops.find((stop) => stop.position === view.activeStopPosition);
  if (active && isRunLive2(view)) return active.status === PARTS_STOP_STATUS.IN_TRANSIT_TO ? `Driving to ${buildStopName(active)}` : `At ${buildStopName(active)}`;
  return resolveMobilityRunBadge(view.status);
};
var toTrackerGroup4 = (view) => {
  if (isCourier(view)) {
    return { bars: toCourierTrackerBars(view.courier), status: resolveOrderStatusToken(view.courier.status.phase), statusLine: buildStatusLine2(view), isLive: isRunLive2(view), isChainBroken: false };
  }
  return { bars: view.stops.map((stop) => toStopTrackerBar(stop, view.activeStopPosition)), status: resolveMobilityRunStatusToken(view.status), statusLine: buildStatusLine2(view), isLive: isRunLive2(view), isChainBroken: false };
};
var buildIdentityTooltip3 = (view) => {
  const content = [{ kind: "title", text: `${displayNameFor(view)} ${view.id}` }];
  if (isCourier(view)) {
    content.push({ kind: "row", key: "Vendor", value: view.courier.vendorLabel });
    content.push({ kind: "row", key: "Courier", value: view.courier.name ?? "Matching courier" });
    if (view.courier.vehicleLine) content.push({ kind: "row", key: "Vehicle", value: view.courier.vehicleLine });
  } else {
    content.push({ kind: "row", key: "Driver", value: view.driver?.name ?? view.offeredDriverName ?? "Unassigned" });
    if (view.vehicleLine) content.push({ kind: "row", key: "Vehicle", value: view.vehicleLine });
  }
  content.push({ kind: "row", key: "Stops", value: String(view.stopCount) });
  content.push({ kind: "row", key: "Packages", value: formatPackages(view.packageCount) });
  return content;
};
var toIdentityPhoto2 = (view) => view.stops.find((stop) => stop.photo)?.photo ?? PLACEHOLDER_PHOTO;
var toIdentityGroup3 = (view) => ({
  displayName: displayNameFor(view),
  ids: [view.id],
  tooltip: buildIdentityTooltip3(view)
});
var toCourierMilestone = (courier) => {
  if (courier.status.phase === "cancelled") return { value: TIME_ABSENT_MARK3, subtext: { text: courier.status.label, tone: "neutral" }, tooltip: null };
  if (courier.status.phase === "completed") return { value: courier.completedAt.display ?? TIME_PENDING_MARK3, subtext: { text: "Delivered", tone: "neutral" }, tooltip: null };
  if (courier.status.stepIndex <= 0) return { value: courier.bookedAt.display ?? TIME_PENDING_MARK3, subtext: { text: "Booked", tone: "neutral" }, tooltip: null };
  return { value: courier.dropoffEta.display ?? TIME_PENDING_MARK3, subtext: { text: "ETA", tone: "neutral" }, tooltip: null };
};
var toOwnDriverMilestone = (view) => {
  switch (view.status) {
    case DRAFT2:
    case SCHEDULED2:
      return { value: view.departure.display ?? TIME_PENDING_MARK3, subtext: { text: "Departure", tone: "neutral" }, tooltip: null };
    case DISPATCHED2:
    case CLAIMED2:
    case IN_TRANSIT2:
      return view.expectedReturn.display ? { value: view.expectedReturn.display, subtext: { text: "Return by", tone: "neutral" }, tooltip: null } : { value: view.departed.display ?? view.departure.display ?? TIME_PENDING_MARK3, subtext: { text: view.departed.display ? "Departed" : "Departure", tone: "neutral" }, tooltip: null };
    case COMPLETED3:
      return { value: view.completed.display ?? TIME_PENDING_MARK3, subtext: { text: "Completed", tone: "neutral" }, tooltip: null };
    case CANCELLED3:
    case CANCELED2:
    default:
      return { value: TIME_ABSENT_MARK3, subtext: { text: resolveMobilityRunBadge(view.status), tone: "neutral" }, tooltip: null };
  }
};
var toMilestoneGroup4 = (view) => isCourier(view) ? toCourierMilestone(view.courier) : toOwnDriverMilestone(view);
var toDriverGroup4 = (view) => {
  const isSettled = isRunSettled2(view);
  if (isCourier(view)) {
    const { courier } = view;
    return { showPill: true, driverName: courier.name ?? "Matching courier", avatarUrl: null, state: "locked", isSettled, tooltip: [{ kind: "description", text: courier.name ? `${courier.name} \xB7 ${courier.vendorLabel} courier` : `${courier.vendorLabel}: matching a courier` }] };
  }
  if (view.driver) {
    return { showPill: true, driverName: view.driver.name, avatarUrl: view.driver.avatarUrl, state: "assigned", isSettled, tooltip: [{ kind: "description", text: `${view.driver.name} is the driver on this run` }] };
  }
  if (view.offeredDriverName) {
    return { showPill: true, driverName: view.offeredDriverName, avatarUrl: null, state: "offered", isSettled, tooltip: [{ kind: "description", text: `Offered to ${view.offeredDriverName}, awaiting acceptance` }] };
  }
  return { showPill: true, driverName: null, avatarUrl: null, state: "unassigned", isSettled, tooltip: [{ kind: "description", text: "Auto-assign: a driver is assigned when the run is dispatched" }] };
};
var toStateGroup = (view) => {
  const courierCancelled = isCourier(view) && view.courier.status.phase === "cancelled";
  return {
    isSettled: isRunSettled2(view),
    isCancelDeclared: resolveMobilityRunPhase(view.status) === "cancelled" || courierCancelled,
    cancelReason: isCourier(view) ? view.courier.cancellationReason : null
  };
};
var promiseTime = (view) => isCourier(view) ? view.courier.dropoffEta : view.departure;

// src/products/parts-delivery/cardProps.ts
var PARTS_DELIVERY_RENDERER_KEY = "partsDeliveryRun";
var defaultPartsDeliveryCardPreferences = () => Object.fromEntries(PARTS_DELIVERY_DISPLAY_PREFERENCES.filter((definition) => definition.surface === "card").map((definition) => [definition.key, definition.defaultValue]));
var toTimeSlot4 = (key, label, time) => ({
  key,
  label,
  value: time.display ?? TIME_PENDING_MARK3,
  treatment: time.display ? "actual" : "pending",
  tooltip: null
});
var toTimesGroup3 = (view) => {
  if (view.courier) {
    const { courier } = view;
    return {
      leftColumn: [toTimeSlot4("booked", "Booked", courier.bookedAt), toTimeSlot4("pickedUp", "Picked up", courier.pickedUpAt)],
      rightColumn: [toTimeSlot4("eta", "ETA", courier.dropoffEta), toTimeSlot4("delivered", "Delivered", courier.completedAt)]
    };
  }
  return {
    leftColumn: [toTimeSlot4("departure", "Departure", view.departure), toTimeSlot4("dispatched", "Dispatched", view.dispatched)],
    rightColumn: [toTimeSlot4("departed", "Departed", view.departed), toTimeSlot4("completed", "Completed", view.completed)]
  };
};
var toDetailRows3 = (view) => {
  const rows = [
    { key: "stops", label: "Stops", value: String(view.stopCount) },
    { key: "orders", label: "Orders", value: String(view.orderCount) },
    { key: "packages", label: "Packages", value: formatPackages(view.packageCount) }
  ];
  if (view.courier) {
    rows.push({ key: "vendor", label: "Vendor", value: view.courier.vendorLabel });
    if (view.courier.trackingUrl) rows.push({ key: "tracking", label: "Tracking", value: view.courier.trackingUrl });
  } else if (view.expectedReturn.display) {
    rows.push({ key: "return", label: "Return by", value: view.expectedReturn.display });
  }
  return rows;
};
var toStopItems2 = (view, showOrders, cap = PARTS_DELIVERY_CARD_STOP_CAP) => {
  const window = windowStops(view.stops, view.activeStopPosition, cap);
  const overflow = (count, position) => count > 0 ? [{ kind: "stopOverflow", key: `overflow:${position}`, props: { count, position } }] : [];
  return [
    ...overflow(window.before, "before"),
    ...window.visible.map((stop) => ({ kind: "partStop", key: `stop:${stop.id}`, props: { stop, showOrders } })),
    ...overflow(window.after, "after")
  ];
};
var toPartsDeliveryCardProps = (view, preferences) => {
  const state = toStateGroup(view);
  return {
    rendererKey: PARTS_DELIVERY_RENDERER_KEY,
    statusBar: toStatusBarGroup4(view),
    tracker: toTrackerGroup4(view),
    header: { ...toIdentityGroup3(view), milestone: toMilestoneGroup4(view) },
    items: toStopItems2(view, preferences[PARTS_DELIVERY_SHOW_ORDERS] === true),
    times: toTimesGroup3(view),
    details: { rows: toDetailRows3(view) },
    driver: toDriverGroup4(view),
    actionBar: [],
    state: {
      isHydrated: view.isHydrated,
      isSettled: state.isSettled,
      isCancelDeclared: state.isCancelDeclared,
      cancelReason: state.cancelReason,
      isInDanger: false
    }
  };
};

// src/products/parts-delivery/partsCourierStatus.ts
var PARTS_COURIER_BOOKING_STATES = ["NEW", "PENDING", "BOOKING", "RETRY"];
var PARTS_COURIER_FAILED_STATE = "FAILED";
var BOOKING = { label: "Booking\u2026", phase: "planned", stepIndex: 0, isError: false, isReturning: false, isTerminal: false };
var BOOKING_FAILED = { label: "Booking failed", phase: "cancelled", stepIndex: 0, isError: true, isReturning: false, isTerminal: true };
var BOOKED2 = { label: "Booked", phase: "planned", stepIndex: 0, isError: false, isReturning: false, isTerminal: false };
var DELIVERY_STATUS = {
  driver_assigned: { label: "Assigned", phase: "planned", stepIndex: 1, isError: false, isReturning: false, isTerminal: false },
  arriving_pickup: { label: "Arriving at pickup", phase: "active", stepIndex: 1, isError: false, isReturning: false, isTerminal: false },
  picked_up: { label: "Picked up", phase: "active", stepIndex: 2, isError: false, isReturning: false, isTerminal: false },
  arriving_dropoff: { label: "In transit", phase: "active", stepIndex: 3, isError: false, isReturning: false, isTerminal: false },
  return_in_progress: { label: "Returning", phase: "active", stepIndex: 3, isError: false, isReturning: true, isTerminal: false },
  delivered: { label: "Delivered", phase: "completed", stepIndex: 4, isError: false, isReturning: false, isTerminal: true },
  canceled: { label: "Canceled", phase: "cancelled", stepIndex: 0, isError: true, isReturning: false, isTerminal: true },
  returned: { label: "Returned", phase: "cancelled", stepIndex: 4, isError: false, isReturning: true, isTerminal: true }
};
var derivePartsCourierStatus = (orchestration, delivery) => {
  if (orchestration && PARTS_COURIER_BOOKING_STATES.includes(orchestration)) return BOOKING;
  if (orchestration === PARTS_COURIER_FAILED_STATE) return BOOKING_FAILED;
  return (delivery ? DELIVERY_STATUS[delivery] : void 0) ?? BOOKED2;
};
var resolvePartsCourierPhase = (status) => status.phase;
var partsCourierDeliveryStatesIn = (phases) => Object.keys(DELIVERY_STATUS).filter((state) => phases.includes(DELIVERY_STATUS[state].phase));

// src/products/parts-delivery/partsRunOrderAdapter.ts
var COURIER_FULFILLMENT = "external_courier";
var PARTS_INDEX_CAP = 2e3;
var PARTS_PAGE_SIZES = [10, 25, 50, 100];
var PARTS_PAGE_POLL_INTERVAL_MS = 6e4;
var customerScope3 = (customerIds) => ({ customer_id: { _in: customerIds } });
var windowScope3 = (window) => window ? { departure_time: { _gte: window.startIso, _lte: window.endIso } } : {};
var buildIndexVariables4 = ({ customerIds, window, limit }) => ({
  scope: customerScope3(customerIds),
  window: windowScope3(window),
  limit
});
var buildPageVariables4 = (ids) => ({ runIds: ids });
var runAnchorMs2 = (departureTime) => {
  if (!departureTime) return null;
  const ms = Date.parse(departureTime);
  return Number.isNaN(ms) ? null : ms;
};
var isCourier2 = (row2) => row2.fulfillment_type === COURIER_FULFILLMENT;
var resolvePartsRunPhase = (row2) => isCourier2(row2) && row2.parts_delivery ? resolvePartsCourierPhase(derivePartsCourierStatus(row2.parts_delivery.orchestration_status, row2.parts_delivery.delivery_status)) : resolveMobilityRunPhase(row2.status);
var isPartsRunLive = (row2) => {
  if (isCourier2(row2)) return row2.parts_delivery ? isLivePhase(resolvePartsRunPhase(row2)) : false;
  return isMobilityRunLive(row2.status);
};
var toModel3 = (row2) => {
  const phase = resolvePartsRunPhase(row2);
  const anchorMs = runAnchorMs2(row2.departure_time);
  const driverName = row2.driver?.user?.display_name ?? null;
  return {
    key: orderKey("parts-delivery", row2.id),
    product: "parts-delivery",
    id: row2.id,
    customerId: row2.customer_id,
    rendererKey: PARTS_DELIVERY_RENDERER_KEY,
    phase,
    metric: metricForPhase(phase),
    anchorMs,
    promiseMs: anchorMs,
    operationalModel: isCourier2(row2) ? "hopdrive-managed" : "self-managed",
    isLive: isPartsRunLive(row2),
    isHydrated: false,
    flags: { isUnconfirmed: false, hasUnreadMessages: false },
    sortValues: {
      status: ORDER_PHASE_ORDER.indexOf(phase),
      order: row2.id,
      driver: driverName
    },
    groupKeys: { driver: driverName },
    source: { index: row2, detail: null }
  };
};
var adaptIndex4 = (data, _context) => {
  const rows = data?.mobility_runs ?? [];
  const capReached = rows.length > PARTS_INDEX_CAP;
  const capped = capReached ? rows.slice(0, PARTS_INDEX_CAP) : rows;
  return { entries: capped.map((row2) => ({ kind: "order", model: toModel3(row2) })), capReached };
};
var adaptPage4 = (data) => data?.mobility_runs ?? [];
var mergePage4 = (entries, page) => {
  const byId = new Map(page.map((run) => [run.id, run]));
  return entries.map((entry) => {
    if (entry.kind !== "order" || entry.model.product !== "parts-delivery") return entry;
    const model = entry.model;
    const detail = byId.get(model.id);
    if (!detail) return entry;
    return { kind: "order", model: { ...model, isHydrated: true, source: { ...model.source, detail } } };
  });
};
var partsRunOrderAdapter = {
  product: "parts-delivery",
  dayColumns: ["departure_time"],
  indexCap: PARTS_INDEX_CAP,
  pageSizes: PARTS_PAGE_SIZES,
  pollMs: PARTS_PAGE_POLL_INTERVAL_MS,
  live: "poll",
  documents: { index: PartsRunIndexDocument, page: PartsRunPageDocument },
  buildIndexVariables: buildIndexVariables4,
  buildPageVariables: buildPageVariables4,
  adaptIndex: adaptIndex4,
  adaptPage: adaptPage4,
  mergePage: mergePage4
};

// src/products/parts-delivery/resolvePartsDeliveryOrders.ts
import { PiPackageFill } from "react-icons/pi";

// src/products/parts-delivery/columns.ts
var buildRouteTooltip3 = (stops) => [
  { kind: "title", text: `${stops.length} ${stops.length === 1 ? "stop" : "stops"}` },
  ...stops.map((stop) => ({ kind: "row", key: String(stop.position), value: `${buildStopName(stop)} \xB7 ${resolveStopBadge2(stop.status)}` }))
];
var buildRouteChainStops2 = (stops, cap = PARTS_DELIVERY_ROUTE_CHAIN_CAP) => {
  if (stops.length <= cap) return stops.map((stop) => ({ key: String(stop.id), name: buildStopName(stop) }));
  const named = stops.slice(0, Math.max(cap - 1, 0)).map((stop) => ({ key: String(stop.id), name: buildStopName(stop) }));
  const hidden = stops.length - named.length;
  return [...named, { key: "more", name: `+${hidden} more ${hidden === 1 ? "stop" : "stops"}` }];
};
var promiseMs = (view) => {
  const iso = promiseTime(view).iso;
  return iso ? Date.parse(iso) : null;
};
var driverSortName = (view) => view.driver?.name ?? view.offeredDriverName ?? view.courier?.name ?? null;
var partsDeliveryColumns = [
  {
    name: "status",
    label: "Status",
    tooltip: "Where the run is in its lifecycle",
    minWidth: 110,
    sortable: true,
    // The lifecycle rank, never the badge word.
    sortValue: (view) => ORDER_PHASE_ORDER.indexOf(view.phase),
    cell: "status",
    cellProps: (view) => ({ statusBar: toStatusBarGroup4(view), tracker: toTrackerGroup4(view) }),
    mobile: "pair",
    // The badge hangs from the pair's right edge; the tracker keeps the full width under it.
    mobileValueSx: { "& > div": { alignItems: "flex-end" }, "& > div > :last-child": { alignSelf: "stretch" } }
  },
  {
    name: "run",
    label: "Run",
    tooltip: "The run and the driver or courier, stops, and packages on it",
    minWidth: 150,
    sortable: true,
    sortValue: (view) => view.id,
    cell: "identity",
    // A run has no photo of its own, so the tile borrows the first proof-of-delivery capture.
    cellProps: (view) => ({ ...toIdentityGroup3(view), photo: toIdentityPhoto2(view), photoItemId: null }),
    mobile: "pair",
    mobileValueSx: { "& > div": { flexDirection: "row-reverse" }, "& > div > div": { alignItems: "flex-end" } }
  },
  {
    name: "driver",
    label: "Driver",
    minWidth: 150,
    sortable: true,
    sortValue: driverSortName,
    cell: "driver",
    cellProps: (view) => toDriverGroup4(view),
    mobile: "bar"
  },
  {
    name: "promise",
    label: "Departure / ETA",
    tooltip: "When an own-driver run is scheduled to leave, or a courier run is due",
    minWidth: 140,
    sortable: true,
    sortValue: promiseMs,
    cell: "promise",
    cellProps: (view) => {
      const promise = promiseTime(view);
      return promise.display ? { value: promise.display, tooltip: null } : null;
    },
    mobile: "pair"
  },
  {
    name: "stops",
    label: "Stops",
    align: "right",
    minWidth: 70,
    sortable: true,
    sortValue: (view) => view.stopCount,
    cell: "text",
    text: (view) => ({ value: String(view.stopCount), isBold: true }),
    mobile: "pair"
  },
  {
    name: "packages",
    label: "Packages",
    align: "right",
    minWidth: 90,
    sortable: true,
    sortValue: (view) => view.packageCount,
    cell: "text",
    text: (view) => ({ value: String(view.packageCount) }),
    mobile: "pair"
  },
  {
    name: "route",
    label: "Route",
    tooltip: "Every stop on the run, in order",
    minWidth: 200,
    cell: "route",
    cellProps: (view) => view.stops.length ? { stops: buildRouteChainStops2(view.stops), tooltip: buildRouteTooltip3(view.stops) } : null
  }
];

// src/products/parts-delivery/resolvePartsDeliveryOrders.ts
var clockParts4 = (ms, timeZone) => new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", hour12: true }).format(ms);
var formatRunClock2 = (iso, timeZone) => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return `${new Intl.DateTimeFormat("en-US", { timeZone, month: "numeric", day: "numeric" }).format(ms)}, ${clockParts4(ms, timeZone)}`;
};
var toTime3 = (iso, timeZone) => ({ iso, display: formatRunClock2(iso, timeZone) });
var toVehicleLine2 = (vehicle) => {
  if (!vehicle) return null;
  const line = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ");
  return line || null;
};
var OUTCOMES = /* @__PURE__ */ new Set([PARTS_ORDER_OUTCOME.DELIVERED, PARTS_ORDER_OUTCOME.PARTIAL, PARTS_ORDER_OUTCOME.REJECTED]);
var toOutcome = (deliveryStatus) => deliveryStatus && OUTCOMES.has(deliveryStatus) ? deliveryStatus : null;
var stopOutcome = (orders) => {
  if (orders.some((order) => order.outcome === PARTS_ORDER_OUTCOME.REJECTED)) return PARTS_ORDER_OUTCOME.REJECTED;
  if (orders.some((order) => order.outcome === PARTS_ORDER_OUTCOME.PARTIAL)) return PARTS_ORDER_OUTCOME.PARTIAL;
  if (orders.length > 0 && orders.every((order) => order.outcome === PARTS_ORDER_OUTCOME.DELIVERED)) return PARTS_ORDER_OUTCOME.DELIVERED;
  return null;
};
var activeStopPosition2 = (stops) => stops.find((stop) => resolveStopMarkerState2({ status: stop.status, outcome: stop.outcome }) === "active")?.position ?? null;
var runPhase = (fulfillment, status, courier) => fulfillment === "courier" && courier ? resolvePartsCourierPhase(derivePartsCourierStatus(courier.orchestration_status, courier.delivery_status)) : resolveMobilityRunPhase(status);
var fulfillmentOf = (fulfillmentType) => fulfillmentType === COURIER_FULFILLMENT ? "courier" : "own-driver";
var toIndexOrderView = (order) => ({
  id: order.id,
  orderNumber: "",
  partNumber: null,
  description: null,
  quantity: order.quantity ?? 0,
  status: order.delivery_status ?? "pending",
  outcome: toOutcome(order.delivery_status),
  isRush: false
});
var toIndexStopView2 = (stop, position) => {
  const orders = stop.delivery_orders.map(toIndexOrderView);
  return {
    id: stop.id,
    position,
    status: stop.status,
    recipientBusiness: null,
    recipientName: null,
    address: null,
    isCheckIn: orders.length === 0,
    orders,
    packageCount: orders.reduce((total, order) => total + order.quantity, 0),
    hasRush: false,
    outcome: stopOutcome(orders),
    photo: null,
    arrivedIso: null,
    departedIso: null
  };
};
var toCourierView = (pd, timeZone) => {
  const status = derivePartsCourierStatus(pd.orchestration_status, pd.delivery_status);
  return {
    vendor: pd.vendor,
    vendorLabel: resolveVendorLabel(pd.vendor ?? ""),
    name: null,
    vehicleLine: null,
    status,
    dropoffEta: toTime3(pd.dropoff_eta ?? null, timeZone),
    bookedAt: toTime3(null, timeZone),
    pickedUpAt: toTime3(null, timeZone),
    completedAt: toTime3(null, timeZone),
    trackingUrl: null,
    cancellationReason: null
  };
};
var buildIndexView3 = (row2, timeZone) => {
  const fulfillment = fulfillmentOf(row2.fulfillment_type);
  const stops = row2.stops.map((stop, index) => toIndexStopView2(stop, index + 1));
  return {
    id: row2.id,
    fulfillment,
    status: row2.status,
    phase: runPhase(fulfillment, row2.status, row2.parts_delivery),
    driver: row2.driver?.user ? { name: row2.driver.user.display_name, avatarUrl: null } : null,
    offeredDriverName: null,
    vehicleLine: toVehicleLine2(row2.vehicle),
    departure: toTime3(row2.departure_time, timeZone),
    expectedReturn: toTime3(row2.expected_return_time, timeZone),
    dispatched: toTime3(row2.dispatched_at, timeZone),
    departed: toTime3(row2.departed_at, timeZone),
    completed: toTime3(row2.completed_at, timeZone),
    courier: fulfillment === "courier" && row2.parts_delivery ? toCourierView(row2.parts_delivery, timeZone) : null,
    stopCount: row2.stops_aggregate.aggregate?.count ?? stops.length,
    orderCount: stops.reduce((total, stop) => total + stop.orders.length, 0),
    packageCount: stops.reduce((total, stop) => total + stop.packageCount, 0),
    stops,
    activeStopPosition: activeStopPosition2(stops),
    isHydrated: false
  };
};
var toDetailOrderView = (order) => ({
  id: order.id,
  orderNumber: order.order_number ?? "",
  partNumber: null,
  description: null,
  quantity: order.quantity ?? 0,
  status: order.status ?? "pending",
  outcome: toOutcome(order.delivery_status),
  isRush: false
});
var toDetailStopView2 = (stop, position) => {
  const orders = stop.delivery_orders.map(toDetailOrderView);
  return {
    id: stop.id,
    position,
    status: stop.status,
    recipientBusiness: stop.delivery_orders[0]?.recipient_business ?? null,
    recipientName: null,
    address: stop.address ?? null,
    isCheckIn: orders.length === 0,
    orders,
    packageCount: orders.reduce((total, order) => total + order.quantity, 0),
    hasRush: false,
    outcome: stopOutcome(orders),
    photo: null,
    arrivedIso: stop.geofence_arrived_at ?? null,
    departedIso: stop.geofence_departed_at ?? null
  };
};
var offeredDriverName2 = (row2) => row2.scenario?.bundletoscenarios[0]?.bundle.offers[0]?.driver.user?.display_name ?? null;
var toDetailCourierView = (pd, timeZone) => {
  const status = derivePartsCourierStatus(pd.orchestration_status, pd.delivery_status);
  return {
    vendor: pd.vendor,
    vendorLabel: resolveVendorLabel(pd.vendor ?? ""),
    name: typeof pd.driver === "string" ? pd.driver : null,
    vehicleLine: null,
    status,
    dropoffEta: toTime3(pd.dropoff_eta ?? null, timeZone),
    bookedAt: toTime3(pd.booked_at ?? null, timeZone),
    pickedUpAt: toTime3(null, timeZone),
    completedAt: toTime3(pd.completed_at ?? null, timeZone),
    trackingUrl: pd.tracking_url ?? null,
    cancellationReason: pd.cancelation_reason ?? null
  };
};
var buildDetailView3 = (row2, timeZone) => {
  const fulfillment = fulfillmentOf(row2.fulfillment_type);
  const driver = row2.driver?.user ? { name: row2.driver.user.display_name, avatarUrl: null } : null;
  const stops = row2.stops.map((stop, index) => toDetailStopView2(stop, index + 1));
  return {
    id: row2.id,
    fulfillment,
    status: row2.status,
    phase: runPhase(fulfillment, row2.status, row2.parts_delivery),
    driver,
    offeredDriverName: driver ? null : offeredDriverName2(row2),
    vehicleLine: toVehicleLine2(row2.vehicle),
    departure: toTime3(row2.departure_time, timeZone),
    expectedReturn: toTime3(row2.expected_return_time, timeZone),
    dispatched: toTime3(row2.dispatched_at, timeZone),
    departed: toTime3(row2.departed_at, timeZone),
    completed: toTime3(row2.completed_at, timeZone),
    courier: fulfillment === "courier" && row2.parts_delivery ? toDetailCourierView(row2.parts_delivery, timeZone) : null,
    stopCount: stops.length,
    orderCount: stops.reduce((total, stop) => total + stop.orders.length, 0),
    packageCount: stops.reduce((total, stop) => total + stop.packageCount, 0),
    stops,
    activeStopPosition: activeStopPosition2(stops),
    isHydrated: true
  };
};
var PARTS_METRIC_TILES = [
  { key: "all", label: "All Runs", inAll: true },
  { key: "upcoming", label: "Upcoming", inAll: true },
  { key: "active", label: "Active", inAll: true },
  { key: "completed", label: "Completed", inAll: true },
  { key: "cancelled", label: "Canceled", inAll: true }
];
var PARTS_SORT_OPTIONS = [
  { key: "departure", label: "Departure Time", anchor: true },
  { key: "status", label: "Status", missingValue: "highest" },
  { key: "order", label: "Run ID" },
  { key: "driver", label: "Driver" }
];
var PARTS_DATE_RANGE_PRESETS = [
  { key: "tomorrow", label: "Tomorrow", group: 0 },
  { key: "today", label: "Today", group: 0 },
  { key: "yesterday", label: "Yesterday", group: 0 },
  { key: "nextweek", label: "Next Week", group: 1 },
  { key: "thisweek", label: "This Week", group: 1 },
  { key: "lastweek", label: "Last Week", group: 1 },
  { key: "custom", label: "Custom", group: 3 }
];
var PARTS_SEARCH_FIELDS = ["id", "driver.user.display_name", "vehicle.make", "vehicle.model", "stops.delivery_orders.order_number", "stops.delivery_orders.recipient_business"];
var resolvePartsDeliveryOrders = (context) => {
  const timeZone = context.rooftop.timezone ?? "UTC";
  const referenceLabel = context.referenceNumLabel ?? "PO Number";
  return {
    identity: { key: "parts-delivery", label: "Parts Delivery", icon: PiPackageFill },
    operationalModel: "self-managed",
    copy: {
      unit: { singular: "run", plural: "runs" },
      referenceLabel,
      searchPlaceholder: "Search runs\u2026",
      capNotice: "This range holds more runs than the display shows at once. Narrow the date range to see them all.",
      empty: {
        noOrders: { title: "No runs in this range", description: "Widen the date range to see runs from other days." },
        filtered: { title: "No runs match your filters", description: "Try adjusting your search or filter criteria." }
      }
    },
    rendererKeys: [PARTS_DELIVERY_RENDERER_KEY],
    itemKinds: ["partStop", "stopOverflow"],
    toView(model) {
      const source = model.source;
      try {
        return source.detail ? buildDetailView3(source.detail, timeZone) : buildIndexView3(source.index, timeZone);
      } catch {
        return null;
      }
    },
    toCardProps: toPartsDeliveryCardProps,
    columns: partsDeliveryColumns,
    controlsDefaults: { enabled: ["search", "dateRange", "metricTiles", "sort", "groupBy", "viewMode", "displayPreferences", "activeChips"] },
    metricTiles: PARTS_METRIC_TILES,
    sortOptions: PARTS_SORT_OPTIONS,
    defaultSort: { sortBy: "departure", sortOrder: "asc" },
    groupOptions: [
      { key: "none", label: "None", availability: "always" },
      { key: "driver", label: "Driver", availability: "always", emptyTitle: "Unassigned" }
    ],
    dateRangePresets: PARTS_DATE_RANGE_PRESETS,
    displayPreferences: PARTS_DELIVERY_DISPLAY_PREFERENCES,
    defaultPageSize: 25,
    searchFields: PARTS_SEARCH_FIELDS
  };
};

// src/products/parts-delivery/renderers.ts
import { createElement as createElement6 } from "react";

// src/products/parts-delivery/items/PartOrderRow.tsx
import { Box as Box47, Typography as Typography23 } from "@mui/material";
import { jsx as jsx63, jsxs as jsxs46 } from "react/jsx-runtime";
var PartOrderRow = ({ order }) => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsxs46(Box47, { sx: { display: "flex", alignItems: "baseline", gap: "8px", minWidth: 0 }, children: [
    /* @__PURE__ */ jsxs46(Typography23, { component: "span", sx: { flex: 1, minWidth: 0, fontSize: "12px", lineHeight: 1.25, color: tokens.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: [
      /* @__PURE__ */ jsx63(Box47, { component: "span", sx: { color: tokens.text.primary, fontWeight: 600 }, children: order.orderNumber }),
      ` \xB7 ${buildOrderRowNote2(order)}`
    ] }),
    /* @__PURE__ */ jsx63(Typography23, { component: "span", sx: { flex: "none", fontSize: "10px", lineHeight: 1, color: tokens.text.muted, whiteSpace: "nowrap" }, children: order.outcome ? resolveOutcomeBadge(order.outcome) : resolveOrderBadge2(order.status) })
  ] });
};
var PartOrderRow_default = PartOrderRow;

// src/products/parts-delivery/items/PartStopUrgentChip.tsx
import { Box as Box48 } from "@mui/material";
import { jsx as jsx64 } from "react/jsx-runtime";
var PartStopUrgentChip = () => {
  const tokens = useOrderTokens();
  return /* @__PURE__ */ jsx64(
    Box48,
    {
      component: "span",
      sx: {
        display: "inline-flex",
        alignItems: "center",
        borderRadius: `${tokens.radii.pill}px`,
        px: "6px",
        py: "3px",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        bgcolor: tokens.semantic.error,
        color: tokens.text.onBadge
      },
      children: "Urgent"
    }
  );
};
var PartStopUrgentChip_default = PartStopUrgentChip;

// src/products/parts-delivery/items/OrderItemPartStop.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var OrderItemPartStop = ({ stop, showOrders }) => {
  const showInnerList = showOrders && stop.orders.length > 0;
  return /* @__PURE__ */ jsx65(
    OrderItemStopBase_default,
    {
      density: "dense",
      marker: { state: resolveStopMarkerState2(stop), position: stop.position },
      name: buildStopName(stop),
      note: buildStopNote2(stop),
      tooltip: buildStopTooltip2(stop),
      thumbnail: stop.photo ? /* @__PURE__ */ jsx65(OrderPhotoTile_default, { photo: stop.photo }) : void 0,
      trailing: stop.hasRush ? /* @__PURE__ */ jsx65(PartStopUrgentChip_default, {}) : void 0,
      children: showInnerList ? stop.orders.map((order) => /* @__PURE__ */ jsx65(PartOrderRow_default, { order }, order.id)) : null
    }
  );
};
var OrderItemPartStop_default = OrderItemPartStop;

// src/products/parts-delivery/renderers.ts
var PartStopItem = ({ props }) => createElement6(OrderItemPartStop_default, props);
var partsDeliveryRenderers = {
  items: { partStop: PartStopItem }
};

// src/products/parts-delivery/index.ts
var partsRunPlugin = {
  product: "parts-delivery",
  adapter: partsRunOrderAdapter,
  resolve: resolvePartsDeliveryOrders,
  renderers: partsDeliveryRenderers
};

// src/products/composeResolvedProducts.ts
import { PiHouseFill } from "react-icons/pi";
var uniqueBy = (items, key) => {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const item of items) {
    const k = key(item);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(item);
    }
  }
  return out;
};
var uniqueStrings = (items) => [...new Set(items)];
var EMPTY_RESOLVED = {
  identity: { key: "moves", label: "Orders", icon: PiHouseFill },
  operationalModel: "self-managed",
  copy: {
    unit: { singular: "order", plural: "orders" },
    referenceLabel: "Reference",
    searchPlaceholder: "Search orders\u2026",
    capNotice: "",
    empty: { noOrders: { title: "No orders", description: "" }, filtered: { title: "No orders", description: "" } }
  },
  rendererKeys: [],
  itemKinds: [],
  toView: () => null,
  toCardProps: () => {
    throw new Error("the empty order resolver renders no cards");
  },
  columns: [],
  controlsDefaults: { enabled: [] },
  metricTiles: [],
  sortOptions: [],
  defaultSort: { sortBy: "anchor", sortOrder: "asc" },
  groupOptions: [],
  dateRangePresets: [],
  displayPreferences: [],
  defaultPageSize: 25,
  searchFields: []
};
var composeResolvedProducts = (perProduct) => {
  const first = perProduct[0];
  if (!first) return EMPTY_RESOLVED;
  const byProduct = new Map(perProduct.map((resolver) => [resolver.identity.key, resolver]));
  const viewOwner = /* @__PURE__ */ new WeakMap();
  return {
    // Home renders no product header, so identity is inert; keep a valid product key with a neutral label.
    identity: { key: first.identity.key, label: "Orders", icon: first.identity.icon },
    operationalModel: first.operationalModel,
    copy: {
      unit: { singular: "order", plural: "orders" },
      referenceLabel: first.copy.referenceLabel,
      searchPlaceholder: "Search orders\u2026",
      capNotice: "This range holds more orders than the display shows at once. Narrow the range to see them all.",
      empty: {
        noOrders: { title: "No orders in this range", description: "Widen the range to see orders from other days." },
        filtered: { title: "No orders match your filters", description: "Try adjusting your search or filter criteria." }
      }
    },
    rendererKeys: uniqueStrings(perProduct.flatMap((resolver) => [...resolver.rendererKeys])),
    itemKinds: uniqueStrings(perProduct.flatMap((resolver) => [...resolver.itemKinds])),
    toView(model, input) {
      const resolver = byProduct.get(model.product);
      if (!resolver) return null;
      const view = resolver.toView(model, input);
      if (view && typeof view === "object") viewOwner.set(view, resolver);
      return view;
    },
    toCardProps(view, preferences) {
      const resolver = (view && typeof view === "object" ? viewOwner.get(view) : void 0) ?? first;
      return resolver.toCardProps(view, preferences);
    },
    // A placeholder; a grid mount never reads columns.
    columns: first.columns,
    controlsDefaults: { enabled: [] },
    metricTiles: uniqueBy(perProduct.flatMap((resolver) => resolver.metricTiles), (tile) => tile.key),
    sortOptions: uniqueBy(perProduct.flatMap((resolver) => resolver.sortOptions), (option) => option.key),
    defaultSort: first.defaultSort,
    groupOptions: uniqueBy(perProduct.flatMap((resolver) => resolver.groupOptions), (option) => option.key),
    dateRangePresets: uniqueBy(perProduct.flatMap((resolver) => resolver.dateRangePresets), (preset) => preset.key),
    displayPreferences: uniqueBy(perProduct.flatMap((resolver) => resolver.displayPreferences), (definition) => definition.key),
    defaultPageSize: first.defaultPageSize,
    searchFields: uniqueStrings(perProduct.flatMap((resolver) => [...resolver.searchFields]))
  };
};

// src/products/valet/moveMilestoneTime.ts
var resolveMoveMilestoneTime = (move) => {
  const deliveredAt = move.delivery_successful || move.delivery_arrived;
  if (deliveredAt) return { kind: "delivered", at: deliveredAt };
  const pickedUpAt = move.pickup_successful || move.pickup_arrived;
  if (pickedUpAt) return { kind: "pickup", at: pickedUpAt };
  return { kind: "promise", at: move.ready_by ?? null };
};

// src/products/parts-delivery/partsRunBuckets.ts
var PARTS_RUN_BUCKETS = ["upcoming", "active", "completed", "cancelled"];
var COURIER_WITH_DELIVERY = { fulfillment_type: { _eq: COURIER_FULFILLMENT }, parts_delivery_id: { _is_null: false } };
var STATUS_DERIVED = {
  _or: [{ fulfillment_type: { _is_null: true } }, { fulfillment_type: { _neq: COURIER_FULFILLMENT } }, { parts_delivery_id: { _is_null: true } }]
};
var BOOKING2 = { orchestration_status: { _in: [...PARTS_COURIER_BOOKING_STATES] } };
var FAILED = { orchestration_status: { _eq: PARTS_COURIER_FAILED_STATE } };
var PAST_BOOKING = { _or: [{ orchestration_status: { _is_null: true } }, { orchestration_status: { _nin: [...PARTS_COURIER_BOOKING_STATES, PARTS_COURIER_FAILED_STATE] } }] };
var deliveryIn = (states) => ({ delivery_status: { _in: states } });
var COURIER_STATE = {
  // A delivery state the vocabulary does not name reads as Booked, so Upcoming is "not any other".
  upcoming: {
    _or: [
      BOOKING2,
      { _and: [PAST_BOOKING, { _or: [{ delivery_status: { _is_null: true } }, { delivery_status: { _nin: partsCourierDeliveryStatesIn(["active", "completed", "cancelled"]) } }] }] }
    ]
  },
  active: { _and: [PAST_BOOKING, deliveryIn(partsCourierDeliveryStatesIn(["active"]))] },
  completed: { _and: [PAST_BOOKING, deliveryIn(partsCourierDeliveryStatesIn(["completed"]))] },
  cancelled: { _or: [FAILED, { _and: [PAST_BOOKING, deliveryIn(partsCourierDeliveryStatesIn(["cancelled"]))] }] }
};
var RUN_STATUS = {
  upcoming: {
    _or: [
      { status: { _is_null: true } },
      { status: { _nin: [...MOBILITY_RUN_TILE_STATUSES.active, ...MOBILITY_RUN_TILE_STATUSES.completed, ...MOBILITY_RUN_TILE_STATUSES.cancelled] } }
    ]
  },
  active: { status: { _in: [...MOBILITY_RUN_TILE_STATUSES.active] } },
  completed: { status: { _in: [...MOBILITY_RUN_TILE_STATUSES.completed] } },
  cancelled: { status: { _in: [...MOBILITY_RUN_TILE_STATUSES.cancelled] } }
};
var bucketWhere = (bucket) => ({
  _or: [
    { _and: [STATUS_DERIVED, RUN_STATUS[bucket]] },
    { _and: [COURIER_WITH_DELIVERY, { parts_delivery: COURIER_STATE[bucket] }] }
  ]
});
var PARTS_RUN_BUCKET_WHERE = {
  upcoming: bucketWhere("upcoming"),
  active: bucketWhere("active"),
  completed: bucketWhere("completed"),
  cancelled: bucketWhere("cancelled")
};
var resolvePartsRunBucket = (run) => metricForPhase(resolvePartsRunPhase(run));

// src/products/composeCombinedOrderProducts.ts
var composeCombinedOrderProducts = (perProduct, options = {}) => {
  const base = composeResolvedProducts(perProduct);
  const byProduct = new Map(perProduct.map((resolver) => [resolver.identity.key, resolver]));
  const owners = /* @__PURE__ */ new WeakMap();
  const owner = (view) => view && typeof view === "object" ? owners.get(view) : void 0;
  const card = (view) => {
    const entry = owner(view);
    if (!entry) return void 0;
    return entry.card ?? (entry.card = entry.resolver.toCardProps(view, {}));
  };
  const nativeProps = (view, cell) => owner(view)?.resolver.columns.find((column) => column.cell === cell)?.cellProps?.(view) ?? null;
  const columns2 = [
    {
      name: "product",
      label: "Product",
      cell: "text",
      mobile: "pair",
      minWidth: 130,
      text: (view) => ({ value: owner(view)?.resolver.identity.label ?? null })
    },
    {
      name: "order",
      label: "Order / reference",
      cell: "text",
      mobile: "pair",
      minWidth: 180,
      text: (view) => {
        const entry = owner(view);
        const props = card(view);
        if (!entry || !props) return { value: null };
        const reference = props.details.rows.find((row2) => row2.key === "reference")?.value;
        return {
          value: [props.header.displayName, `#${entry.model.id}`, reference].filter(Boolean).join(" \xB7 "),
          isBold: true
        };
      }
    },
    {
      name: "customer",
      label: "Customer",
      cell: "text",
      mobile: "pair",
      minWidth: 140,
      text: (view) => {
        const entry = owner(view);
        return { value: entry ? options.customerLabel?.(entry.model) ?? null : null };
      }
    },
    {
      name: "status",
      label: "Status",
      cell: "status",
      mobile: "pair",
      minWidth: 110,
      cellProps: (view) => {
        const props = card(view);
        return props ? { statusBar: props.statusBar, tracker: props.tracker } : null;
      }
    },
    {
      name: "time",
      label: "Order time",
      cell: "promise",
      mobile: "pair",
      minWidth: 150,
      sortable: true,
      sortKey: "anchor",
      sortValue: (view) => owner(view)?.model.anchorMs ?? null,
      cellProps: (view) => nativeProps(view, "promise")
    },
    {
      name: "route",
      label: "Route / stops",
      cell: "route",
      mobile: "pair",
      minWidth: 200,
      cellProps: (view) => nativeProps(view, "route")
    },
    {
      name: "driverProvider",
      label: "Driver / provider",
      cell: "text",
      mobile: "pair",
      minWidth: 150,
      text: (view) => {
        const props = card(view);
        const provider = props?.details.rows.find((row2) => row2.key === "provider" || row2.key === "vendor")?.value;
        return { value: [props?.driver.driverName, provider].filter(Boolean).join(" \xB7 ") || null };
      }
    },
    { name: "actions", label: "Actions", cell: "actions", mobile: "bar", align: "right", pinned: "right" }
  ];
  const preferences = perProduct.flatMap((resolver) => resolver.displayPreferences);
  return {
    ...base,
    toView(model, input) {
      const resolver = byProduct.get(model.product);
      const view = resolver?.toView(model, input);
      if (!resolver || !view || typeof view !== "object") return null;
      owners.set(view, { model, resolver });
      return view;
    },
    toCardProps(view, preferences2) {
      const entry = owner(view);
      if (!entry) throw new Error("Unknown combined order view");
      entry.card = entry.resolver.toCardProps(view, preferences2);
      return entry.card;
    },
    columns: columns2,
    controlsDefaults: {
      enabled: [
        "search",
        "dateRange",
        "metricTiles",
        "sort",
        "viewMode",
        "displayPreferences",
        "activeChips",
        "product"
      ]
    },
    metricTiles: [
      { key: "all", label: "All orders", inAll: true },
      { key: "upcoming", label: "Upcoming", inAll: true },
      { key: "active", label: "Active", inAll: true },
      { key: "completed", label: "Completed", inAll: true },
      { key: "cancelled", label: "Canceled", inAll: true },
      { key: "unconfirmed", label: "Awaiting confirmation", inAll: true, flag: "isUnconfirmed", hideWhenZero: true }
    ],
    sortOptions: [{ key: "anchor", label: "Order time", anchor: true }],
    defaultSort: { sortBy: "anchor", sortOrder: "asc" },
    groupOptions: [{ key: "none", label: "None", availability: "always" }],
    // Card and row settings with the same key are separate preferences.
    displayPreferences: preferences.filter(
      (definition, index) => preferences.findIndex(
        (candidate) => candidate.surface === definition.surface && candidate.key === definition.key
      ) === index
    )
  };
};

// src/runtime/platform/portalFormatters.ts
import dayjs3 from "dayjs";
var capFirst2 = (value) => value && typeof value === "string" ? value.charAt(0).toUpperCase() + value.slice(1) : value;
var capEach = (value) => {
  if (!value || typeof value !== "string") return value;
  if (!value.includes(" ")) return value.charAt(0).toUpperCase() + value.slice(1);
  return value.split(" ").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
};
var getSnakeCase = (value, upperCase = false) => {
  if (!value) return value;
  const joined = value.split(/[,.\s-]/g).map((part) => part.trim()).join("_");
  return upperCase ? joined.toUpperCase() : joined.toLowerCase();
};
var getReadableText = (value, nbsp = false) => {
  if (!value) return value;
  return value.split(/[.\s_-]/g).map((part) => (capFirst2(part) ?? "").trim()).join(nbsp ? "\xA0" : " ");
};
var formatVehicleLine2 = (move, options) => {
  const includeYear = options?.includeYear ?? true;
  const includeColor = options?.includeColor ?? false;
  const fallbackVehicle = "Vehicle";
  const row2 = move;
  if (!row2) return fallbackVehicle;
  let type = "";
  if (row2.consumer_type === "customer") type = "Customer ";
  if (row2.consumer_type === "loaner") type = "Loaner ";
  const year = row2.vehicle_year || row2.driver_vehicle_year || null;
  const make = row2.vehicle_make || row2.driver_vehicle_make || null;
  const model = row2.vehicle_model || row2.driver_vehicle_model || null;
  const color = row2.vehicle_color || row2.driver_vehicle_color || null;
  let vehicle = "";
  if (make && model) vehicle = `${make} ${model}`;
  else if (make) vehicle = make;
  else if (model) vehicle = model;
  else vehicle = type + fallbackVehicle;
  if (includeYear && year) vehicle = `${year} ${vehicle}`;
  if (includeColor && color) vehicle = `${vehicle} (${capFirst2(color)})`;
  return vehicle;
};
var formatPhoneNumber2 = (phone) => {
  if (!phone) return "";
  const cleansed = `${phone}`.replace(/\D/g, "");
  const match = cleansed.match(/^(\d{1})?(\d{3})(\d{3})(\d{4})$/);
  if (!match) return phone;
  const intlCode = match[1] ? `+${match[1]} ` : "";
  return [intlCode, "(", match[2], ")\xA0", match[3], "-\u2060", match[4]].join("");
};
var formatMoveType = (move) => {
  if (move) {
    if (move.consumer_type === "customer" && move.consumer_at_pickup === 0) return "Concierge - Return";
    if (move.consumer_type === "customer" && move.consumer_at_pickup === 1) return "Concierge - Pickup";
    if (move.consumer_type === "loaner") return "Loaner";
  }
  return "Operational";
};
var formatCombinedStatus = (move) => {
  if (!move) return "Unknown";
  const cancelStatus = move.cancel_status ? move.cancel_status.toLowerCase() : null;
  const status = move.status ? move.status.toLowerCase() : null;
  if (cancelStatus) {
    if (cancelStatus === "pending") return "Pending Cancel";
    if (cancelStatus === "seen") return "Cancel Was Seen";
    if (cancelStatus === "canceled") return "Cancel Before Started";
    if (cancelStatus === "started") return "Cancel After Started";
    if (cancelStatus === "delivered") return "Cancel After Delivered";
    return capEach(cancelStatus) ?? "Upcoming";
  }
  if (status) {
    if (status === "dispatched") return "Dispatched";
    if (status === "pickup started" || status === "pickup_started") return "Pickup Started";
    if (status === "pickup arrived" || status === "pickup_arrived") return "Pickup Arrived";
    if (status === "pickup successful" || status === "pickup_successful") return "Pickup Successful";
    if (status === "delivery started" || status === "delivery_started") return "Delivery Started";
    if (status === "delivery arrived" || status === "delivery_arrived") return "Delivery Arrived";
    if (status === "delivery successful" || status === "delivery_successful") return "Delivery Successful";
    if (status === "awaitingresponse" || status === "awaiting response" || status === "awaiting_response") return "Awaiting Response";
    if (status === "accepted") return "Accepted";
    if (status === "arrived") return "Arrived";
    if (status === "pickedup" || status === "picked up" || status === "picked_up") return "Picked Up";
    if (status === "droppedoff" || status === "dropped off" || status === "dropped_off") return "Dropped Off";
    if (status === "canceled") return "Canceled";
    if (status === "failed") return "Failed";
    return capEach(status) ?? "Upcoming";
  }
  return "Upcoming";
};
var formatClockTime2 = (time) => {
  if (time && typeof time === "string") return dayjs3(time).format("MM/DD/YYYY hh:mm A z").replace(/\s/g, "\xA0");
  return null;
};
var IMAGE_ORDER = [
  // Legacy workflow step IDs (maintain backwards compatibility)
  "driver-front",
  "driver-front-wheel",
  "driver-rear",
  "driver-rear-wheel",
  "passenger-front",
  "passenger-front-wheel",
  "passenger-rear",
  "passenger-rear-wheel",
  "instrument-cluster",
  "interior-front",
  "interior-rear",
  "interior-trunk",
  "interior-vin",
  "vehicle-vin",
  // the same as interior-vin, but for older moves
  "vehicle-weight-badge",
  "fuel-pump",
  // New workflow step IDs
  "front-bumper",
  "windshield",
  "driver-side-full-view",
  "trunk-rear-bumper",
  "passenger-side-full-view",
  "engine-bay",
  "roof-photo",
  "license-plate-photo",
  "center-console",
  "backup-camera-photo",
  "vehicle-keys",
  "driver-carpet-photo",
  "third-row-photo",
  "vehicle-contents-photo",
  "exterior-damage-photos",
  "interior-damage-photos"
];
var orderOf = (stepId) => {
  if (stepId === "damages") return 500;
  if (stepId === "signature") return 1e3;
  const index = stepId ? IMAGE_ORDER.indexOf(stepId) : -1;
  return index >= 0 ? index : 100;
};
var sortPhotosByStandardOrder2 = (photos) => {
  if (!photos || !photos.length) return [];
  return photos.map((photo) => ({ ...photo, order: orderOf(photo.step_id) })).sort((a, b) => a.order - b.order);
};
var getPrivateStepIds = (workflowset) => {
  const collect = (steps, into) => {
    if (!steps || !steps.length) return into;
    for (const step of steps) {
      if (step.config && step.config.private) into.push(step);
      if (step.steps && step.steps.length) collect(step.steps, into);
    }
    return into;
  };
  if (!workflowset) return [];
  const privateSteps = [];
  collect(workflowset.pickupWorkflow?.steps, privateSteps);
  collect(workflowset.deliveryWorkflow?.steps, privateSteps);
  collect(workflowset.fuelWorkflow?.steps, privateSteps);
  return privateSteps.flatMap((step) => step.id ? [step.id] : []);
};
var getHumanReadableLabel = (value) => value.replace(/[.\s_-]/g, " ").split(" ").map((part) => part ? part[0].toUpperCase() + part.substring(1) : part).join(" ");
var getPhotoDescription = ({
  move,
  type,
  signer,
  photo
}) => {
  try {
    if (photo) {
      if (photo.step_id === "signature") return `Signer Name: ${signer}`;
      if (photo.step_id === "damages" && photo.name) {
        const damageNoteProp = photo.name.replace("damages-", "damages-note-");
        const workflowData = type === "pickup" ? move?.pickup_workflow_data : move?.delivery_workflow_data;
        const damageNote = workflowData ? workflowData[damageNoteProp] : void 0;
        return damageNote ? `Damage: ${String(damageNote)}` : "Damage";
      }
    }
  } catch (error) {
    console.log("Error getting photo description", error.message);
  }
  return null;
};
var STEP_ID_CATEGORIES = {
  // Legacy mappings
  "driver-front": "exterior",
  "passenger-front": "exterior",
  "driver-rear": "exterior",
  "passenger-rear": "exterior",
  "driver-front-wheel": "wheel",
  "driver-rear-wheel": "wheel",
  "passenger-front-wheel": "wheel",
  "passenger-rear-wheel": "wheel",
  "interior-front": "interior",
  "interior-rear": "interior",
  "interior-trunk": "interior",
  "interior-vin": "interior",
  "vehicle-vin": "interior",
  "instrument-cluster": "interior",
  "fuel-pump": "interior",
  "vehicle-weight-badge": "interior",
  // New workflow mappings
  "front-bumper": "exterior",
  windshield: "exterior",
  "trunk-rear-bumper": "exterior",
  "engine-bay": "exterior",
  "roof-photo": "exterior",
  "license-plate-photo": "exterior",
  "driver-side-full-view": "exterior",
  "passenger-side-full-view": "exterior",
  "center-console": "interior",
  "backup-camera-photo": "interior",
  "third-row-photo": "interior",
  "driver-carpet-photo": "interior",
  "vehicle-contents-photo": "interior",
  "vehicle-keys": "interior",
  // Damage photos (from multiple images)
  "exterior-damage-photos": "damage",
  "interior-damage-photos": "damage",
  damages: "damage"
};
var getImageCategory = (stepId, label) => {
  if (stepId && STEP_ID_CATEGORIES[stepId]) return STEP_ID_CATEGORIES[stepId];
  if (stepId && stepId.includes("wheel")) return "wheel";
  const labelLower = label?.toLowerCase() || "";
  if (labelLower.includes("exterior") || labelLower.includes("driver") || labelLower.includes("passenger")) return "exterior";
  if (labelLower.includes("interior") || labelLower.includes("instrument") || labelLower.includes("trunk") || labelLower.includes("vin")) return "interior";
  if (labelLower.includes("wheel")) return "wheel";
  if (labelLower.includes("damage")) return "damage";
  return "other";
};
var buildCarouselImages = ({ move, type, signer }) => {
  const row2 = move ?? null;
  const source = type === "pickup" ? row2?.pickup_photos : row2?.delivery_photos;
  const sorted = sortPhotosByStandardOrder2(source ?? []);
  const privateStepIds = getPrivateStepIds(row2?.workflowset);
  const visible = sorted.filter((photo) => !privateStepIds.includes(photo.step_id ?? ""));
  const images = [];
  visible.forEach((photo, index) => {
    if (!photo || !photo.step_id || !photo.url) return;
    const label = getHumanReadableLabel(photo.step_id === "damages" ? photo.name ?? "" : photo.step_id);
    const description2 = getPhotoDescription({ move: row2, type, signer, photo });
    images.push({
      id: photo.step_id,
      label,
      original: photo.url,
      originalTitle: label,
      thumbnail: photo.url,
      thumbnailTitle: label,
      thumbnailLabel: label,
      description: description2 || label,
      isSignature: photo.step_id === "signature",
      category: getImageCategory(photo.step_id, label),
      index: `${index}`
    });
  });
  return images;
};
function getWorkflowData(type, workflowData, format = "csv", keyValue = false, workflowset = null) {
  const typeStr = type ? `${type}_` : "extra_";
  if (!workflowData || typeof workflowData !== "object") return null;
  const edited = { ...workflowData };
  if (workflowset) {
    for (const id of getPrivateStepIds(workflowset)) delete edited[id];
  }
  const keys = Object.keys(edited);
  const formatKey = (key) => {
    if (format === "csv") return typeStr + getSnakeCase(key);
    if (format === "move_details") return getReadableText(key, true);
    return key;
  };
  if (keyValue) return keys.map((key, index) => ({ index, key: formatKey(key), val: edited[key] }));
  const out = {};
  keys.forEach((key) => {
    out[formatKey(key)] = edited[key];
  });
  return out;
}
var portalOrderFormatters = {
  formatVehicleLine: formatVehicleLine2,
  formatPhoneNumber: formatPhoneNumber2,
  buildCarouselImages,
  sortPhotosByStandardOrder: sortPhotosByStandardOrder2
};

// src/runtime/platform/createOrderIndexPlatform.ts
var createOrderIndexPlatform = (overrides) => ({
  ...portalOrderFormatters,
  copyToClipboard: (value) => {
    if (value) void navigator.clipboard?.writeText(value);
  },
  ...overrides
});

// src/runtime/platform/rooftopFacts.ts
var resolveViewerTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
var resolveRooftopTimezone = (rooftop) => rooftop?.location?.timezone || resolveViewerTimezone();
var toOrderOperationalModel = (model) => model === "self-managed" ? "self-managed" : DEFAULT_ORDER_OPERATIONAL_MODEL;
var toOrderRooftopFacts = (rooftop, operationalModel = DEFAULT_ORDER_OPERATIONAL_MODEL) => ({
  id: rooftop?.id ?? 0,
  name: rooftop?.name ?? null,
  operationalModel,
  timezone: resolveRooftopTimezone(rooftop),
  locationId: rooftop?.location?.id ?? null,
  effectiveConfig: rooftop?.effective_config ?? null
});

// src/runtime/context/OrderIndexProvider.tsx
import { createContext as createContext3, useContext as useContext3 } from "react";

// src/runtime/context/interaction.ts
var INERT_ORDER_INTERACTION = { isAssigning: () => false };

// src/runtime/context/OrderIndexProvider.tsx
import { jsx as jsx66 } from "react/jsx-runtime";
var DEFAULT_ORDER_INDEX_CONFIG = {
  rooftop: NO_ROOFTOP_FACTS,
  configLoaded: false,
  referenceNumLabel: null,
  viewer: { role: null, isAdminViewer: false, customerId: null },
  // A surface with no platform cannot render; a consumer that mounts one always supplies it.
  platform: void 0,
  interaction: INERT_ORDER_INTERACTION
};
var OrderIndexContext = createContext3(DEFAULT_ORDER_INDEX_CONFIG);
var OrderIndexProvider = ({ value, children }) => /* @__PURE__ */ jsx66(OrderIndexContext.Provider, { value, children });
var useOrderIndex = () => useContext3(OrderIndexContext);

// src/runtime/context/presentation.ts
var PORTAL_PRESENTATION = {
  readOnly: false,
  disabledControls: [],
  hiddenActionKeys: [],
  collapsibleControls: false,
  refresh: false,
  integratorExport: false
};
var resolvePresentation = (presentation) => ({
  readOnly: presentation?.readOnly ?? PORTAL_PRESENTATION.readOnly,
  disabledControls: presentation?.controls?.disable ?? PORTAL_PRESENTATION.disabledControls,
  hiddenActionKeys: presentation?.hiddenActionKeys ?? PORTAL_PRESENTATION.hiddenActionKeys,
  compact: presentation?.compact,
  collapsibleControls: presentation?.collapsibleControls ?? PORTAL_PRESENTATION.collapsibleControls,
  refresh: presentation?.refresh ?? PORTAL_PRESENTATION.refresh,
  integratorExport: presentation?.integratorExport ?? PORTAL_PRESENTATION.integratorExport
});

// src/runtime/products/guestRides/useGuestRideStream.ts
import { useEffect as useEffect5, useMemo as useMemo6, useState as useState9 } from "react";
import { useSubscription } from "@apollo/client";
var STREAM_LOOKBACK_MINUTES = 15;
var useGuestRideStream = ({ customerIds, includePayer, window, filters, refetchIndex, refetchPage }) => {
  const [cursor] = useState9(() => new Date(Date.now() - STREAM_LOOKBACK_MINUTES * 60 * 1e3).toISOString());
  const idsKey = customerIds.join(",");
  const variables = useMemo6(
    () => guestRideOrderAdapter.buildStreamVariables({
      customerIds,
      includePayer,
      window,
      filters,
      limit: guestRideOrderAdapter.indexCap + 1,
      cursor
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids' content and window bounds are the inputs; filters never enter the stream where
    [idsKey, includePayer, window?.startIso, window?.endIso, cursor]
  );
  const { error } = useSubscription(GuestRideStreamDocument, {
    variables,
    skip: customerIds.length === 0,
    // A dropped stream must not take the surface with it: the queries keep serving from cache and refetch on the next interaction.
    errorPolicy: "all",
    onData: ({ data }) => {
      if (!(data.data?.guestrides_stream ?? []).length) return;
      refetchIndex();
      refetchPage();
    }
  });
  useEffect5(() => {
    if (error) console.error("Guest ride stream failed; falling back to refetch and poll.", error);
  }, [error]);
};

// src/runtime/products/valet/useValetOrderStream.ts
import { useEffect as useEffect6, useMemo as useMemo7, useRef as useRef4, useState as useState10 } from "react";
import { useSubscription as useSubscription2 } from "@apollo/client";
var STREAM_LOOKBACK_MINUTES2 = 15;
var useValetOrderStream = ({ customerIds, includePayer, window, filters, refetchIndex, refetchPage, pageEntries }) => {
  const [cursor] = useState10(() => new Date(Date.now() - STREAM_LOOKBACK_MINUTES2 * 60 * 1e3).toISOString());
  const idsKey = customerIds.join(",");
  const variables = useMemo7(
    () => valetOrderAdapter.buildStreamVariables({
      customerIds,
      includePayer,
      window,
      filters,
      limit: valetOrderAdapter.indexCap + 1,
      cursor
    }),
    // The filters are not a dependency on purpose: the stream's where never reads them, and re-subscribing on a filter change would replay the lookback for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey, includePayer, window?.startIso, window?.endIso, cursor]
  );
  const pageMoveIds = useMemo7(() => new Set(pageEntries.flatMap(valetEntryMoveIds)), [pageEntries]);
  const attemptedUnrecognizedRef = useRef4(/* @__PURE__ */ new Set());
  const { error } = useSubscription2(ValetMovesStreamDocument, {
    variables,
    skip: customerIds.length === 0,
    // A dropped stream must not take the surface with it: the queries keep serving from cache and refetch on the next interaction.
    errorPolicy: "all",
    onData: ({ client, data }) => {
      const rows = data.data?.moves_stream ?? [];
      if (!rows.length) return;
      const startMs = window ? Date.parse(window.startIso) : null;
      const endMs = window ? Date.parse(window.endIso) : null;
      const isInWindow = (value) => {
        if (startMs === null || endMs === null) return true;
        if (!value) return false;
        const ms = Date.parse(value);
        return ms >= startMs && ms <= endMs;
      };
      const isRecognized = (moveId) => !!client.cache.readFragment({
        id: client.cache.identify({ __typename: "moves", id: moveId }),
        fragment: ValetStreamRecognitionFragmentDoc
      });
      const effects = classifyStreamBatch({
        rows,
        isRecognized,
        hasDeparted: (row2) => row2.active === 0 || !isInWindow(row2.pickup_time) && !isInWindow(row2.ready_by),
        attemptedUnrecognized: attemptedUnrecognizedRef.current
      });
      if (effects.refetchIndex) refetchIndex();
      if (effects.changedIds.some((id) => pageMoveIds.has(id))) refetchPage();
    }
  });
  useEffect6(() => {
    if (error) console.error("Valet order stream failed; falling back to refetch and poll.", error);
  }, [error]);
};

// src/runtime/plugins.ts
var RUNTIME_PLUGINS = {
  moves: { ...valetPlugin, useLive: useValetOrderStream },
  // Mobile Service polls; no live hook. Its runs join the merged feed through this registration.
  "mobile-service": { ...mobileServicePlugin },
  // Guest Rides streams (the guestrides_stream), reused as a set-change signal.
  "guest-rides": { ...guestRidePlugin, useLive: useGuestRideStream },
  // Parts Delivery polls (own-driver runs and brokered-courier runs alike); no live hook.
  "parts-delivery": { ...partsRunPlugin }
};
var resolveOrderPlugins = (products) => products.flatMap((product) => {
  const plugin = RUNTIME_PLUGINS[product];
  return plugin ? [plugin] : [];
});
var resolveOrderRenderers = (products) => composeRenderers(SHARED_RENDERERS, resolveOrderPlugins(products));

// src/runtime/hooks/useOrderFeed.ts
import { createElement as createElement10, Fragment as Fragment12, useCallback as useCallback6, useMemo as useMemo10 } from "react";

// src/runtime/hooks/useOrderFeedIndex.ts
import { createElement as createElement7, useCallback as useCallback4, useEffect as useEffect7, useMemo as useMemo8 } from "react";
import { useQuery } from "@apollo/client";

// src/runtime/hooks/useOrderQueryResults.ts
import { useCallback as useCallback3, useState as useState11 } from "react";
var useOrderQueryResults = () => {
  const [results, setResults] = useState11(() => /* @__PURE__ */ new Map());
  const report = useCallback3((plugin, result) => {
    setResults((previous) => {
      if (!result && !previous.has(plugin)) return previous;
      const next = new Map(previous);
      if (result) next.set(plugin, result);
      else next.delete(plugin);
      return next;
    });
  }, []);
  return { results, report };
};

// src/runtime/hooks/useOrderFeedIndex.ts
function OrderIndexQuery({
  plugin,
  customerIds,
  includePayer,
  window,
  filters,
  report
}) {
  const { adapter } = plugin;
  const skip = customerIds.length === 0;
  const variables = useMemo8(
    () => adapter.buildIndexVariables({ customerIds, includePayer, window, filters, limit: adapter.indexCap + 1 }),
    [adapter, customerIds, includePayer, window, filters]
  );
  const { data, loading, error, refetch } = useQuery(adapter.documents.index, {
    variables,
    fetchPolicy: "cache-and-network",
    skip
  });
  useEffect7(() => {
    report(plugin, { plugin, data, loading, error, run: !skip, refetch });
  }, [report, plugin, data, loading, error, skip, refetch]);
  useEffect7(() => () => report(plugin), [report, plugin]);
  return null;
}
var useOrderFeedIndex = ({
  plugins,
  customerIds,
  includePayer,
  window,
  filters,
  rooftops,
  nowMs,
  extras
}) => {
  const { results, report } = useOrderQueryResults();
  const skip = customerIds.length === 0;
  const adapted = useMemo8(() => {
    const perPlugin = plugins.map(
      (plugin) => plugin.adapter.adaptIndex(results.get(plugin)?.data, {
        rooftops,
        nowMs,
        extras: extras?.[plugin.product]
      })
    );
    return mergeAdapterResults(perPlugin);
  }, [plugins, results, rooftops, extras]);
  const loading = !skip && plugins.some((plugin) => results.get(plugin)?.loading ?? true);
  const error = plugins.map((plugin) => results.get(plugin)?.error).find(Boolean) ?? null;
  const refetch = useCallback4(() => {
    if (skip) return Promise.resolve();
    return Promise.all(plugins.flatMap((plugin) => {
      const result = results.get(plugin);
      return result?.run ? [result.refetch()] : [];
    }));
  }, [plugins, results, skip]);
  const observers = plugins.map(
    (plugin) => createElement7(OrderIndexQuery, {
      key: plugin.product,
      plugin,
      customerIds,
      includePayer,
      window,
      filters,
      report
    })
  );
  return { entries: adapted.entries, capReached: adapted.capReached, loading, error, refetch, observers };
};

// src/runtime/hooks/useOrderFeedPage.ts
import { createElement as createElement8, useCallback as useCallback5, useEffect as useEffect8, useMemo as useMemo9 } from "react";
import { useQuery as useQuery2 } from "@apollo/client";
var NO_IDS = [];
function OrderPageQuery({ plugin, ids, report }) {
  const { adapter } = plugin;
  const run = !!adapter.documents.page && ids.length > 0;
  const variables = useMemo9(() => adapter.buildPageVariables?.(ids) ?? {}, [adapter, ids]);
  const { data, loading, error, refetch } = useQuery2(adapter.documents.page ?? adapter.documents.index, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !run,
    pollInterval: run ? adapter.pollMs ?? 0 : 0
  });
  useEffect8(() => {
    report(plugin, { plugin, data, loading, error, run, refetch });
  }, [report, plugin, data, loading, error, run, refetch]);
  useEffect8(() => () => report(plugin), [report, plugin]);
  return null;
}
var failedModels = /* @__PURE__ */ new WeakMap();
var markHydrationFailed = (entries, product) => entries.map((entry) => {
  if (entry.kind !== "order" || entry.model.product !== product || entry.model.isHydrated) return entry;
  let failed = failedModels.get(entry.model);
  if (!failed) {
    failed = { ...entry.model, hydrationFailed: true };
    failedModels.set(entry.model, failed);
  }
  return { kind: "order", model: failed };
});
var useOrderFeedPage = ({
  plugins,
  entries,
  state,
  resolved,
  isAdminViewer
}) => {
  const derived = useMemo9(
    () => deriveFeed({ entries, state, resolved, isAdminViewer }),
    [entries, state, resolved, isAdminViewer]
  );
  const { results, report } = useOrderQueryResults();
  const running = useMemo9(
    () => plugins.filter((plugin) => !!plugin.adapter.documents.page && !!derived.hydrateIds[plugin.product]?.length),
    [plugins, derived.hydrateIds]
  );
  const pagedEntries = useMemo9(() => {
    let merged = derived.pagedEntries;
    for (const plugin of running) {
      const { adapter } = plugin;
      if (!adapter.adaptPage || !adapter.mergePage) continue;
      merged = adapter.mergePage(merged, adapter.adaptPage(results.get(plugin)?.data));
      if (results.get(plugin)?.error) merged = markHydrationFailed(merged, plugin.product);
    }
    return merged;
  }, [derived.pagedEntries, running, results]);
  const loading = running.some((plugin) => results.get(plugin)?.loading ?? true);
  const pageError = running.map((plugin) => results.get(plugin)?.error).find(Boolean) ?? null;
  const refetch = useCallback5(
    () => Promise.all(running.flatMap((plugin) => {
      const result = results.get(plugin);
      return result?.run ? [result.refetch()] : [];
    })),
    [running, results]
  );
  const observers = plugins.map(
    (plugin) => createElement8(OrderPageQuery, {
      key: plugin.product,
      plugin,
      ids: derived.hydrateIds[plugin.product] ?? NO_IDS,
      report
    })
  );
  return { ...derived, pagedEntries, loading, pageError, refetch, observers };
};

// src/runtime/hooks/useOrderLive.ts
import { createElement as createElement9 } from "react";
var liveComponents = /* @__PURE__ */ new WeakMap();
function liveComponent(useLive) {
  let component = liveComponents.get(useLive);
  if (!component) {
    component = function OrderLive({ input }) {
      useLive(input);
      return null;
    };
    liveComponents.set(useLive, component);
  }
  return component;
}
var useOrderLive = (plugins, input) => plugins.map(
  (plugin) => plugin.useLive ? createElement9(liveComponent(plugin.useLive), { key: plugin.product, input }) : null
);

// src/runtime/hooks/useOrderFeed.ts
var useOrderFeed = ({
  plugins,
  window,
  rooftops,
  includePayer,
  nowMs,
  extras,
  filters,
  state,
  resolved,
  isAdminViewer
}) => {
  const customerIds = useMemo10(() => rooftops.map((rooftop) => rooftop.id).filter((id) => !!id), [rooftops]);
  const index = useOrderFeedIndex({ plugins, customerIds, includePayer, window, filters, rooftops, nowMs, extras });
  const page = useOrderFeedPage({ plugins, entries: index.entries, state, resolved, isAdminViewer });
  const live = useOrderLive(plugins, {
    customerIds,
    includePayer,
    window,
    filters,
    refetchIndex: index.refetch,
    refetchPage: page.refetch,
    pageEntries: page.pagedEntries
  });
  const feed = useMemo10(
    () => ({
      entries: page.entries,
      pagedEntries: page.pagedEntries,
      pageSections: page.pageSections,
      metricCounts: page.metricCounts,
      offerStatusCounts: page.offerStatusCounts,
      capReached: index.capReached,
      loading: index.loading || page.loading,
      error: index.error,
      pageError: page.pageError,
      page: page.page,
      pageCount: page.pageCount,
      totalCount: page.totalCount
    }),
    [
      page.entries,
      page.pagedEntries,
      page.pageSections,
      page.metricCounts,
      page.offerStatusCounts,
      page.pageError,
      page.page,
      page.pageCount,
      page.totalCount,
      page.loading,
      index.capReached,
      index.loading,
      index.error
    ]
  );
  const { refetch: refetchIndex } = index;
  const { refetch: refetchPage } = page;
  const refetch = useCallback6(() => Promise.all([refetchIndex(), refetchPage()]), [refetchIndex, refetchPage]);
  return {
    observers: createElement10(Fragment12, null, index.observers, page.observers, live),
    feed,
    filteredIds: page.filteredIds,
    sortBy: page.sortBy,
    groupBy: page.groupBy,
    visibleSortOptions: page.visibleSortOptions,
    availableGroupOptions: page.availableGroupOptions,
    refetch
  };
};

// src/runtime/hooks/buildOrderWindow.ts
var buildOrderWindow = ({ preset, startDate, endDate }, reference) => resolveEffectiveDateWindow({ preset, startDate, endDate, reference });

// src/runtime/hooks/useOrderWindow.ts
import { useMemo as useMemo11 } from "react";
var useOrderWindow = ({ preset, startDate, endDate }) => useMemo11(() => buildOrderWindow({ preset, startDate, endDate }), [preset, startDate, endDate]);

// src/runtime/products/valet/toOrderCardActions.ts
var toOrderCardActions = (handlers) => ({
  openMove: (targetId) => {
    if (targetId !== void 0) handlers.onOpenMove(targetId);
  },
  openChat: () => handlers.onOpenChat(),
  createDuplicate: () => handlers.onCreateDuplicate(),
  createInverse: () => handlers.onCreateInverse(),
  changePromiseTime: () => handlers.onChangePromiseTime(),
  cancelMove: () => handlers.onCancelOrder(),
  assignDriver: () => handlers.onAssignDriver()
});

// src/runtime/products/valet/useValetCardActions.ts
import { useMemo as useMemo12 } from "react";
var useValetCardActions = (presentation) => {
  const { interaction } = useOrderIndex();
  const { readOnly, hiddenActionKeys } = presentation;
  return useMemo12(() => {
    if (readOnly) return INERT_ORDER_INTERACTION;
    if (!hiddenActionKeys.length) return interaction;
    const hidden = new Set(hiddenActionKeys);
    const resolveCardActions = interaction.resolveCardActions;
    return {
      ...interaction,
      resolveCardActions: resolveCardActions ? (model, view) => {
        const actions = resolveCardActions(model, view);
        if (!actions) return actions;
        return Object.fromEntries(Object.entries(actions).filter(([key]) => !hidden.has(key)));
      } : void 0
    };
  }, [interaction, readOnly, hiddenActionKeys]);
};

// src/runtime/products/valet/tabs/OrdersTab.tsx
import { useEffect as useEffect10, useMemo as useMemo15 } from "react";
import { Box as Box50, LinearProgress, useMediaQuery, useTheme as useTheme2 } from "@mui/material";
import { PAGE_SX } from "@hopdrive/sdk-theme";

// src/runtime/products/valet/export/countDriveMoves.ts
var countDriveMovesForScenarioIds = (entries, scenarioIds) => {
  const driveCountByScenarioId = /* @__PURE__ */ new Map();
  for (const item of entries) {
    if (!isResolvedOrderEntry(item.entry)) continue;
    driveCountByScenarioId.set(item.entry.order.scenarioId, item.entry.order.legs.filter(isDriveLeg).length);
  }
  return scenarioIds.reduce((sum, id) => sum + (driveCountByScenarioId.get(id) ?? 0), 0);
};

// src/runtime/products/valet/resolveMetricDeepLink.ts
function resolveMetricDeepLink(param, validKeys) {
  if (!param) return null;
  return validKeys.includes(param) ? param : null;
}

// src/runtime/products/valet/useValetFilterPools.ts
import { useMemo as useMemo13 } from "react";
import { useQuery as useQuery3 } from "@apollo/client";
var EMPTY_ROWS = [];
var toOptions = (values) => values.map((value) => ({ value, label: value }));
var useValetFilterPools = ({ customerId, window, filters }) => {
  const variables = useMemo13(() => ({ customerId, start: window.startIso, end: window.endIso }), [customerId, window.startIso, window.endIso]);
  const { data } = useQuery3(ValetFilterPoolsDocument, { variables, fetchPolicy: "cache-and-network", skip: !customerId });
  const rows = data?.moves ?? EMPTY_ROWS;
  return useMemo13(
    () => ({
      createdBy: toOptions(deriveCreatorOptions(rows, filters.createdBy)),
      market: toOptions(deriveMarketOptions(rows, filters.market)),
      spansMultipleCustomers: poolSpansMultipleCustomers(rows)
    }),
    [rows, filters.createdBy, filters.market]
  );
};

// src/runtime/products/valet/tabs/OrdersTabActions.tsx
import { useEffect as useEffect9, useMemo as useMemo14 } from "react";

// src/runtime/products/valet/tabs/OrderExportDialog.tsx
import React13 from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as Button6,
  Typography as Typography24,
  Box as Box49,
  CircularProgress
} from "@mui/material";
import { BiFile, BiCheckCircle } from "react-icons/bi";
import { jsx as jsx67, jsxs as jsxs47 } from "react/jsx-runtime";
var OrderExportDialog = ({
  open,
  onClose,
  totalCount,
  currentPageCount,
  onExportAll,
  onExportCurrentPage
}) => {
  const [loading, setLoading] = React13.useState(false);
  const handleExportAll = async () => {
    setLoading(true);
    try {
      await onExportAll();
      onClose();
    } catch (error) {
      console.error("Error exporting all results:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleExportCurrentPage = () => {
    onExportCurrentPage();
    onClose();
  };
  return /* @__PURE__ */ jsxs47(Dialog, { open, onClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx67(DialogTitle, { children: /* @__PURE__ */ jsxs47(Box49, { display: "flex", alignItems: "center", gap: 1, children: [
      /* @__PURE__ */ jsx67(BiFile, { size: 24 }),
      "Export Moves"
    ] }) }),
    /* @__PURE__ */ jsxs47(DialogContent, { children: [
      /* @__PURE__ */ jsx67(Typography24, { variant: "body1", gutterBottom: true, children: "Choose what you'd like to export:" }),
      /* @__PURE__ */ jsxs47(Box49, { mt: 2, display: "flex", flexDirection: "column", gap: 2, children: [
        /* @__PURE__ */ jsxs47(
          Box49,
          {
            sx: {
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              "&:hover": {
                backgroundColor: loading ? "transparent" : "action.hover"
              }
            },
            onClick: loading ? void 0 : handleExportAll,
            children: [
              /* @__PURE__ */ jsxs47(Box49, { display: "flex", alignItems: "center", gap: 1, mb: 1, children: [
                /* @__PURE__ */ jsx67(BiCheckCircle, { size: 20 }),
                /* @__PURE__ */ jsx67(Typography24, { variant: "subtitle1", fontWeight: "bold", children: "Export All Results" })
              ] }),
              /* @__PURE__ */ jsxs47(Typography24, { variant: "body2", color: "text.secondary", children: [
                "Export all ",
                totalCount.toLocaleString(),
                " matching moves. This may take a moment for large datasets."
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs47(
          Box49,
          {
            sx: {
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              "&:hover": {
                backgroundColor: loading ? "transparent" : "action.hover"
              }
            },
            onClick: loading ? void 0 : handleExportCurrentPage,
            children: [
              /* @__PURE__ */ jsxs47(Box49, { display: "flex", alignItems: "center", gap: 1, mb: 1, children: [
                /* @__PURE__ */ jsx67(BiCheckCircle, { size: 20 }),
                /* @__PURE__ */ jsx67(Typography24, { variant: "subtitle1", fontWeight: "bold", children: "Export Current Page" })
              ] }),
              /* @__PURE__ */ jsxs47(Typography24, { variant: "body2", color: "text.secondary", children: [
                "Export only the ",
                currentPageCount,
                " moves currently displayed on this page."
              ] })
            ]
          }
        )
      ] }),
      loading && /* @__PURE__ */ jsxs47(Box49, { display: "flex", alignItems: "center", gap: 1, mt: 2, children: [
        /* @__PURE__ */ jsx67(CircularProgress, { size: 16 }),
        /* @__PURE__ */ jsx67(Typography24, { variant: "body2", color: "text.secondary", children: "Fetching all moves for export..." })
      ] })
    ] }),
    /* @__PURE__ */ jsx67(DialogActions, { children: /* @__PURE__ */ jsx67(Button6, { onClick: onClose, disabled: loading, children: "Cancel" }) })
  ] });
};
var OrderExportDialog_default = OrderExportDialog;

// src/runtime/products/valet/tabs/ordersTabContribution.tsx
import { BiFile as BiFile2, BiListCheck } from "react-icons/bi";
import { jsx as jsx68 } from "react/jsx-runtime";
function ordersTabContribution({ openExport, nothingToExport, refresh, openAssignmentsReport }) {
  return {
    ...refresh ? { sync: refresh } : {},
    overflow: [
      ...openAssignmentsReport ? [{ key: "view-assignments", label: "View Assignments", icon: /* @__PURE__ */ jsx68(BiListCheck, {}), onSelect: openAssignmentsReport }] : [],
      {
        key: "export-csv",
        label: "Export CSV",
        icon: /* @__PURE__ */ jsx68(BiFile2, {}),
        onSelect: openExport,
        disabledReason: nothingToExport ? "No orders to export" : void 0
      }
    ]
  };
}

// src/runtime/products/valet/tabs/useOrdersExport.ts
import { useCallback as useCallback7, useState as useState12 } from "react";
import { useLazyQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { ExportToCsv } from "export-to-csv";

// src/runtime/products/valet/export/ordersCsvExport.ts
import dayjs4 from "dayjs";
var flattenDriveMoves = (scenarios, orderedScenarioIds) => {
  const byId = new Map(scenarios.map((row2) => [row2.id, row2]));
  return orderedScenarioIds.map((id) => byId.get(id)).filter((row2) => !!row2).flatMap((row2) => row2.moves.map((member) => member.move).filter((move) => move.move_type === "drive"));
};
var gatherWorkflowHeaders = (moves, phase) => {
  const headers = /* @__PURE__ */ new Set();
  for (const move of moves) {
    const data = getWorkflowData(phase, phase === "pickup" ? move.pickup_workflow_data : move.delivery_workflow_data, "csv");
    for (const key of Object.keys(data ?? {})) headers.add(key);
  }
  return [...headers];
};
var buildWorkflowColumns = (move, phase, headers) => {
  const data = getWorkflowData(phase, phase === "pickup" ? move.pickup_workflow_data : move.delivery_workflow_data, "csv") ?? {};
  return Object.fromEntries(headers.map((key) => [key, data[key] !== void 0 ? data[key] : ""]));
};
var FIXED_COLUMN_KEYS = /* @__PURE__ */ new Set([
  "move_id",
  "status",
  "market",
  "type",
  "consumer_name",
  "consumer_phone",
  "ready_by",
  "time_completed",
  "time_canceled",
  "cancel_reason",
  "vehicle_stock",
  "vehicle_vin",
  "vehicle_make",
  "vehicle_model",
  "vehicle_year",
  "vehicle_color",
  "vehicle_odometer",
  "formatted_vehicle",
  "lane",
  "distance",
  "created_by",
  "workflow"
]);
var buildScenarioOrdersCsvRows = ({
  scenarios,
  orderedScenarioIds,
  isAdminViewer,
  referenceLabel,
  integratorExport
}) => {
  const moves = flattenDriveMoves(scenarios, orderedScenarioIds);
  const pickupHeaders = gatherWorkflowHeaders(moves, "pickup");
  const deliveryHeaders = gatherWorkflowHeaders(moves, "delivery");
  const referenceKey = integratorExport || FIXED_COLUMN_KEYS.has(referenceLabel) || pickupHeaders.includes(referenceLabel) || deliveryHeaders.includes(referenceLabel) ? "reference_num" : referenceLabel;
  const adminColumns = isAdminViewer && !integratorExport;
  return moves.map((move) => {
    return {
      move_id: move.id,
      status: formatCombinedStatus(move) || "",
      ...integratorExport ? { customer_name: move.customer?.name || "", payer: move.payer_id || "" } : {},
      ...adminColumns ? { market: move.customer?.name || "" } : {},
      type: formatMoveType(move) || "",
      consumer_name: move.consumer_name || "",
      consumer_phone: formatPhoneNumber2(move.consumer_phone || "") || "",
      ready_by: formatClockTime2(move.ready_by || "") || "",
      time_completed: formatClockTime2(move.delivery_successful || "") || "",
      time_canceled: formatClockTime2(move.canceled_at || "") || "",
      cancel_reason: move.cancel_reason || "",
      [referenceKey]: move.reference_num || "",
      vehicle_stock: move.vehicle_stock || "",
      vehicle_vin: move.vehicle_vin || "",
      vehicle_make: move.vehicle_make || "",
      vehicle_model: move.vehicle_model || "",
      vehicle_year: move.vehicle_year || "",
      vehicle_color: move.vehicle_color || "",
      vehicle_odometer: move.vehicle_odometer || "",
      formatted_vehicle: formatVehicleLine2(move, { includeColor: true }) || "",
      lane: move.lane?.description || "",
      distance: move.lane?.distance_miles || 0,
      created_by: move.createdBy && move.createdBy.includes("@") ? move.createdBy : "",
      ...adminColumns ? { workflow: move.workflowset?.name || "" } : {},
      ...buildWorkflowColumns(move, "pickup", pickupHeaders),
      ...buildWorkflowColumns(move, "delivery", deliveryHeaders)
    };
  });
};
var resolveScenarioCsvFilename = (startIso, endIso) => `moves_dashboard_${dayjs4(startIso).format("MM-DD-YYYY")}_to_${dayjs4(endIso).format("MM-DD-YYYY")}`;

// src/runtime/products/valet/export/scenarioOrdersExport.query.generated.ts
import { resolveExecutor as resolveExecutor11 } from "@hopdrive/sdk-core";
var ScenarioOrdersExportDocument = { "__text": "query scenarioOrdersExport($scenarioIds: [bigint!]!) {\n  scenarios(where: {id: {_in: $scenarioIds}}) {\n    id\n    moves(order_by: {cardinality: asc}) {\n      id\n      cardinality\n      move {\n        id\n        move_type\n        status\n        cancel_status\n        cancel_reason\n        canceled_at\n        consumer_name\n        consumer_phone\n        consumer_type\n        consumer_at_pickup\n        ready_by\n        delivery_successful\n        reference_num\n        vehicle_stock\n        vehicle_vin\n        vehicle_make\n        vehicle_model\n        vehicle_year\n        vehicle_color\n        vehicle_odometer\n        createdBy\n        payer_id\n        pickup_workflow_data\n        delivery_workflow_data\n        customer {\n          id\n          name\n        }\n        workflowset {\n          id\n          name\n        }\n        lane {\n          id\n          description\n          distance_miles\n        }\n      }\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "scenarioOrdersExport" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "scenarioIds" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "scenarios" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "scenarioIds" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "cardinality" }, "value": { "kind": "EnumValue", "value": "asc" } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cardinality" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "move_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "cancel_reason" } }, { "kind": "Field", "name": { "kind": "Name", "value": "canceled_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_phone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "consumer_at_pickup" } }, { "kind": "Field", "name": { "kind": "Name", "value": "ready_by" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_successful" } }, { "kind": "Field", "name": { "kind": "Name", "value": "reference_num" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_stock" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_vin" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_make" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_model" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_year" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_color" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vehicle_odometer" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdBy" } }, { "kind": "Field", "name": { "kind": "Name", "value": "payer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_workflow_data" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_workflow_data" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "workflowset" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "lane" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }] } }] } }] } }] } }] } }] };

// src/runtime/products/valet/tabs/useOrdersExport.ts
function useOrdersExport({
  windowStartIso,
  windowEndIso,
  isAdminViewer,
  referenceLabel,
  integratorExport
}) {
  const [exportDialogOpen, setExportDialogOpen] = useState12(false);
  const [fetchExportRows] = useLazyQuery(ScenarioOrdersExportDocument, { fetchPolicy: "no-cache" });
  const openExportDialog = useCallback7(() => setExportDialogOpen(true), []);
  const closeExportDialog = useCallback7(() => setExportDialogOpen(false), []);
  const runExport = useCallback7(
    async (scenarioIds) => {
      try {
        const { data } = await fetchExportRows({ variables: { scenarioIds } });
        const rows = buildScenarioOrdersCsvRows({
          scenarios: data?.scenarios ?? [],
          orderedScenarioIds: scenarioIds,
          isAdminViewer,
          referenceLabel,
          integratorExport
        });
        if (!rows.length) {
          toast.error("Failed to generate CSV - No moves found!", { toastId: "export-csv" });
          return;
        }
        new ExportToCsv({
          filename: resolveScenarioCsvFilename(windowStartIso, windowEndIso),
          useKeysAsHeaders: true
        }).generateCsv(rows);
        toast.success("Generated CSV!", { autoClose: 2e3, toastId: "export-csv" });
      } catch (error) {
        toast.error("Failed to generate CSV!", { toastId: "export-csv" });
        console.error("Failed to generate CSV:", error);
      }
    },
    [fetchExportRows, isAdminViewer, referenceLabel, integratorExport, windowStartIso, windowEndIso]
  );
  return { exportDialogOpen, openExportDialog, closeExportDialog, runExport };
}

// src/runtime/products/valet/tabs/OrdersTabActions.tsx
import { jsx as jsx69 } from "react/jsx-runtime";
var OrdersTabActions = ({
  currentPageScenarioIds,
  filteredScenarioIds,
  currentPageMoveCount,
  filteredMoveCount,
  windowStartIso,
  windowEndIso,
  isAdminViewer,
  referenceLabel,
  integratorExport,
  refresh,
  openAssignmentsReport
}) => {
  const { exportDialogOpen, openExportDialog, closeExportDialog, runExport } = useOrdersExport({
    windowStartIso,
    windowEndIso,
    isAdminViewer,
    referenceLabel,
    integratorExport
  });
  const nothingToExport = filteredScenarioIds.length === 0;
  const tabActions = useMemo14(
    () => ordersTabContribution({ openExport: openExportDialog, nothingToExport, refresh, openAssignmentsReport }),
    [openExportDialog, nothingToExport, refresh, openAssignmentsReport]
  );
  const { registerTabActions } = useOrderIndex();
  useEffect9(() => {
    if (!registerTabActions) return void 0;
    registerTabActions(tabActions);
    return () => registerTabActions(null);
  }, [registerTabActions, tabActions]);
  return /* @__PURE__ */ jsx69(
    OrderExportDialog_default,
    {
      open: exportDialogOpen,
      onClose: closeExportDialog,
      totalCount: filteredMoveCount,
      currentPageCount: currentPageMoveCount,
      onExportAll: () => runExport(filteredScenarioIds),
      onExportCurrentPage: () => void runExport(currentPageScenarioIds)
    }
  );
};
var OrdersTabActions_default = OrdersTabActions;

// src/runtime/products/valet/tabs/OrdersTab.tsx
import { jsx as jsx70, jsxs as jsxs48 } from "react/jsx-runtime";
var PRODUCTS = ["moves"];
var PLUGINS = resolveOrderPlugins(PRODUCTS);
var [valet] = PLUGINS;
var RENDERERS = resolveOrderRenderers(PRODUCTS);
var useOrdersStore = createOrderIndexStore({
  storageKey: "scenario-dashboard",
  defaults: resolveStoreDefaults(valet.resolve({ rooftop: NO_ROOFTOP_FACTS, isAdminViewer: false, referenceNumLabel: null }))
});
var ValetOrdersTab = ({ presentation }) => {
  const { configLoaded, platform } = useOrderIndex();
  if (!configLoaded) return /* @__PURE__ */ jsx70(LinearProgress, {});
  return /* @__PURE__ */ jsx70(OrderIndexPlatformProvider, { platform, children: /* @__PURE__ */ jsx70(OrderRendererRegistryProvider, { renderers: RENDERERS, children: /* @__PURE__ */ jsx70(OrdersTabContent, { presentation }) }) });
};
var OrdersTabContent = ({ presentation }) => {
  const config = useOrderIndex();
  const resolvedPresentation = useMemo15(() => resolvePresentation(presentation), [presentation]);
  const {
    filters,
    dateRange,
    metric,
    sortBy,
    sortOrder,
    groupBy,
    page,
    pageSize,
    viewMode,
    cardPreferences,
    rowPreferences,
    searchClearSignal,
    setFilters,
    setDateRange,
    resetDateRange,
    resetFilters,
    setMetric,
    resetMetric,
    setSorting,
    setGroupBy,
    setPage,
    setPageSize,
    setViewMode,
    setCardPreference,
    setRowPreference
  } = useOrdersStore();
  const nowMs = useOrderClockTick();
  const theme = useTheme2();
  const viewportCompact = useMediaQuery(theme.breakpoints.down("md"));
  const compact = resolvedPresentation.compact ?? viewportCompact;
  const { rooftop, referenceNumLabel } = config;
  const isAdminViewer = config.viewer.isAdminViewer;
  const rooftops = useMemo15(() => [rooftop], [rooftop]);
  const writeControlsAllowed = config.writeControlsAllowed?.moves;
  const resolved = useMemo15(() => valet.resolve({ rooftop, isAdminViewer, referenceNumLabel }), [rooftop, isAdminViewer, referenceNumLabel]);
  const isSelfManaged = resolved.operationalModel === "self-managed";
  const offerStatus = resolveActiveOfferStatus(filters.offerStatus, isSelfManaged);
  const activeFilters = useMemo15(() => offerStatus === filters.offerStatus ? filters : { ...filters, offerStatus }, [filters, offerStatus]);
  const metricDeepLink = config.metricDeepLink;
  const deepLinkedMetric = resolveMetricDeepLink(
    metricDeepLink?.metric ?? null,
    resolved.metricTiles.filter((tile) => isSelfManaged || !tile.saasOnly).map((tile) => tile.key)
  );
  useEffect10(() => {
    if (!metricDeepLink || metricDeepLink.metric === null) return;
    if (deepLinkedMetric) {
      setMetric(deepLinkedMetric);
      setDateRange({ preset: "today", startDate: "", endDate: "" });
    }
    metricDeepLink.clear();
  }, [metricDeepLink, deepLinkedMetric, setMetric, setDateRange]);
  const window = useOrderWindow(dateRange);
  const state = useMemo15(
    () => ({ metric, sortBy, sortOrder, groupBy, page, pageSize, viewMode, offerStatus }),
    [metric, sortBy, sortOrder, groupBy, page, pageSize, viewMode, offerStatus]
  );
  const {
    feed,
    observers,
    filteredIds,
    sortBy: effectiveSortBy,
    groupBy: effectiveGroupBy,
    availableGroupOptions,
    refetch
  } = useOrderFeed({ plugins: PLUGINS, window, rooftops, includePayer: true, nowMs, extras: config.adaptExtras, filters: activeFilters, state, resolved, isAdminViewer });
  const pools = useValetFilterPools({ customerId: rooftop.id, window, filters: activeFilters });
  const preferences = useMemo15(
    () => ({
      card: resolveEffectiveDisplayPreferences({ preferences: cardPreferences, definitions: resolved.displayPreferences, isAdminViewer }),
      row: resolveEffectiveDisplayPreferences({ preferences: rowPreferences, definitions: resolved.displayPreferences, isAdminViewer })
    }),
    [cardPreferences, rowPreferences, resolved, isAdminViewer]
  );
  const handlers = useMemo15(
    () => ({
      onSearchChange: (search) => setFilters({ search }),
      onDateRangePresetChange: (preset) => setDateRange({ preset }),
      onDateRangeCustomChange: (startDate, endDate) => setDateRange({ startDate: startDate ?? "", endDate: endDate ?? "" }),
      onMetricChange: setMetric,
      onSortingChange: setSorting,
      onGroupByChange: setGroupBy,
      onViewModeChange: setViewMode,
      onPreferenceToggle: (surface, key) => surface === "card" ? setCardPreference(key, !cardPreferences[key]) : setRowPreference(key, !rowPreferences[key]),
      onFilterChange: (field, values) => setFilters({ [field]: values }),
      onClearFilter: (key) => {
        const removal = resolveChipRemoval(key);
        if (removal.kind === "dateRange") resetDateRange();
        else if (removal.kind === "search") setFilters({ search: "" });
        else if (removal.kind === "metric") resetMetric();
        else if (removal.kind === "filter") setFilters({ [removal.field]: filters[removal.field].filter((value) => value !== removal.value) });
      },
      onClearAll: resetFilters
    }),
    [filters, cardPreferences, rowPreferences, setFilters, setDateRange, resetDateRange, resetFilters, setMetric, resetMetric, setSorting, setGroupBy, setViewMode, setCardPreference, setRowPreference]
  );
  const disabledControls = useMemo15(
    () => [...pools.spansMultipleCustomers ? [] : ["market"], ...resolvedPresentation.disabledControls],
    [pools.spansMultipleCustomers, resolvedPresentation.disabledControls]
  );
  const controls = useMemo15(
    () => resolveControlsModel({
      resolved,
      overrides: { disable: disabledControls },
      viewer: { isAdminViewer, isSelfManaged },
      state: { filters: activeFilters, dateRange, metric, sortBy: effectiveSortBy, sortOrder, groupBy: effectiveGroupBy, viewMode, cardPreferences, rowPreferences, searchClearSignal },
      options: {
        createdBy: pools.createdBy,
        market: pools.market,
        // Live counts over the metric-filtered board, so an option reads "Accepted (3)".
        offerStatus: ORDER_OFFER_STATUS_OPTIONS.map((option) => ({ ...option, count: feed.offerStatusCounts?.[option.value] ?? 0 })),
        groupOptions: availableGroupOptions
      },
      counts: feed.metricCounts,
      handlers
    }),
    [resolved, pools, disabledControls, isAdminViewer, isSelfManaged, activeFilters, dateRange, metric, effectiveSortBy, sortOrder, effectiveGroupBy, viewMode, cardPreferences, rowPreferences, searchClearSignal, availableGroupOptions, feed.metricCounts, feed.offerStatusCounts, handlers]
  );
  const { resolveCardActions, isAssigning, onOpenPhotos } = useValetCardActions(resolvedPresentation);
  const exportInputs = useMemo15(() => {
    const entries = feed.entries.flatMap((entry) => entry.kind === "order" ? [{ entry: entry.model.source.entry }] : []);
    const currentPageScenarioIds = feed.pagedEntries.flatMap((entry) => entry.kind === "order" ? [entry.model.id] : []);
    const filteredScenarioIds = filteredIds.moves ?? [];
    return {
      currentPageScenarioIds,
      filteredScenarioIds,
      currentPageMoveCount: countDriveMovesForScenarioIds(entries, currentPageScenarioIds),
      filteredMoveCount: countDriveMovesForScenarioIds(entries, filteredScenarioIds)
    };
  }, [feed.entries, feed.pagedEntries, filteredIds]);
  return /* @__PURE__ */ jsxs48(Box50, { sx: PAGE_SX, children: [
    observers,
    resolvedPresentation.readOnly ? null : /* @__PURE__ */ jsx70(
      OrdersTabActions_default,
      {
        currentPageScenarioIds: exportInputs.currentPageScenarioIds,
        filteredScenarioIds: exportInputs.filteredScenarioIds,
        currentPageMoveCount: exportInputs.currentPageMoveCount,
        filteredMoveCount: exportInputs.filteredMoveCount,
        windowStartIso: window.startIso,
        windowEndIso: window.endIso,
        isAdminViewer,
        referenceLabel: resolved.copy.referenceLabel,
        integratorExport: resolvedPresentation.integratorExport,
        refresh: resolvedPresentation.refresh ? refetch : void 0,
        openAssignmentsReport: isSelfManaged ? config.openAssignmentsReport : void 0
      }
    ),
    /* @__PURE__ */ jsx70(
      OrderIndex_default,
      {
        feed,
        controls,
        viewMode,
        resolved,
        preferences,
        nowMs,
        pageSize,
        pageSizes: valet.adapter.pageSizes,
        resolveCardActions,
        writeControlsAllowed,
        isAssigning,
        onOpenPhotos,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
        onRetry: () => void refetch(),
        compact,
        collapsibleControls: resolvedPresentation.collapsibleControls
      }
    )
  ] });
};
var OrdersTab_default = ValetOrdersTab;

// src/runtime/products/valet/tabs/OrdersTabErrorBoundary.tsx
import { Box as Box51, Button as Button7, Typography as Typography25 } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import { jsx as jsx71, jsxs as jsxs49 } from "react/jsx-runtime";
var OrdersTabErrorFallback = ({ resetErrorBoundary }) => /* @__PURE__ */ jsxs49(
  Box51,
  {
    role: "alert",
    sx: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: 2,
      py: 10,
      px: 3
    },
    children: [
      /* @__PURE__ */ jsx71(Typography25, { variant: "h6", color: "error", children: "Your orders couldn't be displayed" }),
      /* @__PURE__ */ jsx71(Typography25, { variant: "body2", color: "text.secondary", sx: { maxWidth: 420 }, children: "Something went wrong loading your orders. Try again, or narrow the date range to skip the order that's causing trouble." }),
      /* @__PURE__ */ jsx71(Button7, { variant: "outlined", onClick: resetErrorBoundary, sx: { mt: 1 }, children: "Try again" })
    ]
  }
);
var ValetOrdersTabErrorBoundary = ({ children }) => {
  const { rooftop } = useOrderIndex();
  return /* @__PURE__ */ jsx71(
    ErrorBoundary,
    {
      FallbackComponent: OrdersTabErrorFallback,
      resetKeys: [rooftop.id],
      onError: (error, info) => {
        console.error("Orders tab render error:", error, info);
      },
      children
    }
  );
};
var OrdersTabErrorBoundary_default = ValetOrdersTabErrorBoundary;

// src/runtime/products/valet/OrderCardByOrderId.tsx
import { useMemo as useMemo16 } from "react";
import { useQuery as useQuery4 } from "@apollo/client";
import { jsx as jsx72 } from "react/jsx-runtime";
var RENDERERS2 = resolveOrderRenderers(["moves"]);
var capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
var OrderCardByOrderIdContent = ({ orderId, rooftop, displayPreferences }) => {
  const config = useOrderIndex();
  const platform = config.platform;
  const nowMs = useOrderClockTick();
  const isAdminViewer = config.viewer.isAdminViewer;
  const referenceNumLabel = config.referenceNumLabel;
  const resolved = useMemo16(() => resolveValetOrders({ rooftop, isAdminViewer, referenceNumLabel }), [rooftop, isAdminViewer, referenceNumLabel]);
  const preferences = useMemo16(
    () => resolveEffectiveDisplayPreferences({
      preferences: displayPreferences ?? resolveStoreDefaults(resolved).cardPreferences,
      definitions: resolved.displayPreferences,
      isAdminViewer
    }),
    [displayPreferences, resolved, isAdminViewer]
  );
  const variables = useMemo16(() => buildValetStandaloneVariables(orderId), [orderId]);
  const { data, loading, error } = useQuery4(ValetOrderStandaloneDocument, { variables, fetchPolicy: "cache-and-network" });
  const entry = useMemo16(() => adaptValetStandalone(data, { rooftops: [rooftop], nowMs }), [data, rooftop, nowMs]);
  const { resolveCardActions, isAssigning, onOpenPhotos } = config.interaction;
  const card = useMemo16(() => {
    if (!entry || entry.kind !== "order") return null;
    const view = resolved.toView(entry.model, { platform, nowMs });
    return view ? { view, props: resolved.toCardProps(view, preferences) } : null;
  }, [entry, resolved, platform, nowMs, preferences]);
  if (loading && !card) return /* @__PURE__ */ jsx72(OrderCardSkeleton_default, { variant: "card" });
  if (error) return /* @__PURE__ */ jsx72(OrderCardError_default, { ids: entry ? valetEntryMoveIds(entry) : [] });
  if (entry && entry.kind === "error") return /* @__PURE__ */ jsx72(OrderCardError_default, { ids: entry.ids });
  if (!entry || !card) return /* @__PURE__ */ jsx72(OrderCardEmpty_default, { displayName: capitalize(resolved.copy.unit.singular), ids: entry ? valetEntryMoveIds(entry) : [] });
  return /* @__PURE__ */ jsx72(OrderCard_default, { ...card.props, actions: resolveCardActions?.(entry.model, card.view), isAssigning: isAssigning(entry.model), onOpenPhotos });
};
var OrderCardByOrderId = (props) => {
  const { platform } = useOrderIndex();
  return /* @__PURE__ */ jsx72(OrderIndexPlatformProvider, { platform, children: /* @__PURE__ */ jsx72(OrderRendererRegistryProvider, { renderers: RENDERERS2, children: /* @__PURE__ */ jsx72(OrderCardByOrderIdContent, { ...props }) }) });
};
var OrderCardByOrderId_default = OrderCardByOrderId;

// src/runtime/products/valet/metricDeepLinkParam.ts
var METRIC_PARAM = "metric";
function readMetricParam(search) {
  return new URLSearchParams(search).get(METRIC_PARAM);
}
function withoutMetricParam(search) {
  const params = new URLSearchParams(search);
  params.delete(METRIC_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

// src/runtime/products/valet/counts/useValetCounts.ts
import { useMemo as useMemo17 } from "react";
import { useQuery as useQuery5 } from "@apollo/client";

// src/runtime/counts/types.ts
var ORDER_COUNT_BUCKET_KEYS = ["upcoming", "active", "completed", "cancelled"];
var EMPTY_ORDER_COUNT_BUCKETS = { upcoming: 0, active: 0, completed: 0, cancelled: 0, total: 0 };

// src/runtime/products/valet/counts/tallyValetCounts.ts
var isBucket = (metric) => ORDER_COUNT_BUCKET_KEYS.includes(metric);
var emptyBuckets = () => ({ ...EMPTY_ORDER_COUNT_BUCKETS });
var add = (buckets, key) => {
  buckets[key] += 1;
  buckets.total += 1;
};
var tallyValetCounts = (entries, capReached, rooftopIds) => {
  const buckets = emptyBuckets();
  const byCustomer = Object.fromEntries(rooftopIds.map((id) => [id, emptyBuckets()]));
  let unconfirmed = 0;
  for (const entry of entries) {
    if (entry.kind !== "order") continue;
    const { metric, customerId, flags } = entry.model;
    if (flags.isUnconfirmed) unconfirmed += 1;
    if (!isBucket(metric)) continue;
    add(buckets, metric);
    if (customerId !== null && byCustomer[customerId]) add(byCustomer[customerId], metric);
  }
  return { product: "moves", unit: "orders", buckets, unconfirmed, truncated: capReached, byCustomer };
};

// src/runtime/products/valet/counts/useValetCounts.ts
var usableCustomerIds = (customerIds) => customerIds.filter((id) => !!id);
var useValetCounts = ({
  customerIds,
  window,
  includePayer = false,
  breakdown = false,
  skip = false,
  pollOptions,
  rooftops
}) => {
  const ids = usableCustomerIds(customerIds);
  const idsKey = ids.join(",");
  const variables = useMemo17(
    () => (
      // The adapter types its variables itself; the document's generic carries the schema's stubbed bool_exp.
      valetOrderAdapter.buildIndexVariables({
        customerIds: ids,
        includePayer,
        window,
        filters: EMPTY_ORDER_FILTER_STATE,
        limit: valetOrderAdapter.indexCap + 1
      })
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids' content, not the array, is the input
    [idsKey, includePayer, window?.startIso, window?.endIso]
  );
  const run = !skip && ids.length > 0;
  const { data, loading, error, refetch } = useQuery5(ValetOrderIndexDocument, {
    variables,
    skip: !run,
    fetchPolicy: "cache-and-network",
    ...pollOptions
  });
  const counts = useMemo17(() => {
    if (!data) return void 0;
    const { entries, capReached } = valetOrderAdapter.adaptIndex(data, { rooftops, nowMs: Date.now() });
    const tallied = tallyValetCounts(entries, capReached, rooftops.map((rooftop) => rooftop.id));
    return breakdown ? tallied : { ...tallied, byCustomer: void 0 };
  }, [data, rooftops, breakdown]);
  return {
    counts,
    loading: loading && !data,
    error,
    // A skipped query's refetch would run it network-only, so only a running query is refetched.
    refetch: () => {
      if (run) void refetch();
    }
  };
};
export {
  CELL_EMPTY,
  COMPACT_REGION_WIDTH,
  COURIER_STEPS,
  COURIER_VENDOR,
  CellStack,
  CellText,
  DEFAULT_ORDER_OPERATIONAL_MODEL,
  EMPTY_ORDER_COUNT_BUCKETS,
  EMPTY_ORDER_FILTER_STATE,
  GUEST_RIDE_ACTION,
  GUEST_RIDE_ACTIVE_STATUSES,
  GUEST_RIDE_DELETED_STATUSES,
  GUEST_RIDE_HISTORY_STATUSES,
  GUEST_RIDE_RENDERER_KEY,
  GUEST_RIDE_STATUS,
  GUEST_RIDE_UPCOMING_STATUSES,
  GuestRideStreamDocument,
  INERT_ORDER_INTERACTION,
  MOBILE_SERVICE_CARD_STOP_CAP,
  MOBILE_SERVICE_DISPLAY_PREFERENCES,
  MOBILE_SERVICE_ORDER_STATUS,
  MOBILE_SERVICE_RENDERER_KEY,
  MOBILE_SERVICE_ROUTE_CHAIN_CAP,
  MOBILE_SERVICE_SHOW_FLEET_VEHICLES,
  MOBILE_SERVICE_STOP_STATUS,
  MOBILITY_RUN_ACTIVE_STATUSES,
  MOBILITY_RUN_STATUS,
  MOBILITY_RUN_TILE_STATUSES,
  MOBILITY_RUN_UPCOMING_STATUSES,
  NO_ROOFTOP_FACTS,
  ORDER_COUNT_BUCKET_KEYS,
  ORDER_GROUP_NONE,
  ORDER_OFFER_STATUS_LABELS,
  ORDER_OFFER_STATUS_OPTIONS,
  ORDER_PHASE_ORDER,
  ORDER_PHASE_PRECEDENCE,
  ORDER_ROW_MIN_HEIGHT,
  ORDER_STATUS_TOKEN,
  OrderActionButton,
  OrderActionButtons_default as OrderActionButtons,
  OrderActionMenu_default as OrderActionMenu,
  OrderActiveFilterChips_default as OrderActiveFilterChips,
  OrderCard_default as OrderCard,
  OrderCardByOrderId_default as OrderCardByOrderId,
  OrderCardEmpty_default as OrderCardEmpty,
  OrderCardError_default as OrderCardError,
  OrderCardHeaderIdentity_default as OrderCardHeaderIdentity,
  OrderCardItems,
  OrderCardSkeleton_default as OrderCardSkeleton,
  OrderDateRangeStrip_default as OrderDateRangeStrip,
  OrderDisplayPreferencesMenu_default as OrderDisplayPreferencesMenu,
  OrderDriverFilter_default as OrderDriverFilter,
  OrderDriverPill_default as OrderDriverPill,
  OrderFilterMultiselect_default as OrderFilterMultiselect,
  OrderGroupControl_default as OrderGroupControl,
  OrderIndex_default as OrderIndex,
  OrderIndexControls_default as OrderIndexControls,
  OrderIndexEmpty_default as OrderIndexEmpty,
  OrderIndexGrid_default as OrderIndexGrid,
  OrderIndexPagination_default as OrderIndexPagination,
  OrderIndexPlatformProvider,
  OrderIndexProvider,
  OrderIndexTable_default as OrderIndexTable,
  OrderInfoBlock_default as OrderInfoBlock,
  OrderItemStopBase_default as OrderItemStopBase,
  OrderItemStopOverflow_default as OrderItemStopOverflow,
  OrderMetricTiles_default as OrderMetricTiles,
  OrderMilestone_default as OrderMilestone,
  OrderPhotoTile_default as OrderPhotoTile,
  OrderProductFilter_default as OrderProductFilter,
  OrderProviderFilter_default as OrderProviderFilter,
  OrderRendererRegistryProvider,
  OrderRouteChain_default as OrderRouteChain,
  OrderRow_default as OrderRow,
  OrderRowError_default as OrderRowError,
  OrderRowErrorCard,
  OrderRowMobileCard_default as OrderRowMobileCard,
  OrderRowMobileCardSkeleton_default as OrderRowMobileCardSkeleton,
  OrderSearch_default as OrderSearch,
  OrderSectionHeader_default as OrderSectionHeader,
  OrderSortControl_default as OrderSortControl,
  OrderStatusBar_default as OrderStatusBar,
  OrderTooltip_default as OrderTooltip,
  OrderTracker_default as OrderTracker,
  OrderViewModeToggle_default as OrderViewModeToggle,
  PARTS_DELIVERY_CARD_STOP_CAP,
  PARTS_DELIVERY_DISPLAY_PREFERENCES,
  PARTS_DELIVERY_RENDERER_KEY,
  PARTS_DELIVERY_ROUTE_CHAIN_CAP,
  PARTS_DELIVERY_SHOW_ORDERS,
  PARTS_ORDER_OUTCOME,
  PARTS_ORDER_STATUS,
  PARTS_RUN_BUCKETS,
  PARTS_RUN_BUCKET_WHERE,
  PARTS_STOP_STATUS,
  PLACEHOLDER_PHOTO,
  PORTAL_PRESENTATION,
  RIDE_PROVIDER,
  SHARED_RENDERERS,
  STOP_ROW_GEOMETRY,
  ScenarioOrdersExportDocument,
  VALET_DATE_RANGE_PRESETS,
  VALET_INDEX_CAP,
  VALET_METRIC_TILES,
  VALET_PAGE_POLL_INTERVAL_MS,
  VALET_PAGE_SIZES,
  VALET_SEARCH_FIELDS,
  VALET_SORT_OPTIONS,
  ValetFilterPoolsDocument,
  ValetLegFieldsFragmentDoc,
  ValetMovesStreamDocument,
  ValetOrderIndexDocument,
  ValetOrderPageDocument,
  ValetOrderStandaloneDocument,
  OrdersTab_default as ValetOrdersTab,
  OrdersTabErrorBoundary_default as ValetOrdersTabErrorBoundary,
  ValetStreamRecognitionFragmentDoc,
  adaptIndexRows,
  adaptScenarioOrderIndex,
  adaptScenarioOrderPage,
  adaptScenarioOrderStandalone,
  adaptValetStandalone,
  anchorOf,
  buildActiveChips,
  buildCarouselImages,
  buildGroupedComparator,
  buildGuestRideActionDescriptors,
  buildOrderComparator,
  buildOrderWindow,
  buildOverflowLabel,
  buildPageSections,
  buildScenarioOrdersCsvRows,
  buildValetActionDescriptors,
  buildValetScope,
  buildValetStandaloneVariables,
  classifyStreamBatch,
  collectEntryMoveIds,
  compareKeys,
  compareNullness,
  composeCombinedOrderProducts,
  composeRenderers,
  composeResolvedProducts,
  countDriveMovesForScenarioIds,
  countGroupSections,
  countMetrics,
  countOfferStatuses,
  createOrderIndexPlatform,
  createOrderIndexStore,
  createRenderValueCache,
  createTrailingDebounce,
  defaultMobileServiceCardPreferences,
  defaultPartsDeliveryCardPreferences,
  deriveCreatorOptions,
  deriveFeed,
  deriveMarketOptions,
  entryMatchesTile,
  filterByMetric,
  filterByOfferStatus,
  fixturePlatform,
  formatClockTime2 as formatClockTime,
  formatCombinedStatus,
  formatDateWindowLabel,
  formatMoveType,
  formatPhoneNumber2 as formatPhoneNumber,
  formatStatusWord,
  formatVehicleLine2 as formatVehicleLine,
  getPrivateStepIds,
  getWorkflowData,
  guestRideOrderAdapter,
  guestRidePlugin,
  guestRidesColumns,
  guestRidesRenderers,
  isAdminViewerRole,
  isDriveLeg,
  isEmptyFilterState,
  isErrorEntry,
  isLivePhase,
  isMobilityRunLive,
  isOrderAssignmentSettled,
  isOrderEntry,
  isResolvedOrderEntry,
  isRideAtOrPastPickup,
  keyOf,
  mapAssignmentToOfferFilterKey,
  mergeAdapterResults,
  metricForPhase,
  mobileServiceColumns,
  mobileServiceOrderAdapter,
  mobileServicePlugin,
  mobileServiceRenderers,
  orderKey,
  partsDeliveryColumns,
  partsDeliveryRenderers,
  partsRunPlugin,
  poolSpansMultipleCustomers,
  portalOrderFormatters,
  readMetricParam,
  resequenceHydrated,
  resolveActiveOfferStatus,
  resolveAssignmentDetail,
  resolveAvailableGroupOptions,
  resolveChipRemoval,
  resolveControlsModel,
  resolveDateWindowFromPreset,
  resolveEffectiveDateWindow,
  resolveEffectiveDisplayPreferences,
  resolveEffectiveGroupBy,
  resolveEffectiveSortBy,
  resolveEnabledPieces,
  resolveGroupSection,
  resolveMetricDeepLink,
  resolveMobileServiceOrders,
  resolveMobilityRunBadge,
  resolveMobilityRunChip,
  resolveMobilityRunPhase,
  resolveMobilityRunStatusToken,
  resolveMoveMilestoneTime,
  resolveOrderBadge2 as resolveOrderBadge,
  resolveOrderDeliveryModel,
  resolveOrderIndexRegions,
  resolveOrderPlugins,
  resolveOrderRenderers,
  resolveOrderStatusColor,
  resolveOrderStatusToken,
  resolveOrderTokens,
  resolveOutcomeBadge,
  resolvePartsRunBucket,
  resolveStopBadge2 as resolvePartsStopBadge,
  resolveStopMarkerState2 as resolvePartsStopMarkerState,
  resolvePresentation,
  resolveProviderLabel,
  resolveRideBadge,
  resolveRidePhase2 as resolveRidePhase,
  resolveRideStage,
  resolveRideStatusToken,
  resolveRooftopTimezone,
  resolveScenarioConfig,
  resolveScenarioCsvFilename,
  resolveStopBadge,
  resolveStopMarkerState,
  resolveStopMarkerTone,
  resolveStoreDefaults,
  resolveValetOrders,
  resolveVendorLabel,
  resolveViewMode,
  resolveViewModes,
  resolveVisibleSortOptions,
  reusePagedEntry,
  sliceToPage,
  sortPhotosByStandardOrder2 as sortPhotosByStandardOrder,
  stopRowTextInset,
  tallyValetCounts,
  toContribution,
  toGuestRideCardProps,
  toMobileServiceCardProps,
  toOrderCardActions,
  toOrderOperationalModel,
  toOrderPhase,
  toOrderRooftopFacts,
  toPartsDeliveryCardProps,
  toValetCardProps,
  useCompactRegion,
  useDebouncedCallback,
  useGuestRideStream,
  useOrderClockTick,
  useOrderFeed,
  useOrderFeedIndex,
  useOrderFeedPage,
  useOrderIndex,
  useOrderIndexPlatform,
  useOrderLive,
  useOrderRenderers,
  useOrderTokens,
  useOrderWindow,
  useValetCardActions,
  useValetCounts,
  useValetFilterPools,
  useValetOrderStream,
  useOrdersStore as useValetOrdersStore,
  valetEntryMoveIds,
  valetOrderAdapter,
  valetPlugin,
  valetQueryFixtures_exports as valetQueryFixtures,
  valetRenderers,
  windowStops,
  withoutMetricParam
};
//# sourceMappingURL=index.mjs.map